import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { useCallback } from "react";
import { MentionsInput, Mention } from "react-mentions";
import ChatBox from "../sales/orders/orders-single/order-components/orderProgressBar/ChatBox";
import { useParams, useHistory } from "react-router-dom";
import {
  appendProductionBoardFilters,
  isAggregateProductionRoute,
} from "./productionBoardQuery";
import {
  createProductionPollingController,
  getProductionPollingLanes,
} from "./productionPolling";
import SmartProofPreview from "../../../components/proofs/SmartProofPreview";
import PdfViewerDialog from "../../../components/proofs/PdfViewerDialog";
import {
  areCompatibleProductionBatchCards,
  getProductionBatchGroupId,
  normalizeProductionBatchFields,
} from "./productionBatchHelpers";
import { getProductionDetailExternalKey, getProductionStepId } from "./productionCardIdentity";
import { createProductionRequestGate } from "./productionRequestGate";
import { createProductionLaneRequestRegistry } from "./productionLaneRequests";
import { createProductionBoardLoadCoordinator } from "./productionBoardLoadCoordinator";
import {
  createProductionMutationRefreshQueue, getProductionScheduleKey,
  requirePersistedSchedule, reconcileProductionStep, replaceProductionLane,
  createProductionPendingMutations, protectPendingProductionColumns, productionMutationError,
  canDragProductionCard, getProductionHoldReturnLane,
  captureProductionCardRollback, tagProductionCardMutation, rollbackProductionCard, ownsProductionCardMutation, canApplyProductionCardMutation,
} from "./productionMutationLifecycle";
import { createProductionRequestDiagnostics } from "./productionRequestDiagnostics";
import { formatProductionBusinessDate } from "./productionBusinessDates";
import { getProductionArchiveViewState } from "./productionArchiveView";
import ProductionLaneList, { PRODUCTION_PAGE_SIZE } from "./ProductionLaneList";
import {
  getProductionLanePageState,
  mergeProductionLaneCards,
  fetchProductionLaneRefresh,
} from "./productionLanePagination";
import { appendProductionInstanceScope } from "./productionRequestScope";
import ProductionProjectLink from "./ProductionProjectLink";
import { shouldShowFirmBadge } from "./productionFirmBadge";
import { getProductionRepresentativeLabel } from "./productionRepresentativeLabel";
import {
  createLaneRequestStateMap,
  getLanePagination,
  getLaneTotal,
  runConcurrentLaneRequests,
} from "./productionLaneLoading";

import Swal from "sweetalert2";
import { borderRadius } from "@mui/system";
window.Swal = Swal;

// import ChatBox from "../sales/customers/ChatBox"

/**
 * EliteKanbanBoardV2 (V2.5 + V2.7 features, original V2.5 DnD kept)
 * - Keeps simple, deterministic DnD from V2.5 (onDragEnter per card, insert-at-top cross-lane)
 * - Adds: batching (split/join), partial moves, job card "this/total" + Batches table
 * - Blocks Planning → Paused (QC → Paused allowed)
 * - Batch badge counts siblings across ALL lanes
 * - Reason optional preserved
 * - NEW: SweetAlert notifications, MoveGate on Planning→Scheduled, clearer inputs, proper revert + error
 */

// ========== QC LANE VISIBILITY CONTROL ==========
const SHOW_QC_LANE = false; // Set to true to enable QC lane, false to hide
// ================================================

const productionAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem("accessToken");
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

// Keep the existing request call sites consistent while the Production API is
// authenticated at its router boundary.
const fetch = (input, init = {}) =>
  window.fetch(appendProductionInstanceScope(input, {
    pathname: window.location.pathname,
    apiBase: process.env.REACT_APP_NODEJS_SERVER_URL,
  }), {
    ...init,
    headers: productionAuthHeaders(init.headers || {}),
  });

const productionFetch = fetch;

const productionCardBusinessDateFields = (card = {}) => ({
  inHandsDate: card.inHandsDate ?? card.customerInHandsDate ?? null,
  customerInHandsDate: card.customerInHandsDate ?? card.inHandsDate ?? null,
  decoratorPoInHandsDate: card.decoratorPoInHandsDate || null,
  decoratorPoInHandsDateSource: card.decoratorPoInHandsDateSource || null,
  decoratorPoInHandsDateStatus: card.decoratorPoInHandsDateStatus || null,
  decoratorPoId: card.decoratorPoId ?? null,
});

function normalizeProductionPlannedCard(card, index) {
  return {
    id: card.external_key,
    orderNumber: card.projectNumber,
    projectName: card.projectName,
    productName: card.productName,
    binNumber: card.binNumber || "",
    orderRepName: card.orderRepName || "",
    orderRepSource: card.orderRepSource || null,
    sku: card.skuNumber,
    qtyPlanned: card.qtyRequired,
    qtyRequired: card.qtyRequired,
    external_key: card.external_key,
    department: card.department,
    matrix: card.matrixLabel,
    location: card.processLocation,
    salesOrderStatus: card.salesOrderStatus,
    purchaseOrderStatus: card.purchaseOrderStatus,
    poNumber: card.poNumber,
    purchaseOrderType: card.purchaseOrderType,
    purchaseOrders: Array.isArray(card.purchaseOrders) ? card.purchaseOrders : [],
    relatedPurchaseOrders: Array.isArray(card.relatedPurchaseOrders) ? card.relatedPurchaseOrders : [],
    proofRequired: card.proofRequired,
    proofStatus: card.proofStatus,
    productionProofSummary: card.productionProofSummary,
    allProofsUploaded: card.allProofsUploaded,
    ...productionCardBusinessDateFields(card),
    proofs: Array.isArray(card.proofs) ? card.proofs : [],
    plannedStart: card.plannedStart,
    plannedFinish: card.plannedFinish,
    machine: card.machine,
    customer: card.customer || "Unknown Customer",
    status: "Planned",
    createdAt: card.createdAt,
    updatedAt: card.updatedAt,
    disabled: card.disabled || false,
    cancelled: card.cancelled || card.disabled || false,
    activeForProduction: card.activeForProduction || false,
    orderDbId: card.orderDbId,
    itemId: card.itemId,
    orderItemId: card.itemId,
    productId: card.skuNumber,
    pwo_id: card.pwo_id,
    decorationId: card.decorationId,
    chargeId: card.chargeId,
    holdReason: card.holdReason,
    holdFrom: card.holdFrom,
    rank: index,
    isStep: true,
    lifecycle: [],
  };
}

function normalizeProductionStepCard(card, index) {
  return {
    id: (() => {
      const rawStepId = card?.step_id ?? card?.stepid;
      const stepId = Number(rawStepId);
      return Number.isFinite(stepId) && stepId > 0
        ? String(stepId)
        : card?.external_key || card?.externalkey || null;
    })(),
    step_id: card.step_id,
    orderNumber: card.projectNumber,
    projectName: card.projectName,
    productName: card.productName,
    binNumber: card.binNumber || "",
    orderRepName: card.orderRepName || "",
    orderRepSource: card.orderRepSource || null,
    sku: card.skuNumber,
    qtyPlanned: card.qtyRequired,
    qtyRequired: card.qtyRequired,
    external_key: card.external_key,
    department: card.department,
    matrix: card.matrixLabel,
    location: card.processLocation,
    salesOrderStatus: card.salesOrderStatus,
    purchaseOrderStatus: card.purchaseOrderStatus,
    poNumber: card.poNumber,
    purchaseOrderType: card.purchaseOrderType,
    purchaseOrders: Array.isArray(card.purchaseOrders) ? card.purchaseOrders : [],
    relatedPurchaseOrders: Array.isArray(card.relatedPurchaseOrders) ? card.relatedPurchaseOrders : [],
    proofRequired: card.proofRequired,
    proofStatus: card.proofStatus,
    productionProofSummary: card.productionProofSummary,
    allProofsUploaded: card.allProofsUploaded,
    ...productionCardBusinessDateFields(card),
    proofs: Array.isArray(card.proofs) ? card.proofs : [],
    plannedStart: card.plannedStart,
    plannedFinish: card.plannedFinish,
    machine: card.machine || card.machineName,
    machineName: card.machineName,
    machineCode: card.machineCode,
    isFirm: !!(card.isFirm || card.is_firm),
    customer: card.customerName || "Unknown Customer",
    status: card.status,
    createdAt: card.createdAt,
    updatedAt: card.updatedAt,
    disabled: card.disabled || false,
    cancelled: card.cancelled || card.disabled || false,
    activeForProduction: card.activeForProduction || false,
    orderDbId: card.orderDbId,
    itemId: card.itemId,
    orderItemId: card.itemId,
    productId: card.skuNumber,
    pwo_id: card.pwo_id,
    decorationId: card.decorationId,
    chargeId: card.chargeId,
    holdReason: card.holdReason,
    holdFrom: card.holdFrom,
    batchGroupId: card.batchGroupId,
    batchIndex: card.batchIndex,
    batchLabel: card.batchLabel,
    batchTotal: card.batchTotal,
    rank: card.rank || index,
    isStep: true,
    isFullyCompleted: card.isFullyCompleted || false,
    pendingDepartments: card.pendingDepartments || [],
    isPaused: card.isPaused ?? false,
    runtimeHHMM: card.runtimeHHMM ?? "00:00",
    runtimeMinutes: card.runtimeMinutes ?? 0,
    pauseMinutes: card.pauseMinutes ?? 0,
    startedAt: card.runtime?.startedAt ?? null,
    lifecycle: [],
  };
}

const STATUSES = [
  { key: "Planned", label: "Upcoming Orders" },
  { key: "Scheduled", label: "Scheduled" },
  { key: "In_Production", label: "In Production" },
  // { key: "QC", label: "QC" },
  ...(SHOW_QC_LANE ? [{ key: "QC", label: "QC" }] : []),
  { key: "On_Hold", label: "Paused / Hold" },
  { key: "Completed", label: "Completed" },
];
const STATUS_INDEX = STATUSES.reduce((m, s, i) => ((m[s.key] = i), m), {});

const DATE_FMT = (d) => (d ? new Date(d).toLocaleDateString("en-CA") : "–"); // yyyy-mm-dd
const todayYMD = () => new Date().toISOString().slice(0, 10);
const toISOStart = (ymd) =>
  ymd && ymd.length === 10 ? `${ymd}T00:00:00` : ymd || "";
const toISOEnd = (ymd) =>
  ymd && ymd.length === 10 ? `${ymd}T23:59:59` : ymd || "";

const scheduleDateValue = (value) => {
  if (!value) return "";
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(value));
  const date = new Date(dateOnly ? `${value}T12:00:00` : value);
  if (!Number.isFinite(date.getTime())) return "";
  const local = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  return dateOnly && local !== value ? "" : local;
};

const scheduleTimestamp = (value, finish = false) => {
  if (!scheduleDateValue(value)) return null;
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(String(value))
    ? `${value}T${finish ? "23:59:59.999" : "00:00:00"}` : value);
  return date.toISOString();
};

const validScheduleDateRange = (start, finish) => {
  const from = scheduleTimestamp(start);
  const to = scheduleTimestamp(finish, true);
  return Boolean(from && to && new Date(to) >= new Date(from));
};

const persistedPoStageMessage = (result) => {
  const stages = result?.purchaseOrderStages || [];
  return stages.map(po => po.restorationSkipped
    ? `\nDecorator PO #${po.id} remains ${po.supplierStatus}. ${po.reason || ""}`
    : `\nDecorator PO #${po.id}: ${po.supplierStatus}.`).join("");
};
function daysLate(ymd) {
  if (!ymd) return 0;
  const a = new Date(`${ymd}T00:00:00`);
  const b = new Date(`${todayYMD()}T00:00:00`);
  return Math.max(0, Math.floor((b - a) / 86400000));
}

const EMPTY_DISPLAY = DATE_FMT(null);

function normalizeBinNumber(binNumber) {
  return String(binNumber || "")
    .replace(/Â·/g, "·")
    .replace(/\s+/g, " ")
    .trim();
}

function parseBinNumber(binNumber) {
  const full = normalizeBinNumber(binNumber);
  if (!full) return [];

  return full
    .split(/\s*\|\s*/)
    .map((segment) => {
      const [locationRaw, refsRaw = ""] = segment.split(/\s*·\s*/, 2);
      const location = String(locationRaw || "").trim();
      const refs = refsRaw
        .split(/[,\s]+/)
        .map((ref) => ref.trim())
        .filter(Boolean);

      return {
        full: segment.trim(),
        location,
        refs,
      };
    })
    .filter((part) => part.full || part.location || part.refs.length);
}

function uniqueValues(values) {
  return [
    ...new Set(values.map((value) => String(value || "").trim()).filter(Boolean)),
  ];
}

function firstNonBlankValue(...values) {
  return values.find((value) => {
    if (value === undefined || value === null) return false;
    return String(value).trim() !== "";
  });
}

function formatFullBin(binNumber) {
  return normalizeBinNumber(binNumber) || EMPTY_DISPLAY;
}

function formatCompactBin(binNumber) {
  const parts = parseBinNumber(binNumber);
  if (!parts.length) return EMPTY_DISPLAY;

  const locations = uniqueValues(parts.map((part) => part.location));
  const refs = uniqueValues(parts.flatMap((part) => part.refs));

  if (locations.length === 1) {
    if (!refs.length) return locations[0];
    return `${locations[0]} · ${refs.length} ${refs.length === 1 ? "box" : "boxes"}`;
  }

  if (locations.length > 1) {
    return `${locations.length} locations${refs.length ? ` · ${refs.length} boxes` : ""}`;
  }

  return refs.length
    ? `${refs.length} ${refs.length === 1 ? "box" : "boxes"}`
    : parts[0].full || EMPTY_DISPLAY;
}

function getBinDetailsRows(binNumber) {
  const parts = parseBinNumber(binNumber);
  const full = formatFullBin(binNumber);
  if (!parts.length) {
    return {
      full,
      locationSummary: EMPTY_DISPLAY,
      refSummary: EMPTY_DISPLAY,
    };
  }

  const locations = uniqueValues(parts.map((part) => part.location));
  const refs = uniqueValues(parts.flatMap((part) => part.refs));

  return {
    full,
    locationSummary: locations.length ? locations.join(" | ") : full,
    refSummary: refs.length ? refs.join(", ") : EMPTY_DISPLAY,
  };
}

// SweetAlert wrapper (falls back to native dialogs)
const SA = {
  info(text, title = "Info") {
    const s = window.Swal || window.swal;
    return s?.fire
      ? s.fire({ icon: "info", title, text })
      : Promise.resolve(alert(`${title}: ${text}`));
  },
  error(text, title = "Error") {
    const s = window.Swal || window.swal;
    return s?.fire
      ? s.fire({ icon: "error", title, text })
      : Promise.resolve(alert(`${title}: ${text}`));
  },
  success(text, title = "Success") {
    const s = window.Swal || window.swal;
    return s?.fire
      ? s.fire({ icon: "success", title, text })
      : Promise.resolve(alert(`${title}: ${text}`));
  },
  confirm({
    title = "Are you sure?",
    text = "",
    confirmButtonText = "OK",
    cancelButtonText = "Cancel",
    icon = "question",
  } = {}) {
    const s = window.Swal || window.swal;
    return s?.fire
      ? s
          .fire({
            icon,
            title,
            text,
            showCancelButton: true,
            confirmButtonText,
            cancelButtonText,
          })
          .then((r) => !!r.isConfirmed)
      : Promise.resolve(window.confirm(text || title));
  },
};

// Capacity
const DEFAULT_DAY_CAPACITY_MIN = 480;
const DEFAULT_CARD_MIN = 60;

// Dept inference
const DEPT_MATCHERS = {
  Screening: [/screen ?print/i, /pad ?print/i, /silk/i],
  Embroidery: [/embroider/i],
  "Direct to Film": [/\bDTF\b/i, /direct\s*to\s*film/i],
  Engraving: [/engrave/i, /laser/i],
  Cresting: [/crest/i, /heat ?press/i, /vinyl/i],
  Awards: [/award/i, /trophy/i],
};
const CHARGE_ID_TO_DEPT = {
  15: "Embroidery",
  45: "Screening",
  107: "Direct to Film",
};
function inferDepartmentForDeco(deco) {
  if (!deco) return "";
  if (deco.chargeId != null && CHARGE_ID_TO_DEPT[deco.chargeId])
    return CHARGE_ID_TO_DEPT[deco.chargeId];
  const name = String(deco.chargeName || "");
  for (const [dept, tests] of Object.entries(DEPT_MATCHERS))
    if (tests.some((re) => re.test(name))) return dept;
  return "";
}
const money = (n) => (typeof n === "number" ? n.toFixed(2) : "–");
const clampInt = (v, min = 1, max = 1e9) =>
  Math.max(min, Math.min(max, parseInt(v || 0, 10) || 0));

function departmentToSlug(name = "") {
  const normalized = String(name || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  const overrides = {
    "direct-to-film": "direct-to-film",
    dtf: "direct-to-film",
    "screen-printing": "screening",
    "screen-print": "screening",
    screening: "screening",
    embroidery: "embroidery",
    engraving: "engraving",
    cresting: "cresting",
    awards: "awards",
  };

  return overrides[normalized] || normalized;
}

function resolveProofPreviewPath(proof = {}) {
  return (
    proof.filePath ||
    proof.proofFilePath ||
    proof.fileUrl ||
    proof.url ||
    proof.Image ||
    proof.image ||
    proof.previewUrl ||
    proof.thumbnailUrl ||
    proof.path ||
    proof.FilePath ||
    proof?.proof?.filePath ||
    proof?.proof?.proofFilePath ||
    null
  );
}

function resolveProofPreviewFileName(proof = {}, filePath = "") {
  return (
    proof.fileName ||
    proof.name ||
    proof.originalName ||
    proof.filename ||
    proof.proofFileName ||
    proof.displayName ||
    (filePath ? String(filePath).split("/").pop() : "")
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Job Card — V2.5 base with v2.7 batch qty/total + Batches table
// ────────────────────────────────────────────────────────────────────────────
function JobCardModal({
  open,
  onClose,
  card,
  modalLoading, // ← receive as prop
  detailedCard, // ← receive as prop
  orderMeta,
  orderCosting,
  orderCharges,
  onPatchCard,
  onCancelCard,
  onReinstateCard,
  siblingsAll = [],
  siblingsSameLane = [],
  onOpenSplit,
  onOpenJoin,
  onNavigateDepartmentBoard,
  hasNestedDialog = false,
  mutationPending = false,
  isMutationBlocked = () => false,
}) {
  // Guard transport too: a callback opened before scheduling/completion may
  // still fire after its buttons were disabled. Reads and Close remain usable.
  const mutationBlockedRef = useRef(isMutationBlocked);
  mutationBlockedRef.current = isMutationBlocked;
  const fetch = useCallback((input, init = {}) => {
    if (!["GET", "HEAD"].includes(String(init.method || "GET").toUpperCase()) && mutationBlockedRef.current()) {
      return Promise.reject(new Error("This card has an unconfirmed Production mutation. Check saved status first."));
    }
    return productionFetch(input, init);
  }, []);
  const jobCardRef = useRef(null);
  const closeConfirmationPendingRef = useRef(false);
  const [tab, setTab] = useState("overview");
  const [bom, setBOM] = useState(card?.bom || []);
  const [chat, setChat] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatText, setChatText] = useState("");

  // JobCardModal
  const [auditEvents, setAuditEvents] = useState([]);
  const [bomHeaderId, setBomHeaderId] = useState(null);
  const [editingBomIdx, setEditingBomIdx] = useState(null);
  const [bomBackup, setBomBackup] = useState(null);
  const [bomPreviewOpen, setBomPreviewOpen] = useState(false);
  const [bomPreviewLoading, setBomPreviewLoading] = useState(false);
  const [bomPreviewData, setBomPreviewData] = useState(null);
  const [previewPdf, setPreviewPdf] = useState("");
  const [previewFileName, setPreviewFileName] = useState("");
  const [processes, setProcesses] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [costingLoading, setCostingLoading] = useState(false);
  const [costingError, setCostingError] = useState("");
  const [costingData, setCostingData] = useState(null);

  // ======= Edit State =======
  const [editingProcessIdx, setEditingProcessIdx] = useState(null);
  const [processBackup, setProcessBackup] = useState(null);

  const [auditFilters, setAuditFilters] = useState({
    department: "",
    types: "",
    scope: "full",
    withMeta: false,
  });
  const isArchived = Boolean(card?.isArchived || card?.status === "Archive");
  const isReadOnly = isArchived || mutationPending;
  const showArchivedReadOnlyMessage = () =>
    SA.info(mutationPending ? "This card is waiting for saved Production status. Other cards remain available." :
      "This Production card is completed and archived. Editing is disabled.", mutationPending ? "Saving Production" : "Archived Production");

  const closePreviewPdf = useCallback(() => {
    setPreviewPdf("");
    setPreviewFileName("");
  }, []);

  const openPreviewPdf = useCallback((src, fileName) => {
    setPreviewPdf(src || "");
    setPreviewFileName(fileName || "");
  }, []);

  useEffect(() => {
    if (!open || !card) return undefined;
    let active = true;

    const handleEscape = async (event) => {
      if (event.key !== "Escape" || event.defaultPrevented || event.repeat || event.isComposing) return;
      if (hasNestedDialog || previewPdf || Swal.isVisible()) return;

      const nestedDialogOpen = Array.from(document.querySelectorAll(
        '.MuiModal-root, [role="dialog"], [role="alertdialog"], [role="listbox"], [aria-modal="true"]'
      )).some((element) => element !== jobCardRef.current && element.getClientRects().length > 0);
      if (nestedDialogOpen || event.target?.tagName === "SELECT") return;

      event.preventDefault();
      event.stopPropagation();
      if (bomPreviewOpen) {
        setBomPreviewOpen(false);
        setBomPreviewData(null);
        return;
      }
      if (closeConfirmationPendingRef.current) return;

      const hasUnsavedChanges =
        (editingBomIdx !== null && JSON.stringify(bom?.[editingBomIdx]) !== JSON.stringify(bomBackup)) ||
        (editingProcessIdx !== null && JSON.stringify(processes?.[editingProcessIdx]) !== JSON.stringify(processBackup)) ||
        (bom || []).some((item) => String(item.id).startsWith("bom-")) ||
        (processes || []).some((item) => String(item.id).startsWith("proc-"));

      if (hasUnsavedChanges) {
        closeConfirmationPendingRef.current = true;
        try {
          const discard = await SA.confirm({
            title: "Discard unsaved changes?",
            text: "Unsaved BOM or process changes will be lost when this Job Card closes.",
            confirmButtonText: "Discard changes",
            cancelButtonText: "Keep editing",
            icon: "warning",
          });
          if (!discard || !active) return;
        } finally {
          closeConfirmationPendingRef.current = false;
        }
      }
      if (active) onClose();
    };

    // Capture the modal stack before a nested dialog handles and dismisses Escape.
    document.addEventListener("keydown", handleEscape, true);
    return () => {
      active = false;
      document.removeEventListener("keydown", handleEscape, true);
    };
  }, [open, card, hasNestedDialog, previewPdf, bomPreviewOpen, bom, bomBackup,
    processes, processBackup, editingBomIdx, editingProcessIdx, onClose]);

  const enhancedCard = {
    background: "white",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    height: "fit-content",
    transition: "box-shadow 0.2s ease",
  };

  const cardHeader = {
    padding: "14px 18px",
    background: "#fafafa",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const cardTitle = {
    margin: 0,
    fontSize: "13px",
    fontWeight: 600,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  const cardBadge = {
    padding: "3px 8px",
    background: "#f3f4f6",
    color: "#6b7280",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: 600,
    border: "1px solid #e5e7eb",
  };

  const cardBody = {
    padding: "18px",
  };

  const dataRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "10px 0",
    borderBottom: "1px solid #f9fafb",
  };

  const labelStyle = {
    fontSize: "12px",
    color: "#6b7280",
    fontWeight: 500,
    flex: "0 0 45%",
  };

  const valueStyle = {
    fontSize: "13px",
    color: "#111827",
    fontWeight: 500,
    textAlign: "right",
    flex: "1",
  };

  const sectionDivider = {
    height: "1px",
    background: "#e5e7eb",
    margin: "16px 0",
  };

  const chipStyle = {
    display: "inline-block",
    padding: "4px 10px",
    background: "#f3f4f6",
    color: "#374151",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: 600,
    border: "1px solid #e5e7eb",
  };

  const decorationTableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
  };

  const decorationTableHeaderStyle = {
    background: "#fafafa",
    borderBottom: "2px solid #e5e7eb",
    textAlign: "left",
    padding: "10px 12px",
    fontWeight: 600,
    color: "#374151",
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  const decorationTableCellStyle = {
    padding: "10px 12px",
    borderBottom: "1px solid #f9fafb",
    color: "#111827",
    fontSize: "13px",
  };

  // FIXED: Simple helper function without hooks
  const renderDataRow = (label, value, options = {}) => {
    const { highlight = false, multiline = false, mono = false } = options;

    return (
      <div style={dataRowStyle} key={label}>
        <span style={labelStyle}>{label}</span>
        <span
          style={{
            ...valueStyle,
            fontWeight: highlight ? 600 : 500,
            color: highlight ? "#111827" : "#374151",
            fontFamily: mono ? "monospace" : "inherit",
            whiteSpace: multiline ? "normal" : "nowrap",
            lineHeight: multiline ? "1.5" : "normal",
          }}
        >
          {value}
        </span>
      </div>
    );
  };

  // CSS
  const hoverStyles = `
  .data-row:hover {
    background: #fafafa;
  }
  
  .table-row:hover {
    background: #fafafa !important;
  }
  
  .hover-card:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
`;

  useEffect(() => {
    if (!open) return;

    const ek = card.external_key || card.externalkey;

    if (!ek && auditFilters.scope !== "card") {
      console.log("No external key found");
      return;
    }

    // Build API URL based on filters
    let apiUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/audit?`;
    const params = new URLSearchParams();

    // Determine which identifier to use
    if (auditFilters.scope === "card" && card.step_id) {
      params.append("stepId", card.step_id);
      params.append("includeBatch", "true");
    } else if (ek) {
      params.append("external_key", ek);
      params.append("scope", auditFilters.scope);
    }

    if (auditFilters.department) {
      params.append("department", auditFilters.department);
    }

    if (auditFilters.types) {
      params.append("types", auditFilters.types);
    }

    if (auditFilters.withMeta) {
      params.append("withMeta", "true");
    }

    params.append("limit", "200");

    fetch(`${apiUrl}${params.toString()}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((js) => {
        console.log("Audit response:", js);
        // setAuditEvents(js.events ?? []);

        // })

        const mappedEvents = (js.events ?? []).map((ev) => ({
          id: ev.timestamp || ev.id,
          at: ev.timestamp, // KEY CHANGE: map timestamp to at
          type: ev.type,
          from: ev.from,
          to: ev.to,
          note: ev.note,
          department: ev.department,
          machineName: ev.machineName,
          machineCode: ev.machineCode,
          step_id: ev.step_id,
          seq: ev.seq,
          lane: ev.lane,
          batch_group_id: ev.batch_group_id,
          source: ev.source,
        }));

        setAuditEvents(mappedEvents);
      })
      .catch((err) => {
        console.error("Audit fetch failed:", err);
        setAuditEvents([]);
      });
  }, [open, card.external_key, card.externalkey, card.step_id, auditFilters, fetch]);

  // ======= Fetch Processes =======
  useEffect(() => {
    if (!open || !card) {
      setProcesses([]);
      return;
    }

    async function fetchProcesses() {
      try {
        const params = new URLSearchParams();
        if (card.orderDbId) params.append("orderId", card.orderDbId);
        if (card.itemId) params.append("itemId", card.itemId);
        if (card.pwoid) params.append("pwoId", card.pwoid);

        // if (card.externalkey) params.append("external_key", card.externalkey);
        // if (card.external_key) params.append("external_key", card.external_key);

        // THIS ONE LINE (checks both properties)
        if (card.externalkey || card.external_key) {
          params.append("external_key", card.externalkey || card.external_key);
        }

        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/order-processes?${params}`
        );
        const data = await response.json();

        if (data.success && data.data.length > 0) {
          const items = data.data.map((item) => ({
            id: item.id,
            step_no: item.step_no || 1,
            name: item.name || "",
            person: item.person || "",
            type: item.type || "manual",
            machine_id: item.machine_id || null,
            machine_name: item.machine?.name || "",
            est_minutes: item.est_minutes || null,
            due_date: item.due_date || null,
            cost: item.cost || null,
            notes: item.notes || "",
            department_id: item.department_id || null,
            department_name: item.Department?.name || "",
          }));
          setProcesses(items);
        } else {
          setProcesses([]);
        }
      } catch (err) {
        console.error("Failed to fetch processes:", err);
        setProcesses([]);
      }
    }

    fetchProcesses();
  }, [open, card, fetch]);

  const authJsonHeaders = () => {
    const token = localStorage.getItem("accessToken");
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}`, accessToken: token } : {}),
    };
  };

  const isAdmin = String(userRole || "").toLowerCase() === "administrator";

  function decodeRoleFromToken() {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) return "";
      const parts = token.split(".");
      if (parts.length < 2) return "";
      const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
      return String(payload?.role || "").toLowerCase();
    } catch {
      return "";
    }
  }

  function getInstanceUniqueIdFromPath() {
    try {
      const m = String(window.location.pathname || "").match(/\/instance\/([^/]+)/i);
      return m?.[1] ? String(m[1]).trim() : null;
    } catch {
      return null;
    }
  }

  useEffect(() => {
    setUserRole(decodeRoleFromToken());
  }, [open]);

  useEffect(() => {
    if (!open || !isAdmin || tab !== "costing" || !card) return;

    let cancelled = false;

    async function loadCosting() {
      try {
        setCostingLoading(true);
        setCostingError("");

        const qs = new URLSearchParams();
        if (card.external_key || card.externalkey) {
          qs.set("external_key", String(card.external_key || card.externalkey));
        }
        if (card.pwo_id) qs.set("pwo_id", String(card.pwo_id));
        if (card.step_id || card.stepid) qs.set("step_id", String(card.step_id || card.stepid));
        const pathInstanceUniqueId = getInstanceUniqueIdFromPath();
        if (pathInstanceUniqueId) qs.set("instanceUniqueId", pathInstanceUniqueId);

        const resp = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/costing/hierarchy?${qs.toString()}`,
          {
            method: "GET",
            headers: authJsonHeaders(),
          }
        );
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok || !json?.success) {
          throw new Error(json?.message || `HTTP ${resp.status}`);
        }
        if (!cancelled) {
          setCostingData(json);
        }
      } catch (err) {
        if (!cancelled) {
          setCostingData(null);
          setCostingError(err?.message || "Failed to load costing hierarchy");
        }
      } finally {
        if (!cancelled) setCostingLoading(false);
      }
    }

    loadCosting();
    return () => {
      cancelled = true;
    };
  }, [open, isAdmin, tab, card, fetch]);

  const mapBomItemsForUI = (items = []) =>
    (items || []).map((item) => ({
      id: item.id,
      sku: item.sku || "",
      description: item.description || "",
      qtyper:
        item.qty_per !== null && item.qty_per !== undefined
          ? parseFloat(item.qty_per)
          : "",
      unit: item.unit || "",
      unitprice:
        item.unit_price !== null && item.unit_price !== undefined
          ? parseFloat(item.unit_price)
          : "",
      cost: parseFloat(item.cost) || null,
      source: item.source || "stock",
      location: item.location || "",
      scrappct:
        item.scrap_pct !== null && item.scrap_pct !== undefined
          ? parseFloat(item.scrap_pct)
          : 0,
    }));

  useEffect(() => {
    if (!open) return;

    const ek = card.external_key;

    console.log("card", card);
    console.log("card.external_key", card.external_key);

    console.log("card?.externalkey", card?.externalkey);
    console.log(
      "detailedCard?.meta?.external_key",
      detailedCard?.meta?.external_key
    );
    console.log("Final ek:", ek);

    if (!ek) {
      console.log("No external key found, skipping audit fetch");
      return;
    }

    fetch(
      `${
        process.env.REACT_APP_NODEJS_SERVER_URL
      }/production/audit?external_key=${encodeURIComponent(ek)}`
    )
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((js) => {
        console.log("Audit response:", js);
        setAuditEvents(js.events ?? []);
      })
      .catch((err) => {
        console.error("Audit fetch failed:", err);
        setAuditEvents([]);
      });
  }, [open, card?.externalkey, card?.external_key, detailedCard?.meta?.external_key, fetch]);

  useEffect(() => {
    if (!open || !card) return;
    setTab("overview");
    setProcesses(card.processes);
    setBOM(card.bom);

    // Chat messages are handled via /chat/messages API endpoint
    // No need to fetch step-specific messages here
  }, [open, card]);

  useEffect(() => {
    if (!open || !card) {
      setBOM([]);
      setBomHeaderId(null);
      return;
    }

    async function fetchBOM() {
      try {
        const params = new URLSearchParams();

        if (card.orderDbId) params.append("orderId", card.orderDbId);
        if (card.itemId) params.append("itemId", card.itemId);
        if (card.pwo_id) params.append("pwoId", card.pwo_id);
        if (card.external_key) params.append("external_key", card.external_key);

        console.log("🔍 BOM fetch params:", params.toString());
        console.log("🔍 Card data:", {
          orderDbId: card.orderDbId,
          itemId: card.itemId,
          pwo_id: card.pwo_id,
          external_key: card.external_key,
        });

        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom?${params}`
        );
        const data = await response.json();

        console.log("✅ BOM API Response:", data);

        if (data.success && data.data.length > 0) {
          const bomHeader = data.data[0];
          setBomHeaderId(bomHeader.id);

          setBOM(mapBomItemsForUI(bomHeader.items || []));
        } else {
          setBomHeaderId(null);
          setBOM([]);
        }
      } catch (err) {
        console.error("❌ Failed to fetch BOM:", err);
        setBOM([]);
      }
    }

    fetchBOM();
  }, [open, card, fetch]);

  if (!open || !card) return null;
  const o = card.raw?.order || {};
  const it = card.raw?.item || {};
  const d = card.raw?.deco || {};
  const ctx = {
    customer: card.customer || o.customerName,
    qty: it.quantity ?? card.qtyPlanned ?? 0,
    inHandsDate: o.inHandsDate || card.dueDate,
  };

  const totalAcross =
    siblingsAll.reduce((t, c) => t + (Number(c.qtyPlanned || 0) || 0), 0) ||
    card.rootQty ||
    ctx.qty ||
    0;
  const isBatch = Boolean(normalizeProductionBatchFields(card).batchLabel) || siblingsAll.length > 1;
  const qtyDisplay = isBatch
    ? `${card.qtyPlanned ?? 0} / ${totalAcross}`
    : card.qtyPlanned ?? ctx.qty ?? 0;

  // ======= Simple Add Process =======
  const addProcess = () => {
    if (isReadOnly || isMutationBlocked()) return showArchivedReadOnlyMessage();
    return setProcesses((p) => [
      ...p,
      {
        id: `proc-${Date.now()}`,
        step_no: (p.length || 0) + 1,
        name: "",
        person: "",
        type: "manual",
        machine_id: null,
        machine_name: "",
        est_minutes: null,
        due_date: null,
        cost: null,
        notes: "",
      },
    ]);
  };

  const addBOM = () => {
    if (isReadOnly || isMutationBlocked()) return showArchivedReadOnlyMessage();
    return setBOM((b) => [
      ...b,
      {
        id: `bom-${Date.now()}`,
        sku: "",
        description: "",
        qtyper: "",
        unit: "",
        unitprice: "",
        supplier: "",
        cost: "",
      },
    ]);
  };

  // 1. UPDATE the removeBOM function to call the DELETE API
  const removeBOM = async (idx) => {
    if (isReadOnly || isMutationBlocked()) return showArchivedReadOnlyMessage();
    const item = bom[idx];

    if (String(item.id).startsWith("bom-")) {
      setBOM((b) => b.filter((_, i) => i !== idx));
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom/items/${item.id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = await response.json();

      if (data.success) {
        // Remove from UI after successful deletion
        setBOM((b) => b.filter((_, i) => i !== idx));
        SA.success("BOM item deleted", "Success");
      } else {
        throw new Error(data.message || "Failed to delete");
      }
    } catch (err) {
      SA.error("Failed to delete BOM item", err.message);
    }
  };

  const previewSuggestedBOM = async () => {
    try {
      setBomPreviewLoading(true);
      const payload = {
        pwoId: card.pwo_id || card.pwoid || null,
        externalKey: card.external_key || card.externalkey || null,
      };
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom/preview-seed`,
        {
          method: "POST",
          headers: authJsonHeaders(),
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || data.reason || "Preview failed");
      }
      setBomPreviewData(data.data || null);
      setBomPreviewOpen(true);
    } catch (err) {
      SA.error(err.message || "Failed to preview suggested BOM", "Preview failed");
    } finally {
      setBomPreviewLoading(false);
    }
  };

  const applyPreviewSuggestedBOM = async () => {
    if (isReadOnly || isMutationBlocked()) return showArchivedReadOnlyMessage();
    const ok = await SA.confirm({
      title: "Apply Suggested BOM?",
      text: "Are you sure you want to replace the current BOM with the recalculated BOM based on default Production Rules?",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      icon: "warning",
    });
    if (!ok) return;

    try {
      setBomPreviewLoading(true);
      const payload = {
        pwoId: card.pwo_id || card.pwoid || null,
        externalKey: card.external_key || card.externalkey || null,
      };
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom/apply-preview`,
        {
          method: "POST",
          headers: authJsonHeaders(),
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || "Apply failed");
      }

      const items = mapBomItemsForUI(data?.data?.items || []);
      setBomHeaderId(data?.data?.bomHeaderId || null);
      setBOM(items);
      setBomPreviewOpen(false);
      setBomPreviewData(null);
      SA.success("BOM replaced with suggested rules output", "Applied");
    } catch (err) {
      SA.error(err.message || "Failed to apply suggested BOM", "Apply failed");
    } finally {
      setBomPreviewLoading(false);
    }
  };

  const saveMeta = async () => {
    if (isReadOnly || isMutationBlocked()) return showArchivedReadOnlyMessage();
    try {
      // Save processes
      if (card.isStep) {
        await fetch(
          `${
            process.env.REACT_APP_NODEJS_SERVER_URL
          }/production/steps/${encodeURIComponent(card.id)}/processes`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(processes),
          }
        );
      }

      //  FIXED: Save BOM data
      if (bom && bom.length > 0) {
        let currentBomHeaderId = bomHeaderId; // Track the current header ID

        if (!currentBomHeaderId) {
          console.log("Creating new BOM header with all items...");

          const headerResponse = await fetch(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                order_id: card.orderDbId,
                order_item_id: card.itemId,
                pwo_id: card.pwoid,
                // external_key: card.externalkey,
                external_key: card.external_key || card.externalkey,
                notes: "",
                version: 1,
                // Include ALL items when creating header
                items: bom.map((item) => ({
                  sku: item.sku,
                  description: item.description,
                  qty_per: item.qtyper,
                  unit: item.unit,
                  unit_price: item.unitprice,
                  cost: item.cost,
                  source: item.source,
                  location: item.location,
                  scrap_pct: item.scrappct,
                })),
              }),
            }
          );

          const headerData = await headerResponse.json();
          console.log("BOM header response:", headerData);

          if (headerData.success) {
            currentBomHeaderId = headerData.id;
            setBomHeaderId(headerData.id);
            console.log("BOM header created with ID:", headerData.id);

            // Success! All items were created with the header
            SA.success("BOM saved successfully!", "Saved");
          } else {
            throw new Error(headerData.message || "Failed to create BOM");
          }
        } else {
          // Header already exists - update individual items
          console.log(
            "Updating existing BOM items for header:",
            currentBomHeaderId
          );

          for (const item of bom) {
            if (String(item.id).startsWith("bom-")) {
              // New item - POST it to existing header
              console.log(
                "Adding new item to existing header:",
                currentBomHeaderId
              );

              const addResponse = await fetch(
                `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom/${currentBomHeaderId}/items`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    sku: item.sku,
                    description: item.description,
                    qty_per: item.qtyper,
                    unit: item.unit,
                    unit_price: item.unitprice,
                    cost: item.cost,
                    source: item.source,
                    location: item.location,
                    scrap_pct: item.scrappct,
                  }),
                }
              );

              const addData = await addResponse.json();
              if (!addData.success) {
                throw new Error(addData.message || "Failed to add item");
              }
            } else {
              // Existing item - PATCH it
              console.log("Updating existing item:", item.id);

              const updateResponse = await fetch(
                `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom/items/${item.id}`,
                {
                  method: "PATCH",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    sku: item.sku,
                    description: item.description,
                    qty_per: item.qtyper,
                    unit: item.unit,
                    unit_price: item.unitprice,
                    cost: item.cost,
                    source: item.source,
                    location: item.location,
                    scrap_pct: item.scrappct,
                  }),
                }
              );

              const updateData = await updateResponse.json();
              if (!updateData.success) {
                throw new Error(updateData.message || "Failed to update item");
              }
            }
          }

          SA.success("BOM updated successfully!", "Saved");
        }
      }

      onPatchCard?.(card.id, { processes, bom });
      onClose();
    } catch (e) {
      console.error("Save error:", e);
      SA.error(e.message || "Failed to save", "Save failed");
    }
  };

  const startEditBom = (idx) => {
    if (isReadOnly || isMutationBlocked()) return showArchivedReadOnlyMessage();
    // If there's an unsaved edit in progress, revert it first
    if (editingBomIdx !== null && editingBomIdx !== idx && bomBackup) {
      updateAt(setBOM, editingBomIdx, bomBackup); // Restore the previous row
    }

    setEditingBomIdx(idx);
    setBomBackup({ ...bom[idx] }); // Backup the current row's data
  };

  // Function to cancel editing
  const cancelEditBom = () => {
    if (bomBackup && editingBomIdx !== null) {
      updateAt(setBOM, editingBomIdx, bomBackup); // Restore original
    }
    setEditingBomIdx(null);
    setBomBackup(null);
  };

  const saveBomRow = async (idx) => {
    if (isReadOnly || isMutationBlocked()) return showArchivedReadOnlyMessage();
    const item = bom[idx];

    // CRITICAL: Validate item.id exists
    console.log("Saving BOM item:", item);

    if (!item || !item.id || item.id === "undefined") {
      SA.error("Cannot save - item ID is missing", "Invalid Item");
      return;
    }

    try {
      if (String(item.id).startsWith("bom-")) {
        // New item - POST logic
        let currentHeaderId = bomHeaderId;

        if (!currentHeaderId) {
          const headerResponse = await fetch(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                order_id: card.orderDbId,
                order_item_id: card.itemId,
                // pwo_id: card.pwoid,
                pwo_id: card.pwoid || card.pwo_id,
                external_key: card.external_key,
                notes: "",
                version: 1,
                items: [],
              }),
            }
          );

          const headerData = await headerResponse.json();
          if (!headerData.success) {
            throw new Error(
              headerData.message || "Failed to create BOM header"
            );
          }

          currentHeaderId = headerData.id;
          setBomHeaderId(headerData.id);
        }

        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom/${currentHeaderId}/items`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              sku: item.sku,
              description: item.description,
              qty_per: item.qtyper,
              unit: item.unit,
              unit_price: item.unitprice,
              source: item.source,
              location: item.location,
              scrap_pct: item.scrappct,
            }),
          }
        );

        const data = await response.json();

        if (data.success) {
          // CRITICAL: Update with the correct ID field from response
          const newId = data.itemId || data.itemid || data.id;
          console.log("New item created with ID:", newId);

          updateAt(setBOM, idx, { ...item, id: newId });
          SA.success("BOM item added", "Success");
        } else {
          throw new Error(data.message || "Failed to add item");
        }
      } else {
        // Existing item - PATCH
        console.log("Updating existing item with ID:", item.id);

        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom/items/${item.id}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              sku: item.sku,
              description: item.description,
              qty_per: item.qtyper,
              unit: item.unit,
              unit_price: item.unitprice,
              source: item.source,
              location: item.location,
              scrap_pct: item.scrappct,
            }),
          }
        );

        const data = await response.json();

        if (data.success) {
          SA.success("BOM item updated", "Success");
        } else {
          throw new Error(data.message || "Failed to update item");
        }
      }

      setEditingBomIdx(null);
      setBomBackup(null);
    } catch (err) {
      console.error("Save BOM row error:", err);
      SA.error(`Failed to save BOM item: ${err.message}`, "Save failed");
    }
  };

  // ======= Remove Process  =======
  const removeProcess = async (idx) => {
    if (isReadOnly || isMutationBlocked()) return showArchivedReadOnlyMessage();
    const item = processes[idx];

    if (String(item.id).startsWith("proc-")) {
      setProcesses((p) => p.filter((_, i) => i !== idx));
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/order-processes/${item.id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = await response.json();

      if (data.success) {
        // Remove from UI after successful deletion
        setProcesses((p) => p.filter((_, i) => i !== idx));
        SA.success("Process deleted", "Success");
      } else {
        throw new Error(data.message || "Failed to delete");
      }
    } catch (err) {
      SA.error("Failed to delete process", err.message);
    }
  };

  // ======= Start/Cancel/Save Edit Functions =======

  const startEditProcess = (idx) => {
    if (isReadOnly || isMutationBlocked()) return showArchivedReadOnlyMessage();
    // Auto-revert previous unsaved edits
    if (
      editingProcessIdx !== null &&
      editingProcessIdx !== idx &&
      processBackup
    ) {
      updateAt(setProcesses, editingProcessIdx, processBackup);
    }

    setEditingProcessIdx(idx);
    setProcessBackup({ ...processes[idx] });
  };

  const cancelEditProcess = () => {
    if (processBackup && editingProcessIdx !== null) {
      updateAt(setProcesses, editingProcessIdx, processBackup);
    }
    setEditingProcessIdx(null);
    setProcessBackup(null);
  };

  const saveProcessRow = async (idx) => {
    if (isReadOnly || isMutationBlocked()) return showArchivedReadOnlyMessage();
    const item = processes[idx];

    try {
      if (String(item.id).startsWith("proc-")) {
        // New item - POST it
        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/order-processes`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              external_key: card.external_key,
              order_id: card.orderDbId,
              order_item_id: card.itemId,
              pwo_id: card.pwoid,
              // external_key: card.externalkey,
              sku: card.sku || null,
              step_no: item.step_no,
              name: item.name,
              person: item.person,
              type: item.type,
              machine_id: item.machine_id,
              est_minutes: item.est_minutes,
              due_date: item.due_date,
              cost: item.cost,
              notes: item.notes,
              department_id: item.department_id,
              department: card.department,
            }),
          }
        );

        const data = await response.json();

        if (data.success) {
          // Update with real ID from server
          updateAt(setProcesses, idx, { ...item, id: data.id });
          SA.success("Process added", "Success");
        }
      } else {
        // Existing item - PATCH it
        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/order-processes/${item.id}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              step_no: item.step_no,
              name: item.name,
              person: item.person,
              type: item.type,
              machine_id: item.machine_id,
              est_minutes: item.est_minutes,
              due_date: item.due_date,
              cost: item.cost,
              notes: item.notes,
              department_id: item.department_id,
            }),
          }
        );

        const data = await response.json();

        if (data.success) {
          SA.success("Process updated", "Success");
        }
      }

      setEditingProcessIdx(null);
      setProcessBackup(null);
    } catch (err) {
      SA.error("Failed to save process", err.message);
    }
  };

  const sendChat = async () => {
    const text = chatText.trim();
    if (!text) return;
    setChatText("");
    const localMsg = {
      id: `local-${Date.now()}`,
      text,
      when: new Date().toISOString(),
      author: "You",
    };
    setChat((c) => [...c, localMsg]);
    try {
      if (card.isStep) {
        await fetch(
          `${
            process.env.REACT_APP_NODEJS_SERVER_URL
          }/production/steps/${encodeURIComponent(card.id)}/messages`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text }),
          }
        );
      }
    } catch {}
  };

  async function cancelJob(card, reason = "") {
    try {
      console.log(card.step_id);
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps/cancel`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            step_id: card.step_id,
            reason: reason,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Cancel Job API Error:", error);
      throw error;
    }
  }

  const cancelFlow = async () => {
    if (isReadOnly || isMutationBlocked()) return showArchivedReadOnlyMessage();
    const result = await window.Swal.fire({
      title: "❌ Cancel This Job?",
      text: "This will permanently cancel the job and stop all production.",
      input: "textarea",
      inputLabel: "Cancellation reason (required):",
      inputPlaceholder: "Why is this job being cancelled?",
      showCancelButton: true,
      confirmButtonText: "Yes, Cancel Job",
      cancelButtonText: "Keep Job",
      icon: "warning",
      confirmButtonColor: "#dc2626",
      inputValidator: (value) => {
        if (!value || value.trim() === "") {
          return "Please provide a reason for cancellation";
        }
        return null;
      },
    });

    if (!result.isConfirmed) return;
    const reason = result.value.trim();

    if (card.isStep) {
      try {
        await cancelJob(card, reason);
        SA.success("Job cancelled successfully.", "Production stopped");

        onPatchCard(card.id, {
          cancelled: true,

          cancelledAt: new Date().toISOString(),
          lifecycle: [
            ...(card.lifecycle || []),
            {
              when: new Date().toLocaleString(),
              text: `Cancelled: ${reason}`,
            },
          ],
        });
      } catch (e) {
        SA.error(e.message || "Could not cancel job.", "Cancel failed");
      }
    }
  };

  async function reinstateJob(card, operator = "") {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps/reinstate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            step_id: card.step_id,
            operator: operator,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Reinstate Job API Error:", error);
      throw error;
    }
  }

  const reinstateFlow = async () => {
    if (isReadOnly || isMutationBlocked()) return showArchivedReadOnlyMessage();
    const result = await window.Swal.fire({
      title: "Reinstate This Job?",
      text: "This will make the job active again and resume production.",
      input: "textarea",
      inputLabel: "Reason for reinstatement (optional):",
      inputPlaceholder: "Why is this job being reinstated?",
      showCancelButton: true,
      confirmButtonText: "Yes, Reinstate Job",
      cancelButtonText: "Cancel",
      icon: "question",
      confirmButtonColor: "#059669",
    });

    if (!result.isConfirmed) return;
    const note = result.value || "";

    onReinstateCard(card, note); //  Just call the prop
    onClose();
  };

  const hasSibs = siblingsAll.length > 1;

  const canJoinHere = (siblingsSameLane || []).length > 1; // same-lane siblings exist

  const aggregatedSizes =
    detailedCard?.Items?.map((i) => i.Size).filter(Boolean) || [];
  const aggregatedColors =
    detailedCard?.Items?.map((i) => i.Color).filter(Boolean) || [];
  const totalNetCostGoods = detailedCard?.Items?.reduce(
    (sum, i) => sum + (i.NetCostGoods || 0),
    0
  );
  const totalRetailCostGoods = detailedCard?.Items?.reduce(
    (sum, i) => sum + (i.RetailCostGoods || 0),
    0
  );
  const supplierName = detailedCard?.supplierName || "–";
  const category = detailedCard?.category || "–";

  const getEventColor = (type) => {
    switch (type) {
      case "SCHEDULE":
        return "bg-blue-500";
      case "START":
        return "bg-green-500";
      case "STATUS_CHANGE":
        return "bg-purple-500";
      case "COMPLETE":
        return "bg-emerald-500";
      case "PAUSE":
        return "bg-yellow-500";
      case "CANCEL":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const getEventBadge = (type) => {
    switch (type) {
      case "SCHEDULE":
        return "bg-blue-100 text-blue-800";
      case "START":
        return "bg-green-100 text-green-800";
      case "STATUS_CHANGE":
        return "bg-purple-100 text-purple-800";
      case "COMPLETE":
        return "bg-emerald-100 text-emerald-800";
      case "PAUSE":
        return "bg-yellow-100 text-yellow-800";
      case "CANCEL":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Planned":
        return "bg-gray-100 text-gray-700";
      case "Scheduled":
        return "bg-blue-100 text-blue-700";
      case "In_Production":
        return "bg-cyan-100 text-cyan-700";
      case "QC":
        return "bg-yellow-100 text-yellow-700";
      case "Completed":
        return "bg-green-100 text-green-700";
      case "On_Hold":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const parseSimpleNote = (note) => {
    if (!note) return "";
    return note
      .split("; ")
      .map((part) => {
        if (part.includes("=")) {
          const [key, value] = part.split("=");
          return `${key}: ${value}`;
        }
        return part;
      })
      .join(" • ");
  };

  const getStatusLabel = (statusKey) => {
    const status = STATUSES.find((s) => s.key === statusKey);
    return status ? status.label : statusKey?.replace("_", " ") || "Unknown";
  };

  // Parse detailed note data
  function parseDetailedNote(note) {
    if (!note) return {};

    const data = {};
    const pairs = note.split(";").map((p) => p.trim());

    pairs.forEach((pair) => {
      const [key, value] = pair.split("=").map((s) => s.trim());
      if (key && value) {
        data[key] = value;
      }
    });

    return data;
  }

  // Get event icon
  function getEventIcon(type) {
    const icons = {
      SCHEDULE: "bi-calendar-check",
      START: "bi-play-circle",
      PAUSE: "bi-pause-circle",
      RESUME: "bi-play-circle",
      COMPLETE: "bi-check-circle",
      STATUS_CHANGE: "bi-arrow-left-right",
    };
    return icons[type] || "bi-circle";
  }

  // Get event dot color
  function getEventDotColor(type) {
    const colors = {
      SCHEDULE: "bg-blue-500",
      START: "bg-green-500",
      PAUSE: "bg-orange-500",
      RESUME: "bg-green-500",
      COMPLETE: "bg-purple-500",
      STATUS_CHANGE: "bg-gray-500",
    };
    return colors[type] || "bg-gray-400";
  }

  // Calculate total time from first event to last
  function calculateTotalTime(events) {
    if (!events || events.length === 0) return "0h 0m";

    const first = new Date(events[0].at);
    const last = new Date(events[events.length - 1].at);
    const diffMs = last - first;

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
  }

  // Calculate time spent in a specific stage
  function calculateStageTime(events, stageName) {
    let totalMs = 0;
    let stageStart = null;

    for (let i = 0; i < events.length; i++) {
      const ev = events[i];

      // Entering the stage
      if (ev.to === stageName) {
        stageStart = new Date(ev.at);
      }

      // Leaving the stage
      if (ev.from === stageName && stageStart) {
        const stageEnd = new Date(ev.at);
        totalMs += stageEnd - stageStart;
        stageStart = null;
      }
    }

    // Still in stage (no exit event yet)
    if (stageStart) {
      totalMs += new Date() - stageStart;
    }

    const hours = Math.floor(totalMs / (1000 * 60 * 60));
    const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m`;
  }

  // Calculate duration of a single event (time until next event)
  function calculateEventDuration(events, currentIndex) {
    if (currentIndex >= events.length - 1) return null;

    const current = new Date(events[currentIndex].at);
    const next = new Date(events[currentIndex + 1].at);
    const diffMs = next - current;

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours === 0 && minutes < 1) return "< 1m";
    if (hours === 0) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
  }

  // Calculate estimated production cost based on time and machine rates
  function calculateProductionCost(events, card) {
    // Machine hourly rates (you can adjust these or fetch from DB)
    const machineRates = {
      Embroidery: 45.0,
      Screening: 35.0,
      "Direct to Film": 40.0,
      "Heat Press": 25.0,
      Default: 30.0,
    };

    let totalCost = 0;
    let productionStart = null;

    for (let i = 0; i < events.length; i++) {
      const ev = events[i];

      // Start production
      if (ev.to === "In_Production") {
        productionStart = new Date(ev.at);
      }

      // End production phase
      if (ev.from === "In_Production" && productionStart) {
        const endTime = new Date(ev.at);
        const hours = (endTime - productionStart) / (1000 * 60 * 60);

        // Get rate for this department
        const rate = machineRates[ev.department] || machineRates["Default"];
        totalCost += hours * rate;

        productionStart = null;
      }
    }

    // Still in production
    if (productionStart) {
      const hours = (new Date() - productionStart) / (1000 * 60 * 60);
      const rate = machineRates[card.department] || machineRates["Default"];
      totalCost += hours * rate;
    }

    return totalCost.toFixed(2);
  }

  // Get stage breakdown with percentages
  function getStageBreakdown(events) {
    let stages = ["Scheduled", "In_Production", "On_Hold"];

    if (SHOW_QC_LANE) {
      stages = ["Scheduled", "In_Production", "QC", "On_Hold"];
    }

    const stageTimes = {};
    let totalMs = 0;

    // Calculate time for each stage
    stages.forEach((stage) => {
      let stageMs = 0;
      let stageStart = null;

      for (let i = 0; i < events.length; i++) {
        const ev = events[i];

        if (ev.to === stage) {
          stageStart = new Date(ev.at);
        }

        if (ev.from === stage && stageStart) {
          stageMs += new Date(ev.at) - stageStart;
          stageStart = null;
        }
      }

      // Still in stage
      if (stageStart) {
        stageMs += new Date() - stageStart;
      }

      stageTimes[stage] = stageMs;
      totalMs += stageMs;
    });

    // Build breakdown array
    const breakdown = stages
      .map((stage) => {
        const ms = stageTimes[stage];
        const hours = Math.floor(ms / (1000 * 60 * 60));
        const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
        const percentage = totalMs > 0 ? (ms / totalMs) * 100 : 0;

        return {
          name: stage,
          label: getStatusLabel(stage),
          duration: `${hours}h ${minutes}m`,
          percentage: percentage.toFixed(1),
          color: getStageColor(stage),
        };
      })
      .filter((s) => s.percentage > 0);

    return breakdown;
  }

  // Get progress bar color for each stage
  function getStageColor(stage) {
    const colors = {
      Scheduled: "bg-blue-500",
      In_Production: "bg-cyan-500",
      QC: "bg-yellow-500",
      On_Hold: "bg-orange-500",
      Completed: "bg-green-500",
    };
    return colors[stage] || "bg-gray-500";
  }

  const costingHierarchy = costingData?.data || null;
  const costingMeta = costingData?.meta || null;
  const costingOrder = costingHierarchy?.order || {};
  const costingProducts = Array.isArray(costingHierarchy?.products)
    ? costingHierarchy.products
    : [];
  const costingSteps = Array.isArray(costingHierarchy?.steps)
    ? costingHierarchy.steps
    : [];
  const costingSplits = Array.isArray(costingHierarchy?.splits)
    ? costingHierarchy.splits
    : [];
  const costingCurrency = costingHierarchy?.currency || costingMeta?.currency || "CAD";
  const fmtCostingNum = (v, d = 2) =>
    v === null || v === undefined || v === "" || Number.isNaN(Number(v))
      ? "–"
      : Number(v).toFixed(d);
  const orderRepName = firstNonBlankValue(
    orderMeta?.orderRepName,
    detailedCard?.orderRepName,
    detailedCard?.meta?.orderRepName,
    card?.orderRepName,
    card?.meta?.orderRepName,
    ""
  );
  const orderRepSource = firstNonBlankValue(
    orderMeta?.orderRepSource,
    detailedCard?.orderRepSource,
    detailedCard?.meta?.orderRepSource,
    card?.orderRepSource,
    card?.meta?.orderRepSource,
    ""
  );
  const representativeLabel = getProductionRepresentativeLabel(orderRepSource);
  const binNumber = firstNonBlankValue(
    orderMeta?.binNumber,
    detailedCard?.binNumber,
    detailedCard?.meta?.binNumber,
    card?.binNumber,
    card?.meta?.binNumber,
    ""
  );
  const binDetails = getBinDetailsRows(binNumber);

  return (
    <div ref={jobCardRef} role="dialog" aria-modal="true" aria-label="Production Job Card" style={modal.overlay} onClick={onClose}>
      <div style={modal.card} onClick={(e) => e.stopPropagation()}>
        <div style={modal.header}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <h3 style={{ margin: 0 }}>
              Project No. - {card.orderNumber || card.title || "Job Card"}
            </h3>
            {card.cancelled && <span style={ui.cancelBadge}>CANCELLED</span>}
            {card.status && (
              <span style={ui.statusPill}>
                {card.status.replace(/_/g, " ")}
              </span>
            )}
            {isArchived && (
              <span style={{ ...ui.statusPill, background: "#f1f5f9", color: "#475569" }}>
                READ-ONLY
              </span>
            )}
            {hasSibs && <span style={ui.badge}>Batch</span>}
          </div>

          {/* ACTIONS (Split = Scheduled-only; Join = any status if same-lane siblings) */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {!card.cancelled && (
              <>
                {!isArchived && card.status === "Scheduled" && (
                  <button
                    style={ui.linkBtn}
                    onClick={() => onOpenSplit?.(card)}
                  >
                    Split…
                  </button>
                )}
                {!isArchived && canJoinHere && (
                  <button style={ui.linkBtn} onClick={() => onOpenJoin?.(card)}>
                    Join…
                  </button>
                )}
              </>
            )}

            {!card.cancelled ? (
              <button style={ui.btnDanger} onClick={cancelFlow} disabled={isReadOnly} title={isArchived ? "Archived Production is read-only" : "Cancel job"}>
                Cancel
              </button>
            ) : (
              <button style={ui.btnWarn} onClick={reinstateFlow} disabled={isReadOnly} title={isArchived ? "Archived Production is read-only" : "Reinstate job"}>
                Reinstate
              </button>
            )}
            <button style={ui.btnGhost} onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        <div className="border-b border-gray-200 bg-white">
          <nav className="flex -mb-px overflow-x-auto" aria-label="Tabs">
            {[
              { id: "overview", label: "Details", icon: "bi-info-circle" },
              // { id: "overview2", label: "Details", icon: "bi-info-circle" },
              { id: "processes", label: "Processes", icon: "bi-list-check" },
              { id: "bom", label: "BOM", icon: "bi-box-seam" },
              { id: "chat", label: "Chat", icon: "bi-chat-dots" },
              { id: "activity", label: "Activity", icon: "bi-clock-history" },
              { id: "cost", label: "Cost", icon: "bi-calculator" },
              ...(isAdmin
                ? [{ id: "costing", label: "Costing", icon: "bi-graph-up-arrow" }]
                : []),
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`
          group inline-flex items-center gap-2 px-6 py-3 
          border-b-2 font-medium text-sm transition-all duration-200
          ${
            tab === t.id
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
          }
        `}
              >
                <i
                  className={`${t.icon} ${
                    tab === t.id
                      ? "text-blue-600"
                      : "text-gray-400 group-hover:text-gray-600"
                  }`}
                ></i>
                <span>{t.label}</span>
              </button>
            ))}
          </nav>
        </div>
        {isArchived && (
          <div className="alert alert-secondary rounded-0 mb-0 border-0 border-bottom d-flex align-items-center gap-2">
            <i className="bi bi-lock-fill"></i>
            <span>This Production card is completed and archived. Editing is disabled.</span>
          </div>
        )}

        {tab === "overview" && (
          <>
            {/* Enterprise-Grade Professional Styles */}
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
              
              .overview-metric {
                transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                border-left: 2px solid #dee2e6;
              }
              
              .overview-metric:hover {
                border-left-color: #0d6efd;
                box-shadow: 0 0.125rem 0.375rem rgba(0, 0, 0, 0.075);
              }
              
              .overview-card {
                transition: box-shadow 0.2s ease;
              }
              
              .overview-card:hover {
                box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.08);
              }
              
              .overview-table {
                font-size: 0.875rem;
              }
              
              .overview-table thead th {
                background-color: #f8f9fa;
                font-weight: 600;
                font-size: 0.75rem;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                color: #6c757d;
                border-bottom: 2px solid #dee2e6;
                padding: 0.75rem;
              }
              
              .overview-table tbody td {
                padding: 0.75rem;
                vertical-align: middle;
                border-bottom: 1px solid #f0f0f0;
              }
              
              .overview-table tbody tr:hover {
                background-color: #f8f9fa;
              }
              
              .section-header {
                font-size: 0.8125rem;
                font-weight: 600;
                color: #495057;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                margin-bottom: 0.75rem;
                padding-bottom: 0.5rem;
                border-bottom: 1px solid #dee2e6;
              }
              
              .info-row {
                display: flex;
                justify-content: space-between;
                padding: 0.625rem 0;
                border-bottom: 1px solid #f0f0f0;
              }
              
              .info-row:last-child {
                border-bottom: none;
              }
              
              .info-label {
                font-size: 0.8125rem;
                color: #6c757d;
                font-weight: 500;
              }
              
              .info-value {
                font-size: 0.8125rem;
                color: #212529;
                font-weight: 600;
                text-align: right;
              }
            `}</style>

            <div
              style={{
                padding: "1.5rem",
                backgroundColor: "#f5f6f8",
                // minHeight: "400px",
              }}
            >
              {/* ========== LOADING STATE ========== */}
              {modalLoading && (
                <div
                  className="d-flex flex-column align-items-center justify-content-center"
                  style={{ padding: "5rem 1.5rem" }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      border: "3px solid #e9ecef",
                      borderTopColor: "#0d6efd",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  <div
                    className="mt-3"
                    style={{
                      color: "#6c757d",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    }}
                  >
                    Loading order details...
                  </div>
                </div>
              )}

              {/* ========== MAIN CONTENT ========== */}
              {!modalLoading && detailedCard && (
                <>
                  {/* STOP SIGNAL ALERT */}
                  {card.meta?.stopSignal && (
                    <div
                      className="alert alert-danger d-flex align-items-center mb-3"
                      style={{ fontSize: "0.875rem" }}
                    >
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                      <strong>Production Stopped:</strong>
                      <span className="ms-2">
                        {card.meta?.stopLabel || "Production halted"}
                      </span>
                    </div>
                  )}

                  {/* Key Metrics Row */}
                  <div className="row g-3 mb-3">
                    {/* Order Number */}
                    <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                      <div
                        className="card overview-metric border-0 h-100"
                        style={{ backgroundColor: "#ffffff" }}
                      >
                        <div className="card-body" style={{ padding: "1rem" }}>
                          <div className="d-flex align-items-center mb-2">
                            <i
                              className="bi bi-receipt text-primary me-2"
                              style={{ fontSize: "1.125rem" }}
                            ></i>
                            <span
                              style={{
                                fontSize: "0.6875rem",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                color: "#6c757d",
                              }}
                            >
                              Project No.
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: "1.25rem",
                              fontWeight: 700,
                              color: "#212529",
                            }}
                          >
                            <ProductionProjectLink
                              projectNumber={detailedCard.OrderId || orderMeta?.orderNumber}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Representative */}
                    <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                      <div
                        className="card overview-metric border-0 h-100"
                        style={{ backgroundColor: "#ffffff" }}
                      >
                        <div className="card-body" style={{ padding: "1rem" }}>
                          <div className="d-flex align-items-center mb-2">
                            <i
                              className="bi bi-folder2-open text-primary me-2"
                              style={{ fontSize: "1.125rem" }}
                            ></i>
                            <span
                              style={{
                                fontSize: "0.6875rem",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                color: "#6c757d",
                              }}
                            >
                              {representativeLabel || ""}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: "0.875rem",
                              fontWeight: 600,
                              color: "#212529",
                            }}
                            className="text-truncate"
                          >
                            {/* {detailedCard.ProjectName ||
                              card.meta?.projectName ||
                              "Untitled"} */}

                            {orderRepName || "–"}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quantity */}
                    <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                      <div
                        className="card overview-metric border-0 h-100"
                        style={{ backgroundColor: "#ffffff" }}
                      >
                        <div className="card-body" style={{ padding: "1rem" }}>
                          <div className="d-flex align-items-center mb-2">
                            <i
                              className="bi bi-box-seam text-primary me-2"
                              style={{ fontSize: "1.125rem" }}
                            ></i>
                            <span
                              style={{
                                fontSize: "0.6875rem",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                color: "#6c757d",
                              }}
                            >
                              Quantity
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: "1.25rem",
                              fontWeight: 700,
                              color: "#212529",
                            }}
                          >
                            {detailedCard.Items?.reduce(
                              (sum, item) => sum + (item.Quantity || 0),
                              0
                            ) || 0}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* In-Hands Date */}
                    <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                      <div
                        className="card overview-metric border-0 h-100"
                        style={{ backgroundColor: "#ffffff" }}
                      >
                        <div className="card-body" style={{ padding: "1rem" }}>
                          <div className="d-flex align-items-center mb-2">
                            <i
                              className="bi bi-calendar3 text-primary me-2"
                              style={{ fontSize: "1.125rem" }}
                            ></i>
                            <span
                              style={{
                                fontSize: "0.6875rem",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                color: "#6c757d",
                              }}
                            >
                              Decorator PO In-Hands
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: "0.875rem",
                              fontWeight: 600,
                              color: "#212529",
                            }}
                          >
                            {formatProductionBusinessDate(detailedCard.decoratorPoInHandsDate)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Total Cost */}
                    <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                      <div
                        className="card overview-metric border-0 h-100"
                        style={{ backgroundColor: "#ffffff" }}
                      >
                        <div className="card-body" style={{ padding: "1rem" }}>
                          <div className="d-flex align-items-center mb-2">
                            <i
                              className="bi bi-currency-dollar text-primary me-2"
                              style={{ fontSize: "1.125rem" }}
                            ></i>
                            <span
                              style={{
                                fontSize: "0.6875rem",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                color: "#6c757d",
                              }}
                            >
                              Total Cost
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: "1.125rem",
                              fontWeight: 700,
                              color: "#212529",
                            }}
                          >
                            $
                            {(
                              detailedCard.Items?.reduce(
                                (sum, item) => sum + (item.Subtotal || 0),
                                0
                              ) || 0
                            ).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Status */}
                    {/* <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                      <div
                        className="card overview-metric border-0 h-100"
                        style={{ backgroundColor: "#ffffff" }}
                      >
                        <div className="card-body" style={{ padding: "1rem" }}>
                          <div className="d-flex align-items-center mb-2">
                            <i
                              className="bi bi-check-circle text-primary me-2"
                              style={{ fontSize: "1.125rem" }}
                            ></i>
                            <span
                              style={{
                                fontSize: "0.6875rem",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                color: "#6c757d",
                              }}
                            >
                              Status
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: "0.875rem",
                              fontWeight: 600,
                              color: "#212529",
                            }}
                            className="text-truncate"
                          >
                            {detailedCard.OrderStatus?.SO ||
                              card.meta?.salesOrderStatus ||
                              "Pending"}<br/>

                               {detailedCard.OrderStatus?.PO ||
                              card.meta?.salesOrderStatus ||
                              "Pending"}
                          </div>
                        </div>
                      </div>
                    </div> */}

                    <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                      <div
                        className="card overview-metric border-0 h-100"
                        style={{ backgroundColor: "#ffffff" }}
                      >
                        <div className="card-body" style={{ padding: "1rem" }}>
                          <div className="d-flex align-items-center mb-2">
                            <i
                              className="bi bi-check-circle text-primary me-2"
                              style={{ fontSize: "1.125rem" }}
                            ></i>
                            <span
                              style={{
                                fontSize: "0.6875rem",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                color: "#6c757d",
                              }}
                            >
                              Status
                            </span>
                          </div>

                          <div
                            style={{
                              fontSize: "0.875rem",
                              fontWeight: 600,
                              color: "#212529",
                              lineHeight: "1.4",
                            }}
                          >
                            {/* SO Status - with individual truncate */}
                            <div
                              className="text-truncate"
                              title={
                                detailedCard.OrderStatus?.SO ||
                                card.meta?.salesOrderStatus ||
                                "Pending"
                              }
                            >
                              {detailedCard.OrderStatus?.SO ||
                                card.meta?.salesOrderStatus ||
                                "Pending"}
                            </div>

                            {/* PO Status - with word break for wrapping */}
                            <div
                              style={{
                                wordBreak: "break-word",
                                fontSize: "0.8125rem",
                                marginTop: "0.25rem",
                              }}
                            >
                              {detailedCard.OrderStatus?.PO ||
                                card.meta?.salesOrderStatus ||
                                "Pending"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Information Cards Grid */}
                  <div className="row g-3 mb-3">
                    {/* Project Information */}
                    <div className="col-lg-4 col-md-6">
                      <div
                        className="card overview-card border-0 h-100"
                        style={{ backgroundColor: "#ffffff" }}
                      >
                        <div
                          className="card-body"
                          style={{ padding: "1.25rem" }}
                        >
                          <h6 className="section-header">
                            <i className="bi bi-info-circle me-2"></i>Project
                            Details
                          </h6>
                          <div className="info-row">
                            <span className="info-label">Project name</span>
                            <span className="info-value">
                              {detailedCard.ProjectName ||
                                orderMeta?.projectName ||
                                "Untitled"}
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Bin Location</span>
                            <span
                              className="info-value"
                              title={binDetails.full}
                              style={{ fontWeight: 700 }}
                            >
                              {binDetails.locationSummary}
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Box / Ref</span>
                            <span
                              className="info-value"
                              title={binDetails.full}
                              style={{
                                fontWeight: 700,
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              {binDetails.refSummary}
                            </span>
                          </div>

                          <div className="info-row">
                            <span className="info-label">Created Date</span>
                            <span className="info-value">
                              {detailedCard.createdAt
                                ? DATE_FMT(detailedCard.createdAt)
                                : "–"}
                            </span>
                          </div>

                          {/* Purchase Orders */}

                          <div className="info-row">
                            <div>
                              <div
                                style={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: "#6c757d",
                                  marginBottom: "0.5rem",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Purchase Orders
                                {orderMeta?.purchaseOrders?.length > 0 &&
                                  ` (${orderMeta.purchaseOrders.length})`}
                              </div>

                              {orderMeta?.purchaseOrders &&
                              orderMeta.purchaseOrders.length > 0 ? (
                                <div className="d-flex flex-column gap-2">
                                  {orderMeta.purchaseOrders.map((po, idx) => (
                                    <div
                                      key={idx}
                                      style={{
                                        padding: "0.5rem",
                                        backgroundColor: "#f8f9fa",
                                        border: "1px solid #e9ecef",
                                        borderRadius: "0.25rem",
                                        fontSize: "0.8125rem",
                                      }}
                                    >
                                      <div className="d-flex justify-content-between align-items-center">
                                        <span
                                          className="font-monospace"
                                          style={{ fontWeight: 600 }}
                                        >
                                          {po.poNumber}
                                        </span>
                                        {/* <span
                // className="badge badgeNew bg-secondary"
                                  className="info-value"
                style={{ fontSize: "0.65rem" }}
              >
                ID: {po.id}
              </span> */}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "0.75rem",
                                          color: "#6c757d",
                                          marginTop: "0.25rem",
                                        }}
                                      >
                                        Status: {po.supplierStatus || "–"}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div
                                  style={{
                                    fontSize: "0.8125rem",
                                    color: "#9ca3af",
                                    fontStyle: "italic",
                                  }}
                                >
                                  –
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Global Sales Order */}

                          {/* Global Sales Order */}
                          {/* <div> */}
                          <div className="info-row">
                            <span className="info-label">Global SO</span>
                            <span
                              className="info-value"
                              style={{
                                color: orderMeta?.salesOrderGlobal?.trim()
                                  ? "#212529"
                                  : "#9ca3af",
                              }}
                            >
                              {orderMeta?.salesOrderGlobal?.trim() || "–"}
                            </span>
                          </div>
                          {/* </div> */}

                          {/* Production Active Flag */}

                          <div className="info-row">
                            <span className="info-label">
                              Production Active
                            </span>
                            <span
                              // className={`badge ${
                              //   orderMeta?.activeForProduction
                              //     ? "bg-success"
                              //     : "bg-secondary"
                              // }`}
                              className="info-value"
                              style={{ fontSize: "0.6875rem" }}
                            >
                              {orderMeta?.activeForProduction ? "Yes" : "No"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Product Summary */}
                    <div className="col-lg-4 col-md-6">
                      <div
                        className="card overview-card border-0 h-100"
                        style={{ backgroundColor: "#ffffff" }}
                      >
                        <div
                          className="card-body"
                          style={{ padding: "1.25rem" }}
                        >
                          {/* <div className="d-flex justify-content-between align-items-center mb-3"> */}
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            {/* <h6 className="section-header mb-0 pb-0 border-0">
                              <i className="bi bi-box-seam me-2"></i>Product
                              Summary
                            </h6> */}

                            <h6 className="section-header mb-0 pb-0 border-0">
                              <i className="bi bi-box-seam me-2"></i>Product
                              Summary
                            </h6>
                            {/* <span
                              className="badge badgeNew bg-primary"
                              style={{ fontSize: "0.6875rem" }}
                            >
                              {detailedCard.Items?.length || 0}{" "}
                              {detailedCard.Items?.length === 1
                                ? "Item"
                                : "Items"}
                            </span> */}
                          </div>

                          <div
                            style={{
                              borderBottom: "1px solid #dee2e6",
                              marginBottom: "0.75rem",
                            }}
                          ></div>
                          <div className="info-row">
                            <span className="info-label">Sizes</span>
                            <span className="info-value">
                              {[
                                ...new Set(
                                  detailedCard.Items?.map((i) => i.Size).filter(
                                    Boolean
                                  )
                                ),
                              ].join(", ") || "–"}
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Colors</span>
                            <span className="info-value">
                              {[
                                ...new Set(
                                  detailedCard.Items?.map(
                                    (i) => i.Color
                                  ).filter(Boolean)
                                ),
                              ].join(", ") || "–"}
                            </span>
                          </div>
                          {/* <hr style={{ margin: "1rem 0", borderColor: "#e9ecef" }} /> */}
                          <div className="info-row">
                            <span className="info-label">Product Subtotal</span>
                            <span
                              className="info-value"
                              style={{ fontWeight: 700 }}
                            >
                              $
                              {(
                                detailedCard.Items?.reduce(
                                  (sum, i) => sum + (i.Subtotal || 0),
                                  0
                                ) || 0
                              ).toFixed(2)}
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Tax Amount</span>
                            <span className="info-value">
                              $
                              {(
                                detailedCard.Items?.reduce(
                                  (sum, i) =>
                                    sum +
                                    Number(
                                      i.TaxAmount ?? i.taxAmount ?? i.Tax ?? 0
                                    ),
                                  0
                                ) || 0
                              ).toFixed(2)}
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Currency</span>
                            <span className="info-value">
                              {detailedCard.Items?.[0]?.CurrencyCode || "–"}
                            </span>
                          </div>
                          {/* <hr style={{ margin: "1rem 0", borderColor: "#e9ecef" }} /> */}
                          <div className="info-row">
                            <span className="info-label">Supplier</span>
                            <span className="info-value">
                              {detailedCard.Items?.[0]?.SupplierName || "–"}
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Supplier ID</span>
                            <span
                              className="info-value font-monospace"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {detailedCard.Items?.[0]?.SupplierId || "–"}
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Source</span>
                            <span className="info-value text-uppercase">
                              {detailedCard.Items?.[0]?.Source || "–"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Schedule & Status */}
                    <div className="col-lg-4 col-md-6">
                      <div
                        className="card overview-card border-0 h-100"
                        style={{ backgroundColor: "#ffffff" }}
                      >
                        <div
                          className="card-body"
                          style={{ padding: "1.25rem" }}
                        >
                          <h6 className="section-header">
                            <i className="bi bi-clock-history me-2"></i>Schedule
                            & Status
                          </h6>
                          <div className="info-row">
                            <span className="info-label">Customer</span>
                            <span className="info-value">
                              {/* {detailedCard.CustomerName ||
                                card.meta?.customerName ||
                                "–"} */}

                              {detailedCard.CustomerName ||
                                orderMeta?.customerName ||
                                "–"}
                            </span>
                          </div>

                          {/* <hr style={{ margin: "1rem 0", borderColor: "#e9ecef" }} /> */}
                          <div className="info-row">
                            <span className="info-label">Customer In-Hands</span>
                            <span className="info-value">
                              {detailedCard.inHandsDate
                                ? formatProductionBusinessDate(detailedCard.inHandsDate)
                                : "–"}
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Decorator PO In-Hands</span>
                            <span className="info-value">
                              {formatProductionBusinessDate(detailedCard.decoratorPoInHandsDate)}
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Scheduled</span>
                            <span className="info-value">
                              {detailedCard.plannedStart
                                ? DATE_FMT(detailedCard.plannedStart)
                                : "–"}
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Machine</span>
                            <span className="info-value">
                              {detailedCard.machine ||
                                (hasSibs ? "Multiple" : "–")}
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Operator</span>
                            <span className="info-value">
                              {detailedCard.assignedUserName ||
                                detailedCard.operator ||
                                "–"}
                            </span>
                          </div>
                          {/* <hr style={{ margin: "1rem 0", borderColor: "#e9ecef" }} /> */}
                          <div className="info-row">
                            <span className="info-label">Sales Order</span>
                            <span className="info-value">
                              {/* {detailedCard.OrderStatus?.SO ||
                                card.meta?.salesOrderStatus ||
                                "–"} */}

                              {detailedCard.OrderStatus?.SO ||
                                orderMeta?.salesOrderStatus ||
                                "Pending"}
                            </span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">Purchase Order</span>
                            <span className="info-value">
                              {/* {detailedCard.OrderStatus?.PO ||
                                card.meta?.purchaseOrderStatus ||
                                "–"} */}

                              {detailedCard.OrderStatus?.PO ||
                                orderMeta?.purchaseOrderStatus}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Costing Information */}
                  {orderCosting && (
                    <div
                      className="card overview-card border-0 mb-3"
                      style={{ backgroundColor: "#ffffff" }}
                    >
                      <div className="card-body" style={{ padding: "1.25rem" }}>
                        <h6 className="section-header">
                          <i className="bi bi-calculator me-2"></i>Cost
                          Breakdown
                        </h6>
                        <div className="row">
                          <div className="col-md-3 col-sm-6">
                            <div
                              className="text-center p-3"
                              style={{
                                backgroundColor: "#f8f9fa",
                                borderRadius: "0.25rem",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "0.75rem",
                                  color: "#6c757d",
                                  marginBottom: "0.5rem",
                                  fontWeight: 600,
                                }}
                              >
                                BOM Cost
                              </div>
                              <div
                                style={{
                                  fontSize: "1.125rem",
                                  fontWeight: 700,
                                  color: "#212529",
                                }}
                              >
                                {/* $
                                {(card.costing.bomCostSubtotal || 0).toFixed(2)} */}
                                $
                                {(orderCosting.bomCostSubtotal || 0).toFixed(2)}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3 col-sm-6">
                            <div
                              className="text-center p-3"
                              style={{
                                backgroundColor: "#f8f9fa",
                                borderRadius: "0.25rem",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "0.75rem",
                                  color: "#6c757d",
                                  marginBottom: "0.5rem",
                                  fontWeight: 600,
                                }}
                              >
                                Process Cost
                              </div>
                              <div
                                style={{
                                  fontSize: "1.125rem",
                                  fontWeight: 700,
                                  color: "#212529",
                                }}
                              >
                                {/* $
                                {(
                                  card.costing.processCostSubtotal || 0
                                ).toFixed(2)} */}
                                $
                                {(
                                  orderCosting.processCostSubtotal || 0
                                ).toFixed(2)}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3 col-sm-6">
                            <div
                              className="text-center p-3"
                              style={{
                                backgroundColor: "#f8f9fa",
                                borderRadius: "0.25rem",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "0.75rem",
                                  color: "#6c757d",
                                  marginBottom: "0.5rem",
                                  fontWeight: 600,
                                }}
                              >
                                Est. Minutes
                              </div>
                              <div
                                style={{
                                  fontSize: "1.125rem",
                                  fontWeight: 700,
                                  color: "#212529",
                                }}
                              >
                                {/* {card.costing.estMinutesTotal || 0} min */}
                                {orderCosting.estMinutesTotal || 0} min
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3 col-sm-6">
                            <div
                              className="text-center p-3"
                              style={{
                                backgroundColor: "#fff3cd",
                                borderRadius: "0.25rem",
                                border: "1px solid #ffc107",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "0.75rem",
                                  color: "#856404",
                                  marginBottom: "0.5rem",
                                  fontWeight: 700,
                                }}
                              >
                                Grand Total
                              </div>
                              <div
                                style={{
                                  fontSize: "1.125rem",
                                  fontWeight: 700,
                                  color: "#856404",
                                }}
                              >
                                {/* ${(card.costing.grandTotalCost || 0).toFixed(2)} */}
                                ${(orderCosting.grandTotalCost || 0).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Department Progress */}
                  {/* {card.meta?.eligible_departments &&
                    card.meta.eligible_departments.length > 0 && ( */}
                  {orderMeta?.eligible_departments &&
                    orderMeta.eligible_departments.length > 0 && (
                      <div
                        className="card overview-card border-0 mb-3"
                        style={{ backgroundColor: "#ffffff" }}
                      >
                        <div
                          className="card-body"
                          style={{ padding: "1.25rem" }}
                        >
                          <h6 className="section-header">
                            <i className="bi bi-diagram-3 me-2"></i>Department
                            Progress
                          </h6>
                          <div className="row g-3">
                            {/* {card.meta.eligible_departments.map((dept, idx) => ( */}
                            {orderMeta.eligible_departments.map((dept, idx) => (
                              <div
                                key={idx}
                                className="col-lg-3 col-md-4 col-sm-6"
                              >
                                <div
                                  role="button"
                                  tabIndex={0}
                                  title={`Open ${dept.name} board`}
                                  onClick={() =>
                                    onNavigateDepartmentBoard?.(dept)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      onNavigateDepartmentBoard?.(dept);
                                    }
                                  }}
                                  style={{
                                    padding: "0.875rem 1rem",
                                    backgroundColor:
                                      dept.status === "Completed"
                                        ? "#d1fae5"
                                        : dept.status
                                        ? "#fef3c7"
                                        : "#f8f9fa",
                                    border: `1px solid ${
                                      dept.status === "Completed"
                                        ? "#10b981"
                                        : dept.status
                                        ? "#f59e0b"
                                        : "#e9ecef"
                                    }`,
                                    borderRadius: "0.25rem",
                                    cursor: "pointer",
                                    transition:
                                      "transform 0.15s ease, box-shadow 0.15s ease",
                                  }}
                                >
                                  <div className="d-flex align-items-start justify-content-between mb-2">
                                    <div className="d-flex align-items-center">
                                      {dept.status === "Completed" ? (
                                        <i
                                          className="bi bi-check-circle-fill text-success me-2"
                                          style={{ fontSize: "1.25rem" }}
                                        ></i>
                                      ) : dept.status ? (
                                        <i
                                          className="bi bi-arrow-repeat text-warning me-2"
                                          style={{ fontSize: "1.25rem" }}
                                        ></i>
                                      ) : (
                                        <i
                                          className="bi bi-circle text-secondary me-2"
                                          style={{ fontSize: "1.25rem" }}
                                        ></i>
                                      )}
                                      <div>
                                        <div
                                          style={{
                                            fontSize: "0.875rem",
                                            fontWeight: 600,
                                            color: "#212529",
                                          }}
                                        >
                                          {dept.name}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "0.7rem",
                                            color: "#6c757d",
                                            fontFamily: "monospace",
                                          }}
                                        >
                                          ID: {dept.id}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.75rem",
                                      color:
                                        dept.status === "Completed"
                                          ? "#065f46"
                                          : dept.status
                                          ? "#92400e"
                                          : "#6c757d",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {dept.status || "Pending"}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                  {/* Products with Decorations */}
                  {detailedCard.Items?.map((item, itemIndex) => {
                    const decorationCount =
                      item.decorationCount ?? item.decorations?.length ?? 0;
                    const totalCharges =
                      item.chargeCount ??
                      item.decorations?.reduce(
                        (sum, deco) => sum + (deco.Charge?.length || 0),
                        0
                      ) ??
                      0;

                    return (
                      <div
                        key={item.id}
                        className="card overview-card border-0 mb-3"
                        style={{ backgroundColor: "#ffffff" }}
                      >
                        <div
                          className="card-body"
                          style={{ padding: "1.25rem" }}
                        >
                          {/* Product Header */}
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                            <h6 className="section-header mb-0 pb-0 border-0">
                              <i className="bi bi-box me-2"></i>
                              Product
                              {/* {itemIndex + 1} */}: {item.Name || "–"}
                            </h6>

                            <div
                              className="d-flex gap-2 flex-wrap"
                              style={{
                                gap: "0.5rem",
                                flexWrap: "wrap",
                                alignItems: "center",
                              }}
                            >
                              <span
                                className="badge badgeNew bg-secondary"
                                style={{
                                  fontSize: "0.6875rem",
                                  whiteSpace: "nowrap",
                                  padding: "0.35rem 0.65rem",
                                }}
                              >
                                ID: {item.id}
                              </span>
                              <span
                                className="badge badgeNew bg-primary"
                                style={{
                                  fontSize: "0.6875rem",
                                  whiteSpace: "nowrap",
                                  padding: "0.35rem 0.65rem",
                                }}
                              >
                                Qty: {item.Quantity}
                              </span>
                              {decorationCount > 0 && (
                                <span
                                  className="badge badgeNew bg-info"
                                  style={{
                                    fontSize: "0.6875rem",
                                    whiteSpace: "nowrap",
                                    padding: "0.35rem 0.65rem",
                                  }}
                                >
                                  <i className="bi bi-palette me-1"></i>
                                  {decorationCount} Decoration
                                  {decorationCount !== 1 ? "s" : ""}
                                </span>
                              )}
                              {totalCharges > 0 && (
                                <span
                                  className="badge badgeNew bg-secondary"
                                  style={{
                                    fontSize: "0.6875rem",
                                    whiteSpace: "nowrap",
                                    padding: "0.35rem 0.65rem",
                                  }}
                                >
                                  <i className="bi bi-tags me-1"></i>
                                  {totalCharges} Charge
                                  {totalCharges !== 1 ? "s" : ""}
                                </span>
                              )}
                              {item.isDeleted && (
                                <span
                                  className="badge badgeNew bg-danger"
                                  style={{
                                    fontSize: "0.6875rem",
                                    whiteSpace: "nowrap",
                                    padding: "0.35rem 0.65rem",
                                  }}
                                >
                                  Deleted
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Product Details Grid */}
                          <div className="row g-3 mb-4">
                            {/* Product Image */}

                            {/* Always show image column */}
                            <div className="col-lg-3 col-md-4">
                              <div
                                style={{
                                  padding: "1rem",
                                  backgroundColor: "#f8f9fa",
                                  border: "1px solid #e9ecef",
                                  borderRadius: "0.25rem",
                                  textAlign: "center",
                                  minHeight: "280px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  height: "100%",
                                }}
                              >
                                {item.ImageUrl ? (
                                  <img
                                    src={item.ImageUrl}
                                    alt={item.Name}
                                    style={{
                                      maxWidth: "100%",
                                      maxHeight: "250px",
                                      objectFit: "contain",
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      e.target.parentElement.innerHTML =
                                        '<i class="bi bi-image" style="font-size: 3rem; color: #adb5bd;"></i>';
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#adb5bd",
                                    }}
                                  >
                                    <i
                                      className="bi bi-image"
                                      style={{
                                        fontSize: "3rem",
                                        marginBottom: "0.5rem",
                                      }}
                                    ></i>
                                    <span
                                      style={{
                                        fontSize: "0.875rem",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      No Image Available
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Product Info */}
                            <div
                              className={
                                item.ImageUrl ? "col-lg-9 col-md-8" : "col-12"
                              }
                            >
                              <div className="row g-3">
                                <div className="col-md-3 col-sm-6">
                                  <div
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#6c757d",
                                      marginBottom: "0.25rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    SKU
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.875rem",
                                      fontWeight: 600,
                                      color: "#212529",
                                    }}
                                  >
                                    {item.SKU || "–"}
                                  </div>
                                </div>
                                <div className="col-md-3 col-sm-6">
                                  <div
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#6c757d",
                                      marginBottom: "0.25rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Product ID
                                  </div>
                                  <div
                                    className="font-monospace"
                                    style={{
                                      fontSize: "0.8125rem",
                                      fontWeight: 500,
                                      color: "#212529",
                                    }}
                                  >
                                    {item.ProductId || "–"}
                                  </div>
                                </div>
                                <div className="col-md-3 col-sm-6">
                                  <div
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#6c757d",
                                      marginBottom: "0.25rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Color
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.875rem",
                                      fontWeight: 600,
                                      color: "#212529",
                                    }}
                                  >
                                    {item.Color || "–"}
                                  </div>
                                </div>
                                <div className="col-md-3 col-sm-6">
                                  <div
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#6c757d",
                                      marginBottom: "0.25rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Size
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.875rem",
                                      fontWeight: 600,
                                      color: "#212529",
                                    }}
                                  >
                                    {item.Size || "–"}
                                  </div>
                                </div>
                                <div className="col-md-3 col-sm-6">
                                  <div
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#6c757d",
                                      marginBottom: "0.25rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Supplier
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.875rem",
                                      fontWeight: 600,
                                      color: "#212529",
                                    }}
                                  >
                                    {item.SupplierName || "–"}
                                  </div>
                                </div>
                                <div className="col-md-3 col-sm-6">
                                  <div
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#6c757d",
                                      marginBottom: "0.25rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Subtotal
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.9375rem",
                                      fontWeight: 700,
                                      color: "#212529",
                                    }}
                                  >
                                    ${item.Subtotal?.toFixed(2)}{" "}
                                    {item.CurrencyCode}
                                  </div>
                                </div>
                                <div className="col-md-3 col-sm-6">
                                  <div
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#6c757d",
                                      marginBottom: "0.25rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Tax
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.875rem",
                                      fontWeight: 600,
                                      color: "#212529",
                                    }}
                                  >
                                    ${Number(
                                      item.TaxAmount ??
                                        item.taxAmount ??
                                        item.Tax ??
                                        0
                                    ).toFixed(2)}
                                  </div>
                                </div>
                                <div className="col-md-3 col-sm-6">
                                  <div
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#6c757d",
                                      marginBottom: "0.25rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Source
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.875rem",
                                      fontWeight: 600,
                                      color: "#212529",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    {item.Source || "–"}
                                  </div>
                                </div>
                                <div className="col-md-3 col-sm-6">
                                  <div
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#6c757d",
                                      marginBottom: "0.25rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Created
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.8125rem",
                                      fontWeight: 500,
                                      color: "#212529",
                                    }}
                                  >
                                    {item.createdAt
                                      ? DATE_FMT(item.createdAt)
                                      : "–"}
                                  </div>
                                </div>
                                <div className="col-md-3 col-sm-6">
                                  <div
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#6c757d",
                                      marginBottom: "0.25rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Updated
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.8125rem",
                                      fontWeight: 500,
                                      color: "#212529",
                                    }}
                                  >
                                    {item.updatedAt
                                      ? DATE_FMT(item.updatedAt)
                                      : "–"}
                                  </div>
                                </div>
                              </div>

                              {/* SHORT DESCRIPTION - Always show */}
                              <div
                                className="mt-3"
                                style={{
                                  padding: "0.75rem",
                                  backgroundColor: "#f8f9fa",
                                  border: "1px solid #e9ecef",
                                  borderRadius: "0.25rem",
                                  fontSize: "0.8125rem",
                                  lineHeight: "1.6",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    color: "#6c757d",
                                    marginBottom: "0.375rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  Product Summary - Short Description
                                </div>
                                <div
                                  style={{
                                    color: item.ShortDescription?.trim()
                                      ? "#495057"
                                      : "#9ca3af",
                                    fontStyle: item.ShortDescription?.trim()
                                      ? "normal"
                                      : "italic",
                                  }}
                                >
                                  {item.ShortDescription?.trim() || "–"}
                                </div>
                              </div>

                              {/* FULL DESCRIPTION - Always show */}
                              <div
                                className="mt-2"
                                style={{
                                  padding: "0.75rem",
                                  backgroundColor: "#ffffff",
                                  border: "1px solid #dee2e6",
                                  borderRadius: "0.25rem",
                                  fontSize: "0.8125rem",
                                  lineHeight: "1.6",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    color: "#6c757d",
                                    marginBottom: "0.375rem",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  Full Product Description
                                </div>
                                <div
                                  style={{
                                    color: item.Description?.trim()
                                      ? "#495057"
                                      : "#9ca3af",
                                    fontStyle: item.Description?.trim()
                                      ? "normal"
                                      : "italic",
                                  }}
                                >
                                  {item.Description?.trim() || "–"}
                                </div>
                              </div>

                              {/* </div> */}
                            </div>
                          </div>

                          {/* Decorations Section */}
                          {item.decorations && item.decorations.length > 0 && (
                            <div>
                              <h6 className="section-header">
                                <i className="bi bi-palette me-2"></i>
                                Decoration Details ({decorationCount} Decoration
                                {decorationCount !== 1 ? "s" : ""}, {totalCharges}{" "}
                                Charge{totalCharges !== 1 ? "s" : ""})
                              </h6>

                              {item.decorations.map((deco, decoIdx) => (
                                <div
                                  key={deco.id}
                                  style={{ marginBottom: "1.5rem" }}
                                >
                                  {/* Location Header */}
                                  <div
                                    className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3"
                                    style={{
                                      padding: "0.75rem",
                                      backgroundColor: "#f8f9fa",
                                      border: "1px solid #e9ecef",
                                      borderRadius: "0.25rem",
                                    }}
                                  >
                                    <div className="d-flex align-items-center gap-3 flex-wrap">
                                      <div className="d-flex align-items-center">
                                        <i
                                          className="bi bi-pin-map text-primary me-2"
                                          style={{ fontSize: "1rem" }}
                                        ></i>
                                        <span
                                          style={{
                                            fontSize: "0.875rem",
                                            fontWeight: 700,
                                            color: "#212529",
                                          }}
                                        >
                                          Location: {deco.Location || "–"}
                                        </span>
                                      </div>
                                      <span
                                        className="badge badgeNew bg-secondary text-dark font-monospace"
                                        style={{ fontSize: "0.65rem" }}
                                      >
                                        Decoration ID: {deco.id}
                                      </span>
                                      <span
                                        className="badge badgeNew bg-secondary text-dark font-monospace"
                                        style={{ fontSize: "0.65rem" }}
                                      >
                                        Item ID: {deco.OrderItemId}
                                      </span>
                                      {deco.DecoratorDetails
                                        ?.isEliteDecorator && (
                                        <span
                                          className="badge badgeNew bg-success"
                                          style={{ fontSize: "0.65rem" }}
                                        >
                                          <i className="bi bi-star-fill me-1"></i>
                                          Elite Decorator
                                        </span>
                                      )}
                                      {deco.DecoratorDetails?.source && (
                                        <span
                                          className="badge badgeNew bg-info"
                                          style={{ fontSize: "0.65rem" }}
                                        >
                                          Source: {deco.DecoratorDetails.source}
                                        </span>
                                      )}
                                    </div>
                                    {deco.Charge && deco.Charge.length > 0 && (
                                      <span
                                        className="badge badgeNew bg-primary"
                                        style={{ fontSize: "0.7rem" }}
                                      >
                                        {deco.Charge.length} Charge
                                        {deco.Charge.length !== 1 ? "s" : ""}
                                      </span>
                                    )}
                                  </div>

                                  {/* Decorator Details */}
                                  {deco.DecoratorDetails && (
                                    <div
                                      className="alert alert-light border mb-3"
                                      style={{
                                        fontSize: "0.8125rem",
                                        padding: "0.75rem",
                                      }}
                                    >
                                      <div className="row g-2">
                                        <div className="col-md-4">
                                          <strong>Decorator ID:</strong>{" "}
                                          {deco.DecoratorDetails.decoratorId ||
                                            "–"}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>Details ID:</strong>{" "}
                                          {deco.DecoratorDetails.id || "–"}
                                        </div>
                                        <div className="col-md-4">
                                          <strong>Charge ID:</strong>{" "}
                                          {deco.DecoratorDetails.chargeId ||
                                            "–"}
                                        </div>
                                        {deco.DecoratorDetails.methodName && (
                                          <div className="col-12">
                                            <strong>Method:</strong>{" "}
                                            {deco.DecoratorDetails.methodName}
                                          </div>
                                        )}
                                        {deco.DecoratorDetails.rowBindings &&
                                          deco.DecoratorDetails.rowBindings
                                            .length > 0 && (
                                            <div className="col-12">
                                              <strong>Row Bindings:</strong>{" "}
                                              {
                                                deco.DecoratorDetails
                                                  .rowBindings.length
                                              }{" "}
                                              binding
                                              {deco.DecoratorDetails.rowBindings
                                                .length !== 1
                                                ? "s"
                                                : ""}
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  )}

                                  <h6 className="section-header">
                                    <i className="bi bi-tags me-2"></i>Charges
                                    by Department
                                  </h6>

                                  {/* Charges Table - COMPLETE WITH ALL FIELDS */}
                                  {deco.Charge && deco.Charge.length > 0 && (
                                    <div className="table-responsive mb-3">
                                      <table className="table overview-table table-bordered mb-0">
                                        <thead>
                                          <tr>
                                            <th>Charge Name</th>
                                            <th className="text-center">
                                              Type
                                            </th>
                                            <th className="text-center">Qty</th>
                                            <th className="text-end">
                                              Net Cost
                                            </th>
                                            <th className="text-end">Retail</th>
                                            <th className="text-end">
                                              Client Price
                                            </th>
                                            <th className="text-center">
                                              Margin %
                                            </th>
                                            <th className="text-end">Total</th>
                                            <th className="text-center">
                                              Display
                                            </th>
                                            {/* <th className="text-center">IDs</th> */}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {deco.Charge.map(
                                            (charge, chargeIdx) => (
                                              <tr key={chargeIdx}>
                                                <td style={{ fontWeight: 600 }}>
                                                  {charge.Name || "–"}
                                                </td>
                                                <td className="text-center">
                                                  <span
                                                    className="badge badgeNew bg-secondary text-dark"
                                                    style={{
                                                      fontSize: "0.7rem",
                                                    }}
                                                  >
                                                    {charge.Type || "–"}
                                                  </span>
                                                </td>
                                                <td
                                                  className="text-center"
                                                  style={{ fontWeight: 600 }}
                                                >
                                                  {charge.Quantity}
                                                </td>
                                                {/* <td className="text-end">
                                                  $
                                                  {(
                                                    charge?.NetCost || 0
                                                  ).toFixed(2)}
                                                </td> */}
                                                <td className="text-end">
                                                  $
                                                  {Number(
                                                    charge?.NetCost || 0
                                                  ).toFixed(2)}
                                                </td>
                                                {/* <td className="text-end">
                                                  $
                                                  {(charge.Retail || 0).toFixed(
                                                    2
                                                  )}
                                                </td> */}
                                                <td className="text-end">
                                                  $
                                                  {Number(
                                                    charge?.Retail || 0
                                                  ).toFixed(2)}
                                                </td>
                                                <td
                                                  className="text-end"
                                                  style={{ fontWeight: 600 }}
                                                >
                                                  $
                                                  {/* {(
                                                    charge.ClientPrice || 0
                                                  ).toFixed(2)} */}
                                                  {(
                                                    parseFloat(
                                                      charge.ClientPrice
                                                    ) || 0
                                                  ).toFixed(2)}
                                                </td>
                                                <td className="text-center">
                                                  <span
                                                    className="badge"
                                                    style={{
                                                      backgroundColor:
                                                        charge.Margin >= 40
                                                          ? "#d1fae5"
                                                          : "#fef3c7",
                                                      color:
                                                        charge.Margin >= 40
                                                          ? "#065f46"
                                                          : "#92400e",
                                                      fontSize: "0.7rem",
                                                    }}
                                                  >
                                                    {charge.Margin}%
                                                  </span>
                                                </td>
                                                <td
                                                  className="text-end"
                                                  style={{
                                                    fontWeight: 700,
                                                    fontSize: "0.9375rem",
                                                  }}
                                                >
                                                  $
                                                  {/* {(charge.Total || 0).toFixed(
                                                    2
                                                  )} */}
                                                  {Number(
                                                    charge.Total || 0
                                                  ).toFixed(2)}
                                                </td>
                                                <td className="text-center">
                                                  <span
                                                    className={`badge ${
                                                      charge.DisplayStatus?.includes(
                                                        "Display"
                                                      )
                                                        ? "bg-success"
                                                        : "bg-secondary"
                                                    }`}
                                                    style={{
                                                      fontSize: "0.65rem",
                                                    }}
                                                  >
                                                    {charge.DisplayStatus ||
                                                      "–"}
                                                  </span>
                                                </td>
                                                {/* <td
                                                  style={{ fontSize: "0.7rem" }}
                                                >
                                                  <div className="d-flex flex-column gap-1">
                                                    <span className="badge badgeNew bg-secondary font-monospace">
                                                      C:{" "}
                                                      {charge.chargeId || "–"}
                                                    </span>
                                                    <span className="badge badgeNew bg-info font-monospace">
                                                      D:{" "}
                                                      {charge.decoratorId ||
                                                        "–"}
                                                    </span>
                                                    {charge.supplierId && (
                                                      <span className="badge badgeNew bg-warning text-dark font-monospace">
                                                        S: {charge.supplierId}
                                                      </span>
                                                    )}
                                                    {charge.addressId && (
                                                      <span className="badge badgeNew bg-success font-monospace">
                                                        A: {charge.addressId}
                                                      </span>
                                                    )}
                                                  </div>
                                                </td> */}
                                              </tr>
                                            )
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {/* ROW BINDINGS TABLE - NEW SECTION */}
                                  {deco.DecoratorDetails?.rowBindings &&
                                    deco.DecoratorDetails.rowBindings.length >
                                      0 && (
                                      <div className="mb-3">
                                        <div
                                          style={{
                                            fontSize: "0.75rem",
                                            fontWeight: 600,
                                            color: "#495057",
                                            marginBottom: "0.75rem",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.5rem",
                                          }}
                                        >
                                          <i className="bi bi-diagram-2 text-primary"></i>
                                          <span>
                                            Decorations (
                                            {
                                              deco.DecoratorDetails.rowBindings
                                                .length
                                            }
                                            )
                                          </span>
                                        </div>
                                        <div className="table-responsive">
                                          <table className="table overview-table table-bordered table-sm mb-0">
                                            <thead>
                                              <tr
                                                style={{
                                                  backgroundColor: "#f8f9fa",
                                                }}
                                              >
                                                <th
                                                  className="text-center"
                                                  style={{ fontSize: "0.7rem" }}
                                                >
                                                  No.
                                                </th>
                                                <th
                                                  style={{ fontSize: "0.7rem" }}
                                                >
                                                  Charge Name
                                                </th>
                                                {/* <th
                                                  className="text-center"
                                                  style={{ fontSize: "0.7rem" }}
                                                >
                                                  Charge ID
                                                </th> */}
                                                {/* <th
                                                  className="text-center"
                                                  style={{ fontSize: "0.7rem" }}
                                                >
                                                  Decorator ID
                                                </th> */}
                                                <th
                                                  className="text-center"
                                                  style={{ fontSize: "0.7rem" }}
                                                >
                                                  Department Name
                                                </th>

                                                <th
                                                  className="text-center"
                                                  style={{ fontSize: "0.7rem" }}
                                                >
                                                  Department Status
                                                </th>
                                                {/* <th
                                                  className="text-center"
                                                  style={{ fontSize: "0.7rem" }}
                                                >
                                                  Supplier ID
                                                </th> */}
                                                {/* <th
                                                  className="text-center"
                                                  style={{ fontSize: "0.7rem" }}
                                                >
                                                  Address ID
                                                </th> */}
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {deco.DecoratorDetails.rowBindings.map(
                                                (binding, bindingIdx) => (
                                                  <tr key={bindingIdx}>
                                                    <td
                                                      className="text-center"
                                                      style={{
                                                        fontWeight: 700,
                                                        fontSize: "0.8125rem",
                                                        backgroundColor:
                                                          "#f8f9fa",
                                                      }}
                                                    >
                                                      {/* {binding.rowIndex} */}
                                                      {bindingIdx + 1}
                                                    </td>
                                                    <td
                                                      style={{
                                                        fontWeight: 600,
                                                        fontSize: "0.8125rem",
                                                      }}
                                                    >
                                                      {binding.chargeName}
                                                    </td>
                                                    {/* <td className="text-center">
                        <span className="badge badgeNew bg-primary font-monospace" style={{ fontSize: "0.65rem" }}>
                          {binding.chargeId}
                        </span>
                      </td> */}
                                                    {/* <td
                                                      className="text-center font-monospace"
                                                      style={{
                                                        fontSize: "0.8125rem",
                                                        fontWeight: 600,
                                                      }}
                                                    >
                                                      {binding.chargeId}
                                                    </td> */}
                                                    {/* <td className="text-center">
                                                      {binding.decoratorId ? (
                                                        <span
                                                          className="badge badgeNew bg-info font-monospace"
                                                          style={{
                                                            fontSize: "0.65rem",
                                                          }}
                                                        >
                                                          {binding.decoratorId}
                                                        </span>
                                                      ) : (
                                                        <span className="text-muted small">
                                                          –
                                                        </span>
                                                      )}
                                                    </td> */}

                                                    <td
                                                      style={{
                                                        fontWeight: 600,
                                                        fontSize: "0.8125rem",
                                                      }}
                                                    >
                                                      {
                                                        binding.productionDepartmentName
                                                      }
                                                    </td>
                                                    {/* <td className="text-center">
                                                      {binding.productionDepartmentName ? (
                                                        <span
                                                          className="badge badgeNew bg-success font-monospace"
                                                          style={{
                                                            fontSize: "0.65rem",
                                                          }}
                                                        >
                                                          {
                                                            binding.productionDepartmentName
                                                          }
                                                        </span>
                                                      ) : (
                                                        <span className="text-muted small">
                                                          –
                                                        </span>
                                                      )}
                                                    </td> */}
                                                    <td className="text-center">
                                                      {binding.status ? (
                                                        <span
                                                          className="badge badgeNew bg-success font-monospace"
                                                          style={{
                                                            fontSize: "0.65rem",
                                                          }}
                                                        >
                                                          {binding.status}
                                                        </span>
                                                      ) : (
                                                        <span className="text-muted small">
                                                          –
                                                        </span>
                                                      )}
                                                    </td>
                                                    {/* <td className="text-center">
                                                      {binding.supplierId ? (
                                                        <span
                                                          className="badge badgeNew bg-warning text-dark font-monospace"
                                                          style={{
                                                            fontSize: "0.65rem",
                                                          }}
                                                        >
                                                          {binding.supplierId}
                                                        </span>
                                                      ) : (
                                                        <span className="text-muted small">
                                                          null
                                                        </span>
                                                      )}
                                                    </td> */}
                                                    {/* <td className="text-center">
                                                      {binding.addressId ? (
                                                        <span
                                                          className="badge badgeNew bg-secondary font-monospace"
                                                          style={{
                                                            fontSize: "0.65rem",
                                                          }}
                                                        >
                                                          {binding.addressId}
                                                        </span>
                                                      ) : (
                                                        <span className="text-muted small">
                                                          null
                                                        </span>
                                                      )}
                                                    </td> */}
                                                  </tr>
                                                )
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    )}

                                  {/* Artwork Details - COMPLETE WITH ALL FIELDS */}

                                  {/* Artwork Details - IMPROVED LAYOUT */}
                                  {deco.artworks &&
                                    deco.artworks.length > 0 && (
                                      <div>
                                        <div
                                          style={{
                                            fontSize: "0.75rem",
                                            fontWeight: 600,
                                            color: "#6c757d",
                                            marginBottom: "0.75rem",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                          }}
                                        >
                                          <i className="bi bi-image me-1"></i>
                                          Artwork Details (
                                          {deco.artworks.length})
                                        </div>
                                        <div className="row g-3">
                                          {deco.artworks.map((art) => (
                                            <div
                                              key={art.id}
                                              className="col-12"
                                            >
                                              <div
                                                style={{
                                                  padding: "1rem",
                                                  backgroundColor: "#f8f9fa",
                                                  border: "1px solid #e9ecef",
                                                  borderRadius: "0.25rem",
                                                }}
                                              >
                                                {/* Horizontal Layout: Image Left, Details Right */}
                                                <div className="row g-3">
                                                  {/* Left Side - Artwork Image */}
                                                  {/* {art.Image && (
                                                    <div className="col-lg-4 col-md-5">
                                                      <div
                                                        style={{
                                                          padding: "0.75rem",
                                                          backgroundColor:
                                                            "#ffffff",
                                                          border:
                                                            "1px solid #e9ecef",
                                                          borderRadius:
                                                            "0.25rem",
                                                          textAlign: "center",
                                                          minHeight: "200px",
                                                          display: "flex",
                                                          alignItems: "center",
                                                          justifyContent:
                                                            "center",
                                                          height: "100%",
                                                        }}
                                                      >
                                                        <img
                                                          src={art.Image}
                                                          alt="Artwork"
                                                          style={{
                                                            maxWidth: "100%",
                                                            maxHeight: "180px",
                                                            objectFit:
                                                              "contain",
                                                          }}
                                                        />
                                                      </div>
                                                    </div>
                                                  )} */}

                                                  {/* Left Side - Artwork Image - Always show */}
                                                  <div className="col-lg-4 col-md-5">
                                                    <div
                                                      style={{
                                                        padding: "0.75rem",
                                                        backgroundColor:
                                                          "#ffffff",
                                                        border:
                                                          "1px solid #e9ecef",
                                                        borderRadius: "0.25rem",
                                                        textAlign: "center",
                                                        minHeight: "200px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                          "center",
                                                        height: "100%",
                                                      }}
                                                    >
                                                      {art.Image ? (
                                                        <img
                                                          src={art.Image}
                                                          alt="Artwork"
                                                          style={{
                                                            maxWidth: "100%",
                                                            maxHeight: "180px",
                                                            objectFit:
                                                              "contain",
                                                          }}
                                                        />
                                                      ) : (
                                                        <div
                                                          style={{
                                                            display: "flex",
                                                            flexDirection:
                                                              "column",
                                                            alignItems:
                                                              "center",
                                                            justifyContent:
                                                              "center",
                                                            color: "#9ca3af",
                                                          }}
                                                        >
                                                          <i
                                                            className="bi bi-image"
                                                            style={{
                                                              fontSize: "3rem",
                                                              marginBottom:
                                                                "0.5rem",
                                                            }}
                                                          ></i>
                                                          <span
                                                            style={{
                                                              fontSize:
                                                                "0.875rem",
                                                              fontStyle:
                                                                "italic",
                                                            }}
                                                          >
                                                            No Image Available
                                                          </span>
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>

                                                  {/* Right Side - Artwork Information */}
                                                  {/* <div
                                                    className={
                                                      art.Image
                                                        ? "col-lg-8 col-md-7"
                                                        : "col-12"
                                                    }
                                                  > */}

                                                  <div className="col-lg-8 col-md-7">
                                                    <div
                                                      style={{
                                                        fontSize: "0.8125rem",
                                                      }}
                                                    >
                                                      {/* Two Column Grid for Details */}
                                                      <div className="row g-3">
                                                        <div className="col-md-6">
                                                          <div className="d-flex justify-content-between py-2 border-bottom">
                                                            <span
                                                              style={{
                                                                color:
                                                                  "#6c757d",
                                                                fontWeight: 500,
                                                              }}
                                                            >
                                                              Artwork ID:
                                                            </span>
                                                            <span className="font-monospace fw-semibold">
                                                              {art.id}
                                                            </span>
                                                          </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                          <div className="d-flex justify-content-between py-2 border-bottom">
                                                            <span
                                                              style={{
                                                                color:
                                                                  "#6c757d",
                                                                fontWeight: 500,
                                                              }}
                                                            >
                                                              Type:
                                                            </span>
                                                            <span className="fw-semibold">
                                                              {art.ImprintType}
                                                            </span>
                                                          </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                          <div className="d-flex justify-content-between py-2 border-bottom">
                                                            <span
                                                              style={{
                                                                color:
                                                                  "#6c757d",
                                                                fontWeight: 500,
                                                              }}
                                                            >
                                                              Location:
                                                            </span>
                                                            <span className="fw-semibold">
                                                              {art.Location ||
                                                                deco.Location ||
                                                                "–"}
                                                            </span>
                                                          </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                          <div className="d-flex justify-content-between py-2 border-bottom">
                                                            <span
                                                              style={{
                                                                color:
                                                                  "#6c757d",
                                                                fontWeight: 500,
                                                              }}
                                                            >
                                                              Logo Name:
                                                            </span>
                                                            <span className="fw-semibold" style={{ whiteSpace: "pre-line" }}>
                                                              {art.LogoName}
                                                            </span>
                                                          </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                          <div className="d-flex justify-content-between py-2 border-bottom">
                                                            <span
                                                              style={{
                                                                color:
                                                                  "#6c757d",
                                                                fontWeight: 500,
                                                              }}
                                                            >
                                                              Logo Size:
                                                            </span>
                                                            <span className="fw-semibold">
                                                              {art.LogoSize}
                                                            </span>
                                                          </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                          <div className="d-flex justify-content-between py-2 border-bottom">
                                                            <span
                                                              style={{
                                                                color:
                                                                  "#6c757d",
                                                                fontWeight: 500,
                                                              }}
                                                            >
                                                              Logo Color:
                                                            </span>
                                                            <span className="fw-semibold">
                                                              {art.LogoColor}
                                                            </span>
                                                          </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                          <div className="d-flex justify-content-between py-2 border-bottom">
                                                            <span
                                                              style={{
                                                                color:
                                                                  "#6c757d",
                                                                fontWeight: 500,
                                                              }}
                                                            >
                                                              Repeat Logo:
                                                            </span>
                                                            <span className="fw-semibold">
                                                              {art.RepeatLogo}
                                                            </span>
                                                          </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                          <div className="d-flex justify-content-between py-2 border-bottom">
                                                            <span
                                                              style={{
                                                                color:
                                                                  "#6c757d",
                                                                fontWeight: 500,
                                                              }}
                                                            >
                                                              Proof Required:
                                                            </span>
                                                            <span
                                                              className={`badge badgeNew ${
                                                                art.ProofRequired ===
                                                                "1"
                                                                  ? "bg-danger"
                                                                  : "bg-success"
                                                              }`}
                                                              style={{
                                                                fontSize:
                                                                  "0.7rem",
                                                              }}
                                                            >
                                                              {art.ProofRequired ===
                                                              "1"
                                                                ? `Yes (Action: ${art.ProofRequiredAction})`
                                                                : "No"}
                                                            </span>
                                                          </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                          <div className="d-flex justify-content-between py-2 border-bottom">
                                                            <span
                                                              style={{
                                                                color:
                                                                  "#6c757d",
                                                                fontWeight: 500,
                                                              }}
                                                            >
                                                              Charge:
                                                            </span>
                                                            <span className="fw-semibold">
                                                              $
                                                              {(
                                                                art.Charge || 0
                                                              ).toFixed(2)}
                                                            </span>
                                                          </div>
                                                        </div>
                                                      </div>

                                                      {/* Supplier Notes - Full Width if exists */}
                                                      {/* {art.SupplierNotes && (
                                                        <div className="mt-3 pt-3 border-top">
                                                          <div
                                                            style={{
                                                              color: "#6c757d",
                                                              fontWeight: 600,
                                                              fontSize:
                                                                "0.75rem",
                                                              marginBottom:
                                                                "0.5rem",
                                                              textTransform:
                                                                "uppercase",
                                                              letterSpacing:
                                                                "0.5px",
                                                            }}
                                                          >
                                                            Supplier Notes
                                                          </div>
                                                          <div
                                                            style={{
                                                              color: "#495057",
                                                              fontSize:
                                                                "0.8125rem",
                                                              lineHeight: "1.6",
                                                            }}
                                                          >
                                                            {art.SupplierNotes}
                                                          </div>
                                                        </div>
                                                      )} */}

                                                      {/* Supplier Notes - Always show section */}
                                                      {/* <div className="mt-3 pt-3 border-top"> */}
                                                      <div className="mt-3">
                                                        <div
                                                          style={{
                                                            color: "#6c757d",
                                                            fontWeight: 600,
                                                            fontSize: "0.75rem",
                                                            marginBottom:
                                                              "0.5rem",
                                                            textTransform:
                                                              "uppercase",
                                                            letterSpacing:
                                                              "0.5px",
                                                          }}
                                                        >
                                                          Supplier Notes
                                                        </div>
                                                        <div
                                                          style={{
                                                            color:
                                                              art.SupplierNotes &&
                                                              art.SupplierNotes.trim()
                                                                ? "#495057"
                                                                : "#9ca3af",
                                                            fontSize:
                                                              "0.8125rem",
                                                            lineHeight: "1.6",
                                                            fontStyle:
                                                              art.SupplierNotes &&
                                                              art.SupplierNotes.trim()
                                                                ? "normal"
                                                                : "italic",
                                                          }}
                                                        >
                                                          {art.SupplierNotes &&
                                                          art.SupplierNotes.trim()
                                                            ? art.SupplierNotes
                                                            : "No supplier notes"}
                                                        </div>
                                                      </div>

                                                      {/* Timestamps */}
                                                      <div
                                                        className="d-flex justify-content-between mt-3 pt-3 border-top"
                                                        style={{
                                                          fontSize: "0.75rem",
                                                          color: "#6c757d",
                                                        }}
                                                      >
                                                        <span>
                                                          <i className="bi bi-calendar-plus me-1"></i>
                                                          Art Created:{" "}
                                                          {art.createdAt
                                                            ? DATE_FMT(
                                                                art.createdAt
                                                              )
                                                            : "–"}
                                                        </span>
                                                        <span>
                                                          <i className="bi bi-calendar-check me-1"></i>
                                                          Art Updated:{" "}
                                                          {art.updatedAt
                                                            ? DATE_FMT(
                                                                art.updatedAt
                                                              )
                                                            : "–"}
                                                        </span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                  {/* Proofing Details - Display-only */}
                                  {(() => {
                                    const proofingRaw = [
                                      ...(Array.isArray(detailedCard?.poProofs)
                                        ? detailedCard.poProofs
                                        : []),
                                      ...(Array.isArray(orderMeta?.proofsNormalized)
                                        ? orderMeta.proofsNormalized
                                        : []),
                                      ...(Array.isArray(deco.proofs) ? deco.proofs : []),
                                      ...(Array.isArray(deco.proofings) ? deco.proofings : []),
                                      ...(Array.isArray(deco.proofingItems) ? deco.proofingItems : []),
                                    ];
                                    const seen = new Set();
                                    const proofingItems = proofingRaw.filter((proof) => {
                                      if (!proof || typeof proof !== "object") return false;
                                      const key = String(
                                        proof.id ||
                                          proof.filePath ||
                                          proof.proofFilePath ||
                                          proof.previewUrl ||
                                          `${proof.createdAt || ""}-${proof.updatedAt || ""}`
                                      );
                                      if (seen.has(key)) return false;
                                      seen.add(key);
                                      return true;
                                    });
                                    if (!proofingItems.length) return null;
                                    const valueOrDash = (v) => (v === 0 || v ? v : "–");
                                    return (
                                      <div className="mt-3">
                                        <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#6c757d", marginBottom: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                          <i className="bi bi-check2-square me-1"></i>
                                          Proofing ({proofingItems.length})
                                        </div>
                                        <div className="row g-3">
                                          {proofingItems.map((proof, idx) => {
                                            const previewSrc = resolveProofPreviewPath(proof);
                                            const fileName = resolveProofPreviewFileName(proof, previewSrc);
                                            return (
                                              <div key={proof.id || `${idx}-${fileName || "proof"}`} className="col-12">
                                                <div style={{ padding: "1rem", backgroundColor: "#f8f9fa", border: "1px solid #e9ecef", borderRadius: "0.25rem" }}>
                                                  <div className="row g-3">
                                                    <div className="col-lg-4 col-md-5">
                                                      <div style={{ padding: "0.75rem", backgroundColor: "#ffffff", border: "1px solid #e9ecef", borderRadius: "0.25rem", textAlign: "center", minHeight: "200px", display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                                                        <SmartProofPreview
                                                          filePath={previewSrc}
                                                          fileName={fileName}
                                                          height={200}
                                                          onPreviewOpen={openPreviewPdf}
                                                          sx={{
                                                            height: "100%",
                                                            minHeight: 200,
                                                            maxWidth: "100%",
                                                            border: "none",
                                                            borderRadius: 0,
                                                            bgcolor: "transparent",
                                                            p: 0,
                                                          }}
                                                        />
                                                      </div>
                                                    </div>
                                                    <div className="col-lg-8 col-md-7">
                                                      <div style={{ fontSize: "0.8125rem" }}>
                                                        <div className="row g-3">
                                                          <div className="col-md-6"><div className="d-flex justify-content-between py-2 border-bottom"><span style={{ color: "#6c757d", fontWeight: 500 }}>File Name:</span><span className="fw-semibold text-end ms-2">{valueOrDash(fileName)}</span></div></div>
                                                          <div className="col-md-6"><div className="d-flex justify-content-between py-2 border-bottom"><span style={{ color: "#6c757d", fontWeight: 500 }}>Proof Status:</span><span className="fw-semibold">{valueOrDash(proof.status || proof.proofStatus)}</span></div></div>
                                                          <div className="col-md-6"><div className="d-flex justify-content-between py-2 border-bottom"><span style={{ color: "#6c757d", fontWeight: 500 }}>Uploaded By:</span><span className="fw-semibold">{valueOrDash(proof.uploadedByName || proof.uploadedBy || proof.createdByName)}</span></div></div>
                                                          <div className="col-md-6"><div className="d-flex justify-content-between py-2 border-bottom"><span style={{ color: "#6c757d", fontWeight: 500 }}>Approval State:</span><span className="fw-semibold">{valueOrDash(proof.approvalState || proof.approvalStatus || proof.clientApprovalStatus)}</span></div></div>
                                                          <div className="col-md-6"><div className="d-flex justify-content-between py-2 border-bottom"><span style={{ color: "#6c757d", fontWeight: 500 }}>Location:</span><span className="fw-semibold">{valueOrDash(proof.location || proof.Location || deco.Location)}</span></div></div>
                                                          <div className="col-md-6"><div className="d-flex justify-content-between py-2 border-bottom"><span style={{ color: "#6c757d", fontWeight: 500 }}>Decoration:</span><span className="fw-semibold">{valueOrDash(proof.decorationName || deco.DecorationMethod || deco.ImprintType)}</span></div></div>
                                                        </div>
                                                        <div className="mt-3">
                                                          <div style={{ color: "#6c757d", fontWeight: 600, fontSize: "0.75rem", marginBottom: "0.5rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Notes</div>
                                                          <div style={{ color: proof.notes || proof.note ? "#495057" : "#9ca3af", fontSize: "0.8125rem", lineHeight: "1.6", fontStyle: proof.notes || proof.note ? "normal" : "italic" }}>
                                                            {proof.notes || proof.note || "No proof notes"}
                                                          </div>
                                                        </div>
                                                        <div className="d-flex justify-content-between mt-3 pt-3 border-top" style={{ fontSize: "0.75rem", color: "#6c757d" }}>
                                                          <span><i className="bi bi-calendar-plus me-1"></i>Created: {proof.createdAt ? DATE_FMT(proof.createdAt) : "–"}</span>
                                                          <span><i className="bi bi-calendar-check me-1"></i>Updated: {proof.updatedAt ? DATE_FMT(proof.updatedAt) : "–"}</span>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })()}

                                  {/* Decoration Timestamps */}
                                  <div
                                    className="d-flex justify-content-between mt-3 pt-2 border-top"
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#6c757d",
                                    }}
                                  >
                                    <span>
                                      Created:{" "}
                                      {deco.createdAt
                                        ? DATE_FMT(deco.createdAt)
                                        : "–"}
                                    </span>
                                    <span>
                                      Updated:{" "}
                                      {deco.updatedAt
                                        ? DATE_FMT(deco.updatedAt)
                                        : "–"}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Batches Section */}
                  {hasSibs && (
                    <div
                      className="card overview-card border-0"
                      style={{ backgroundColor: "#ffffff" }}
                    >
                      <div className="card-body" style={{ padding: "1.25rem" }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="section-header mb-0 pb-0 border-0">
                            <i className="bi bi-layers me-2"></i>Batches
                          </h6>
                          <span
                            className="badge badgeNew bg-primary"
                            style={{ fontSize: "0.6875rem" }}
                          >
                            {siblingsAll.length} Total
                          </span>
                        </div>
                        <div className="table-responsive">
                          <table className="table overview-table table-bordered mb-0">
                            <thead>
                              <tr>
                                <th>Batch ID</th>
                                <th>Status</th>
                                <th className="text-center">Quantity</th>
                                <th>Machine</th>
                                <th>Planned Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {siblingsAll.map((s) => {
                                const isCurrent = s.step_id === card.step_id;
                                return (
                                  <tr
                                    key={s.id}
                                    style={{
                                      backgroundColor: isCurrent
                                        ? "#f8f9fa"
                                        : "#ffffff",
                                    }}
                                  >
                                    <td
                                      style={{
                                        fontWeight: isCurrent ? 700 : 500,
                                      }}
                                    >
                                      {isCurrent && (
                                        <i className="bi bi-arrow-right-circle-fill text-primary me-2"></i>
                                      )}
                                      {isCurrent
                                        ? "Current Batch"
                                        : s.batchLabel ||
                                          s.displayNumber ||
                                          s.id}
                                    </td>
                                    <td>
                                      {s.status?.replace(/_/g, " ") || "–"}
                                    </td>
                                    <td
                                      className="text-center"
                                      style={{ fontWeight: 600 }}
                                    >
                                      {s.qtyPlanned ?? 0}
                                    </td>
                                    <td>{s.machine || "–"}</td>
                                    <td>
                                      {s.plannedStart
                                        ? DATE_FMT(s.plannedStart)
                                        : "–"}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* No Details State */}
              {!modalLoading && !detailedCard && (
                <div className="text-center" style={{ padding: "5rem 1.5rem" }}>
                  <i
                    className="bi bi-inbox"
                    style={{ fontSize: "4rem", color: "#dee2e6" }}
                  ></i>
                  <div
                    className="mt-3"
                    style={{
                      fontSize: "0.9375rem",
                      fontWeight: 600,
                      color: "#6c757d",
                    }}
                  >
                    No order details available
                  </div>
                  <div style={{ fontSize: "0.8125rem", color: "#adb5bd" }}>
                    Select an order to view details
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* {tab === "overview2" && (
          <div
            style={{
              padding: 16,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
              gap: 16,
            }}
          >
            {modalLoading && (
              <div
                style={{ gridColumn: "1/-1", textAlign: "center", padding: 20 }}
              >
                Loading…
              </div>
            )}

            {!modalLoading && detailedCard && (
              <>
                <Section title="Project Information" style={sectionCard}>
                  <Row l="Project name" v={detailedCard.ProjectName || "–"} />
                  <Row l="Project Number" v={detailedCard.OrderId || "–"} />
                  <RowChip
                    l="PO Numbers"
                    list={
                      Array.isArray(detailedCard.poNumbers)
                        ? detailedCard.poNumbers
                        : []
                    }
                  />
                </Section>

                <Section title="Product Summary" style={sectionCard}>
                  <Row
                    l="Sizes"
                    v={(aggregatedSizes || []).join(", ") || "–"}
                  />
                  <Row
                    l="Colors"
                    v={(aggregatedColors || []).join(", ") || "–"}
                  />
                  <Row l="Net cost goods" v={money(totalNetCostGoods ?? 0)} />
                  <Row
                    l="Retail cost goods"
                    v={money(totalRetailCostGoods ?? 0)}
                  />
                  <Row l="Supplier information" v={supplierName} />
                  <Row l="Category" v={category} />
                </Section>

                {detailedCard.Items?.map((item) => (
                  <Section
                    key={item.id}
                    title={`Product: ${item.Name || "–"}`}
                    style={sectionCard}
                  >
                    <Row l="SKU" v={item.ProductId || "–"} />
                    <Row l="Name" v={item.Name || "–"} />
                    <Row l="Quantity" v={item.Quantity ?? 0} />
                    <Row l="Color" v={item.Color || "–"} />
                    <Row l="Size" v={item.Size || "–"} />
                    <Row
                      l="Description"
                      v={item.ShortDescription || item.Description || "–"}
                    />
                    <Row
                      l="Image"
                      v={
                        item.ImageUrl ? (
                          <img
                            src={item.ImageUrl}
                            alt={item.Name}
                            style={{ maxWidth: 100 }}
                          />
                        ) : (
                          "–"
                        )
                      }
                    />

                    {item.decorations?.map((dec) => (
                      <div
                        key={dec.id}
                        style={{ marginTop: 8, paddingLeft: 8 }}
                      >
                        <Row
                          l="Deco Dept"
                          v={dec.DecoratorDetails?.source || "–"}
                        />
                        {dec.Charge?.map((ch, i) => (
                          <Row
                            key={i}
                            l={ch.Name || "–"}
                            v={`Total: ${ch.Total ?? 0}, Net: ${
                              ch.NetCost ?? 0
                            }, Retail: ${ch.Retail ?? 0}`}
                          />
                        ))}
                      </div>
                    ))}
                  </Section>
                ))}

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 text-orange-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                      />
                    </svg>
                    Decoration Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">
                        Deco department
                      </span>
                      <span className="font-medium text-gray-900">
                        {card.claimedDept ||
                          card.department ||
                          detailedCard?.Items?.[0]?.decorations?.[0]
                            ?.DecoratorDetails?.source ||
                          "–"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">
                        Matrix pricing
                      </span>
                      <span className="font-medium text-gray-900">
                        {card.matrix ||
                          card.chargeName ||
                          detailedCard?.Items?.[0]?.decorations?.[0]
                            ?.Charge?.[0]?.Name ||
                          "–"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">
                        Net cost goods
                      </span>
                      <span className="font-medium text-gray-900">
                        {money(
                          detailedCard?.Items?.reduce((sum, item) => {
                            return (
                              sum +
                              (item.decorations?.reduce((decoSum, deco) => {
                                return (
                                  decoSum +
                                  (deco.Charge?.reduce((chargeSum, charge) => {
                                    return (
                                      chargeSum +
                                      parseFloat(charge.NetCost || 0)
                                    );
                                  }, 0) || 0)
                                );
                              }, 0) || 0)
                            );
                          }, 0) ||
                            card.netCostGoods ||
                            0
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">
                        Retail cost goods
                      </span>
                      <span className="font-medium text-gray-900">
                        {money(
                          detailedCard?.Items?.reduce((sum, item) => {
                            return (
                              sum +
                              (item.decorations?.reduce((decoSum, deco) => {
                                return (
                                  decoSum +
                                  (deco.Charge?.reduce((chargeSum, charge) => {
                                    return (
                                      chargeSum + parseFloat(charge.Retail || 0)
                                    );
                                  }, 0) || 0)
                                );
                              }, 0) || 0)
                            );
                          }, 0) ||
                            card.retailCostGoods ||
                            0
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">Location</span>
                      <span className="font-medium text-gray-900">
                        {detailedCard?.Items?.[0]?.decorations?.[0]?.Location ||
                          card.location ||
                          "–"}
                      </span>
                    </div>
                  </div>
                </div>

                <Section title="Art Information" style={sectionCard}>
                  {detailedCard.artProofUrl ? (
                    <a
                      href={detailedCard.artProofUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: "underline" }}
                    >
                      Open proof
                    </a>
                  ) : (
                    <div style={ui.value}>–</div>
                  )}
                </Section>

                <Section title="Customer & Dates" style={sectionCard}>
                  <Row l="Customer" v={detailedCard.CustomerName || "–"} />
                  <Row
                    l="Qty (this / total)"
                    v={detailedCard.qtyPlanned ?? detailedCard.rootQty ?? 0}
                  />
                  <Row l="Decorator PO In-Hands" v={formatProductionBusinessDate(detailedCard.decoratorPoInHandsDate)} />
                  <Row
                    l="Customer In-Hands"
                    v={
                      detailedCard.inHandsDate
                        ? formatProductionBusinessDate(detailedCard.inHandsDate)
                        : "–"
                    }
                  />
                  <Row
                    l="Scheduled Date"
                    v={
                      detailedCard.plannedStart
                        ? DATE_FMT(detailedCard.plannedStart)
                        : "–"
                    }
                  />
                  <Row
                    l="Machine"
                    v={detailedCard.machine || (hasSibs ? "Multi" : "–")}
                  />
                  <Row
                    l="Order Status (SO)"
                    v={detailedCard.OrderStatus?.SO || "–"}
                  />
                  <Row
                    l="Order Status (PO)"
                    v={detailedCard.OrderStatus?.PO || "–"}
                  />
                </Section>

                {hasSibs && (
                  <div style={{ gridColumn: "1/-1" }}>
                    <div
                      style={{ ...ui.subtle, marginBottom: 6, fontWeight: 600 }}
                    >
                      Batches
                    </div>
                    <div
                      style={{
                        border: "1px solid #eee",
                        borderRadius: 8,
                        overflow: "hidden",
                      }}
                    >
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: 12,
                        }}
                      >
                        <thead>
                          <tr style={{ background: "#f9fafb" }}>
                            <th style={th}>ID</th>
                            <th style={th}>Lane</th>
                            <th style={th}>Qty</th>
                            <th style={th}>Machine</th>
                            <th style={th}>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {siblingsAll.map((s) => (
                            <tr
                              key={s.id}
                              style={{ borderTop: "1px solid #eee" }}
                            >
                              <td style={td}>
                                {s.step_id === card.step_id ? (
                                  <b>(This)</b>
                                ) : (
                                  <span>
                                    {s.batchLabel || s.displayNumber || s.id}
                                  </span>
                                )}
                              </td>

                              <td style={td}>
                                {s.status?.replace(/_/g, " ") || "–"}
                              </td>
                              <td style={td}>{s.qtyPlanned ?? 0}</td>
                              <td style={td}>{s.machine || "–"}</td>
                              <td style={td}>
                                {s.plannedStart
                                  ? DATE_FMT(s.plannedStart)
                                  : "–"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {!modalLoading && !detailedCard && (
              <div
                style={{ gridColumn: "1/-1", textAlign: "center", padding: 20 }}
              >
                No details available.
              </div>
            )}
          </div>
        )} */}

        {tab === "processes" && (
          <div className="bg-gray-50 min-h-[500px]">
            {/* Header */}
            <div className="bg-gradient-to-b from-white to-gray-50 border-b-2 border-gray-200 px-5 py-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 m-0">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Production Processes
                  </h3>
                  <p className="text-sm text-gray-500 mt-1 mb-0">
                    Manufacturing steps for this job
                  </p>
                </div>
                <button
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={addProcess}
                  disabled={isReadOnly}
                  title={isArchived ? "Archived Production is read-only" : "Add process"}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Add Process
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-5">
              {processes?.length > 0 ? (
                <>
                  {/* Table */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    {/* Header Row */}
                    <div className="grid grid-cols-[60px_2.5fr_2fr_100px_140px_110px_130px_150px] gap-3 px-4 py-3 bg-gray-50 border-b-2 border-gray-200">
                      <div className="text-xs font-semibold text-gray-600 uppercase">
                        Step
                      </div>
                      <div className="text-xs font-semibold text-gray-600 uppercase">
                        Name
                      </div>
                      <div className="text-xs font-semibold text-gray-600 uppercase">
                        Person
                      </div>
                      <div className="text-xs font-semibold text-gray-600 uppercase">
                        Type
                      </div>
                      <div className="text-xs font-semibold text-gray-600 uppercase">
                        Machine
                      </div>
                      <div className="text-xs font-semibold text-gray-600 uppercase">
                        Minutes
                      </div>
                      <div className="text-xs font-semibold text-gray-600 uppercase">
                        Cost ($)
                      </div>
                      {/* <div className="text-xs font-semibold text-gray-600 uppercase">
                        Due Date
                      </div> */}
                      <div className="text-xs font-semibold text-gray-600 uppercase text-center">
                        Actions
                      </div>
                    </div>

                    {/* Data Rows */}
                    {processes.map((p, idx) => {
                      const isEditing = editingProcessIdx === idx;
                      const isNew = String(p.id).startsWith("proc-");

                      return (
                        <div
                          key={p.id}
                          //   className={`grid grid-cols-[60px_1.5fr_1fr_90px_120px_100px_120px_120px_140px] gap-3 px-4 py-4 border-b border-gray-100 last:border-b-0 ${
                          //     isEditing
                          //       ? "bg-blue-50"
                          //       : "bg-white hover:bg-gray-50"
                          //   }`}
                          // >
                          className={`grid grid-cols-[60px_2.5fr_2fr_100px_140px_110px_130px_150px] gap-3 px-4 py-4 border-b border-gray-100 last:border-b-0 ${
                            isEditing
                              ? "bg-blue-50"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >
                          {/* Step # */}
                          <input
                            type="number"
                            value={p.step_no}
                            onChange={(e) =>
                              updateAt(setProcesses, idx, {
                                ...p,
                                step_no: e.target.value,
                              })
                            }
                            disabled={isReadOnly || (!isEditing && !isNew)}
                            className={`form-control form-control-sm text-center ${
                              !isEditing && !isNew ? "bg-gray-50" : ""
                            }`}
                          />

                          {/* Name */}
                          <input
                            type="text"
                            value={p.name}
                            onChange={(e) =>
                              updateAt(setProcesses, idx, {
                                ...p,
                                name: e.target.value,
                              })
                            }
                            disabled={isReadOnly || (!isEditing && !isNew)}
                            placeholder="Process name"
                            className={`form-control form-control-sm ${
                              !isEditing && !isNew ? "bg-gray-50" : ""
                            }`}
                          />

                          {/* Person */}
                          <input
                            type="text"
                            value={p.person}
                            onChange={(e) =>
                              updateAt(setProcesses, idx, {
                                ...p,
                                person: e.target.value,
                              })
                            }
                            disabled={isReadOnly || (!isEditing && !isNew)}
                            placeholder="Operator"
                            className={`form-control form-control-sm ${
                              !isEditing && !isNew ? "bg-gray-50" : ""
                            }`}
                          />

                          {/* Type */}
                          <select
                            value={p.type}
                            onChange={(e) =>
                              updateAt(setProcesses, idx, {
                                ...p,
                                type: e.target.value,
                              })
                            }
                            disabled={isReadOnly || (!isEditing && !isNew)}
                            className={`form-control form-control-sm ${
                              !isEditing && !isNew ? "bg-gray-50" : ""
                            }`}
                          >
                            <option value="manual">Manual</option>
                            <option value="machine">Machine</option>
                          </select>

                          {/* Machine */}
                          <input
                            type="text"
                            value={p.machine_name || ""}
                            onChange={(e) =>
                              updateAt(setProcesses, idx, {
                                ...p,
                                machine_name: e.target.value,
                              })
                            }
                            disabled={isReadOnly || (!isEditing && !isNew)}
                            placeholder="Optional"
                            className={`form-control form-control-sm ${
                              !isEditing && !isNew ? "bg-gray-50" : ""
                            }`}
                          />

                          {/* Est. Minutes */}
                          <input
                            type="number"
                            value={p.est_minutes || ""}
                            onChange={(e) =>
                              updateAt(setProcesses, idx, {
                                ...p,
                                est_minutes: e.target.value,
                              })
                            }
                            disabled={isReadOnly || (!isEditing && !isNew)}
                            placeholder="0"
                            className={`form-control form-control-sm text-end ${
                              !isEditing && !isNew ? "bg-gray-50" : ""
                            }`}
                          />

                          {/* Cost */}
                          <input
                            type="number"
                            value={p.cost || ""}
                            onChange={(e) =>
                              updateAt(setProcesses, idx, {
                                ...p,
                                cost: e.target.value,
                              })
                            }
                            disabled={isReadOnly || (!isEditing && !isNew)}
                            placeholder="0.00"
                            className={`form-control form-control-sm text-end ${
                              !isEditing && !isNew ? "bg-gray-50" : ""
                            }`}
                          />

                          <div className="d-flex gap-2 justify-content-center align-items-center">
                            {isEditing || isNew ? (
                              <>
                                {/* Save Button */}
                                <button
                                  onClick={() => saveProcessRow(idx)}
                                  disabled={isReadOnly}
                                  className="btn btn-sm btn-success d-flex align-items-center gap-1 px-3"
                                  title="Save changes"
                                >
                                  <i className="bi bi-check-lg"></i>
                                  Save
                                </button>

                                {/* Cancel Button (only for existing items) */}
                                  {!isNew && (
                                    <button
                                    onClick={cancelEditProcess}
                                    disabled={isReadOnly}
                                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                    title="Cancel editing"
                                  >
                                    <i className="bi bi-x-lg"></i>
                                  </button>
                                )}

                                {/* Delete Button */}
                                <button
                                  onClick={() => removeProcess(idx)}
                                  disabled={isReadOnly}
                                  className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                  title="Delete process"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </>
                            ) : (
                              <>
                                {/* Edit Button */}
                                <button
                                  onClick={() => startEditProcess(idx)}
                                  disabled={isReadOnly}
                                  className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 px-3"
                                  title="Edit process"
                                >
                                  <i className="bi bi-pencil"></i>
                                  Edit
                                </button>

                                {/* Delete Button */}
                                <button
                                  onClick={() => removeProcess(idx)}
                                  disabled={isReadOnly}
                                  className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                  title="Delete process"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Info Footer */}
                  <div className="alert alert-info d-flex align-items-center gap-2 mt-3 mb-0">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                    </svg>
                    <span className="text-sm">
                      Click <strong>Edit</strong> to modify. Changes save
                      individually.
                    </span>
                  </div>
                </>
              ) : (
                /* Empty State */
                (<div className="text-center py-5 bg-white border-2 border-dashed border-gray-300 rounded-lg">
                  <svg
                    className="w-16 h-16 mx-auto mb-3 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    <path d="M9 12h6m-6 4h6" />
                  </svg>
                  <h4 className="font-semibold text-gray-700 mb-2">
                    No Processes Yet
                  </h4>
                  <p className="text-gray-500 mb-3">
                    Click Add Process to define steps
                  </p>
                  <button
                    className="btn btn-outline-primary"
                    onClick={addProcess}
                    disabled={isReadOnly}
                  >
                    Add First Process
                  </button>
                </div>)
              )}
            </div>
          </div>
        )}

        {tab === "bom" && (
          <div className="bg-gray-50 min-h-[500px]">
            {/* Header Section */}
            <div className="bg-gradient-to-b from-white to-gray-50 border-b-2 border-gray-200 px-5 py-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 m-0">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Bill of Materials
                  </h3>
                  <p className="text-sm text-gray-500 mt-1 mb-0">
                    Manage components and materials for this job
                  </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2 shadow-sm"
                    onClick={previewSuggestedBOM}
                    disabled={bomPreviewLoading}
                  >
                    <i className="bi bi-eye"></i>
                    {bomPreviewLoading ? "Loading..." : "Preview Suggested BOM"}
                  </button>
                  <button
                    className="btn btn-primary d-flex align-items-center gap-2 shadow-sm"
                    onClick={addBOM}
                    disabled={isReadOnly}
                    title={isArchived ? "Archived Production is read-only" : "Add BOM item"}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    Add Item
                  </button>
                </div>
              </div>
            </div>

            {/* Table Container */}
            <div className="p-5">
              {bom?.length > 0 ? (
                <>
                  {/* Table Header */}
                  <div className="bg-white border border-gray-200 rounded-t-lg overflow-hidden">
                    <div className="grid grid-cols-[140px_1.8fr_90px_90px_130px_130px_140px] gap-3 px-4 py-3 bg-gray-50 border-b-2 border-gray-200">
                      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        SKU / Part #
                      </div>
                      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Description
                      </div>
                      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Quantity
                      </div>
                      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Unit
                      </div>
                      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Unit Price
                      </div>
                      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Total Cost
                      </div>
                      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide text-center">
                        Actions
                      </div>
                    </div>

                    {/* Table Rows */}
                    {bom.map((b, idx) => {
                      const isEditing = editingBomIdx === idx;
                      const isNew = String(b.id).startsWith("bom-");
                      const totalCost =
                        (parseFloat(b.qtyper) || 0) *
                        (parseFloat(b.unitprice) || 0);

                      return (
                        <div
                          key={b.id}
                          className={`grid grid-cols-[140px_1.8fr_90px_90px_130px_130px_140px] gap-3 px-4 py-4 border-b border-gray-100 last:border-b-0 transition-colors ${
                            isEditing
                              ? "bg-blue-50"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >
                          {/* SKU */}
                          <input
                            type="text"
                            value={b.sku}
                            onChange={(e) =>
                              updateAt(setBOM, idx, {
                                ...b,
                                sku: e.target.value,
                              })
                            }
                            disabled={isReadOnly || (!isEditing && !isNew)}
                            placeholder="SKU-001"
                            className={`form-control form-control-sm font-monospace ${
                              !isEditing && !isNew
                                ? "bg-gray-50 text-gray-600"
                                : ""
                            }`}
                          />

                          {/* Description */}
                          <input
                            type="text"
                            value={b.description}
                            onChange={(e) =>
                              updateAt(setBOM, idx, {
                                ...b,
                                description: e.target.value,
                              })
                            }
                            disabled={isReadOnly || (!isEditing && !isNew)}
                            placeholder="Item description"
                            className={`form-control form-control-sm ${
                              !isEditing && !isNew
                                ? "bg-gray-50 text-gray-600"
                                : ""
                            }`}
                          />

                          {/* Quantity */}
                          <input
                            type="number"
                            value={b.qtyper}
                            onChange={(e) =>
                              updateAt(setBOM, idx, {
                                ...b,
                                qtyper: e.target.value,
                              })
                            }
                            disabled={isReadOnly || (!isEditing && !isNew)}
                            placeholder="0"
                            className={`form-control form-control-sm text-end ${
                              !isEditing && !isNew
                                ? "bg-gray-50 text-gray-600"
                                : ""
                            }`}
                          />

                          {/* Unit */}
                          <input
                            type="text"
                            value={b.unit}
                            onChange={(e) =>
                              updateAt(setBOM, idx, {
                                ...b,
                                unit: e.target.value,
                              })
                            }
                            disabled={isReadOnly || (!isEditing && !isNew)}
                            placeholder="pcs"
                            className={`form-control form-control-sm ${
                              !isEditing && !isNew
                                ? "bg-gray-50 text-gray-600"
                                : ""
                            }`}
                          />

                          {/* Unit Price */}
                          <input
                            type="number"
                            value={b.unitprice}
                            onChange={(e) =>
                              updateAt(setBOM, idx, {
                                ...b,
                                unitprice: e.target.value,
                              })
                            }
                            disabled={isReadOnly || (!isEditing && !isNew)}
                            placeholder="0.00"
                            className={`form-control form-control-sm text-end ${
                              !isEditing && !isNew
                                ? "bg-gray-50 text-gray-600"
                                : ""
                            }`}
                          />

                          {/* Total Cost (Calculated) */}
                          <div className="px-3 py-2 bg-green-50 border border-green-200 rounded text-end font-semibold text-green-700 font-monospace text-sm">
                            {totalCost.toFixed(2)}
                          </div>

                          {/* Actions */}

                          <div className="d-flex gap-2 justify-content-center align-items-center">
                            {isEditing || isNew ? (
                              <>
                                {/* Save Button */}
                                <button
                                  onClick={() => saveBomRow(idx)}
                                  disabled={isReadOnly}
                                  className="btn btn-sm btn-success d-flex align-items-center gap-1 px-3"
                                  title="Save changes"
                                >
                                  <i className="bi bi-check-lg"></i>
                                  Save
                                </button>

                                {/* Cancel Button (only for existing items) */}
                                  {!isNew && (
                                    <button
                                    onClick={cancelEditBom}
                                    disabled={isReadOnly}
                                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                    title="Cancel editing"
                                  >
                                    <i className="bi bi-x-lg"></i>
                                  </button>
                                )}

                                {/* Delete Button */}
                                <button
                                  onClick={() => removeBOM(idx)}
                                  disabled={isReadOnly}
                                  className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                  title="Delete item"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </>
                            ) : (
                              <>
                                {/* Edit Button */}
                                <button
                                  onClick={() => startEditBom(idx)}
                                  disabled={isReadOnly}
                                  className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 px-3"
                                  title="Edit item"
                                >
                                  <i className="bi bi-pencil"></i>
                                  Edit
                                </button>

                                {/* Delete Button */}
                                <button
                                  onClick={() => removeBOM(idx)}
                                  disabled={isReadOnly}
                                  className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                  title="Delete item"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Grand Total Section */}
                  <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg d-flex justify-content-between align-items-center">
                    <div>
                      <div className="font-semibold text-green-800 mb-1">
                        Grand Total
                      </div>
                      <div className="text-sm text-green-600">
                        {bom.length} item{bom.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-green-700 font-monospace">
                      {bom
                        .reduce((sum, b) => {
                          const itemTotal =
                            (parseFloat(b.qtyper) || 0) *
                            (parseFloat(b.unitprice) || 0);
                          return sum + itemTotal;
                        }, 0)
                        .toFixed(2)}
                    </div>
                  </div>

                  {/* Info Footer */}
                  <div className="alert alert-info d-flex align-items-center gap-2 mt-3 mb-0">
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                    </svg>
                    <span className="text-sm">
                      <strong>Total Cost</strong> is calculated as{" "}
                      <strong>Quantity × Unit Price</strong>. Click Edit to
                      modify item details.
                    </span>
                  </div>
                </>
              ) : (
                /* Empty State */
                (<div className="text-center py-5 bg-white border-2 border-dashed border-gray-300 rounded-lg">
                  <svg
                    className="w-16 h-16 mx-auto mb-3 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    <path d="M9 12h6m-6 4h6" />
                  </svg>
                  <h4 className="font-semibold text-gray-700 mb-2">
                    No BOM Items Yet
                  </h4>
                  <p className="text-gray-500 mb-3">
                    Click "Add Item" to start building your bill of materials
                  </p>
                  <button className="btn btn-outline-primary" onClick={addBOM} disabled={isReadOnly}>
                    Add First Item
                  </button>
                </div>)
              )}
            </div>

            {bomPreviewOpen && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ background: "rgba(0,0,0,0.45)", zIndex: 2000 }}
              >
                <div
                  className="bg-white rounded shadow-lg w-100"
                  style={{ maxWidth: 1000, maxHeight: "85vh", overflow: "hidden" }}
                >
                  <div className="d-flex justify-content-between align-items-center border-bottom px-4 py-3">
                    <h5 className="m-0">Suggested BOM Preview</h5>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        setBomPreviewOpen(false);
                        setBomPreviewData(null);
                      }}
                    >
                      Close
                    </button>
                  </div>
                  <div className="p-4" style={{ overflowY: "auto", maxHeight: "68vh" }}>
                    <div className="alert alert-light border">
                      <div className="d-flex gap-4 flex-wrap">
                        <span>
                          Current: <strong>{bomPreviewData?.changes?.summary?.currentCount ?? 0}</strong>
                        </span>
                        <span>
                          Suggested: <strong>{bomPreviewData?.changes?.summary?.suggestedCount ?? 0}</strong>
                        </span>
                        <span>
                          Added: <strong>{bomPreviewData?.changes?.summary?.added ?? 0}</strong>
                        </span>
                        <span>
                          Removed: <strong>{bomPreviewData?.changes?.summary?.removed ?? 0}</strong>
                        </span>
                        <span>
                          Updated: <strong>{bomPreviewData?.changes?.summary?.updated ?? 0}</strong>
                        </span>
                      </div>
                    </div>

                    {!!(bomPreviewData?.changes?.updated || []).length && (
                      <div className="mb-3">
                        <h6 className="mb-2">Updated Lines</h6>
                        {(bomPreviewData?.changes?.updated || []).map((r, idx) => (
                          <div key={`upd-${idx}`} className="border rounded p-2 mb-2 bg-light">
                            <div className="small text-muted">
                              {r?.suggested?.sku || r?.current?.sku || "N/A"} -{" "}
                              {r?.suggested?.description || r?.current?.description || ""}
                            </div>
                            <div className="small">
                              Qty {r?.current?.qty_per} -> {r?.suggested?.qty_per}, Unit Price{" "}
                              {r?.current?.unit_price} -> {r?.suggested?.unit_price}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {!!(bomPreviewData?.changes?.added || []).length && (
                      <div className="mb-3">
                        <h6 className="mb-2 text-success">Added Lines</h6>
                        <ul className="mb-0">
                          {(bomPreviewData?.changes?.added || []).map((r, idx) => (
                            <li key={`add-${idx}`}>
                              {r?.sku || "N/A"} - {r?.description || ""} (qty {r?.qty_per})
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {!!(bomPreviewData?.changes?.removed || []).length && (
                      <div className="mb-2">
                        <h6 className="mb-2 text-danger">Removed Lines</h6>
                        <ul className="mb-0">
                          {(bomPreviewData?.changes?.removed || []).map((r, idx) => (
                            <li key={`rm-${idx}`}>
                              {r?.sku || "N/A"} - {r?.description || ""} (qty {r?.qty_per})
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="border-top px-4 py-3 d-flex justify-content-end gap-2">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setBomPreviewOpen(false);
                        setBomPreviewData(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={applyPreviewSuggestedBOM}
                      disabled={bomPreviewLoading}
                    >
                      {bomPreviewLoading ? "Applying..." : "Apply Changes"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "chat" && (
          <div style={{ padding: 0 }}>
            <ChatBox
              orderId={
                card.orderNumber || detailedCard?.OrderId || "default-order"
              }
              assignedUsers={[]}
              allUsers={[]}
            />
          </div>
        )}

        {/* {tab === "activity" && (
          <div className="p-6 bg-gray-50 min-h-96">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <i className="bi bi-clock-history mr-2 text-blue-600"></i>
                  Activity Logs
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Order #{card.orderNumber} • {auditEvents.length} events
                </p>
              </div>
            </div>

            <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      #
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      Action
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      From → To
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      Machine
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      Note
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      User
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      Timestamp
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {auditEvents.length ? (
                    auditEvents.map((ev, index) => (
                      <tr key={ev.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2">{index + 1}</td>
                        <td className="px-4 py-2">
                          <span
                            className={`px-2 py-1 text-xs rounded ${getEventBadge(
                              ev.type
                            )}`}
                          >
                            {ev.type.replace("_", " ")}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-xs">
                          <span
                            className={`px-2 py-0.5 rounded ${getStatusBadge(
                              ev.from
                            )}`}
                          >
                            {getStatusLabel(ev.from)}
                          </span>
                          <i className="bi bi-arrow-right text-gray-400 mx-1"></i>
                          <span
                            className={`px-2 py-0.5 rounded ${getStatusBadge(
                              ev.to
                            )}`}
                          >
                            {getStatusLabel(ev.to)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {ev.machineName && (
                            <>
                              {ev.machineName}{" "}
                              {ev.machineCode && `(${ev.machineCode})`}
                            </>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-700">
                          {ev.note ? parseSimpleNote(ev.note) : "-"}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {ev.user || "System"}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-500">
                          {new Date(ev.at).toLocaleString("en-US", {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-6 text-center text-gray-500"
                      >
                        No audit logs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )} */}

        {tab === "activity" && (
          <div className="p-6 bg-gray-50">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <i className="bi bi-clock-history mr-2 text-blue-600"></i>
                  Activity Logs
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Order #{card.orderNumber} • {auditEvents.length} events
                </p>
              </div>

              {/* ✅ NEW: Filters at top right */}
              <div className="flex items-center gap-3">
                {/* Department Filter */}
                <select
                  className="form-select form-select-sm"
                  value={auditFilters.department}
                  onChange={(e) =>
                    setAuditFilters({
                      ...auditFilters,
                      department: e.target.value,
                    })
                  }
                >
                  <option value="">All Departments</option>
                  <option value="Embroidery">Embroidery</option>
                  <option value="Screening">Screening</option>
                  <option value="Direct to Film">Direct to Film</option>
                  <option value="Cresting">Cresting</option>
                </select>

                {/* Event Type Filter */}
                <select
                  className="form-select form-select-sm"
                  value={auditFilters.types}
                  onChange={(e) =>
                    setAuditFilters({ ...auditFilters, types: e.target.value })
                  }
                >
                  <option value="">All Events</option>
                  <option value="SCHEDULE">Schedule</option>
                  <option value="START">Start</option>
                  <option value="STATUS_CHANGE">Status Change</option>
                  <option value="PAUSE">Pause</option>
                  <option value="COMPLETE">Complete</option>
                  <option value="MERGE">Merge</option>
                </select>

                {/* Scope Filter */}
                <select
                  className="form-select form-select-sm"
                  value={auditFilters.scope}
                  onChange={(e) =>
                    setAuditFilters({ ...auditFilters, scope: e.target.value })
                  }
                >
                  <option value="full">Full History</option>
                  <option value="card">This Card Only</option>
                </select>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={auditFilters.withMeta}
                    onChange={(e) =>
                      setAuditFilters({
                        ...auditFilters,
                        withMeta: e.target.checked,
                      })
                    }
                    className="form-check-input"
                  />
                  <span className="text-sm">Include Metadata</span>
                </label>
              </div>
            </div>

            <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      #
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      Action
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      From → To
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      Department
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      Machine
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      Note
                    </th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">
                      Timestamp
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {auditEvents.length ? (
                    auditEvents.map((ev, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2">{index + 1}</td>
                        <td className="px-4 py-2">
                          <span
                            className={`px-2 py-1 text-xs rounded ${getEventBadge(
                              ev.type
                            )}`}
                          >
                            {ev.type.replace("_", " ")}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {ev.from && (
                            <span
                              className={`px-2 py-0.5 rounded ${getStatusBadge(
                                ev.from
                              )}`}
                            >
                              {getStatusLabel(ev.from)}
                            </span>
                          )}
                          {ev.from && ev.to && (
                            <i className="bi bi-arrow-right text-gray-400 mx-1"></i>
                          )}
                          {ev.to && (
                            <span
                              className={`px-2 py-0.5 rounded ${getStatusBadge(
                                ev.to
                              )}`}
                            >
                              {getStatusLabel(ev.to)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {ev.department || "-"}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {ev.machineName && (
                            <>
                              {ev.machineName}{" "}
                              {ev.machineCode && `(${ev.machineCode})`}
                            </>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-700">
                          {ev.note ? parseSimpleNote(ev.note) : "-"}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-500">
                          {new Date(ev.at).toLocaleString("en-US", {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-6 text-center text-gray-500"
                      >
                        No audit logs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "costing" && isAdmin && (
          <div className="p-6 bg-gradient-to-br from-slate-50 to-white">
            <div className="mb-5">
              <h3 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <i className="bi bi-graph-up-arrow text-emerald-600"></i>
                Costing Hierarchy
              </h3>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700">
                  Opened from: Order #{card?.orderNumber || costingMeta?.order_no || "-"}
                </span>
                {card?.productName && (
                  <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700">
                    Product: {card.productName}
                  </span>
                )}
                {(card?.step_id || card?.stepid) && (
                  <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700">
                    Step #{card.step_id || card.stepid}
                  </span>
                )}
                {(card?.batchLabel || card?.batchGroupId) && (
                  <span className="px-2 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700">
                    Split: {card.batchLabel || card.batchGroupId}
                  </span>
                )}
              </div>
            </div>

            {costingLoading && (
              <div className="bg-white border border-slate-200 rounded-lg p-6 text-slate-600">
                Loading costing hierarchy...
              </div>
            )}

            {!costingLoading && costingError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
                {costingError}
              </div>
            )}

            {!costingLoading && !costingError && costingHierarchy && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="text-xs text-slate-500">Material Cost</div>
                    <div className="text-lg font-semibold text-slate-900">
                      {costingCurrency} {Number(costingOrder.material_cost || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="text-xs text-slate-500">Labour Cost</div>
                    <div className="text-lg font-semibold text-slate-900">
                      {costingCurrency} {Number(costingOrder.labour_cost || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="text-xs text-slate-500">Department Cost</div>
                    <div className="text-lg font-semibold text-slate-900">
                      {costingCurrency} {Number(costingOrder.department_cost || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="text-xs text-slate-500">Machine Cost</div>
                    <div className="text-lg font-semibold text-slate-900">
                      {costingCurrency} {Number(costingOrder.machine_cost || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-white border border-emerald-200 rounded-lg p-4 md:col-span-2">
                    <div className="text-xs text-slate-500">Order Total Cost</div>
                    <div className="text-2xl font-bold text-emerald-700">
                      {costingCurrency} {Number(costingOrder.total_cost || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="text-xs text-slate-500">Completed / Locked Steps</div>
                    <div className="text-lg font-semibold text-slate-900">
                      {Number(costingOrder.completed_steps || 0)} / {Number(costingOrder.locked_snapshot_steps || 0)}
                    </div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <div className="text-xs text-slate-500">Remaining / Partial</div>
                    <div className="text-lg font-semibold text-slate-900">
                      {Number(costingOrder.remaining_steps || 0)} / {costingOrder.is_partial ? "Yes" : "No"}
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-200 font-semibold text-slate-800">
                    Product Level (PWO)
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="px-3 py-2 text-left">Product</th>
                          <th className="px-3 py-2 text-left">Qty</th>
                          <th className="px-3 py-2 text-left">Material</th>
                          <th className="px-3 py-2 text-left">Labour</th>
                          <th className="px-3 py-2 text-left">Dept</th>
                          <th className="px-3 py-2 text-left">Machine</th>
                          <th className="px-3 py-2 text-left">Total</th>
                          <th className="px-3 py-2 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {costingProducts.length ? (
                          costingProducts.map((p) => (
                            <tr key={p.pwo_id} className="border-t border-slate-100">
                              <td className="px-3 py-2">{p.label || `PWO#${p.pwo_id}`}</td>
                              <td className="px-3 py-2">{Number(p.qty || 0)}</td>
                              <td className="px-3 py-2">{Number(p.material_cost || 0).toFixed(2)}</td>
                              <td className="px-3 py-2">{Number(p.labour_cost || 0).toFixed(2)}</td>
                              <td className="px-3 py-2">{Number(p.department_cost || 0).toFixed(2)}</td>
                              <td className="px-3 py-2">{Number(p.machine_cost || 0).toFixed(2)}</td>
                              <td className="px-3 py-2 font-semibold">{Number(p.total_cost || 0).toFixed(2)}</td>
                              <td className="px-3 py-2">
                                {p.completed_steps}/{p.total_steps} completed {p.is_partial ? "(partial)" : "(done)"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td className="px-3 py-3 text-slate-500" colSpan={8}>
                              No product-level costing rows available.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-200 font-semibold text-slate-800">
                    Step Level
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="px-3 py-2 text-left">Step</th>
                          <th className="px-3 py-2 text-left">Seq / Process</th>
                          <th className="px-3 py-2 text-left">Department</th>
                          <th className="px-3 py-2 text-left">Machine</th>
                          <th className="px-3 py-2 text-left">Operator</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-left">Labour Min</th>
                          <th className="px-3 py-2 text-left">Hourly Rate</th>
                          <th className="px-3 py-2 text-left">Material</th>
                          <th className="px-3 py-2 text-left">Labour Cost</th>
                          <th className="px-3 py-2 text-left">Dept</th>
                          <th className="px-3 py-2 text-left">Machine Cost</th>
                          <th className="px-3 py-2 text-left">Total</th>
                          <th className="px-3 py-2 text-left">Locked At</th>
                          <th className="px-3 py-2 text-left">Locked</th>
                        </tr>
                      </thead>
                      <tbody>
                        {costingSteps.length ? (
                          costingSteps.map((s) => (
                            <tr key={s.step_id} className="border-t border-slate-100">
                              <td className="px-3 py-2">#{s.step_id}</td>
                              <td className="px-3 py-2">
                                {s.seq || "-"} / {s.process_name || "-"}
                              </td>
                              <td className="px-3 py-2">{s.department || "–"}</td>
                              <td className="px-3 py-2">{s.machine || "–"}</td>
                              <td className="px-3 py-2">{s.assigned_user_name || s.assignedUserName || "–"}</td>
                              <td className="px-3 py-2">{String(s.status || "").replace(/_/g, " ") || "-"}</td>
                              <td className="px-3 py-2">{fmtCostingNum(s.labour_minutes, 2)}</td>
                              <td className="px-3 py-2">{fmtCostingNum(s.labour_rate_used, 2)}</td>
                              <td className="px-3 py-2">{fmtCostingNum(s.material_cost, 2)}</td>
                              <td className="px-3 py-2">{fmtCostingNum(s.labour_cost, 2)}</td>
                              <td className="px-3 py-2">{fmtCostingNum(s.department_cost, 2)}</td>
                              <td className="px-3 py-2">{fmtCostingNum(s.machine_cost, 2)}</td>
                              <td className="px-3 py-2 font-semibold">{fmtCostingNum(s.total_cost, 2)}</td>
                              <td className="px-3 py-2">
                                {s.cost_locked_at ? new Date(s.cost_locked_at).toLocaleString() : "-"}
                              </td>
                              <td className="px-3 py-2">{s.locked ? "Yes" : "No"}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td className="px-3 py-3 text-slate-500" colSpan={15}>
                              No step-level costing rows available.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-200 font-semibold text-slate-800">
                    Split Level (Batch Groups)
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="px-3 py-2 text-left">Batch Group</th>
                          <th className="px-3 py-2 text-left">Steps</th>
                          <th className="px-3 py-2 text-left">Indices</th>
                          <th className="px-3 py-2 text-left">Machines</th>
                          <th className="px-3 py-2 text-left">Operators</th>
                          <th className="px-3 py-2 text-left">Material</th>
                          <th className="px-3 py-2 text-left">Labour</th>
                          <th className="px-3 py-2 text-left">Dept</th>
                          <th className="px-3 py-2 text-left">Machine</th>
                          <th className="px-3 py-2 text-left">Total</th>
                          <th className="px-3 py-2 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {costingSplits.length ? (
                          costingSplits.map((g) => {
                            const splitSteps = costingSteps.filter(
                              (s) =>
                                String(s.batch_group_id || "") ===
                                String(g.batch_group_id || "")
                            );
                            const splitMachines = Array.from(
                              new Set(splitSteps.map((s) => s.machine).filter(Boolean))
                            );
                            const splitOperators = Array.from(
                              new Set(
                                splitSteps
                                  .map((s) => s.assigned_user_name || s.assignedUserName)
                                  .filter(Boolean)
                              )
                            );
                            return (
                            <tr key={g.batch_group_id} className="border-t border-slate-100">
                              <td className="px-3 py-2">{g.batch_group_id}</td>
                              <td className="px-3 py-2">{Number(g.step_count || 0)}</td>
                              <td className="px-3 py-2">
                                {(g.batch_indices || []).length ? (g.batch_indices || []).join(", ") : "–"}
                              </td>
                              <td className="px-3 py-2">{splitMachines.length ? splitMachines.join(", ") : "–"}</td>
                              <td className="px-3 py-2">{splitOperators.length ? splitOperators.join(", ") : "–"}</td>
                              <td className="px-3 py-2">{fmtCostingNum(g.material_cost, 2)}</td>
                              <td className="px-3 py-2">{fmtCostingNum(g.labour_cost, 2)}</td>
                              <td className="px-3 py-2">{fmtCostingNum(g.department_cost, 2)}</td>
                              <td className="px-3 py-2">{fmtCostingNum(g.machine_cost, 2)}</td>
                              <td className="px-3 py-2 font-semibold">{fmtCostingNum(g.total_cost, 2)}</td>
                              <td className="px-3 py-2">
                                {g.completed_steps}/{g.total_steps} completed {g.is_partial ? "(partial)" : "(done)"}
                              </td>
                            </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td className="px-3 py-3 text-slate-500" colSpan={11}>
                              No split/batch groups found for this order.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "cost" && (
          <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100">
            {/* Header with Summary Stats */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    <i className="bi bi-clock-history text-blue-600"></i>
                    Production Activity & Cost Tracking
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Order #{card.orderNumber} • {auditEvents.length} total
                    events
                  </p>
                </div>
              </div>

              {/* Summary Cards - Time & Cost Analysis */}
              {auditEvents.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  {/* Total Production Time */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                          Total Time
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {calculateTotalTime(auditEvents)}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <i className="bi bi-clock text-xl text-blue-600"></i>
                      </div>
                    </div>
                  </div>

                  {/* Time in Production */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                          In Production
                        </p>
                        <p className="text-2xl font-bold text-cyan-600">
                          {calculateStageTime(auditEvents, "In_Production")}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                        <i className="bi bi-gear-fill text-xl text-cyan-600"></i>
                      </div>
                    </div>
                  </div>

                  {/* Time on Hold */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                          On Hold
                        </p>
                        <p className="text-2xl font-bold text-orange-600">
                          {calculateStageTime(auditEvents, "On_Hold")}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                        <i className="bi bi-pause-circle text-xl text-orange-600"></i>
                      </div>
                    </div>
                  </div>

                  {/* Estimated Cost */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                          Est. Cost
                        </p>
                        <p className="text-2xl font-bold text-green-600">
                          ${calculateProductionCost(auditEvents, card)}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <i className="bi bi-currency-dollar text-xl text-green-600"></i>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Stage Duration Breakdown */}
              {auditEvents.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="bi bi-bar-chart text-blue-600"></i>
                    Time Breakdown by Stage
                  </h4>
                  <div className="space-y-3">
                    {getStageBreakdown(auditEvents).map((stage) => (
                      <div key={stage.name}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-700">
                            {stage.label}
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {stage.duration}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${stage.color}`}
                            style={{ width: `${stage.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Timeline View */}
            <div className="relative">
              {auditEvents.length ? (
                <div className="space-y-4">
                  {auditEvents.map((ev, index) => {
                    const noteData = parseDetailedNote(ev.note);
                    const duration = calculateEventDuration(auditEvents, index);

                    return (
                      <div key={ev.id} className="relative pl-8">
                        {/* Timeline connector */}
                        {index !== auditEvents.length - 1 && (
                          <div className="absolute left-[11px] top-8 bottom-0 w-0.5 bg-gray-200"></div>
                        )}
                        {/* Timeline dot */}
                        <div
                          className={`absolute left-0 top-1.5 w-6 h-6 rounded-full flex items-center justify-center ${getEventDotColor(
                            ev.type
                          )}`}
                        >
                          <i
                            className={`bi ${getEventIcon(
                              ev.type
                            )} text-xs text-white`}
                          ></i>
                        </div>
                        {/* Event Card */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between gap-4">
                            {/* Left: Main Content */}
                            <div className="flex-1">
                              {/* Action & Status */}
                              <div className="flex items-center gap-2 mb-3">
                                <span
                                  className={`px-2.5 py-1 text-xs font-medium rounded-md ${getEventBadge(
                                    ev.type
                                  )}`}
                                >
                                  {ev.type
                                    ? ev.type.replace(/_/g, " ")
                                    : "Status Change"}
                                </span>

                                <div className="flex items-center gap-1.5 text-xs">
                                  <span
                                    className={`px-2 py-0.5 rounded ${getStatusBadge(
                                      ev.from
                                    )}`}
                                  >
                                    {getStatusLabel(ev.from)}
                                  </span>
                                  <i className="bi bi-arrow-right text-gray-400"></i>
                                  <span
                                    className={`px-2 py-0.5 rounded ${getStatusBadge(
                                      ev.to
                                    )}`}
                                  >
                                    {getStatusLabel(ev.to)}
                                  </span>
                                </div>

                                {/* Duration Badge */}
                                {duration && (
                                  <span className="ml-auto px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded font-medium">
                                    <i className="bi bi-stopwatch mr-1"></i>
                                    {duration}
                                  </span>
                                )}
                              </div>

                              {/* Details Grid */}
                              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                                {ev.department && (
                                  <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                    <i className="bi bi-building text-gray-400"></i>
                                    <span>{ev.department}</span>
                                  </div>
                                )}

                                {(ev.machineName || noteData.machine) && (
                                  <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                    <i className="bi bi-gear text-gray-400"></i>
                                    <span>
                                      {ev.machineName || noteData.machine}
                                      {ev.machineCode && (
                                        <span className="text-gray-400 ml-1">
                                          ({ev.machineCode})
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                )}

                                {noteData.operator && (
                                  <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                    <i className="bi bi-person text-gray-400"></i>
                                    <span>{noteData.operator}</span>
                                  </div>
                                )}

                                {noteData.qty && (
                                  <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                    <i className="bi bi-box text-gray-400"></i>
                                    <span>Qty: {noteData.qty}</span>
                                  </div>
                                )}
                              </div>

                              {/* Hold Reason */}
                              {noteData.reason && (
                                <div className="mt-3 text-xs bg-red-50 text-red-700 px-3 py-2 rounded-md border border-red-100 flex items-start gap-2">
                                  <i className="bi bi-exclamation-triangle-fill mt-0.5"></i>
                                  <div>
                                    <strong>Hold Reason:</strong>{" "}
                                    {noteData.reason}
                                  </div>
                                </div>
                              )}

                              {/* Batch Info */}
                              {noteData.group && (
                                <div className="mt-2 text-xs bg-blue-50 text-blue-700 px-3 py-2 rounded-md border border-blue-100">
                                  <i className="bi bi-link-45deg mr-1"></i>
                                  Split into batch of {noteData.size} items
                                </div>
                              )}
                            </div>

                            {/* Right: Timestamp */}
                            <div className="text-right flex-shrink-0">
                              <div className="text-xs font-medium text-gray-900">
                                {new Date(ev.at).toLocaleString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                })}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {new Date(ev.at).toLocaleString("en-US", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-200">
                  <i className="bi bi-inbox text-4xl text-gray-300 mb-3"></i>
                  <p className="text-gray-500">No activity logs found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* <footer style={{ display: "flex", gap: 8, padding: 12 }}>
          <button style={ui.btnPrimary} onClick={saveMeta}>
            Save
          </button>

          <button style={ui.btnGhost} onClick={onClose}>
            Close
          </button>
        </footer> */}
        {/* Only show footer for overview, processes, and bom tabs */}
        {
          // tab !== "details" &&
          tab !== "overview" &&
            tab !== "overview2" &&
            tab !== "chat" &&
            tab !== "activity" &&
            tab !== "processes" &&
            tab !== "bom" &&
            tab !== "cost" && 
            tab !== "costing" &&
            (
              <footer className="flex gap-3 px-5 py-4 border-t border-gray-200 bg-gray-50 justify-end items-center sticky bottom-0 z-10">
                <button
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                  onClick={onClose}
                >
                  <i className="bi bi-x-lg text-xs"></i>
                  Close
                </button>

                <button
                  className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm"
                  onClick={saveMeta}
                >
                  <i className="bi bi-check-lg"></i>
                  Save Changes
                </button>
              </footer>
            )
        }
        <PdfViewerDialog
          open={Boolean(previewPdf)}
          src={previewPdf}
          fileName={previewFileName}
          title={previewFileName || "Proof Preview"}
          onClose={closePreviewPdf}
        />
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={ui.section}>
      <div style={{ ...ui.subtle, marginBottom: 6, fontWeight: 600 }}>
        {title}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,1fr)",
          gap: 10,
        }}
      >
        {children}
      </div>
    </div>
  );
}
function Row({ l, v }) {
  return (
    <div>
      <div style={ui.label}>{l}</div>
      <div style={ui.value}>{v || v === 0 ? v : "–"}</div>
    </div>
  );
}
function RowChip({ l, list }) {
  return (
    <div>
      <div style={ui.label}>{l}</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {(list || []).length ? (
          list.map((x, i) => (
            <span key={i} style={ui.chip}>
              {x}
            </span>
          ))
        ) : (
          <span style={ui.value}>–</span>
        )}
      </div>
    </div>
  );
}
function Input({ l, value, onChange, type = "text", readOnly }) {
  return (
    <label style={{ display: "grid", gap: 4 }}>
      <span style={ui.subtle}>{l}</span>
      <input
        type={type}
        value={value ?? ""}
        readOnly={readOnly}
        onChange={(e) => onChange?.(e.target.value)}
        style={ui.field}
      />
    </label>
  );
}
function Select({ l, value, onChange, options = [] }) {
  return (
    <label style={{ display: "grid", gap: 4 }}>
      <span style={ui.subtle}>{l}</span>
      <select
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        style={ui.field}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
const updateAt = (set, i, v) =>
  set((list) => list.map((x, idx) => (idx === i ? v : x)));
const removeAt = (set, i) => set((list) => list.filter((_, idx) => idx !== i));

/* ──────────────────────────────────────────────────────────────────────────
    Split / Join / Partial move (lightweight, no backend side-effects for local children)
    ────────────────────────────────────────────────────────────────────────── */

function SplitWizard({ open, card, onCancel, onConfirm, department }) {
  const [machines, setMachines] = useState([]);
  const [machinesLoading, setMachinesLoading] = useState(false);
  const [departmentMap, setDepartmentMap] = useState({});
  const [rows, setRows] = useState([
    {
      id: 1,
      qty: "",
      machine: card?.machine || "",
      machineId: card?.machineId || "",
      date: card?.plannedStart || todayYMD(),
    },
  ]);

  useEffect(() => {
    if (open) {
      setRows([
        {
          id: 1,
          qty: "",
          machine: card?.machine || "",
          machineId: card?.machineId || "",
          date: card?.plannedStart || todayYMD(),
        },
      ]);
    }
  }, [open, card]);

  useEffect(() => {
    const fetchDepartmentMapping = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/departments?active=true`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch departments");
        const data = await response.json();
        const mapping = {};
        (data.data || []).forEach((dept) => {
          mapping[dept.name.toLowerCase().replace(/\s+/g, "-")] = dept.id;
        });
        setDepartmentMap(mapping);
      } catch (error) {
        console.error("Error fetching departments for split:", error);
        setDepartmentMap({});
      }
    };
    if (open) fetchDepartmentMapping();
  }, [open]);

  useEffect(() => {
    const fetchMachines = async () => {
      setMachinesLoading(true);
      try {
        const cardDepartmentId = card?.departmentId || card?.department_id || null;
        const routeDepartmentId =
          departmentMap[(department || "").toLowerCase().replace(/\s+/g, "-")] || null;
        const departmentId = cardDepartmentId || routeDepartmentId;
        const url = departmentId
          ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/machines?departmentId=${departmentId}&status=&search=&limit=100&offset=0&active=true`
          : `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/machines?active=true&limit=100`;
        const response = await fetch(url, {
          headers: {
            accessToken: localStorage.getItem("accessToken"),
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) throw new Error("Failed to fetch machines");
        const data = await response.json();
        setMachines(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        console.error("Error fetching machines for split:", error);
        setMachines([]);
      } finally {
        setMachinesLoading(false);
      }
    };

    if (open && card) fetchMachines();
  }, [open, card, department, departmentMap]);

  useEffect(() => {
    if (!open || machinesLoading || !machines.length) return;
    setRows((prev) =>
      prev.map((r) => {
        if (r.machineId) return r;
        const firstMachine = machines[0];
        return {
          ...r,
          machineId: firstMachine?.id || "",
          machine: firstMachine?.name || "",
        };
      })
    );
  }, [open, machinesLoading, machines]);

  if (!open || !card) return null;

  const available = Math.max(
    0,
    Number(card.qtyPlanned || 0) - Number(card.qtyDone || 0)
  );

  const total = rows.reduce((t, r) => t + (parseInt(r.qty || 0, 10) || 0), 0);
  const isValid =
    total === available &&
    rows.length >= 2 &&
    rows.every((r) => parseInt(r.qty) > 0);

  const addRow = () =>
    setRows((rs) => [
      ...rs,
      {
        id: Date.now(),
        qty: "",
        machine: card?.machine || "",
        machineId: card?.machineId || "",
        date: card.plannedStart || todayYMD(),
      },
    ]);

  const update = (i, patch) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const remove = (i) =>
    setRows((rs) => (rs.length > 2 ? rs.filter((_, idx) => idx !== i) : rs));

  return (
    <div style={modal.overlay} onClick={onCancel}>
      <div
        style={{ ...modal.card, width: "min(760px,96vw)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={modal.header}>
          <div>
            <h3 style={{ margin: 0 }}>Split into Multiple Batches</h3>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
              Divide this card into 2 or more batches
            </div>
          </div>
          <button style={ui.btnGhost} onClick={onCancel}>
            ✕
          </button>
        </div>

        <div style={{ padding: 16 }}>
          <div
            style={{
              background: "#f3f4f6",
              padding: 12,
              borderRadius: 6,
              marginBottom: 16,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Available Quantity
              </div>
              <div style={{ fontSize: 20, fontWeight: 600, color: "#111827" }}>
                {available}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Allocated Total
              </div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: total === available ? "#059669" : "#dc2626",
                }}
              >
                {total}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>Remaining</div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: total === available ? "#059669" : "#6b7280",
                }}
              >
                {available - total}
              </div>
            </div>
          </div>

          {total !== available && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#991b1b",
                padding: 10,
                borderRadius: 6,
                fontSize: 13,
                marginBottom: 16,
              }}
            >
              ⚠️ Total qty must equal {available}. Current total: {total}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <strong>Batches ({rows.length})</strong>
          </div>

          {rows.map((r, i) => (
            <div
              key={r.id}
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 16,
                marginBottom: 12,
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  background: "#3b82f6",
                  color: "#fff",
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                {i + 1}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 12,
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      fontWeight: 500,
                      marginBottom: 6,
                    }}
                  >
                    Quantity <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <input
                    type="number"
                    value={r.qty}
                    onChange={(e) => update(i, { qty: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: 6,
                      fontSize: 14,
                    }}
                    placeholder="0"
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      fontWeight: 500,
                      marginBottom: 6,
                    }}
                  >
                    Machine
                  </label>
                  <select
                    value={r.machineId || ""}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedMachine = machines.find(
                        (m) => String(m.id) === String(selectedId)
                      );
                      update(i, {
                        machineId: selectedId ? Number(selectedId) : "",
                        machine: selectedMachine?.name || "",
                      });
                    }}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: 6,
                      fontSize: 14,
                    }}
                  >
                    {machinesLoading ? (
                      <option value="" disabled>
                        Loading machines...
                      </option>
                    ) : (
                      machines.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                          {m.code ? ` (${m.code})` : ""}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      fontWeight: 500,
                      marginBottom: 6,
                    }}
                  >
                    Date <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={r.date}
                    onChange={(e) => update(i, { date: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: 6,
                      fontSize: 14,
                    }}
                  />
                </div>
              </div>

              {rows.length > 2 && (
                <button
                  style={{
                    marginTop: 12,
                    padding: "6px 12px",
                    border: "1px solid #fca5a5",
                    background: "#fef2f2",
                    color: "#dc2626",
                    borderRadius: 6,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                  onClick={() => remove(i)}
                >
                  Remove Batch
                </button>
              )}
            </div>
          ))}

          <button
            style={{
              padding: "10px 16px",
              border: "1px dashed #3b82f6",
              background: "#eff6ff",
              color: "#3b82f6",
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              width: "100%",
            }}
            onClick={addRow}
          >
            + Add Another Batch
          </button>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            padding: 16,
            borderTop: "1px solid #e5e7eb",
            background: "#f9fafb",
          }}
        >
          <button
            style={{
              ...ui.btnPrimary,
              opacity: isValid ? 1 : 0.5,
              cursor: isValid ? "pointer" : "not-allowed",
            }}
            disabled={!isValid}
            onClick={() => onConfirm({ rows })}
          >
            Split into {rows.length} Batches
          </button>
          <button style={ui.btnGhost} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function JoinWizard({ open, card, siblings = [], onCancel, onConfirm }) {
  if (!open || !card) return null;

  const totalQty = siblings.reduce((t, c) => t + Number(c.qtyPlanned || 0), 0);
  const batchCount = siblings.length;

  return (
    <div style={modal.overlay} onClick={onCancel}>
      <div
        style={{ ...modal.card, width: "min(720px,96vw)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={modal.header}>
          <div>
            <h3 style={{ margin: 0 }}>Join Batches</h3>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
              Merge split batches back into one card
            </div>
          </div>
          <button style={ui.btnGhost} onClick={onCancel}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 16 }}>
          {/* Summary Bar */}
          <div
            style={{
              background: "linear-gradient(135deg, #667eea15, #764ba215)",
              border: "1px solid #667eea40",
              borderRadius: 8,
              padding: 16,
              marginBottom: 20,
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              gap: 16,
              alignItems: "center",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: "#6b7280",
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                CURRENT
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>
                {siblings.length} Batches
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                {totalQty} total units
              </div>
            </div>

            <div
              style={{
                fontSize: 28,
                color: "#667eea",
                fontWeight: 300,
              }}
            >
              →
            </div>

            <div>
              <div
                style={{
                  fontSize: 11,
                  color: "#6b7280",
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                AFTER MERGE
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#059669" }}>
                1 Batch
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                {totalQty} units
              </div>
            </div>
          </div>

          {/* Batch List */}
          <div style={{ marginBottom: 12 }}>
            <strong>
              All {siblings.length} batches in this group will be joined
            </strong>
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            {siblings.map((s) => {
              const isTarget = s.step_id === card.step_id;
              const normalizedBatch = normalizeProductionBatchFields(s);

              return (
                <div
                  key={s.step_id}
                  style={{
                    background: isTarget ? "#fef3c7" : "#f8fafc",
                    border: isTarget ? "2px solid #fbbf24" : "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: 14,
                    cursor: "default",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    transition: "all 0.2s",
                  }}
                >
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    disabled
                    checked
                    style={{
                      width: 18,
                      height: 18,
                      cursor: "default",
                    }}
                  />

                  {/* Batch Info */}
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 4,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#111827",
                        }}
                      >
                        Batch{" "}
                        {normalizedBatch.batchLabel || "Unknown"}
                      </span>
                      {isTarget && (
                        <span
                          style={{
                            background: "#fbbf24",
                            color: "#fff",
                            fontSize: 10,
                            padding: "2px 8px",
                            borderRadius: 4,
                            fontWeight: 700,
                            letterSpacing: "0.5px",
                          }}
                        >
                          TARGET
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      Step #{s.step_id} • Qty: {s.qtyPlanned || 0} • Machine:{" "}
                      {s.machine || "Not assigned"}
                    </div>
                  </div>

                  {/* Selection indicator */}
                  {!isTarget && (
                    <div
                      style={{
                        background: "#dbeafe",
                        color: "#1d4ed8",
                        fontSize: 11,
                        padding: "4px 10px",
                        borderRadius: 6,
                        fontWeight: 600,
                      }}
                    >
                      ✓ Selected
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Selection Summary */}
          {batchCount > 1 && (
            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: "#ecfdf5",
                border: "1px solid #10b981",
                borderRadius: 6,
                fontSize: 13,
                color: "#065f46",
                fontWeight: 500,
              }}
            >
              ✓ Joining <strong>all {batchCount} batches</strong> (
              <strong>{totalQty} units</strong>)
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            gap: 8,
            padding: 16,
            borderTop: "1px solid #e5e7eb",
            background: "#f9fafb",
          }}
        >
          <button
            style={{
              flex: 1,
              padding: "10px 20px",
              background: batchCount > 1 ? "#3b82f6" : "#d1d5db",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 14,
              cursor: batchCount > 1 ? "pointer" : "not-allowed",
              transition: "background 0.2s",
            }}
            disabled={batchCount <= 1}
            onClick={() => onConfirm()}
          >
            Join all {batchCount} Batches
          </button>
          <button
            style={{
              padding: "10px 20px",
              background: "#fff",
              color: "#6b7280",
              border: "1px solid #d1d5db",
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
            }}
            onClick={onCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function PartialMoveModal({
  open,
  from,
  // defaultTo = "QC",
  defaultTo,
  card,
  onCancel,
  onConfirm,
}) {
  const getSmartDefault = () => {
    if (from === "In_Production") {
      return SHOW_QC_LANE ? "QC" : "Completed";
    }
    return "Scheduled";
  };

  const [qty, setQty] = useState("");
  const [to, setTo] = useState(defaultTo || getSmartDefault());

  React.useEffect(() => {
    if (!SHOW_QC_LANE && to === "QC") {
      setTo(getSmartDefault());
    }
  }, [SHOW_QC_LANE]);

  if (!open || !card) return null;

  const max = Math.max(
    0,
    Number(card.qtyPlanned || 0) - Number(card.qtyDone || 0)
  );

  let options;
  if (from === "In_Production") {
    options = SHOW_QC_LANE
      ? ["QC", "On_Hold", "Completed"]
      : ["On_Hold", "Completed"];
  } else {
    options = SHOW_QC_LANE
      ? ["Scheduled", "In_Production", "QC", "On_Hold", "Completed"]
      : ["Scheduled", "In_Production", "On_Hold", "Completed"];
  }

  return (
    <div style={modal.overlay} onClick={onCancel}>
      <div style={modal.card} onClick={(e) => e.stopPropagation()}>
        <div style={modal.header}>
          <h3 style={{ margin: 0 }}>
            Partial move from {from.replace(/_/g, " ")}
          </h3>
          <button style={ui.btnGhost} onClick={onCancel}>
            Close
          </button>
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          <div style={ui.subtle}>Available qty: {max}</div>
          <Input
            l="Quantity to move *"
            type="number"
            value={qty}
            onChange={(v) => setQty(v)}
          />
          <Select
            l="Destination *"
            value={to}
            onChange={(v) => setTo(v)}
            options={options}
          />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button
            style={ui.btnPrimary}
            onClick={() => onConfirm({ qty: Number(qty || 0), to })}
          >
            Confirm partial move
          </button>
          <button style={ui.btnGhost} onClick={onCancel}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function createBatchFrom(card, qty, to) {
  const rootId = card.rootId || card.id;
  const batchId = `${rootId}#b${Date.now() % 100000}`;
  const parentQty = Number(card.qtyPlanned || 0);
  const doneQty = Number(card.qtyDone || 0);
  const remaining = Math.max(0, parentQty - doneQty);
  if (qty < 1 || qty > remaining) return null;
  const originalQty = card.rootQty || parentQty;
  const child = {
    ...card,
    id: batchId,
    rootId,
    rootQty: originalQty,
    batchOf: card.id,
    qtyPlanned: qty,
    qtyDone: to === "QC" && card.status === "In_Production" ? qty : 0,
    status: to,
    lifecycle: [
      ...(card.lifecycle || []),
      {
        when: new Date().toLocaleString(),
        text: `Partial move ${qty} → ${to.replace(/_/g, " ")}`,
      },
    ],
    __local: true,
  };
  const parentAfter = {
    ...card,
    qtyPlanned: parentQty - qty,
    lifecycle: [
      ...(card.lifecycle || []),
      {
        when: new Date().toLocaleString(),
        text: `Split out ${qty} to ${to.replace(/_/g, " ")}`,
      },
    ],
  };
  return { child, parentAfter };
}

// ────────────────────────────────────────────────────────────────────────────
// Main Board
// ────────────────────────────────────────────────────────────────────────────
export default function EliteKanbanBoardV2({ department }) {
  const params = useParams();
  const history = useHistory();
  const routeInstanceUniqueId =
    params?.instanceUniqueId ||
    window.location.pathname.match(/\/instance\/([^/]+)/)?.[1] ||
    "";
  const instanceUniqueId = routeInstanceUniqueId;
  const requestDiagnosticsRef = useRef(null);
  if (!requestDiagnosticsRef.current) requestDiagnosticsRef.current = createProductionRequestDiagnostics();
  const traceRequests = requestDiagnosticsRef.current.trace;
  const mountSetupCountRef = useRef(0);
  useEffect(() => {
    traceRequests("mount-effect-setup", { setupCount: ++mountSetupCountRef.current });
    return () => traceRequests("mount-effect-cleanup", { setupCount: mountSetupCountRef.current });
  }, [traceRequests]);
  function goToDepartmentBoard(dept) {
    const slug = departmentToSlug(dept?.name);
    if (!slug || !routeInstanceUniqueId) return;
    history.push(`/instance/${routeInstanceUniqueId}/production/${slug}`);
  }
  const IS_TESTING = false; //  Set false for production
  const PAGE_SIZE = PRODUCTION_PAGE_SIZE;
  console.log(
    IS_TESTING ? " TESTING: Page size = 5" : " PRODUCTION: Page size = 25"
  );

  // const AUTO_LOAD_THRESHOLD = 75; // Cards before showing "Load More" button

  // const AUTO_LOAD_THRESHOLD = IS_TESTING ? 100 : 75;

  // const AUTO_LOAD_THRESHOLD = IS_TESTING ? 5 : 75;


  const customScrollbarStyles = `
    .lane-scroll-container::-webkit-scrollbar {
      width: 6px;
    }
    .lane-scroll-container::-webkit-scrollbar-track {
      background: #f3f4f6;
    }
    .lane-scroll-container::-webkit-scrollbar-thumb {
      background: #cbd5e1;
    }
    .lane-scroll-container::-webkit-scrollbar-thumb:hover {
      background: #94a3b8;
    }
  `;

  const premiumModalStyles = `
  .swal-premium-popup {
    border-radius: 12px !important;
    padding: 24px !important;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
  }
  
  .swal-premium-title {
    font-size: 18px !important;
    font-weight: 600 !important;
    color: #1e293b !important;
    padding: 0 0 12px 0 !important;
    margin: 0 !important;
  }
  
  .swal-premium-html {
    margin: 0 !important;
    padding: 0 !important;
  }
  
  .swal-premium-confirm {
    padding: 10px 20px !important;
    font-size: 13.5px !important;
    font-weight: 600 !important;
    border-radius: 7px !important;
    transition: all 0.2s !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
  }
  
  .swal-premium-confirm:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(5, 150, 105, 0.25) !important;
  }
  
  .swal-premium-cancel {
    padding: 10px 20px !important;
    font-size: 13.5px !important;
    font-weight: 600 !important;
    border-radius: 7px !important;
    transition: all 0.2s !important;
    background: #f1f5f9 !important;
    color: #475569 !important;
    border: none !important;
  }
  
  .swal-premium-cancel:hover {
    background: #e2e8f0 !important;
    color: #334155 !important;
  }
`;

  const customStyles = `
  /* Premium Completed Card Styles */
  .card-fully-completed {
    transition: all 0.2s ease;
  }

  .card-fully-completed:hover {
    opacity: 1 !important;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15) !important;
  }

  /* Fade animation for badges */
  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .card-fully-completed .badge {
    animation: fadeInDown 0.3s ease;
  }
`;

  const [columns, setRawColumns] = useState(() => emptyColumns());
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const setColumns = useCallback((update, operation) => setRawColumns((previous) => {
    if (!canApplyProductionCardMutation(previous, operation, boardScopeRef.current)) return previous;
    return protectPendingProductionColumns(tagProductionCardMutation(typeof update === "function" ? update(previous) : update, operation),
      pendingMutationStoreRef.current.records(), boardScopeRef.current);
  }), []);
  const [error, setError] = useState("");
  const [planningPage, setPlanningPage] = useState(1);
  const [planningHasMore, setPlanningHasMore] = useState(true);
  const [planningLoading, setPlanningLoading] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [archiveEnabled, setArchiveEnabled] = useState(true);
  const [archiveRows, setArchiveRows] = useState([]);
  const [archiveSearch, setArchiveSearch] = useState("");
  const [archivePage, setArchivePage] = useState(1);
  const [archiveTotal, setArchiveTotal] = useState(0);
  const [archiveRequestState, setArchiveRequestState] = useState({ status: "idle", error: null });
  const archiveRequestRegistryRef = useRef(null);
  if (!archiveRequestRegistryRef.current) archiveRequestRegistryRef.current = createProductionLaneRequestRegistry({ trace: traceRequests });
  const archiveGenerationRef = useRef(0);
  const [archiveRowsIdentity, setArchiveRowsIdentity] = useState("");
  const archivePreferenceKey = `production:showArchive:${instanceUniqueId}`;
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const [quickFilter, setQuickFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [orderIdSort, setOrderIdSort] = useState("asc");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [machineFilter, setMachineFilter] = useState("");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [appliedAdvancedFilters, setAppliedAdvancedFilters] = useState({
    machine: "",
    dateFrom: "",
    dateTo: "",
  });

  const archiveIdentity = JSON.stringify([
    instanceUniqueId, department, selectedDepartment, archivePage, archiveSearch,
    appliedSearchQuery, appliedAdvancedFilters.machine, appliedAdvancedFilters.dateFrom,
    appliedAdvancedFilters.dateTo, orderIdSort, showArchive, archiveEnabled,
  ]);
  const archiveHasData = archiveRowsIdentity === archiveIdentity;
  const archiveVisibleRows = archiveHasData ? archiveRows : [];
  const archiveStatus = archiveRequestState.identity === archiveIdentity ? archiveRequestState.status : "loading";
  const archiveIsLoading = archiveStatus === "loading";
  const archiveViewState = getProductionArchiveViewState({
    status: archiveStatus, rowCount: archiveVisibleRows.length, total: archiveHasData ? archiveTotal : 0,
  });

  useEffect(() => {
    if (!instanceUniqueId) return undefined;
    let cancelled = false;
    fetch(`${process.env.REACT_APP_NODEJS_SERVER_URL}/production/settings`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
    })
      .then((response) => response.json())
      .then((payload) => {
        if (cancelled) return;
        const enabled = payload?.settings?.production?.archiveEnabled !== false;
        setArchiveEnabled(enabled);
        if (!enabled) setShowArchive(false);
      })
      .catch(() => {
        if (!cancelled) setArchiveEnabled(true);
      });
    return () => { cancelled = true; };
  }, [instanceUniqueId]);

  useEffect(() => {
    if (!instanceUniqueId) return;
    const savedPreference = localStorage.getItem(archivePreferenceKey) === "true";
    setShowArchive(archiveEnabled && savedPreference);
    setArchivePage(1);
    setArchiveSearch("");
  }, [archiveEnabled, archivePreferenceKey, instanceUniqueId]);

  const handleShowArchiveChange = (checked) => {
    localStorage.setItem(archivePreferenceKey, String(checked));
    setShowArchive(archiveEnabled && checked);
    setArchivePage(1);
  };

  const [modalLoading, setModalLoading] = useState(false);
  const [detailedCard, setDetailedCard] = useState(null);

  const loadArchive = useCallback(async () => {
    if (!archiveEnabled || !showArchive) {
      archiveGenerationRef.current += 1;
      archiveRequestRegistryRef.current.cancelAll("archive-hidden-or-disabled");
      setArchiveRows([]);
      setArchiveTotal(0);
      setArchiveRequestState({ status: "idle", error: null });
      return;
    }
    const token = localStorage.getItem("accessToken");
    const params = new URLSearchParams({ page: String(archivePage), pageSize: "50" });
    if (instanceUniqueId) params.set("instanceUniqueId", instanceUniqueId);
    if (archiveSearch.trim()) params.set("search", archiveSearch.trim());
    if (appliedSearchQuery.trim()) params.set("q", appliedSearchQuery.trim());
    if (appliedAdvancedFilters.machine) params.set("machine", appliedAdvancedFilters.machine);
    if (appliedAdvancedFilters.dateFrom) params.set("dateFrom", `${appliedAdvancedFilters.dateFrom}T00:00:00`);
    if (appliedAdvancedFilters.dateTo) params.set("dateTo", `${appliedAdvancedFilters.dateTo}T23:59:59`);
    const archiveDepartment = selectedDepartment || (isAggregateProductionRoute(department) ? "" : department);
    if (archiveDepartment) params.set("department", archiveDepartment);
    if (orderIdSort) params.set("orderIdSort", orderIdSort);

    const requestKey = params.toString();
    if (archiveRequestRegistryRef.current.size() > 0 && !archiveRequestRegistryRef.current.has(requestKey)) {
      archiveRequestRegistryRef.current.cancelAll("archive-query-change");
    }
    const generation = ++archiveGenerationRef.current;
    setArchiveRequestState({ status: "loading", error: null, identity: archiveIdentity });
    const entry = archiveRequestRegistryRef.current.request(requestKey, async (signal) => {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps/archive?${requestKey}`,
        { headers: { Authorization: `Bearer ${token}` }, signal }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      if (payload?.success === false) throw new Error(payload?.message || "Unable to load Production Archive");
      return payload;
    }, { scope: "archive", lane: "Archive", page: archivePage, generation, reason: "archive-load-or-retry" });

    try {
      const payload = await entry.promise;
      if (archiveGenerationRef.current !== generation) {
        traceRequests("response-ignored", { lane: "Archive", generation, reason: "stale-generation" });
        return;
      }
      setArchiveRows(Array.isArray(payload?.items) ? payload.items : []);
      setArchiveRowsIdentity(archiveIdentity);
      setArchiveTotal(Number(payload?.total) || 0);
      setArchiveRequestState({ status: "loaded", error: null, identity: archiveIdentity });
    } catch (error) {
      if (archiveGenerationRef.current !== generation || error?.name === "AbortError") return;
      setArchiveRequestState({ status: "error", error: error?.message || "Unable to load Production Archive", identity: archiveIdentity });
      console.error("Failed to load Production Archive:", error);
    }
  }, [archiveEnabled, showArchive, archivePage, archiveSearch, appliedSearchQuery, appliedAdvancedFilters, selectedDepartment, orderIdSort, department, instanceUniqueId, archiveIdentity]);

  useEffect(() => {
    requestDiagnosticsRef.current.identity("archive", {
      instance: instanceUniqueId, department, selectedDepartment, archiveEnabled, showArchive,
      page: archivePage, search: archiveSearch, query: appliedSearchQuery,
      machine: appliedAdvancedFilters.machine, dateFrom: appliedAdvancedFilters.dateFrom,
      dateTo: appliedAdvancedFilters.dateTo, sort: orderIdSort,
    });
    traceRequests("effect-setup", { scope: "archive", generation: archiveGenerationRef.current });
    if (!archiveEnabled || !showArchive) { loadArchive(); return undefined; }
    const timer = setTimeout(loadArchive, 300);
    return () => {
      clearTimeout(timer);
      archiveGenerationRef.current += 1;
      traceRequests("effect-cleanup", { scope: "archive", generation: archiveGenerationRef.current });
      archiveRequestRegistryRef.current.cancelAll("archive-effect-cleanup");
    };
  }, [archiveEnabled, showArchive, archivePage, archiveSearch, loadArchive]);

  useEffect(() => () => {
    archiveGenerationRef.current += 1;
    archiveRequestRegistryRef.current.cancelAll("archive-unmount-or-strict-replay");
  }, []);

  useEffect(() => {
    setArchivePage(1);
  }, [instanceUniqueId, department, selectedDepartment, appliedSearchQuery, appliedAdvancedFilters, orderIdSort]);

  const [orderMeta, setOrderMeta] = useState(null);
  const [orderCosting, setOrderCosting] = useState(null);
  const [orderCharges, setOrderCharges] = useState(null);

  // Popups
  const [modalOpen, setModalOpen] = useState(false);
  const closeJobCard = useCallback(() => setModalOpen(false), []);
  const [modalCard, setModalCard] = useState(null);
  const [moveModal, setMoveModal] = useState({
    open: false,
    to: null,
    card: null,
    fields: {
      operator: "",
      machine: "",
      plannedStart: scheduleDateValue(new Date()),
      plannedFinish: scheduleDateValue(new Date()),
      isFirm: false,
      reason: "",
    },
  });
  const [splitUI, setSplitUI] = useState({ open: false, card: null });
  const [joinUI, setJoinUI] = useState({ open: false, card: null });
  const [partialMove, setPartialMove] = useState({
    open: false,
    from: null,
    card: null,
    defaultTo: "QC",
  });

  // DnD state
  const listRefs = useRef(
    Object.fromEntries(STATUSES.map((s) => [s.key, React.createRef()]))
  ).current;
  const dragRef = useRef({
    cardId: null,
    from: null,
    fromIndex: -1,
    overIndex: -1,
    overCol: null,
    lastMain: null,
    lastDragEndAt: 0,
  });

  // Debounced rank patch
  const RANK_DEBOUNCE_MS = 250;
  const rankQueueRef = useRef({ timer: null, pending: new Map() });

  // const [isInitialLoading, setIsInitialLoading] = useState(true);
  // const [isSyncing, setIsSyncing] = useState(false);

  // ========== PAGINATION STATE ==========
  const [lanePagination, setLanePagination] = useState({
    Planned: { page: 1, hasMore: true, loading: false, total: 0 },
    Scheduled: { page: 1, hasMore: true, loading: false, total: 0 },
    In_Production: { page: 1, hasMore: true, loading: false, total: 0 },
    QC: { page: 1, hasMore: true, loading: false, total: 0 },
    Completed: { page: 1, hasMore: true, loading: false, total: 0 },
    On_Hold: { page: 1, hasMore: true, loading: false, total: 0 },
  });

  const [ttlInfo, setTtlInfo] = useState({})
  const fullFetchInFlightRef = useRef(false);
  const partialRefreshInFlightRef = useRef(false);
  const partialRefreshOwnerRef = useRef(null);
  const boardRequestGateRef = useRef(null);
  if (!boardRequestGateRef.current) boardRequestGateRef.current = createProductionRequestGate({ trace: traceRequests });
  const laneRequestRegistryRef = useRef(null);
  if (!laneRequestRegistryRef.current) laneRequestRegistryRef.current = createProductionLaneRequestRegistry({ trace: traceRequests });
  const boardLoadCoordinatorRef = useRef(null);
  if (!boardLoadCoordinatorRef.current) boardLoadCoordinatorRef.current = createProductionBoardLoadCoordinator();
  const pollGenerationRef = useRef(0);
  const paginationInFlightRef = useRef(new Set());
  const paginationTokensRef = useRef({});
  const paginationCursorRef = useRef({});
  const retryInFlightRef = useRef(new Set());
  const mutationRefreshQueueRef = useRef(null);
  if (!mutationRefreshQueueRef.current) mutationRefreshQueueRef.current = createProductionMutationRefreshQueue();
  const boardScopeRef = useRef(null);
  const pendingSchedulesRef = useRef(new Set());
  const [pendingSchedules, setPendingSchedules] = useState(new Set());
  const pendingMutationStoreRef = useRef(null);
  if (!pendingMutationStoreRef.current || pendingMutationStoreRef.current.instance !== instanceUniqueId) {
    pendingMutationStoreRef.current = createProductionPendingMutations(instanceUniqueId, window.sessionStorage);
  }
  const [pendingMutations, setPendingMutations] = useState(() => pendingMutationStoreRef.current.records());
  const performSpecificListsRefreshRef = useRef(null);
  const loadArchiveRef = useRef(loadArchive);
  loadArchiveRef.current = loadArchive;

  function isSchedulingCard(card) {
    const pending = pendingMutationStoreRef.current.find(card);
    return pending?.kind === "schedule" && pending.phase !== "failed";
  }

  function blockCardMutation(card) {
    const pending = pendingMutationStoreRef.current.find(card);
    return Boolean(pending && pending.phase !== "failed") || (card?.status !== "Planned" && !getProductionStepId(card));
  }

  function beginCardRollback(card) {
    return blockCardMutation(card) ? null : captureProductionCardRollback(columnsRef.current, card, boardScopeRef.current);
  }

  function rollbackCardMutation(operation) {
    setColumns(previous => rollbackProductionCard(previous, operation, boardScopeRef.current));
  }

  function beginCardAction(card, rollback) {
    if (blockCardMutation(card)) throw new Error("This card already has a pending Production operation.");
    const store = pendingMutationStoreRef.current;
    const action = store.begin(card, "action", { scope: boardScopeRef.current });
    if (!action) throw new Error("This card already has a pending Production operation.");
    action.store = store;
    action.rollback = rollback;
    publishPendingMutations(store);
    return action;
  }

  function finishCardAction(action) {
    if (!action) return;
    action.store.remove(action);
    publishPendingMutations(action.store);
  }

  const publishPendingMutations = useCallback((store = pendingMutationStoreRef.current) => {
    if (store === pendingMutationStoreRef.current) setPendingMutations([...store.records()]);
  }, []);

  const invalidateMutationLanes = useCallback((lanes) => {
    lanes.forEach((lane) => {
      laneRequestTokensRef.current[lane] = (laneRequestTokensRef.current[lane] || 0) + 1;
    });
  }, []);

  const reconcilePendingMutation = useCallback(async (record, store = pendingMutationStoreRef.current) => {
    if (record.checking) return false;
    record.checking = true;
    publishPendingMutations(store);
    try {
      const params = new URLSearchParams({ instanceUniqueId: store.instance });
      params.set("mutation_id", record.mutationId || "");
      params.set("mutation_kind", record.kind);
      if (record.step_id) params.set("step_id", record.step_id);
      if (record.kind === "schedule" || !record.step_id) {
        params.set("order_id", record.orderDbId); params.set("item_id", record.itemId);
        params.set("decoration_id", record.decorationId); params.set("department", record.department);
      }
      const response = await fetch(`${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps/mutation-state?${params}`);
      if (!response.ok) throw new Error("Saved state could not be verified.");
      const payload = await response.json();
      const matches = payload.success === true && Array.isArray(payload.data) ? payload.data.filter(row =>
        Number(row.orderDbId) === record.orderDbId && Number(row.itemId) === record.itemId &&
        Number(row.decorationId) === record.decorationId && row.mutationCommitted === true &&
        (!record.step_id || Number(row.step_id) === record.step_id ||
          (record.kind === "schedule" && Number(row.removedStepId) === record.step_id))) : [];
      if (matches.length !== 1) throw new Error("Outcome is still uncertain. Do not resubmit; check saved status again.");
      const row = matches[0];
      if (row.mutationOutcome === "unscheduled") {
        if (record.kind !== "schedule" || row.safeToRetry !== true || row.status !== "Planned" ||
            typeof row.planningEligible !== "boolean" || !Number.isSafeInteger(Number(row.removedStepId)) ||
            Number(row.removedStepId) <= 0 || String(row.department).toLowerCase() !== String(record.department).toLowerCase()) {
          throw new Error("The removed schedule could not be verified.");
        }
        store.remove(record);
        record.reconciledStatus = "Planned";
        if (store !== pendingMutationStoreRef.current) return true;
        invalidateMutationLanes([record.from, "Planned", "Scheduled"]);
        setColumns(previous => {
          const next = Object.fromEntries(Object.entries(previous).map(([lane, cards]) => [lane,
            lane === "Archive" ? cards : cards.filter(card => getProductionStepId(card) !== Number(row.removedStepId))]));
          // Only /cards may hydrate an eligible Upcoming card (including Elite,
          // routing and current filters). Never manufacture it from audit data.
          next.Planned = (next.Planned || []).filter(card => getProductionStepId(card) ||
            !(Number(card.orderDbId) === record.orderDbId && Number(card.itemId ?? card.orderItemId) === record.itemId &&
              Number(card.decorationId) === record.decorationId &&
              String(card.department || record.department).toLowerCase() === String(record.department).toLowerCase()));
          return next;
        });
        publishPendingMutations(store);
        await refreshSpecificListsRef.current(["Planned", "Scheduled"]);
        return true;
      }
      const identity = requirePersistedSchedule({ ...row, success: true });
      record.reconciledStatus = row.status;
      store.remove(record);
      if (store !== pendingMutationStoreRef.current) return true;
      invalidateMutationLanes([record.from, record.to, row.status]);
      setColumns(previous => {
        const current = row.mutationOutcome === "replaced" ? Object.fromEntries(Object.entries(previous).map(([lane, cards]) =>
          [lane, lane === "Archive" ? cards : cards.filter(card => getProductionStepId(card) !== Number(row.removedStepId))])) : previous;
        const visible = Object.values(current).flat().find(card => record.step_id
          ? getProductionStepId(card) === record.step_id
          : Number(card.orderDbId) === record.orderDbId && Number(card.itemId ?? card.orderItemId) === record.itemId &&
            Number(card.decorationId) === record.decorationId && String(card.department || record.department).toLowerCase() === String(record.department).toLowerCase());
        const sourceCard = visible || (record.scope === boardScopeRef.current ? record.sourceCard : null);
        return sourceCard ? reconcileProductionStep(current, { ...sourceCard, ...row, ...identity }, row.status, { sourceCard }) : current;
      });
      publishPendingMutations(store);
      await refreshSpecificListsRef.current([...new Set([record.from, record.to, row.status])]);
      return true;
    } catch (error) {
      store.update(record, "uncertain", error.message || "Outcome uncertain. Check saved status before retrying.");
      return false;
    } finally {
      record.checking = false;
      publishPendingMutations(store);
    }
  }, [invalidateMutationLanes, publishPendingMutations, setColumns]);

  useEffect(() => {
    const store = pendingMutationStoreRef.current;
    publishPendingMutations(store);
    store.records().filter(record => record.phase === "uncertain").forEach(record => { void reconcilePendingMutation(record, store); });
  }, [instanceUniqueId, publishPendingMutations, reconcilePendingMutation]);

  function beginExplicitBoardRequest(reason = "explicit-lane-refresh") {
    pollGenerationRef.current += 1;
    laneRequestRegistryRef.current.cancelAll(reason);
    paginationInFlightRef.current.clear();
    paginationTokensRef.current = {};
    setLanePagination((previous) => Object.fromEntries(Object.entries(previous).map(([lane, info]) => [
      lane, info.loading ? { ...info, loading: false } : info,
    ])));
    const request = boardRequestGateRef.current.begin(reason);
    currentBoardRequestRef.current = request;
    return request;
  }

  const requestLaneJson = useCallback((url, { reason = "lane-load", generation } = {}) => {
    const parsedUrl = new URL(url, window.location.origin);
    const lane = parsedUrl.pathname.replace(/\/$/, "").endsWith("/cards")
      ? "Planned" : parsedUrl.searchParams.get("lanes");
    const requestKey = String(url).replace("/production/cards/?", "/production/cards?");
    const entry = laneRequestRegistryRef.current.request(requestKey, async (signal) => {
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json = await response.json();
      if (!json?.success) throw new Error(json?.message || "Production lane request failed");
      return json;
    }, { scope: "board", lane, page: Number(parsedUrl.searchParams.get("page") || 1), generation, reason });
    return entry.promise;
  }, []);

  // Capacity for Scheduled
  const capacityMap = useMemo(
    () => buildCapacityMap(columns.Scheduled || []),
    [columns]
  );
  async function loadPlanning(page, replace = false) {
    if (planningLoading || (!replace && !planningHasMore)) return;
    setPlanningLoading(true);
    setError("");
    try {
      const r = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/orders?page=${page}&pageSize=${PAGE_SIZE}&instanceUniqueId=${encodeURIComponent(instanceUniqueId || '')}`
        , { headers: productionAuthHeaders() }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const js = await r.json();
      const cards = buildPlanningCards(js?.data || []);
      setColumns((prev) => {
        const next = replace ? emptyColumns() : clone(prev);
        const seen = new Set((next.Planned || []).map((c) => c.id));
        for (const c of cards)
          if (!seen.has(c.id))
            next.Planned.unshift(assignRankTop(next.Planned, c));
        return next;
      });
      const total = Number(js?.total ?? 0),
        // size = Number(js?.pageSize ?? 15),
        size = Number(js?.pageSize ?? PAGE_SIZE),
        cur = Number(js?.page ?? page);

      setPlanningHasMore(cur * size < total);
      setPlanningPage(cur);
    } catch (e) {
      setError(e.message || String(e));
      SA.error(
        `Could not load Planning list.\n${e.message || e}`,
        "Network error"
      );
    } finally {
      setPlanningLoading(false);
    }
  }

  // Overdue nudge (and persist rank)
  useEffect(() => {
    setColumns((prev) => {
      const next = clone(prev);
      const list = next.Scheduled || [];
      let changed = false;
      const maxRank = list.reduce((m, c) => Math.max(m, c.rank || 0), 0);
      for (const c of list)
        if (
          !c.startedAt &&
          daysLate(c.plannedStart) > 0 &&
          (c.rank || 0) <= maxRank
        ) {
          c.rank = maxRank + 50;
          changed = true;
          // if (c.isStep)
          //   queueRankPatch(c.id, c.rank);
        }
      return changed ? next : prev;
    });
  }, [setColumns]);

  // Helpers
  function emptyColumns() {
    const o = {};
    STATUSES.forEach((s) => (o[s.key] = []));
    return o;
  }
  const clone = (x) => JSON.parse(JSON.stringify(x));
  const getStepCardId = (card) => {
    const rawStepId = card?.step_id ?? card?.stepid;
    const stepId = Number(rawStepId);
    if (Number.isFinite(stepId) && stepId > 0) return String(stepId);
    return card?.external_key || card?.externalkey || null;
  };
  function buildPlanningCards(orders) {
    const out = [];
    for (const o of orders) {
      for (const it of o.items || []) {
        const decos = (it.decorations || []).filter(Boolean);
        if (!decos.length) continue;
        for (const d0 of decos) {
          const dept = inferDepartmentForDeco(d0);
          out.push({
            id: `ORD-${o.orderNumber}-${it.itemId}-${d0.decorationId}`,
            status: "Planned",
            orderNumber: o.orderNumber,
            customer: o.customerName || "",
            productName: it.productName || "",
            binNumber: o.binNumber || it.binNumber || d0.binNumber || "",
            orderRepName: o.orderRepName || "",
            orderRepSource: o.orderRepSource || null,
            sku: it.productId || "",
            matrix: d0.chargeName || "",
            location: d0.location || "",
            qtyPlanned: Number(it.quantity || 0) || 0,
            qtyDone: 0,
            dueDate: o.inHandsDate || "",
            department: dept,
            claimedDept: "",
            lifecycle: [],
            rank: 0,
            isStep: false,
            raw: {
              order: {
                orderNumber: o.orderNumber,
                customerName: o.customerName,
                inHandsDate: o.inHandsDate,
                projectName: o.projectName,
                binNumber: o.binNumber || it.binNumber || d0.binNumber || "",
                orderRepName: o.orderRepName || "",
                orderRepSource: o.orderRepSource || null,
                poNumbers: o.poNumbers || [],
              },
              item: {
                itemId: it.itemId,
                productId: it.productId,
                productName: it.productName,
                quantity: it.quantity,
                sizes: it.sizes || [],
                colors: it.colors || [],
                netCostGoods: it.netCostGoods,
                retailCostGoods: it.retailCostGoods,
                supplierName: it.supplierName,
                category: it.category,
              },
              deco: {
                decorationId: d0.decorationId,
                chargeId: d0.chargeId,
                chargeName: d0.chargeName,
                location: d0.location,
                netCostGoods: d0.netCostGoods,
                retailCostGoods: d0.retailCostGoods,
                artProofUrl: d0.artProofUrl,
              },
            },
          });
        }
      }
    }
    return out;
  }
  
function normalizeDeptName(value) {
  return String(value || "").trim().toLowerCase();
}

function getStepId(card) {
  return card?.step_id || card?.stepid || null;
}

function getSameDepartmentScheduledNeighbors(targetLane, dropIndex, schedulingDepartment) {
  const dept = normalizeDeptName(schedulingDepartment);

  const isSameDepartmentScheduledStep = (card) => {
    if (!card) return false;
    if (!getStepId(card)) return false;
    if (card.status && card.status !== "Scheduled") return false;
    return normalizeDeptName(card.department || card.claimedDept) === dept;
  };

  let before = null;
  let after = null;

  for (let i = dropIndex - 1; i >= 0; i--) {
    if (isSameDepartmentScheduledStep(targetLane[i])) {
      before = targetLane[i];
      break;
    }
  }

  for (let i = dropIndex; i < targetLane.length; i++) {
    if (isSameDepartmentScheduledStep(targetLane[i])) {
      after = targetLane[i];
      break;
    }
  }

  return {
    before_step_id: getStepId(before),
    after_step_id: getStepId(after),
  };
}

function assignRankTop(arr, card) {
    const maxRank = arr.reduce((m, c) => Math.max(m, c.rank || 0), 0);
    return { ...card, rank: maxRank + 1000 };
  }
  function computeRankForIndex(arr, index) {
    const above = arr[index - 1],
      below = arr[index + 1];
    if (!above && !below) return 1000;
    if (!above) return (below?.rank || 0) + 1000;
    if (!below) return (above?.rank || 0) - 1000;
    return Math.round(((above.rank || 0) + (below?.rank || 0)) / 2);
  }

  // Capacity
  function estimateMinutes(card) {
    const ps = Array.isArray(card.processes) ? card.processes : [];
    const sum = ps
      .filter((p) => String(p.type || "").toLowerCase() === "machine")
      .reduce((t, p) => t + (Number(p.estMinutes) || 0), 0);
    return sum > 0 ? sum : DEFAULT_CARD_MIN;
  }
  function buildCapacityMap(list) {
    const map = {};
    for (const c of list) {
      if (!c.machine || !c.plannedStart) continue;
      const key = `${c.machine}::${c.plannedStart}`;
      const minutes = estimateMinutes(c);
      if (!map[key]) map[key] = { used: 0, cap: DEFAULT_DAY_CAPACITY_MIN };
      map[key].used += minutes;
    }
    return map;
  }

  async function patchStatus(card, body, rollback) {
    if (blockCardMutation(card)) throw new Error("Wait for scheduling to save a persisted step before changing it.");
    const scope = boardScopeRef.current;
    const {
      fromStatus,
      toStatus,
      reason,
      before_step_id: passed_before,
      after_step_id: passed_after,
    } = body;
    const mutationStore = pendingMutationStoreRef.current;
    const completing = toStatus === "Completed" && fromStatus !== "Completed";
    const action = completing ? null : beginCardAction(card, rollback);
    const completionRecord = completing ? mutationStore.begin(card, "complete", { from: fromStatus, to: toStatus, scope }) : null;
    if (completing && !completionRecord) throw new Error("Completion is already pending.");
    if (completionRecord) publishPendingMutations(mutationStore);

    // Use the NEW move API for ALL operations
    try {
      console.log("Using NEW move API:", {
        card: card,
        step_id: card.step_id,
        from: fromStatus,
        to: toStatus,
      });

      // Get the correct step_id (with underscore)
      const stepId = card.step_id || card.stepid || card.stepId;

      if (!stepId) {
        console.error("No valid step ID found for card:", card);
        throw new Error("Card does not have a valid step ID");
      }

      console.log("Using step_id:", stepId);

      // Use positioning values passed from onDrop
      let final_before_step_id =
        passed_before !== undefined ? passed_before : null;
      let final_after_step_id =
        passed_after !== undefined ? passed_after : null;

      console.log("📍 Position Info (in patchStatus):", {
        targetLane: toStatus,
        passed_from_onDrop:
          passed_before !== undefined || passed_after !== undefined,
        final_before_step_id,
        final_after_step_id,
      });

      const requestBody = {
        step_id: parseInt(stepId),
        to: toStatus,
        instanceUniqueId,
        // before_step_id: final_before_step_id,
        // after_step_id: final_after_step_id,
        before_step_id: final_after_step_id,
        after_step_id: final_before_step_id,
        explicit_priority: null,
        ...(completionRecord ? { clientMutationId: completionRecord.mutationId } : {}),
      };

      // Include reason for OnHold moves
      if (toStatus === "On_Hold" && (card.holdReason || reason)) {
        requestBody.reason = card.holdReason || reason;
      }

      // Include department if available
      if (card.department) {
        requestBody.department_id = card.department;
      }

      // Include operator if available
      if (card.operator) {
        requestBody.operator = card.operator;
      }
      if (card.assignedUserId || card.operatorId) {
        requestBody.operatorId = card.assignedUserId || card.operatorId;
      }

      console.log("Request Body:", requestBody);

      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps/move`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      // if (!response.ok) {
      //   let msg = `Move failed (${response.status})`;
      //   try {
      //     const js = await response.json();
      //     if (js?.message) msg = js.message;
      //     else if (Array.isArray(js?.errors)) msg = js.errors.join("; ");
      //   } catch {}
      //   throw new Error(msg);
      // }

      if (!response.ok) {
        let msg = `Move failed (${response.status})`;
        let failurePayload;
        try {
          const js = await response.json();
          failurePayload = js;

          if (
            js?.code === "ALREADY_IN_PRODUCTION_ELSEWHERE" &&
            Array.isArray(js.activeDepartments)
          ) {
            const deptNames = js.activeDepartments
              .map((d) => d.departmentName)
              .join(", ");
            msg = `${js.message} Departments: ${deptNames}`;
            window.Swal.fire({
              icon: "error",
              title: "Save failed",
              html: msg,
              confirmButtonText: "OK",
            });
            throw new Error(js.message);
          } else if (js?.message) {
            msg = js.message;
          } else if (Array.isArray(js?.errors)) {
            msg = js.errors.join("; ");
          }
        } catch {}
        throw productionMutationError(response, { ...failurePayload, message: msg });
      }

      const result = await response.json();
      if (result.success !== true || Number(result.step_id) !== getProductionStepId(card)) {
        throw new Error(result.message || "Move operation failed");
      }

      console.log("Move API Success:", result);
      if (rollback) rollback.committed = true;
      if (completionRecord) {
        mutationStore.remove(completionRecord);
        publishPendingMutations(mutationStore);
      }
      if (boardScopeRef.current === scope && Number(result.step_id) === getProductionStepId(card)) {
        const lane = result.to === "QC" && !SHOW_QC_LANE ? "Completed" : result.to;
        invalidateMutationLanes([fromStatus, toStatus, lane].filter(Boolean));
        if (fromStatus === "Completed" || toStatus === "Completed") archiveGenerationRef.current += 1;
        setColumns((previous) => rollback && !ownsProductionCardMutation(previous, rollback, scope) ? previous : reconcileProductionStep(previous, {
          ...card, rank: result.rank ?? card.rank,
          pendingDepartments: result.pendingDepartments ?? card.pendingDepartments,
          isFullyCompleted: result.isFullyDone ?? card.isFullyCompleted,
        }, lane));
      }
      return result;
    } catch (error) {
      console.error("Move API Error:", error);
      if (completionRecord) {
        mutationStore.update(completionRecord, error.definite ? "failed" : "uncertain", error.message);
        publishPendingMutations(mutationStore);
        if (!error.definite && await reconcilePendingMutation(completionRecord, mutationStore)) {
          return { success: true, step_id: getProductionStepId(card), to: completionRecord.reconciledStatus, recovered: true };
        }
        error.mutationPending = !error.definite;
      }
      throw error;
    } finally {
      finishCardAction(action);
    }

    /* ============================================
     * OLD API - COMMENTED OUT
     * ============================================
     * This code used the old PATCH endpoint for Planned/Cancelled operations
     * Now using the new move API for ALL status changes
     *
     * ============================================ */
  }

  async function unscheduleJob(card, reason = "", rollback) {
    if (blockCardMutation(card)) throw new Error("Scheduling is still saving. Please wait.");
    const action = beginCardAction(card, rollback);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps/unschedule`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            external_key: card.external_key,
            step_id: card.step_id,
            instanceUniqueId,
            reason: reason,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();
      if (result.success !== true) throw new Error(result.message || "Unschedule was not confirmed.");
      if (rollback) rollback.committed = true;
      return result;
    } catch (error) {
      console.error("Unschedule API Error:", error);
      throw error;
    } finally {
      finishCardAction(action);
    }
  }

  async function reinstateJob(card, operator = "", rollback) {
    if (blockCardMutation(card)) throw new Error("Scheduling is still saving. Please wait.");
    const action = beginCardAction(card, rollback);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps/reinstate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            step_id: card.step_id,
            operator: operator,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();
      if (result.success !== true) throw new Error(result.message || "Reinstatement was not confirmed.");
      if (rollback) rollback.committed = true;
      return result;
    } catch (error) {
      console.error("Reinstate Job API Error:", error);
      throw error;
    } finally {
      finishCardAction(action);
    }
  }

  function onReinstateCard(card, operator) {
    const rollback = beginCardRollback(card);
    if (!rollback) return;

    // Mark card as NOT cancelled in UI
    setColumns((prev) => {
      const next = clone(prev);
      for (const k of Object.keys(next)) {
        const arr = next[k];
        const i = arr.findIndex((c) => c.id === card.id);
        if (i !== -1) {
          arr[i].cancelled = false;
          arr[i].disabled = false;
          arr[i].lifecycle = [
            ...arr[i].lifecycle,
            {
              when: new Date().toLocaleString(),
              text: `Reinstated by ${operator || "user"}`,
            },
          ];
          break;
        }
      }
      return next;
    }, rollback);

    if (card.isStep) {
      reinstateJob(card, operator, rollback)
        .then((result) => {
          SA.success(`Card reinstated successfully!${persistedPoStageMessage(result)}`, "Job active again");
        })
        .catch((e) => {
          rollbackCardMutation(rollback);
          SA.error(e.message, "Reinstate failed");
        });
    }
  }

  //  startProduction function
  async function startProduction(card, rollback) {
    if (blockCardMutation(card)) throw new Error("Scheduling is still saving. Please wait.");
    const action = beginCardAction(card, rollback);
    const scope = boardScopeRef.current;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps/start`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            step_id: card.step_id, // Use card.id instead of external_key
            // 'to' is automatically set to 'In_Production' by your backend
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();
      if (result.success !== true || Number(result.step_id) !== getProductionStepId(card)) {
        throw new Error(result.message || "The server did not confirm the Production step.");
      }
      if (rollback) rollback.committed = true;
      if (boardScopeRef.current === scope) {
        invalidateMutationLanes(["Scheduled", "In_Production"]);
        setColumns((previous) => rollback && !ownsProductionCardMutation(previous, rollback, scope)
          ? previous : reconcileProductionStep(previous, card, "In_Production"));
        await refreshSpecificLists(["Scheduled", "In_Production"]);
      }
      return result;
    } catch (error) {
      console.error("Start Production API Error:", error);
      throw error;
    } finally {
      finishCardAction(action);
    }
  }

  function siblingsAcrossAllLanes(card, cols) {
    const groupId = getProductionBatchGroupId(card);
    if (!groupId) return [];
    const all = Object.keys(cols).flatMap((k) => cols[k]);

    return all.filter((c) =>
      getProductionBatchGroupId(c) === groupId &&
      areCompatibleProductionBatchCards(card, c)
    );
  }

  function siblingsSameLane(card, cols) {
    const groupId = getProductionBatchGroupId(card);
    if (!groupId) return [];
    const arr = cols[card.status] || [];

    return arr.filter((c) =>
      getProductionBatchGroupId(c) === groupId &&
      areCompatibleProductionBatchCards(card, c)
    );
  }

  // Schedule gate
  function openScheduleGate(card, beforeStepId = null, afterStepId = null) {
    if (blockCardMutation(card)) return;
    const currentOperatorId = card.assignedUserId || card.operatorId || "";
    const currentOperatorName =
      card.assignedUserName || card.operatorName || card.operator || "";
    setMoveModal({
      open: true,
      to: "Scheduled",
      card,
      fields: {
        operator: currentOperatorId,
        operatorName: currentOperatorName,
        currentAssignedUserId: currentOperatorId,
        currentAssignedUserName: currentOperatorName,
        machine: "",
        plannedStart: card.plannedStart || scheduleDateValue(new Date()),
        plannedFinish: card.plannedFinish || card.plannedStart || scheduleDateValue(new Date()),
        isFirm: false,
        reason: "",
        schedulingDepartment: card.department || card.claimedDept || "",
        qty: card.qtyRequired || "0",
        beforeStepId, // NEW
        afterStepId, // NEW
      },
    });
  }

  // Add this state - replace the old autoScrollTimer
  const [scrollDirection, setScrollDirection] = useState(null);
  const scrollIntervalRef = useRef(null);

  // Improved auto-scroll function
  const handleDragScroll = useCallback(
    (e) => {
      const THRESHOLD = 80;
      const SPEED = 12;
      const mouseX = e.clientX;
      const mouseY = e.clientY;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let newDirection = null;

      // Horizontal scrolling (existing)
      if (mouseX < THRESHOLD) {
        newDirection = "left";
      } else if (mouseX > viewportWidth - THRESHOLD) {
        newDirection = "right";
      }
      //  ADD VERTICAL SCROLLING
      else if (mouseY < THRESHOLD) {
        newDirection = "up";
      } else if (mouseY > viewportHeight - THRESHOLD) {
        newDirection = "down";
      }

      if (newDirection !== scrollDirection) {
        if (scrollIntervalRef.current) {
          clearInterval(scrollIntervalRef.current);
          scrollIntervalRef.current = null;
        }

        if (newDirection) {
          scrollIntervalRef.current = setInterval(() => {
            let scrollX = 0,
              scrollY = 0;

            //  HANDLE ALL DIRECTIONS
            if (newDirection === "right") scrollX = SPEED;
            else if (newDirection === "left") scrollX = -SPEED;
            else if (newDirection === "down") scrollY = SPEED; // DOWN
            else if (newDirection === "up") scrollY = -SPEED; // UP

            window.scrollBy(scrollX, scrollY);
          }, 16);
        }

        setScrollDirection(newDirection);
      }
    },
    [scrollDirection]
  );

  // Clean up function
  const cleanupScroll = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    setScrollDirection(null);

    //  REMOVE ALL EVENT LISTENERS
    document.removeEventListener("mousemove", handleDragScroll);
    document.removeEventListener("mouseup", cleanupScroll);
    document.removeEventListener("dragover", preventDefault); // ADD THIS
    document.removeEventListener("drop", preventDefault); // ADD THIS
  }, [handleDragScroll]);

  // DND handlers
  function onDragStart(e, card, fromCol, fromIndex) {
    if (blockCardMutation(card)) { e.preventDefault(); return; }

    // Block drag for paused cards safety net
  if (!canDragProductionCard(card)) {
    e.preventDefault();
    return;
  }


    setPausePolling(true); // ✅ ADD THIS FIRST

    const isMain = fromCol !== "On_Hold";
    dragRef.current = {
      cardId: card.id,
      from: fromCol,
      fromIndex,
      overIndex: null,
      overCol: null,
      // overIndex: fromIndex,
      // overCol: fromCol,
      lastMain: isMain ? fromCol : card._lastMain || "Planned",
    };
    e.dataTransfer.effectAllowed = "move";

    //  ADD THESE LINES TO PREVENT OS-LEVEL DRAG
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", ""); // Prevent external drops

    // Prevent drag from extending outside browser
    document.addEventListener("dragover", preventDefault, false);
    document.addEventListener("drop", preventDefault, false);

    // NEW: Add scroll listeners with proper cleanup
    document.addEventListener("mousemove", handleDragScroll);
    document.addEventListener("mouseup", cleanupScroll, { once: true });
  }

  // Helper function
  const preventDefault = (e) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  };

  function onDragEnterCard(e, colKey, overIndex) {
    if (!dragRef.current.cardId) return;

      // Don't shift positions when hovering over a paused card
  const targetCard = columns[colKey]?.[overIndex];
  if (targetCard?.isPaused) return;


    dragRef.current.overIndex = overIndex;
    dragRef.current.overCol = colKey;
  }

    function onListOver(e, toCol) {
    const dragging = dragRef.current;
    const card = (columns[dragging?.from] || []).find(row => row.id === dragging?.cardId);
    if (dragging?.from === "On_Hold" && getProductionHoldReturnLane(card) !== toCol) {
      if (e.dataTransfer) e.dataTransfer.dropEffect = "none";
      return;
    }
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";

    //  update intended drop lane even if not hovering a card
    if (dragRef.current?.cardId) {
      dragRef.current.overCol = toCol;

      const el = listRefs[toCol]?.current;
      if (el) {
        // Use your card DOM nodes (they are <article data-index={idx}>)
        const cards = Array.from(el.querySelectorAll("article[data-index]"));

        if (cards.length === 0) {
          dragRef.current.overIndex = 0; // empty lane => top
        } else {
          const y = e.clientY;

          // Find first card whose midpoint is below mouse => insert before it
          let insertAt = cards.length; // default bottom
          for (let i = 0; i < cards.length; i++) {
            const r = cards[i].getBoundingClientRect();
            const mid = r.top + r.height / 2;
            if (y < mid) {
              insertAt = i;
              break;
            }
          }
          dragRef.current.overIndex = insertAt;
        }
      }
    }

    // ✅ keep your existing auto-scroll behavior
    const el = listRefs[toCol]?.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const topZone = rect.top + 40,
      botZone = rect.bottom - 40;
    if (e.clientY < topZone) el.scrollTop -= 10;
    else if (e.clientY > botZone) el.scrollTop += 10;
  }


  async function onDrop(e, toCol) {
    e.preventDefault();

    // NEW: Immediate cleanup of scroll
    cleanupScroll();

    const { cardId, from, overIndex, overCol, lastMain } =
      dragRef.current || {};
    if (!cardId || !from) return;
    if (from === "Archive" || toCol === "Archive") {
      return SA.info("This Production card is completed and archived. Editing is disabled.", "Archived Production");
    }

    // NEW: Pause polling during drag operation
    setPausePolling(true);

    // Block Planning → Paused
    if (from === "Planned" && toCol === "On_Hold") {
      await SA.error(
        "You cannot move directly from Planning to On Hold. Please schedule or progress the job first.",
        "Move blocked"
      );
      return;
    }

    //  NEW CHECK - Block QC lane if disabled
    if (!SHOW_QC_LANE && (toCol === "QC" || from === "QC")) {
      await SA.error(
        "QC lane is currently disabled. Cannot move cards to/from QC.",
        "QC Disabled"
      );
      setPausePolling(false);
      return;
    }

    // Get the source card first
    const srcCard = (columns[from] || []).find((c) => c.id === cardId);
    if (!srcCard) return;
    if (blockCardMutation(srcCard)) { setPausePolling(false); return; }
    if (from === "On_Hold" && toCol !== getProductionHoldReturnLane(srcCard)) {
      setPausePolling(false);
      return SA.error("On Hold work can only return to its saved originating lane.", "Move blocked");
    }
    if (toCol === "Completed" && from !== "Completed") {
      setPausePolling(false);
      return handleMarkAsComplete(srcCard);
    }

    //  Block if trying to move to InProduction without activeForProduction
    if (toCol === "In_Production" && !srcCard.activeForProduction) {
      await SA.error(
        'This job cannot start production yet. Purchase Order status must be "Received" first.',
        "PO Not Confirmed"
      );
      setPausePolling(false); // Resume polling
      return;
    }

    // Additional sibling lock: prevent cross-department starts for same order/item while another dept is active/open.
    if (toCol === "In_Production") {
      const activeStatuses = new Set(["In_Production"]);
      const identityOrder = srcCard.projectNumber || srcCard.orderNumber || null;
      const identityItem = srcCard.itemId || null;
      const myDept = srcCard.departmentId || srcCard.department || null;
      const myStepId = srcCard.step_id || srcCard.stepid || null;

      const allCards = Object.values(columns || {}).flat();
      const foreignOpenSibling = allCards.find((c) => {
        if (!c || c.id === srcCard.id) return false;
        const cOrder = c.projectNumber || c.orderNumber || null;
        const cItem = c.itemId || null;
        const cDept = c.departmentId || c.department || null;
        const cStepId = c.step_id || c.stepid || null;
        if (!identityOrder || !identityItem) return false;
        if (String(cOrder) !== String(identityOrder) || String(cItem) !== String(identityItem)) return false;
        if (myStepId && cStepId && Number(cStepId) === Number(myStepId)) return false;
        if (String(cDept) === String(myDept)) return false; // same department (split-friendly)
        return activeStatuses.has(c.status);
      });

      if (foreignOpenSibling) {
        await SA.error(
          "This item already has active work in another department. If the current department is split, all split batches for that department must be completed before another department can start.",
          "Another department is already in production"
        );
        setPausePolling(false);
        return;
      }
    }

    // Handle Scheduled → Planned (Unschedule)
    if (from === "Scheduled" && toCol === "Planned") {
      // Use Swal directly for the detailed HTML message
      const result = await window.Swal.fire({
        title: "⚠️ Unschedule This Job?",
        html: `
        <div style="text-align: left; margin: 15px 0;">
          <p><strong>This action will:</strong></p>
          <ul style="margin: 10px 0; padding-left: 20px;">
            <li>Move the job back to Planning queue</li>
            <li>Remove all scheduling details (dates, operators, machines)</li>
            <li>Delete capacity allocations and resource assignments</li>
            <li>Clear any department claims</li>
          </ul>
          <p style="color: #dc2626; font-weight: 500; margin: 15px 0;">
            ⚠️ All scheduling data for this order will be permanently removed.
          </p>
        </div>
      `,
        input: "textarea",
        inputLabel: "Reason for unscheduling (optional):",
        inputPlaceholder: "Enter reason...",
        showCancelButton: true,
        confirmButtonText: "Yes, Unschedule",
        cancelButtonText: "Cancel",
        icon: "warning",
        confirmButtonColor: "#dc2626",
        inputValidator: (value) => {
          // Allow empty values since reason is optional
          return null;
        },
      });

      // if (!result.isConfirmed) return; // User cancelled
      if (!result.isConfirmed) {
        setPausePolling(false); //
        return;
      }

      const reason = result.value || ""; // Get the reason from input

      // Find the source card
      const srcCard = columns[from].find((c) => c.id === cardId);
      if (!srcCard) return;

      // Optimistic update
      const rollback = beginCardRollback(srcCard);
      if (!rollback) return;
      setColumns((prev) => {
        const next = clone(prev);
        const fromArr = next[from];
        const i = fromArr.findIndex((c) => c.id === cardId);
        if (i === -1) return prev;

        const [moved] = fromArr.splice(i, 1);

        // Reset scheduling data for unscheduled card
        moved.status = "Planned";
        moved.claimedDept = null;
        moved.plannedStart = null;
        moved.plannedFinish = null;
        moved.operator = null;
        moved.machine = null;
        moved.isStep = false;
        moved.lifecycle = [
          ...(moved.lifecycle || []),
          {
            when: new Date().toLocaleString(),
            text: `Unscheduled - returned to Planning${
              reason ? ` (${reason})` : ""
            }`,
          },
        ];

        // Insert at top of Planned
        const inserted = assignRankTop(next["Planned"], moved);
        next["Planned"].unshift(inserted);

        return next;
      }, rollback);

      // try {
      //   // Call unschedule API with CORRECT property name
      //   await unscheduleJob(srcCard, reason);
      //   SA.success('Job unscheduled successfully.', 'Moved to Planning');

      //   // Refresh both lists after successful API call
      //   await refreshSpecificLists(['Planned', 'Scheduled']);
      // } catch (err) {
      //   // Revert on error
      //   setColumns(JSON.parse(snapshot));
      //   SA.error(err.message || 'Could not unschedule job.', 'Save failed');
      // }

      try {
        // Call unschedule API with CORRECT property name
        const result = await unscheduleJob(srcCard, reason, rollback);
        SA.success(`Job unscheduled successfully.${persistedPoStageMessage(result)}`, "Moved to Planning");

        // Delay refresh to allow backend processing
        // setTimeout(async () => {
        //   await refreshSpecificLists(['Planned', 'Scheduled']);
        // }, 1500); // 1.5 second delay

        //  Refresh both affected lanes immediately
        await refreshSpecificLists(["Planned", "Scheduled"]);
      } catch (err) {
        // Revert on error
        rollbackCardMutation(rollback);
        SA.error(err.message || "Could not unschedule job.", "Save failed");
      }

      return; // Exit early to prevent normal drag logic
    }

    // // Planning → Scheduled: open the Move Gate (no mutation yet)
    // if (from === "Planned" && toCol === "Scheduled") {
    //   const src = (columns[from] || []).find((c) => c.id === cardId);
    //   if (src) openScheduleGate(src);
    //   return;
    // }

    // NEW: If Planned → Scheduled, detect position AND open move gate
    if (from === "Planned" && toCol === "Scheduled") {
      const targetLane = columns[toCol] || [];
      const dropIndex =
        typeof overIndex === "number" && overIndex >= 0
          ? Math.min(overIndex, targetLane.length)
          : targetLane.length;

      const src = columns[from].find((c) => c.id === cardId);
      if (src) {
        const schedulingDepartment =
          src.department || src.claimedDept || department;

        const { before_step_id, after_step_id } =
          getSameDepartmentScheduledNeighbors(
            targetLane,
            dropIndex,
            schedulingDepartment
          );

        setMoveModal({
          open: true,
          to: "Scheduled",
          card: src,
          fields: {
            operator: src.assignedUserId || src.operatorId || "",
            operatorName:
              src.assignedUserName || src.operatorName || src.operator || "",
            currentAssignedUserId: src.assignedUserId || src.operatorId || "",
            currentAssignedUserName:
              src.assignedUserName || src.operatorName || src.operator || "",
            machine: "",
            plannedStart: scheduleDateValue(new Date()),
            plannedFinish: scheduleDateValue(new Date()),
            isFirm: false,
            reason: "",
            qty: src.qtyRequired || src.qtyPlanned || "",
            beforeStepId: before_step_id,
            afterStepId: after_step_id,
            schedulingDepartment,
          },
        });
      }
      return; // EXIT to prevent normal drag logic
    }


    // Same-lane reorder
    if (
      from === toCol &&
      overCol === toCol &&
      typeof overIndex === "number" &&
      overIndex >= 0
    ) {
      // Calculate position BEFORE state update
      const targetLane = columns[toCol];
      const currentIndex = targetLane.findIndex((c) => c.id === cardId);
      const dropIndex =
        overIndex >= targetLane.length ? targetLane.length : overIndex;

      // Calculate neighbors at the NEW position
      let beforestep_id = null;
      let afterstep_id = null;

      if (currentIndex < dropIndex) {
        // Moving DOWN in the list
        const cardBefore = targetLane[dropIndex]; // Card that will be above
        const cardAfter = targetLane[dropIndex + 1]; // Card that will be below
        beforestep_id = cardBefore?.step_id || cardBefore?.stepid || null;
        afterstep_id = cardAfter?.step_id || cardAfter?.stepid || null;
      } else {
        // Moving UP in the list
        const cardBefore = targetLane[dropIndex - 1]; // Card that will be above
        const cardAfter = targetLane[dropIndex]; // Card that will be below
        beforestep_id = cardBefore?.step_id || cardBefore?.stepid || null;
        afterstep_id = cardAfter?.step_id || cardAfter?.stepid || null;
      }

      // Get the card being moved
      const srcCard = targetLane[currentIndex];

      // Update UI optimistically
      const rollback = beginCardRollback(srcCard);
      if (!rollback) return;
      setColumns((prev) => {
        const next = clone(prev);
        const arr = next[toCol];
        const i = arr.findIndex((c) => c.id === cardId);
        if (i === -1) return prev;

        const [moved] = arr.splice(i, 1);
        const j = overIndex >= arr.length ? arr.length : overIndex;
        arr.splice(j, 0, moved);

        const newRank = computeRankForIndex(arr, j);
        moved.rank = newRank;

        return next;
      }, rollback);

      // Call the move API for same-lane reordering
      if (toCol !== "Planned" && srcCard.isStep) {
        try {
          await patchStatus(srcCard, {
            fromStatus: toCol,
            toStatus: toCol, // Same lane
            before_step_id: beforestep_id,
            after_step_id: afterstep_id,
          }, rollback);
          await refreshSpecificLists([toCol]);

          console.log("✅ Same-lane move API called successfully");
        } catch (err) {
          // Revert on error
          rollbackCardMutation(rollback);
          SA.error(err.message || "Could not reorder card.", "Save failed");
        }
      }

      return;
    }

    // // Cross-lane adjacency (Paused is special)
    // if (toCol !== "On_Hold" && from !== "On_Hold") {
    //   const fi = STATUS_INDEX[from],
    //     ti = STATUS_INDEX[toCol];
    //   if (Math.abs(ti - fi) !== 1) {
    //     await SA.error(
    //       "Only adjacent moves are allowed (except Paused/Hold).",
    //       "Move blocked"
    //     );
    //     return;
    //   }
    // }

    // Cross-lane adjacency (Paused is special)
    // if (toCol !== "On_Hold" && from !== "On_Hold") {
    //   const fi = STATUS_INDEX[from];
    //   const ti = STATUS_INDEX[toCol];

    //   // Skip index check if QC is hidden (because indices will shift)
    //   if (SHOW_QC_LANE && Math.abs(ti - fi) !== 1) {
    //     await SA.error(
    //       "Only adjacent moves are allowed (except Paused/Hold).",
    //       "Move blocked"
    //     );
    //     return;
    //   }
    // }

    // Cross-lane adjacency - Paused is special
    if (toCol !== "On_Hold" && from !== "On_Hold" && from !== toCol) {
      // Build the lane sequence dynamically based on QC visibility
      const laneSequence = SHOW_QC_LANE
        ? ["Planned", "Scheduled", "In_Production", "QC", "Completed"]
        : ["Planned", "Scheduled", "In_Production", "Completed"];

      const fromIndex = laneSequence.indexOf(from);
      const toIndex = laneSequence.indexOf(toCol);

      // Check if move is to an adjacent lane (only one step forward or backward)
      const isAdjacent =
        fromIndex !== -1 &&
        toIndex !== -1 &&
        Math.abs(toIndex - fromIndex) === 1;

      if (!isAdjacent) {
        await SA.error(
          "Only adjacent moves are allowed (except Paused/Hold).",
          "Move blocked"
        );
        setPausePolling(false);
        return;
      }
    }

    // We need the card to check rules before mutating state
    // const srcCard = (columns[from] || []).find((c) => c.id === cardId);
    // if (!srcCard) return;

    // Require reason for On_Hold moves
    if (toCol === "On_Hold") {
      const { value: reason } = await Swal.fire({
        title: "Put on Hold",
        text: "Please provide a reason for putting this job on hold:",
        input: "textarea",
        inputPlaceholder: "Reason for hold...",
        inputValidator: (value) => {
          if (!value) return "Reason is required for putting jobs on hold";
        },
        showCancelButton: true,
        confirmButtonText: "Put on Hold",
        cancelButtonText: "Cancel",
      });

      if (!reason) {
        setPausePolling(false);
        return; // User cancelled
      }

      // Store reason for API call
      // srcCard._holdReason = reason;
      srcCard.holdReason = reason;
    }

    // Paused return rule
    // if (from === "On_Hold" && toCol !== (srcCard._lastMain || "Planned")) {
    //   await SA.error(
    //     `On Hold card can only return to ${srcCard._lastMain}.`,
    //     "Move blocked"
    //   );
    //   return;
    // }

    // if (from === "On_Hold" && toCol !== (srcCard.lastMain || "Scheduled")) {
    //   await SA.error(
    //     `On Hold card can only return to ${srcCard.lastMain || "Scheduled"}.`,
    //     "Move blocked"
    //   );
    //   return;
    // }

    if (from === "On_Hold") {
      // Fallback chain: lastMain (frontend) → holdFrom (backend) → default
      const allowedReturn =
        getProductionHoldReturnLane(srcCard);

      if (toCol !== allowedReturn) {
        const displayName = allowedReturn.replace(/_/g, " ");
        await SA.error(
          `This card was put on hold from "${displayName}". It can only return there or to "Planned" for rescheduling.`,
          "Move blocked"
        );
        setPausePolling(false);
        return;
      }
    }

    // Starting late → confirm or reschedule (when moving into In_Production)
    if (
      toCol === "In_Production" &&
      srcCard.plannedStart &&
      daysLate(srcCard.plannedStart) > 0
    ) {
      const ok = await SA.confirm({
        title: "Start late?",
        text: `This was scheduled for ${srcCard.plannedStart}. Start now anyway? (Cancel = reschedule to today)`,
        confirmButtonText: "Start now",
        cancelButtonText: "Reschedule to today",
        icon: "warning",
      });
      if (!ok) {
        // reschedule in place (Scheduled → Scheduled with today's date)
        const rollback = beginCardRollback(srcCard);
        if (!rollback) return;
        try {
          setColumns((prev) => {
            const next = clone(prev);
            const arr = next.Scheduled || [];
            const i = arr.findIndex((x) => x.id === srcCard.id);
            if (i !== -1) {
              arr[i].plannedStart = todayYMD();
              arr[i].plannedFinish = todayYMD();
              arr[i].lifecycle = [
                ...(arr[i].lifecycle || []),
                {
                  when: new Date().toLocaleString(),
                  text: "Rescheduled to today",
                },
              ];
            }
            return next;
          }, rollback);
          if (srcCard.isStep) {
            await patchStatus(srcCard, {
              fromStatus: "Scheduled",
              toStatus: "Scheduled",
              plannedStart: toISOStart(todayYMD()),
              plannedFinish: toISOEnd(todayYMD()),
              reason: "Rescheduled on start",
            }, rollback);
            await refreshSpecificLists(["Scheduled"]);
          }
          await SA.success("Rescheduled to today.", "Updated");
        } catch (err) {
          rollbackCardMutation(rollback);
          SA.error(err.message || "Could not reschedule", "Save failed");
        }
        return;
      }
    }

    // ===== NEW: Calculate position BEFORE state update =====
    let before_step_id = null;
    let after_step_id = null;

    // Only calculate position for cross-lane moves that need positioning
    if (toCol !== "Planned" && from !== toCol) {
      const targetLane = columns[toCol] || [];

      // If we have a specific drop index (from overIndex), use that
      if (typeof overIndex === "number" && overIndex >= 0) {
        const dropIndex =
          overIndex >= targetLane.length ? targetLane.length : overIndex;

        const cardBefore = targetLane[dropIndex - 1]; // Card that will be above
        const cardAfter = targetLane[dropIndex]; // Card that will be below

        before_step_id = cardBefore?.step_id || cardBefore?.stepid || null;
        after_step_id = cardAfter?.step_id || cardAfter?.stepid || null;
      } else {
        // Default to inserting at top (first position)
        before_step_id = null;
        after_step_id = targetLane[0]?.step_id || targetLane[0]?.stepid || null;
      }

      console.log("🎯 Position Calculation (BEFORE state update):", {
        toCol,
        overIndex,
        before_step_id,
        after_step_id,
        targetLaneLength: targetLane.length,
      });
    }

    // Proceed to move (with revert on error)
    const rollback = beginCardRollback(srcCard);
    if (!rollback) return;
    setColumns((prev) => {
      const next = clone(prev);
      const fromArr = next[from] || [];
      const i = fromArr.findIndex((c) => c.id === cardId);
      if (i === -1) return prev;
      const [moved] = fromArr.splice(i, 1);

      // If moving into Paused, remember last main
      // if (toCol === "On_Hold") {
      //   moved._lastMain = from === "On_Hold" ? lastMain : from;
      //   moved.lifecycle = moved.lifecycle || [];
      //   moved.lifecycle.push({
      //     when: new Date().toLocaleString(),
      //     text: `Moved to Paused/On Hold from ${from}`,
      //   });
      // }

      if (toCol === "On_Hold") {
        moved.lastMain = from; // Direct assignment - use the lane we're coming from
        moved.holdReason = srcCard.holdReason;
        moved.lifecycle = moved.lifecycle || [];
        moved.lifecycle.push({
          when: new Date().toLocaleString(),
          //   text: `Moved to Paused/On Hold from ${from}`,
          // });
          text: `Moved to On Hold from ${from}${
            srcCard.holdReason ? ` - ${srcCard.holdReason}` : ""
          }`,
        });
      }
      if (toCol === "Planned") {
        moved.claimedDept = "";
        moved.lifecycle = moved.lifecycle || [];
        moved.lifecycle.push({
          when: new Date().toLocaleString(),
          text: "Returned to Planning (open to all)",
        });
      }

      moved.status = toCol;

      const arr = next[toCol] || (next[toCol] = []);

// ✅ use overIndex only if it was captured from THIS lane
      const validOverIndex =
        overCol === toCol && typeof overIndex === "number" && overIndex >= 0;

      const j = validOverIndex ? Math.min(overIndex, arr.length) : arr.length;

      arr.splice(j, 0, moved);
      moved.rank = computeRankForIndex(arr, j);


      return next;

    }, rollback);

    try {
      // Persist the move except pure Planning reorder
      if (srcCard.isStep && toCol !== "Planned") {
        console.log("About to call patchStatus with:", {
          card_id: srcCard.id,
          step_id: srcCard.step_id,
          fromStatus: from,
          toStatus: toCol,
          isStep: srcCard.isStep,
        });

        // await patchStatus(srcCard, {
        //   fromStatus: from,
        //   toStatus: toCol,
        // });

        await patchStatus(srcCard, {
          fromStatus: from,
          toStatus: toCol,
          before_step_id: before_step_id, // Pass calculated values
          after_step_id: after_step_id, // Pass calculated values
        }, rollback);

        // Refresh both affected lanes immediately
        ///*******************************88888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888888*********************************************************************** */
        // await refreshSpecificLists([from, toCol]);
        // await refreshSpecificLists([from, toCol]);

        //           setTimeout(() => {
        //   const movedCard = columns[toCol]?.find(c => c.step_id === srcCard.step_id);
        //   if (movedCard?.step_id) {
        //     console.log('[onDrop] Queueing rank update for cross-lane move:', movedCard.step_id);
        //     queueRankPatch(movedCard.step_id, movedCard.rank);
        //   }
        // }, 500); // Wait for state to fully update
        //   }

        await refreshSpecificLists([from, toCol]);

        console.log("🚀 Cross-lane move completed");
        console.log("🚀 srcCard:", srcCard);
        console.log("🚀 srcCard.step_id:", srcCard.step_id);
        console.log("🚀 srcCard.stepid:", srcCard.stepid);
        console.log("🚀 toCol:", toCol);
        console.log("🚀 columns[toCol]:", columns[toCol]);

        // // Queue rank update for cross-lane move
        // setTimeout(() => {
        //   // FIX: Use step_id instead of stepid
        //   // const movedCard = columns[toCol]?.find(c =>
        //   //   c.step_id === srcCard.step_id || c.stepid === srcCard.stepid
        //   // );

        //   // const actualStepId = movedCard?.step_id || movedCard?.stepid;

        //   const movedCard = columns[toCol]?.find(c =>
        //   c.step_id === srcCard.step_id ||
        //   c.stepid === srcCard.stepid ||
        //   c.id === srcCard.id
        // );
        // const actualStepId = movedCard?.step_id || movedCard?.stepid;

        //   if (actualStepId) {
        //     console.log('[onDrop] Queueing rank update for cross-lane move:', actualStepId);
        //     queueRankPatch(actualStepId, movedCard.rank);
        //   } else {
        //     console.log('[onDrop] No step_id found for cross-lane card');
        //   }
        // // }, 500);
        // }, 2500);
        //       }

        //       // Persist rank for the newly inserted card
        //       const arr = JSON.parse(snapshot)[toCol] || []; // snapshot pre-move
        //       const newArr = columns[toCol] || []; // might be stale; queue anyway
        //       const movedNow = newArr.find((c) => c.id === srcCard.id);
        //       if (movedNow?.isStep && movedNow?.rank)
        //         queueRankPatch(movedNow.id, movedNow.rank);

        //       setTimeout(() => {
        //         setPausePolling(false);
        //       }, 2000); // Resume polling after 2 seconds
        //     } catch (err) {
        //       // catch (err) {
        //       //   setColumns(JSON.parse(snapshot));
        //       //   SA.error(err.message || "Move could not be saved.", "Save failed");
        //       // }
        //       setColumns(JSON.parse(snapshot));
        //       SA.error(err.message || "Move could not be saved.", "Save failed");

        //       // NEW: Resume polling even on error
        //       setPausePolling(false);
        //     }

        // Use setColumns callback to access FRESH state
        setColumns((prev) => {
          const movedCard = prev[toCol]?.find(
            (c) =>
              c.step_id === srcCard.step_id ||
              c.stepid === srcCard.stepid ||
              c.id === srcCard.id
          );

          const actualStepId = movedCard?.step_id || movedCard?.stepid;

          if (actualStepId && toCol !== "Planned") {
            console.log(
              "[onDrop] Found card, queueing rank update:",
              actualStepId
            );
            setTimeout(() => {
              // queueRankPatch(actualStepId, movedCard.rank);
            }, 2000);
          } else {
            console.log("[onDrop] No rank update needed");
          }

          return prev;
        }, rollback);
      }

      // Resume polling after successful operation
      setTimeout(() => {
        setPausePolling(false);
      }, 2000);
    } catch (err) {
      rollbackCardMutation(rollback);
      SA.error(err.message || "Move could not be saved.", "Save failed");
      // setPausePolling(false);
    } finally {
      //  ALWAYS resume polling, even on error
      setPausePolling(false);
    }
  }

  // Add this cleanup effect
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      cleanupScroll();
    };
  }, [cleanupScroll]);

  useEffect(() => {
    // PREVENT ACCIDENTAL SYSTEM DRAGS
    const preventSystemDrag = (e) => {
      // Only allow drag on your specific draggable elements
      if (!e.target.closest('[draggable="true"]')) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener("dragstart", preventSystemDrag);
    document.addEventListener("dragover", (e) => e.preventDefault());
    document.addEventListener("drop", (e) => e.preventDefault());

    return () => {
      document.removeEventListener("dragstart", preventSystemDrag);
      document.removeEventListener("dragover", (e) => e.preventDefault());
      document.removeEventListener("drop", (e) => e.preventDefault());
    };
  }, []);

  function onScrollPlanning(e) {
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
    if (nearBottom && planningHasMore && !planningLoading)
      loadPlanning(planningPage + 1);
  }

  // function onPatchCard(id, patch) {
  //   setColumns((prev) => {
  //     const next = clone(prev);
  //     const all = Object.keys(next).flatMap((k) => next[k]);
  //     const c = all.find((x) => x.id === id);
  //     if (c) Object.assign(c, patch);
  //     if (modalCard?.id === id) setModalCard({ ...modalCard, ...patch });
  //     return next;
  //   });
  // }

  function onPatchCard(id, patch) {
    const card = Object.values(columns).flat().find((row) => row.id === id);
    if (blockCardMutation(card)) return;
    setColumns((prev) => {
      const next = clone(prev);

      // Find which lane the card is in and update it there
      for (const laneKey of Object.keys(next)) {
        const lane = next[laneKey];
        const cardIndex = lane.findIndex((c) => c.id === id);

        if (cardIndex !== -1) {
          // Found the card - update it in place
          next[laneKey][cardIndex] = {
            ...lane[cardIndex],
            ...patch,
          };
          break; // Stop searching once found
        }
      }

      // Update modal card if it matches
      if (modalCard?.id === id) {
        setModalCard((prev) => ({
          ...prev,
          ...patch,
        }));
      }

      return next;
    });
  }

  function onCancelCard(card, reason) {
    if (blockCardMutation(card)) return;
    if (card?.isArchived || card?.status === "Archive") {
      return SA.info("This Production card is completed and archived. Editing is disabled.", "Archived Production");
    }
    const rollback = beginCardRollback(card);
    if (!rollback) return;
    setColumns((prev) => {
      const next = clone(prev);
      for (const k of Object.keys(next)) {
        const arr = next[k];
        const i = arr.findIndex((c) => c.id === card.id);
        if (i !== -1) {
          arr[i].cancelled = true;
          arr[i].lifecycle = [
            ...(arr[i].lifecycle || []),
            {
              when: new Date().toLocaleString(),
              text: `Cancelled: ${reason || "—"}`,
            },
          ];
          break;
        }
      }
      return next;
    }, rollback);
    if (card.isStep) {
      // patchStatus(card.id, {
      //   fromStatus: card.status,
      //   toStatus: "Cancelled",
      patchStatus(card, {
        // Passing full card instead of card.id
        fromStatus: card.status,
        toStatus: "Cancelled",
        reason,
        //   reason,
      }, rollback).then(() => refreshSpecificLists([card.status])).catch((e) => {
        rollbackCardMutation(rollback);
        SA.error(e.message || "Cancel failed", "Save failed");
      });
    }
  }

  function onReinstateCard(card, note) {
    if (blockCardMutation(card)) return;
    if (card?.isArchived || card?.status === "Archive") {
      return SA.info("This Production card is completed and archived. Editing is disabled.", "Archived Production");
    }
    const rollback = beginCardRollback(card);
    if (!rollback) return;

    setColumns((prev) => {
      const next = clone(prev);
      for (const k of Object.keys(next)) {
        const arr = next[k];
        const i = arr.findIndex((c) => c.id === card.id);
        if (i !== -1) {
          arr[i].cancelled = false;
          arr[i].disabled = false;
          arr[i].lifecycle = [
            ...(arr[i].lifecycle || []),
            {
              when: new Date().toLocaleString(),
              text: `Reinstated: ${note || "—"}`,
            },
          ];
          break;
        }
      }
      return next;
    }, rollback);

    if (card.isStep) {
      reinstateJob(card, note, rollback)
        .then((result) => {
          SA.success(`Card reinstated successfully!${persistedPoStageMessage(result)}`, "Job active again");
        })
        .catch((e) => {
          rollbackCardMutation(rollback);
          SA.error(e.message || "Reinstate failed", "Save failed");
        });
    }
  }

  async function confirmSchedule(fields) {
    const original = moveModal.card;
    const scope = boardScopeRef.current;
    const pendingKey = `${scope}|${getProductionScheduleKey(original)}`;
    if (!original || pendingSchedulesRef.current.has(pendingKey)) return;
    if (blockCardMutation(original)) return;
    // Validate
    if (
      !fields.operator ||
      !fields.machine ||
      !fields.plannedStart ||
      !fields.plannedFinish
    ) {
      await SA.error(
        "Please fill Operator, Machine, Planned Start, and Planned Finish.",
        "Missing fields"
      );
      return;
    }

    const yStart = fields.plannedStart,
      yFinish = fields.plannedFinish;
    if (!validScheduleDateRange(yStart, yFinish)) {
      await SA.error("Planned Finish must be on or after a valid Planned Start.", "Invalid schedule dates");
      return;
    }
    const isoStart = scheduleTimestamp(yStart),
      isoFinish = scheduleTimestamp(yFinish, true);

    // Extract positioning parameters
    const beforeStepId = fields.beforeStepId || null;
    const afterStepId = fields.afterStepId || null;

    const fromKey = Object.keys(columns).find((k) =>
      columns[k].some((c) => getProductionScheduleKey(c) === getProductionScheduleKey(original))
    );

    if (!fromKey) {
      setMoveModal({ open: false, to: null, card: null, fields: {} });
      return;
    }

    const arr = columns[fromKey];
    const idx = arr.findIndex((c) => getProductionScheduleKey(c) === getProductionScheduleKey(original));

    if (idx === -1) {
      setMoveModal({ open: false, to: null, card: null, fields: {} });
      return;
    }

    const orig = arr[idx];

    const schedulingDepartment =
      fields.schedulingDepartment || orig.department || orig.claimedDept || department;

    if (
      !schedulingDepartment ||
      ["all", "production"].includes(String(schedulingDepartment).toLowerCase())
    ) {
      SA.error("Could not determine the production department for this card.", "Scheduling failed");
      return;
    }

    console.log("Scheduling Info:", {
      currentPageDepartment: department,
      originalCardDepartment: orig.department,
      willScheduleAs: schedulingDepartment,
    });

    const scheduled = {
      ...orig,
      status: "Scheduled",
      // claimedDept: orig.department || "Department",
      claimedDept: schedulingDepartment, //  Using prop department
      plannedStart: yStart,
      plannedFinish: yFinish,
      operator: fields.operator,
      operatorId: fields.operator || null,
      operatorName: fields.operatorName || "",
      machine: fields.machine,
      isFirm: !!fields.isFirm,
      lifecycle: [
        ...(Array.isArray(orig.lifecycle) ? orig.lifecycle : []),
        {
          when: new Date().toLocaleString(),
          text: `Scheduled ${fields.operatorName || "Operator"} on ${
            fields.machineName || "Machine"
          }`,
        },
      ],
      isStep: true,
    };

    const store = pendingMutationStoreRef.current;
    const record = store.begin(orig, "schedule", { from: fromKey, to: "Scheduled", department: schedulingDepartment, scope,
      existingSteps: Object.values(columns).flat().map(getProductionStepId).filter(Boolean) });
    if (!record) return;
    record.retryFields = fields;
    publishPendingMutations(store);
    pendingSchedulesRef.current.add(pendingKey);
    setPendingSchedules(new Set(pendingSchedulesRef.current));
    setMoveModal({ open: false, to: null, card: null, fields: {} });
    setPausePolling(false);
    let committed = false;
    try {
      // NEW SCHEDULE API CALL
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps/schedule`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accessToken: localStorage.getItem("accessToken"),
          },
          // MODIFICATION 2: Add positioning parameters
          body: JSON.stringify({
            external_key: orig.external_key,
            order_id: orig.orderDbId,
            clientMutationId: record.mutationId,
            department: scheduled.claimedDept,
            plannedStart: isoStart,
            plannedFinish: isoFinish,
            machine: fields.machineName || fields.machine,
            operatorId: fields.operator || null,
            assigned_user_id: fields.operator || null,
            operator: fields.operatorName || fields.operator,
            reason: fields.reason || "",
            isFirm: !!fields.isFirm,
            qty: parseInt(orig.qtyRequired, 10) || 0,
            // before_step_id: beforeStepId, // NEW: positioning
            // after_step_id: afterStepId, // NEW: positioning
            before_step_id: beforeStepId || null, // NEW: positioning
            after_step_id: afterStepId || null, // NEW: positioning
            explicit_priority: null, // NEW: positioning
            instanceUniqueId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = productionMutationError(response, errorData);
        if (error.definite && errorData.code === "ELITE_STATUS_BLOCK") {
          error.schedulingBlockReason = [
            errorData.message || (errorData.eliteStatus?.so
              ? `Sales Order: ${errorData.eliteStatus.so} is not eligible for Production scheduling.`
              : "The Sales Order is not eligible for Production scheduling."),
            "The order remains in Upcoming Orders.",
          ].filter(Boolean).join("\n\n");
        }
        throw error;
      }

      const result = await response.json();
      const identity = requirePersistedSchedule(result);
      committed = true;
      store.remove(record);
      publishPendingMutations(store);
      if (store !== pendingMutationStoreRef.current) return;
      invalidateMutationLanes([fromKey, "Planned", "Scheduled"]);
      if (boardScopeRef.current === scope) setColumns((previous) => reconcileProductionStep(previous, {
        ...scheduled, ...identity, department: result.department || schedulingDepartment,
        assignedUserId: result.assignedUserId || fields.operator,
        rank: result.rank ?? scheduled.rank,
      }, "Scheduled", { sourceCard: orig, beforeStepId, afterStepId }));
      // A successful write must not be rolled back because a later read fails.
      const refreshed = await refreshSpecificLists(["Planned", "Scheduled"]);
      if (boardScopeRef.current === scope) SA.success(
        `Job scheduled successfully.${persistedPoStageMessage(result)}${refreshed === false ? "\nSome lanes could not refresh. Use the lane Retry button." : ""}`, "Scheduled"
      );
    } catch (e) {
      console.error("Schedule API Error:", e);
      if (!committed) {
        store.update(record, e.definite ? "failed" : "uncertain", e.message || "Scheduling could not be confirmed.");
        publishPendingMutations(store);
        // Alert only for this rejected submit, never from polling or recovery.
        if (e.schedulingBlockReason && store === pendingMutationStoreRef.current) {
          SA.error(e.schedulingBlockReason, "Scheduling Not Allowed");
        }
        if (!e.definite) await reconcilePendingMutation(record, store);
      }
    } finally {
      pendingSchedulesRef.current.delete(pendingKey);
      setPendingSchedules(new Set(pendingSchedulesRef.current));
    }
  }

  async function reorder(dir, colKey, cardId) {
    if (blockCardMutation((columns[colKey] || []).find((card) => card.id === cardId))) return;
    // 1. Save snapshot for rollback
    const rollback = beginCardRollback((columns[colKey] || []).find(row => row.id === cardId));
    if (!rollback) return;

    // 2. Optimistic UI update
    setColumns((prev) => {
      const next = clone(prev);
      const arr = next[colKey];
      const i = arr.findIndex((c) => c.id === cardId);
      if (i === -1) return prev;

      const j = dir === "up" ? i - 1 : i + 1;
      if (j < 0 || j >= arr.length) return prev;

      // Swap cards
      const tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;

      // Update rank
      const newRank = computeRankForIndex(arr, j);
      arr[j].rank = newRank;

      return next;
    }, rollback);

    // 3. Get the card that was moved
    const arr = columns[colKey];
    const i = arr.findIndex((c) => c.id === cardId);

    if (i === -1) {
      rollbackCardMutation(rollback);
      SA.error("Card not found", "Error");
      return;
    }

    const movedCard = arr[i];

    // 4. Calculate neighbors at NEW position
    const cardBefore = i > 0 ? arr[i - 1] : null;
    const cardAfter = i < arr.length - 1 ? arr[i + 1] : null;

    const before_step_id = cardBefore?.step_id || cardBefore?.stepid || null;
    const after_step_id = cardAfter?.step_id || cardAfter?.stepid || null;

    // 5. Call move API (this is the ONLY API call needed)
    try {
      await patchStatus(movedCard, {
        fromStatus: colKey,
        toStatus: colKey, // Same lane
        before_step_id,
        after_step_id,
      }, rollback);
      await refreshSpecificLists([colKey]);

      console.log("✅ Button reorder - Move API called successfully");
      // That's it! No need for queueRankPatch
    } catch (err) {
      // Revert on error
      rollbackCardMutation(rollback);
      SA.error(err.message || "Could not reorder card", "Save failed");
    }
  }

  // function openCard(card) {
  //   setModalCard(card);
  //   setModalOpen(true);
  // }

  async function openCard(card) {
    const externalKey = getProductionDetailExternalKey(card);
    if (!externalKey) {
      return SA.info("Details are unavailable for this Production card because its item/decoration identity is missing or invalid.", "Production Details");
    }
    setModalCard(card); // keep existing card info for instant display
    setModalLoading(true); // show loading state
    setModalOpen(true);

    try {
      const res = await fetch(
        `${
          process.env.REACT_APP_NODEJS_SERVER_URL
        }/production/order-details?external_key=${encodeURIComponent(externalKey)}`
      );
      const json = await res.json();

      if (json.success && json.data.length > 0) {
        setDetailedCard(json.data[0]); // store detailed API response
        // setDetailedCard(json); // Instead of setDetailedCard(json.data[0])
        setOrderMeta(json.meta); // New state for meta
        setOrderCosting(json.costing); // New state for costing
        setOrderCharges(json.charges); // New state for charges

        if (process.env.NODE_ENV !== "production") {
          console.debug("[AvailableForPlanning][proofs]", {
            externalKey,
            poSources: json?.meta?.poProofSources || [],
            proofCount: Array.isArray(json?.meta?.proofsNormalized)
              ? json.meta.proofsNormalized.length
              : 0,
          });
        }

        console.log("Detailed API Result", json.data[0]);
      } else {
        setDetailedCard(null); // no details found
        setOrderMeta(null);
        setOrderCosting(null);
        setOrderCharges(null);
        console.warn("No details found for this card");
      }
    } catch (err) {
      console.error("Failed to fetch order details:", err);
      setDetailedCard(null);
      setOrderMeta(null);
      setOrderCosting(null);
      setOrderCharges(null);
    } finally {
      setModalLoading(false);
    }
  }

  function onScrollCol(key, e) {
    if (key === "Planned") onScrollPlanning(e);
  }
  const ensureProofArray = (value) => (Array.isArray(value) ? value : []);
  const firstTruthy = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");
  const normalizeProofKey = (value) => String(value || "").trim().toLowerCase();
  const hasProofFile = (proof = {}) => Boolean(
    proof?.filePath || proof?.proofFilePath || proof?.path || proof?.url || proof?.Image || proof?.image || proof?.previewUrl || proof?.thumbnailUrl
  );
  const isProofApproved = (proof = {}) => {
    const status = String(proof?.status || proof?.proofStatus || "")
      .trim()
      .toLowerCase();
    const userState = String(proof?.approvals?.user?.state || "")
      .trim()
      .toLowerCase();
    const clientState = String(proof?.approvals?.client?.state || "")
      .trim()
      .toLowerCase();
    const approvalState = String(
      proof?.approvalState || proof?.approvalStatus || proof?.clientApprovalStatus || ""
    )
      .trim()
      .toLowerCase();

    return (
      status === "approved" ||
      status === "complete" ||
      approvalState === "approved" ||
      approvalState === "complete" ||
      (userState === "approved" && clientState === "approved")
    );
  };
  const isProofRejected = (proof = {}) => {
    const status = String(proof?.status || proof?.proofStatus || "")
      .trim()
      .toLowerCase();
    const userState = String(proof?.approvals?.user?.state || "")
      .trim()
      .toLowerCase();
    const clientState = String(proof?.approvals?.client?.state || "")
      .trim()
      .toLowerCase();
    const approvalState = String(
      proof?.approvalState || proof?.approvalStatus || proof?.clientApprovalStatus || ""
    )
      .trim()
      .toLowerCase();

    return (
      status === "rejected" ||
      approvalState === "rejected" ||
      userState === "rejected" ||
      clientState === "rejected"
    );
  };
  const getTileProofContext = (item = {}) => ({
    orderId: normalizeProofKey(firstTruthy(item?.orderId, item?.orderNo, item?.orderNumber, item?.raw?.order?.orderNumber)),
    productId: normalizeProofKey(firstTruthy(item?.productId, item?.ProductId, item?.sku, item?.raw?.item?.productId)),
    orderItemId: normalizeProofKey(firstTruthy(item?.orderItemId, item?.OrderItemId, item?.itemId, item?.raw?.item?.itemId)),
    decorationId: normalizeProofKey(firstTruthy(item?.decorationId, item?.DecorationId, item?.raw?.deco?.decorationId)),
    chargeId: normalizeProofKey(firstTruthy(item?.chargeId, item?.ChargeId, item?.raw?.deco?.chargeId)),
    location: normalizeProofKey(firstTruthy(item?.location, item?.raw?.deco?.location)),
    department: normalizeProofKey(firstTruthy(item?.department, item?.process, item?.claimedDept)),
  });
  const proofMatchesTileContext = (proof = {}, context = {}) => {
    const proofContext = {
      orderId: normalizeProofKey(firstTruthy(proof?.orderId, proof?.orderNo, proof?.orderNumber)),
      productId: normalizeProofKey(firstTruthy(proof?.productId, proof?.ProductId)),
      orderItemId: normalizeProofKey(firstTruthy(proof?.orderItemId, proof?.OrderItemId, proof?.itemId)),
      decorationId: normalizeProofKey(firstTruthy(proof?.decorationId, proof?.DecorationId, proof?.decoration?.id, proof?.decoration?.DecorationId)),
      chargeId: normalizeProofKey(firstTruthy(proof?.chargeId, proof?.ChargeId, proof?.binding?.chargeId, proof?.decoration?.chargeId)),
      location: normalizeProofKey(firstTruthy(proof?.location, proof?.decoration?.location)),
      department: normalizeProofKey(firstTruthy(proof?.department, proof?.process)),
    };

    const comparableKeys = Object.keys(proofContext);
    const proofHasContext = comparableKeys.some((key) => proofContext[key]);
    if (!proofHasContext) return true;

    return comparableKeys.every((key) => {
      if (!context[key] || !proofContext[key]) return true;
      return context[key] === proofContext[key];
    });
  };
  const collectProofCandidates = (item = {}, context = {}) => {
    const purchaseOrders = ensureProofArray(item?.purchaseOrders);
    const relatedPurchaseOrders = ensureProofArray(item?.relatedPurchaseOrders);
    const rawPo = item?.raw?.po ? [item.raw.po] : [];
    const proofContainers = [
      { source: "card.proofs", proofs: ensureProofArray(item?.proofs) },
      { source: "card.proofing", proofs: ensureProofArray(item?.proofing) },
      { source: "card.proofings", proofs: ensureProofArray(item?.proofings) },
      { source: "card.proofingItems", proofs: ensureProofArray(item?.proofingItems) },
      { source: "card.purchaseOrderProofs", proofs: ensureProofArray(item?.purchaseOrderProofs) },
      { source: "card.poProofs", proofs: ensureProofArray(item?.poProofs) },
      { source: "raw.deco.proofs", proofs: ensureProofArray(item?.raw?.deco?.proofs) },
      { source: "raw.deco.proofings", proofs: ensureProofArray(item?.raw?.deco?.proofings) },
      { source: "raw.deco.proofingItems", proofs: ensureProofArray(item?.raw?.deco?.proofingItems) },
      ...purchaseOrders.map((po, idx) => ({
        source: `purchaseOrder:${po?.id ?? idx}`,
        proofs: ensureProofArray(po?.proofs),
        poNumber: po?.poNumber || null,
        purchaseOrderType: po?.purchaseOrderType || null,
        proofRequired: po?.proofRequired,
        proofSummary: po?.proofSummary,
        proofStatus: po?.proofStatus,
        allProofsUploaded: po?.allProofsUploaded,
      })),
      ...relatedPurchaseOrders.map((po, idx) => ({
        source: `relatedPurchaseOrder:${po?.id ?? idx}`,
        proofs: ensureProofArray(po?.proofs),
        poNumber: po?.poNumber || null,
        purchaseOrderType: po?.purchaseOrderType || null,
        proofRequired: po?.proofRequired,
        proofSummary: po?.proofSummary,
        proofStatus: po?.proofStatus,
        allProofsUploaded: po?.allProofsUploaded,
      })),
      ...rawPo.map((po, idx) => ({
        source: `raw.po:${po?.id ?? idx}`,
        proofs: ensureProofArray(po?.proofs),
        poNumber: po?.poNumber || null,
        purchaseOrderType: po?.purchaseOrderType || null,
        proofRequired: po?.proofRequired,
        proofSummary: po?.proofSummary,
        proofStatus: po?.proofStatus,
        allProofsUploaded: po?.allProofsUploaded,
      })),
    ];

    const proofSources = [];
    const proofs = [];
    const seen = new Set();

    proofContainers.forEach((container) => {
      const normalizedProofs = ensureProofArray(container?.proofs).filter((proof) =>
        proof && typeof proof === "object" && proofMatchesTileContext(proof, context)
      );
      if (normalizedProofs.length || container?.proofSummary || container?.proofStatus) {
        proofSources.push({
          source: container?.source || "unknown",
          poNumber: container?.poNumber || null,
          purchaseOrderType: container?.purchaseOrderType || null,
          proofRequired: container?.proofRequired,
          proofSummary: container?.proofSummary || null,
          proofStatus: container?.proofStatus || null,
          allProofsUploaded: container?.allProofsUploaded ?? null,
          proofsCount: normalizedProofs.length,
          proofs: normalizedProofs,
        });
      }
      normalizedProofs.forEach((proof) => {
        const key = String(
          proof?.id ||
            proof?.proofId ||
            proof?.filePath ||
            proof?.proofFilePath ||
            proof?.previewUrl ||
            `${proof?.createdAt || ""}-${proof?.updatedAt || ""}-${proof?.status || proof?.proofStatus || ""}`
        );
        if (seen.has(key)) return;
        seen.add(key);
        proofs.push(proof);
      });
    });

    return { proofs, proofSources };
  };
  const cardMatchesTileContext = (candidate = {}, context = {}) => {
    const candidateContext = getTileProofContext(candidate);
    const requiredKeys = ["orderId", "productId", "orderItemId", "department"];
    const requiredMatch = requiredKeys.every((key) => {
      if (!context[key] || !candidateContext[key]) return false;
      return context[key] === candidateContext[key];
    });
    if (!requiredMatch) return false;
    if (context.decorationId && candidateContext.decorationId && context.decorationId !== candidateContext.decorationId) {
      return false;
    }
    if (context.chargeId && candidateContext.chargeId && context.chargeId !== candidateContext.chargeId) {
      return false;
    }
    return true;
  };
  const isTruthyProofRequiredValue = (value) =>
    value === true ||
    value === 1 ||
    value === "1" ||
    String(value || "").trim().toLowerCase() === "true";
  const normalizeProofState = (value) => String(value || "").trim().toLowerCase();
  const isExplicitNoProofSource = (source = {}) => {
    const hasNoProofFlag =
      source?.proofRequired === false ||
      source?.proofRequired === 0 ||
      source?.proofRequired === "0" ||
      source?.proofRequired === "false";
    const proofStatus = normalizeProofState(
      source?.proofStatus || source?.proofSummary?.displayStatus
    );
    return hasNoProofFlag && proofStatus === "no proof" && (source?.proofsCount || 0) === 0;
  };
  const isProofRequiredSource = (source = {}) => {
    if (isExplicitNoProofSource(source)) return false;

    const proofStatus = normalizeProofState(
      source?.proofStatus || source?.proofSummary?.displayStatus
    );

    return Boolean(
      isTruthyProofRequiredValue(source?.proofRequired) ||
      isTruthyProofRequiredValue(source?.proofRequiredAction) ||
      isTruthyProofRequiredValue(source?.artworkProofRequired) ||
      isTruthyProofRequiredValue(source?.decorationProofRequired) ||
      (source?.proofsCount || 0) > 0 ||
      ["proof needed", "pending", "pending approval", "approved", "complete", "uploaded", "in review", "requested"].includes(proofStatus)
    );
  };
  const isSatisfiedProofSource = (source = {}) =>
    Array.isArray(source?.proofs) && source.proofs.some(isProofApproved);
  const buildProofingDebugTooltip = (debug = {}) =>
    [
      `Proofing: ${debug.computedLabel || "-"}`,
      `Reason: ${debug.reason || "-"}`,
      `Order: ${debug.orderNo || debug.orderId || "-"}`,
      `PO: ${debug.poNumber || debug.poId || "-"}`,
      `Type: ${debug.purchaseOrderType || "-"}`,
      `OrderItem: ${debug.orderItemId || "-"}`,
      `Product: ${debug.productId || "-"}`,
      `Decoration: ${debug.decorationId || "-"}`,
      `Charge: ${debug.chargeId || "-"}`,
      `Department: ${debug.departmentName || "-"}`,
      `proofRequired: ${debug.proofRequired ?? "-"}`,
      `proofStatus: ${debug.proofStatus ?? "-"}`,
      `allProofsUploaded: ${debug.allProofsUploaded ?? "-"}`,
      `local proofs: ${debug.localProofsCount ?? 0}`,
      `related proofs: ${debug.relatedProofsCount ?? 0}`,
      `approved related proofs: ${debug.approvedRelatedProofsCount ?? 0}`,
      `approved required proofs: ${debug.approvedRequiredProofsCount ?? 0}`,
      `related POs: ${debug.relatedPoNumbers?.join(", ") || "-"}`,
      `total related POs: ${debug.totalRelatedPos ?? 0}`,
      `required POs: ${debug.requiredRelatedPoNumbers?.join(", ") || "-"}`,
      `ignored no-proof POs: ${debug.ignoredRelatedPoNumbers?.join(", ") || "-"}`,
      `satisfied required POs: ${debug.satisfiedRelatedPoNumbers?.join(", ") || "-"}`,
      `unsatisfied required POs: ${debug.unsatisfiedRelatedPoNumbers?.join(", ") || "-"}`,
    ].join("\n");
  function getProductionProofingState(item) {
    const summary = item?.productionProofSummary;
    if (summary && typeof summary === "object") {
      const valid = ["Approved", "Pending", "Rejected", "No Proof", "Proof Complete"].includes(summary.status);
      const status = valid ? summary.status : "Pending";
      const label = status === "No Proof" ? null : `Proofing: ${status === "Proof Complete" ? "Complete" : status}`;
      return {
        required: status !== "No Proof",
        complete: status === "Proof Complete",
        approved: status === "Approved",
        rejected: status === "Rejected",
        pending: status === "Pending",
        label,
        proofs: [],
        debug: {
          computedLabel: label,
          reason: "Applicable Purchasing proofs for this production item and decoration",
          relatedProofsCount: summary.proofCount ?? 0,
          approvedRelatedProofsCount: summary.approvedCount ?? 0,
        },
      };
    }
    const context = getTileProofContext(item);
    const directRelatedPurchaseOrders = ensureProofArray(item?.relatedPurchaseOrders);
    const directPurchaseOrders = ensureProofArray(item?.purchaseOrders);
    const allCards = Object.values(columns || {}).flatMap((lane) =>
      Array.isArray(lane) ? lane : []
    );
    const relatedCards = allCards.filter((candidate) =>
      candidate && candidate.id !== item?.id && cardMatchesTileContext(candidate, context)
    );
    const localProofs = [
      ...ensureProofArray(item?.proofs),
      ...ensureProofArray(item?.proofing),
      ...ensureProofArray(item?.proofings),
      ...ensureProofArray(item?.proofingItems),
    ].filter((proof) => proof && typeof proof === "object");
    const current = collectProofCandidates(item, context);
    const siblingCollections = relatedCards.map((card) => collectProofCandidates(card, context));
    const relatedProofs = [
      ...current.proofs,
      ...siblingCollections.flatMap((entry) => entry.proofs),
    ];
    const proofSources = [
      ...current.proofSources,
      ...siblingCollections.flatMap((entry, idx) =>
        entry.proofSources.map((source) => ({
          ...source,
          source: `relatedCard:${relatedCards[idx]?.id}:${source.source}`,
        }))
      ),
    ];
    const approvedRelatedProofs = relatedProofs.filter(isProofApproved);
    const requiredSources = proofSources.filter(isProofRequiredSource);
    const ignoredSources = proofSources.filter(
      (source) => !isProofRequiredSource(source)
    );
    const satisfiedRequiredSources = requiredSources.filter(isSatisfiedProofSource);
    const unsatisfiedRequiredSources = requiredSources.filter(
      (source) => !isSatisfiedProofSource(source)
    );
    const approvedRequiredProofs = satisfiedRequiredSources.flatMap((source) =>
      ensureProofArray(source?.proofs).filter(isProofApproved)
    );
    const topLevelProofState = normalizeProofState(item?.proofStatus);
    const topLevelProofRequired = isProofRequiredSource({
      proofRequired: item?.proofRequired,
      proofStatus: item?.proofStatus,
      proofsCount: localProofs.length,
    });
    const hasRejectedEvidence =
      topLevelProofState === "rejected" ||
      relatedProofs.some(isProofRejected) ||
      proofSources.some(
        (source) =>
          normalizeProofState(source?.proofStatus || source?.proofSummary?.displayStatus) === "rejected"
      );
    const hasApprovedEvidence =
      approvedRelatedProofs.length > 0 ||
      approvedRequiredProofs.length > 0 ||
      topLevelProofState === "approved" ||
      topLevelProofState === "complete";
    const hasPendingLabelEvidence = [
      "proof needed",
      "pending",
      "pending approval",
      "requested",
      "in review",
      "uploaded",
    ].includes(topLevelProofState);
    const hasProofRequirementEvidence =
      requiredSources.length > 0 ||
      topLevelProofRequired ||
      directRelatedPurchaseOrders.some(isProofRequiredSource) ||
      directPurchaseOrders.some(isProofRequiredSource) ||
      hasPendingLabelEvidence;
    const required = hasProofRequirementEvidence;
    const approved =
      !hasRejectedEvidence &&
      hasApprovedEvidence &&
      (requiredSources.length === 0 || unsatisfiedRequiredSources.length === 0);
    const pending =
      !hasRejectedEvidence &&
      !approved &&
      hasProofRequirementEvidence &&
      (
        unsatisfiedRequiredSources.length > 0 ||
        (requiredSources.length > 0 && approvedRequiredProofs.length === 0) ||
        hasPendingLabelEvidence
      );
    const label = hasRejectedEvidence
      ? "Proofing: Rejected"
      : approved
      ? "Proofing: Approved"
      : pending
      ? "Proofing: Pending"
      : null;
    const relatedPoNumbers = Array.from(
      new Set(
        [
          ...ensureProofArray(item?.raw?.order?.poNumbers),
          ...ensureProofArray(item?.purchaseOrders).map((po) => po?.poNumber).filter(Boolean),
          ...ensureProofArray(item?.relatedPurchaseOrders).map((po) => po?.poNumber).filter(Boolean),
          ...relatedCards.map((card) => card?.poNumber).filter(Boolean),
        ].filter(Boolean)
      )
    );
    const candidateMatchKeys = {
      orderId: context.orderId || null,
      productId: context.productId || null,
      orderItemId: context.orderItemId || null,
      decorationId: context.decorationId || null,
      chargeId: context.chargeId || null,
      department: context.department || null,
      location: context.location || null,
    };
    let reason = "No proof-required evidence was loaded for this tile";
    if (hasRejectedEvidence) {
      reason = "Rejected because related proof evidence includes a rejection";
    } else if (approved) {
      reason = "Approved because all proof-required related sources are satisfied";
    } else if (pending) {
      if (
        !proofSources.length &&
        !relatedCards.length &&
        !directRelatedPurchaseOrders.length &&
        !directPurchaseOrders.length
      ) {
        reason = "Pending because proofing is required but related POs were not loaded";
      } else if (!requiredSources.length) {
        reason = "Pending because no proof-required related source was found";
      } else if (!relatedCards.length) {
        reason = "Pending because related POs were not available in this tile dataset";
      } else if (!relatedProofs.length) {
        reason = "Pending because no related proof records were found";
      } else if (!satisfiedRequiredSources.length) {
        reason =
          "Pending because proof-required related sources were found but none were approved";
      } else {
        reason =
          "Pending because at least one proof-required source is unsatisfied";
      }
    }
    const debug = {
      computedLabel: label || "No proof badge",
      reason,
      orderId: item?.orderId ?? null,
      orderNo: item?.orderNo || item?.orderNumber || null,
      poId: item?.poId || item?.purchaseOrderId || null,
      poNumber: item?.poNumber || null,
      purchaseOrderType: item?.purchaseOrderType || null,
      orderItemId: item?.orderItemId || item?.OrderItemId || item?.itemId || item?.raw?.item?.itemId || null,
      productId: item?.productId || item?.ProductId || item?.sku || item?.raw?.item?.productId || null,
      decorationId: item?.decorationId || item?.DecorationId || item?.raw?.deco?.decorationId || null,
      chargeId: item?.chargeId || item?.ChargeId || item?.raw?.deco?.chargeId || null,
      departmentName: item?.department || item?.process || item?.claimedDept || null,
      proofRequired: required,
      proofStatus: item?.proofStatus ?? null,
      allProofsUploaded: item?.allProofsUploaded ?? null,
      localProofsCount: localProofs.length,
      relatedProofsCount: relatedProofs.length,
      approvedRelatedProofsCount: approvedRelatedProofs.length,
      approvedRequiredProofsCount: approvedRequiredProofs.length,
      relatedPoNumbers,
      totalRelatedPos: proofSources.length,
      requiredRelatedPoNumbers: requiredSources
        .map((source) => source?.poNumber || source?.source)
        .filter(Boolean),
      ignoredRelatedPoNumbers: ignoredSources
        .map((source) => source?.poNumber || source?.source)
        .filter(Boolean),
      satisfiedRelatedPoNumbers: satisfiedRequiredSources
        .map((source) => source?.poNumber || source?.source)
        .filter(Boolean),
      unsatisfiedRelatedPoNumbers: unsatisfiedRequiredSources
        .map((source) => source?.poNumber || source?.source)
        .filter(Boolean),
      candidateMatchKeys,
      availableSiblingRowsCount: relatedCards.length,
      siblingRowsSample: relatedCards.slice(0, 5).map((card) => ({
        id: card?.id,
        poNumber: card?.poNumber,
        purchaseOrderType: card?.purchaseOrderType,
        orderNumber: card?.orderNumber || card?.orderNo,
        productId: card?.productId || card?.ProductId || card?.sku || card?.raw?.item?.productId,
        orderItemId: card?.orderItemId || card?.OrderItemId || card?.itemId || card?.raw?.item?.itemId,
        decorationId: card?.decorationId || card?.DecorationId || card?.raw?.deco?.decorationId,
        chargeId: card?.chargeId || card?.ChargeId || card?.raw?.deco?.chargeId,
      })),
    };

    console.log("[production:proofing:tile]", {
      tileId: item?.id,
      orderId: item?.orderId,
      orderNo: item?.orderNo || item?.orderNumber,
      tilePoNumber: item?.poNumber,
      tilePurchaseOrderType: item?.purchaseOrderType,
      productId: item?.productId || item?.ProductId || item?.sku || item?.raw?.item?.productId,
      tileOrderItemId: item?.orderItemId || item?.OrderItemId || item?.itemId || item?.raw?.item?.itemId,
      tileDecorationId: item?.decorationId || item?.DecorationId || item?.raw?.deco?.decorationId,
      tileChargeId: item?.chargeId || item?.ChargeId || item?.raw?.deco?.chargeId,
      localProofs: item?.proofs,
      relatedPoNumbers,
      proofRequired: required,
      proofStatus: item?.proofStatus ?? null,
      allProofsUploaded: item?.allProofsUploaded ?? null,
      localProofsCount: localProofs.length,
      relatedProofsCount: relatedProofs.length,
      approvedRelatedProofsCount: approvedRelatedProofs.length,
      approvedRequiredProofsCount: approvedRequiredProofs.length,
      totalRelatedPos: proofSources.length,
      requiredRelatedPoNumbers: debug.requiredRelatedPoNumbers,
      ignoredRelatedPoNumbers: debug.ignoredRelatedPoNumbers,
      satisfiedRelatedPoNumbers: debug.satisfiedRelatedPoNumbers,
      unsatisfiedRelatedPoNumbers: debug.unsatisfiedRelatedPoNumbers,
      relatedProofStatuses: relatedProofs.map((p) => ({
        status: p?.status,
        proofStatus: p?.proofStatus,
        userApproval: p?.approvals?.user?.state,
        clientApproval: p?.approvals?.client?.state,
        filePath: p?.proofFilePath || p?.filePath,
      })),
      computedLabel: label,
      proofSources,
    });
    if (pending) {
      console.log("[production:proofing:pending-reason]", {
        computedLabel: label,
        reason,
        tile: {
          id: item?.id,
          orderId: debug.orderId,
          orderNo: debug.orderNo,
          poId: debug.poId,
          poNumber: debug.poNumber,
          purchaseOrderType: debug.purchaseOrderType,
          orderItemId: debug.orderItemId,
          productId: debug.productId,
          decorationId: debug.decorationId,
          chargeId: debug.chargeId,
          departmentName: debug.departmentName,
        },
        rawProofFields: {
          proofRequired: item?.proofRequired,
          proofStatus: item?.proofStatus,
          allProofsUploaded: item?.allProofsUploaded,
          proofs: item?.proofs,
        },
        localProofs,
        relatedPoNumbers,
        relatedProofs,
        approvedRelatedProofs,
        approvedRequiredProofs,
        totalRelatedPos: proofSources.length,
        proofRequiredRelatedSources: requiredSources.map((source) => ({
          source: source?.source,
          poNumber: source?.poNumber,
          purchaseOrderType: source?.purchaseOrderType,
          proofRequired: source?.proofRequired,
          proofStatus: source?.proofStatus,
          proofsCount: source?.proofsCount,
        })),
        ignoredNonProofRequiredSources: ignoredSources.map((source) => ({
          source: source?.source,
          poNumber: source?.poNumber,
          purchaseOrderType: source?.purchaseOrderType,
          proofRequired: source?.proofRequired,
          proofStatus: source?.proofStatus,
          proofsCount: source?.proofsCount,
        })),
        candidateMatchKeys,
        availableSiblingRowsCount: relatedCards.length,
        siblingRowsSample: debug.siblingRowsSample,
      });
    }

    return {
      required,
      complete: approved,
      approved,
      rejected: hasRejectedEvidence,
      pending,
      label,
      proofs: relatedProofs,
      debug,
    };
  }
  function getProofingBadge(card) {
    const state = getProductionProofingState(card);
    if (!state.label) return null;
    return {
      label: state.label,
      approved: state.approved,
      rejected: state.rejected,
      tooltip: buildProofingDebugTooltip(state.debug),
    };
  }
  function formatDepartmentBadge(card, colKey) {
    const normalizedBatch = normalizeProductionBatchFields(card);
    const baseText = card.cancelled
      ? "Cancelled"
      : card.isPaused
      ? "⏸ Paused"
      : card.claimedDept
      ? `${card.claimedDept} • Planned`
      : card.department || colKey.replace(/_/g, " ");

    return normalizedBatch.batchLabel
      ? `${baseText} - Batch ${normalizedBatch.batchLabel}`
      : baseText;
  }

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [refreshingLists, setRefreshingLists] = useState(new Set());
  const [isSyncing, setIsSyncing] = useState(false);
  const visibleProductionLanes = useMemo(
    () => SHOW_QC_LANE
      ? ["Planned", "Scheduled", "In_Production", "QC", "On_Hold", "Completed"]
      : ["Planned", "Scheduled", "In_Production", "On_Hold", "Completed"],
    []
  );
  const [laneRequestStates, setLaneRequestStates] = useState(() =>
    createLaneRequestStateMap(visibleProductionLanes)
  );
  const laneRequestTokensRef = useRef({});
  const currentBoardRequestRef = useRef(null);

  const appendBoardFilters = useCallback((url, { includeMachine = false } = {}) => {
    return appendProductionBoardFilters(url, {
      instanceUniqueId,
      appliedSearchQuery,
      selectedDepartment,
      department,
      appliedAdvancedFilters,
      includeMachine,
    });
  }, [appliedAdvancedFilters, appliedSearchQuery, department, selectedDepartment, instanceUniqueId]);

  const boardLoadKey = useMemo(
    () => visibleProductionLanes.map((laneKey) => {
      const endpoint = laneKey === "Planned"
        ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/cards/?page=1&pageSize=${PAGE_SIZE}&ttl=1`
        : `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps?lanes=${laneKey}&group=0&page=1&pageSize=${PAGE_SIZE}`;
      return appendBoardFilters(endpoint, { includeMachine: laneKey !== "Planned" });
    }).join("||"),
    [appendBoardFilters, visibleProductionLanes, PAGE_SIZE]
  );
  boardScopeRef.current = boardLoadKey;
  useEffect(() => {
    boardScopeRef.current = boardLoadKey;
    return () => { boardScopeRef.current = null; };
  }, [boardLoadKey]);

  // NEW: Add this function to your component
  // async function refreshSpecificLists(statusesToRefresh) {
  const refreshSpecificLists = useCallback(
    (statusesToRefresh, options = {}) => {
      if (options.source === "poll") {
        if (mutationRefreshQueueRef.current.busy()) return Promise.resolve(null);
        return performSpecificListsRefreshRef.current(statusesToRefresh, options);
      }
      const scope = boardScopeRef.current;
      return mutationRefreshQueueRef.current.run(async () => {
        // Poll cleanup may release its UI ownership while an adopted transport
        // still finishes. Never use that pre-mutation payload as a fresh read.
        await laneRequestRegistryRef.current.whenIdle();
        if (boardScopeRef.current !== scope) return false;
        const result = await performSpecificListsRefreshRef.current(statusesToRefresh, options);
        if (statusesToRefresh.includes("Completed") && boardScopeRef.current === scope) {
          // Archive has its own scoped pagination. Do not deduplicate a
          // post-mutation refresh against a pre-mutation Archive response.
          await archiveRequestRegistryRef.current.whenIdle();
          if (boardScopeRef.current === scope) await loadArchiveRef.current();
        }
        return result;
      }, () => scope !== null && boardScopeRef.current === scope);
    }, []
  );
  const performSpecificListsRefresh = useCallback(
    async (statusesToRefresh, { source = "explicit" } = {}) => {
      if (fullFetchInFlightRef.current || partialRefreshInFlightRef.current) {
        return null;
      }
      const request = source === "poll"
        ? (() => {
            const generation = ++pollGenerationRef.current;
            traceRequests("poll-cycle-start", { generation, reason: "routine-active-lanes" });
            return {
              id: `poll-${generation}`,
              signal: undefined,
              isCurrent: () => pollGenerationRef.current === generation,
              finish: () => {},
            };
          })()
        : (() => {
            const boardRequest = currentBoardRequestRef.current;
            const scope = boardScopeRef.current;
            return {
              id: boardRequest?.id, signal: undefined,
              isCurrent: () => scope === boardScopeRef.current && (!boardRequest || boardRequest.isCurrent()),
              finish: () => {},
            };
          })();
      const normalizedStatuses = [...new Set(statusesToRefresh)].filter((status) =>
        visibleProductionLanes.includes(status) && (SHOW_QC_LANE || status !== "QC")
      );
      if (!normalizedStatuses.length) {
        request.finish();
        return true;
      }
      let hadFailure = false;
      const finishRead = mutationRefreshQueueRef.current.begin();
      request.releaseRead = finishRead;
      request.source = source;
      request.lanes = normalizedStatuses;
      request.laneTokens = {};
      // A queued refresh can resume before React publishes the last page's
      // state. The cursor is updated synchronously by that successful read.
      const refreshPage = (lane) => {
        const cursor = paginationCursorRef.current[lane];
        return cursor?.requestId === currentBoardRequestRef.current?.id
          ? cursor.page : (lanePagination[lane]?.page || 1);
      };
      normalizedStatuses.forEach((laneKey) => {
        request.laneTokens[laneKey] = (laneRequestTokensRef.current[laneKey] || 0) + 1;
        laneRequestTokensRef.current[laneKey] = request.laneTokens[laneKey];
      });
      partialRefreshOwnerRef.current = request;
      partialRefreshInFlightRef.current = true;
      setLaneRequestStates((prev) => {
        request.previousLaneStates = prev;
        const next = { ...prev };
        normalizedStatuses.forEach((status) => {
          next[status] = { status: "loading", error: null };
        });
        return next;
      });
      try {
        // NEW: Track which lists are refreshing
        setRefreshingLists((prev) => {
          const updated = new Set(prev);
          normalizedStatuses.forEach((status) => updated.add(status));
          return updated;
        });
        // If refreshing Planned status, fetch planning cards
        if (normalizedStatuses.includes("Planned")) {
          // let planningUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/cards/?ttl=1`;

          // Calculate how many cards we've already loaded
          const plannedPageSize = refreshPage("Planned") * PAGE_SIZE;
          let planningUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/cards?page=1&pageSize=${plannedPageSize}&ttl=1`;
          planningUrl = appendBoardFilters(planningUrl);

          try {
            const jsonPlanning = await fetchProductionLaneRefresh({
              url: planningUrl, laneKey: "Planned", page: refreshPage("Planned"), pageSize: PAGE_SIZE,
              request: (url) => requestLaneJson(url, { reason: source, generation: request.id }),
              isCurrent: () => request.isCurrent() && laneRequestTokensRef.current.Planned === request.laneTokens.Planned,
            });

            if (request.isCurrent() && laneRequestTokensRef.current.Planned === request.laneTokens.Planned && jsonPlanning.success) {
              paginationCursorRef.current.Planned = { requestId: currentBoardRequestRef.current?.id, page: jsonPlanning.refreshPage };
              setColumns((prev) => {
                const updated = { ...prev };
                updated.Planned = []; // Clear Planned list

                // Repopulate Planned cards
                jsonPlanning.data.forEach((card, i) => {
                  updated.Planned.push({
                    id: card.external_key,
                    orderNumber: card.projectNumber,
                    projectName: card.projectName,
                    binNumber: card.binNumber || "",
                    orderRepName: card.orderRepName || "",
                    orderRepSource: card.orderRepSource || null,
                    sku: card.skuNumber,
                    qtyPlanned: card.qtyRequired,
                    qtyRequired: card.qtyRequired,
                    external_key: card.external_key,
                    department: card.department,
                    matrix: card.matrixLabel,
                    location: card.processName,
                    salesOrderStatus: card.salesOrderStatus,
                    purchaseOrderStatus: card.purchaseOrderStatus,
                    poNumber: card.poNumber,
                    purchaseOrderType: card.purchaseOrderType,
                    purchaseOrders: Array.isArray(card.purchaseOrders) ? card.purchaseOrders : [],
                    relatedPurchaseOrders: Array.isArray(card.relatedPurchaseOrders) ? card.relatedPurchaseOrders : [],
                    proofRequired: card.proofRequired,
                    proofStatus: card.proofStatus,
                    productionProofSummary: card.productionProofSummary,
                    allProofsUploaded: card.allProofsUploaded,
                    ...productionCardBusinessDateFields(card),
                    plannedStart: card.plannedStart,
                    plannedFinish: card.plannedFinish,
                    machine: card.machine,
                    customer: card.customer || "Unknown Customer",
                    status: "Planned",
                    createdAt: card.createdAt,
                    updatedAt: card.updatedAt,
                    disabled: card.disabled || false,
                    cancelled: card.cancelled || card.disabled || false,
                    activeForProduction: card.activeForProduction || false,
                    orderDbId: card.orderDbId, //
                    itemId: card.itemId, //
                    orderItemId: card.itemId,
                    productId: card.skuNumber,
                    pwo_id: card.pwo_id, //
                    decorationId: card.decorationId,
                    chargeId: card.chargeId,
                    holdReason: card.holdReason, //
                    holdFrom: card.holdFrom,
                    rank: i,
                    isStep: true,
                    lifecycle: [],
                  });
                });

                return updated;
              });
              setLanePagination((prev) => ({
                ...prev,
                Planned: getProductionLanePageState({
                  page: jsonPlanning.refreshPage, total: getLaneTotal(jsonPlanning, "Planned"),
                  pageSize: PAGE_SIZE, hasMore: jsonPlanning.hasMore,
                }),
              }));
              setLaneRequestStates((prev) => ({
                ...prev,
                Planned: { status: "loaded", error: null },
              }));
            }
          } catch (error) {
            hadFailure = true;
            if (request.isCurrent() && laneRequestTokensRef.current.Planned === request.laneTokens.Planned && error?.name !== "AbortError") {
              setLanePagination((prev) => ({
                ...prev,
                Planned: { ...prev.Planned, loading: false },
              }));
              setLaneRequestStates((prev) => ({
                ...prev,
                Planned: { status: "error", error: error?.message || "Unable to refresh lane" },
              }));
              console.error("Failed to refresh Production Planned:", error);
            }
          }
        }

        // For other statuses, fetch from steps API
        let otherStatuses = normalizedStatuses.filter((s) => s !== "Planned");

        //  Filter out QC if QC lane is hidden
        if (!SHOW_QC_LANE) {
          otherStatuses = otherStatuses.filter((s) => s !== "QC");
        }

        if (otherStatuses.length > 0) {
          const laneResponses = (await Promise.all(
            otherStatuses.map(async (status) => {
              const lanePageSize = Math.max(
                PAGE_SIZE,
                refreshPage(status) * PAGE_SIZE
              );
              let laneUrl = `${
                process.env.REACT_APP_NODEJS_SERVER_URL
              }/production/steps?lanes=${status}&group=0&page=1&pageSize=${lanePageSize}`;
              laneUrl = appendBoardFilters(laneUrl, { includeMachine: true });
              try {
                const json = await fetchProductionLaneRefresh({
                  url: laneUrl, laneKey: status, page: refreshPage(status), pageSize: PAGE_SIZE,
                  request: (url) => requestLaneJson(url, { reason: source, generation: request.id }),
                  isCurrent: () => request.isCurrent() && laneRequestTokensRef.current[status] === request.laneTokens[status],
                });
                return { status, json };
              } catch (error) {
                hadFailure = true;
                if (request.isCurrent() && laneRequestTokensRef.current[status] === request.laneTokens[status] && error?.name !== "AbortError") {
                  setLanePagination((prev) => ({
                    ...prev,
                    [status]: { ...prev[status], loading: false },
                  }));
                  setLaneRequestStates((prev) => ({
                    ...prev,
                    [status]: { status: "error", error: error?.message || "Unable to refresh lane" },
                  }));
                  console.error(`Failed to refresh Production ${status}:`, error);
                }
                return null;
              }
            })
          )).filter((response) => response && laneRequestTokensRef.current[response.status] === request.laneTokens[response.status]);

          if (request.isCurrent() && laneResponses.length > 0) {
            const successfulStatuses = laneResponses.map((response) => response.status);
            laneResponses.forEach(({ status, json }) => {
              paginationCursorRef.current[status] = { requestId: currentBoardRequestRef.current?.id, page: json.refreshPage };
            });
            const mergedLaneData = laneResponses.flatMap((r) => r.json?.data || []);
            const mergedTotalsByLane = laneResponses.reduce((acc, r) => {
              acc[r.status] =
                r.json?.totalByLane?.[r.status] ??
                r.json?.total ??
                (r.json?.data || []).length;
              return acc;
            }, {});

            setColumns((prev) => {
              const updated = { ...prev };

              //  SAVE cancelled cards before clearing lists
              const cancelledCards = {};
              successfulStatuses.forEach((status) => {
                cancelledCards[status] = updated[status].filter(
                  (card) => card.cancelled
                );
                updated[status] = []; // Clear the list
              });

              // Repopulate with fresh data
              mergedLaneData.forEach((card, i) => {
                const normalizedCard = {
                  id: getStepCardId(card),
                  step_id: card.step_id,
                  orderNumber: card.projectNumber,
                  // projectName: card.projectName,
                  projectName: card.projectName,
                  productName: card.productName,
                  binNumber: card.binNumber || "",
                  orderRepName: card.orderRepName || "",
                  orderRepSource: card.orderRepSource || null,
                  sku: card.skuNumber,
                  qtyPlanned: card.qtyRequired,
                  qtyRequired: card.qtyRequired,
                  external_key: card.external_key,
                  externalkey: card.external_key,
                  department: card.department,
                  matrix: card.matrixLabel,
                  location: card.processLocation,
                  salesOrderStatus: card.salesOrderStatus,
                  purchaseOrderStatus: card.purchaseOrderStatus,
                  poNumber: card.poNumber,
                  purchaseOrderType: card.purchaseOrderType,
                  purchaseOrders: Array.isArray(card.purchaseOrders) ? card.purchaseOrders : [],
                  relatedPurchaseOrders: Array.isArray(card.relatedPurchaseOrders) ? card.relatedPurchaseOrders : [],
                  proofRequired: card.proofRequired,
                  proofStatus: card.proofStatus,
                  productionProofSummary: card.productionProofSummary,
                  allProofsUploaded: card.allProofsUploaded,
                  ...productionCardBusinessDateFields(card),
                  plannedStart: card.plannedStart,
                  plannedFinish: card.plannedFinish,
                  machine: card.machine || card.machineName,
                  machineName: card.machineName,
                  machineCode: card.machineCode,
                  isFirm: !!(card.isFirm || card.is_firm),
                  customer: card.customerName || "Unknown Customer",
                  status: card.status,
                  createdAt: card.createdAt,
                  updatedAt: card.updatedAt,
                  disabled: card.disabled || false,
                  cancelled: card.cancelled || card.disabled || false,
                  activeForProduction: card.activeForProduction || false,
                  orderDbId: card.orderDbId, //
                  itemId: card.itemId, //
                  orderItemId: card.itemId,
                  productId: card.skuNumber,
                  pwo_id: card.pwo_id, //
                  decorationId: card.decorationId,
                  chargeId: card.chargeId,
                  holdReason: card.holdReason, //
                  holdFrom: card.holdFrom,

                  batchGroupId: card.batchGroupId,
                  batchIndex: card.batchIndex,
                  batchLabel: card.batchLabel,
                  batchTotal: card.batchTotal,
                  rank: card.rank || i,
                  isStep: true,
                  isFullyCompleted: card.isFullyCompleted || false,
                  pendingDepartments: card.pendingDepartments || [],

                  isPaused: card.isPaused ?? false,
              runtimeHHMM: card.runtimeHHMM ?? '00:00',
              runtimeMinutes: card.runtimeMinutes ?? 0,
              pauseMinutes: card.pauseMinutes ?? 0,
              startedAt: card.runtime?.startedAt ?? null,


                  lifecycle: [],

                  // ✅ PRESERVE cancelled state from server or set to false
                  // cancelled: card.cancelled || false,
                };

                // Place in correct status list
                // const statusKey = card.status;
                // if (updated[statusKey]) {
                //   updated[statusKey].push(normalizedCard);
                // }

                const statusKey = card.status;

                // Handle QC cards when QC lane is hidden
                if (statusKey === "QC" && !SHOW_QC_LANE) {
                  // Route QC cards to Completed
                  if (updated.Completed) {
                    updated.Completed.push(normalizedCard);
                  }
                } else if (updated[statusKey]) {
                  updated[statusKey].push(normalizedCard);
                }
              });

              // ✅ RESTORE cancelled cards that might not be in server response
              successfulStatuses.forEach((status) => {
                if (
                  cancelledCards[status] &&
                  cancelledCards[status].length > 0
                ) {
                  // Add cancelled cards back to their lanes
                  cancelledCards[status].forEach((cancelledCard) => {
                    // Only add if not already in the refreshed list
                    const exists = updated[status].find(
                      (c) => c.id === cancelledCard.id
                    );
                    if (!exists) {
                      updated[status].push(cancelledCard);
                    }
                  });
                }
              });

              // UPDATE PAGINATION TOTALS FROM API
              setLanePagination((prev) => ({
                ...prev,
                ...Object.fromEntries(laneResponses.map(({ status, json }) => [status,
                  getProductionLanePageState({
                    page: json.refreshPage, total: mergedTotalsByLane[status],
                    pageSize: PAGE_SIZE, hasMore: json.hasMore,
                  }),
                ])),
              }));

              // Remove the same persisted step from any previous active lane.
              return successfulStatuses.reduce((next, status) =>
                replaceProductionLane(next, status, updated[status]), updated);
            });
            setLaneRequestStates((prev) => {
              const next = { ...prev };
              successfulStatuses.forEach((status) => {
                next[status] = { status: "loaded", error: null };
              });
              return next;
            });
          }
        }
      } catch (err) {
        hadFailure = true;
        if (request.isCurrent() && err?.name !== "AbortError") {
          setLaneRequestStates((prev) => {
            const next = { ...prev };
            normalizedStatuses.forEach((status) => {
              if (next[status]?.status === "loading") {
                next[status] = { status: "error", error: err?.message || "Unable to refresh lane" };
              }
            });
            return next;
          });
          console.error("Failed to refresh specific lists:", err);
        }
      } finally {
        if (partialRefreshOwnerRef.current === request) {
          partialRefreshOwnerRef.current = null;
          partialRefreshInFlightRef.current = false;
        }
        if (request.isCurrent()) {
          // NEW: Clear refreshing state
          setRefreshingLists((prev) => {
            const updated = new Set(prev);
            normalizedStatuses.forEach((status) => updated.delete(status));
            return updated;
          });
        }
        request.finish();
        finishRead();
      }
      return !hadFailure;
      // }
    },
    [appendBoardFilters, department, lanePagination, SHOW_QC_LANE, PAGE_SIZE, requestLaneJson, visibleProductionLanes, setColumns]
  ); //  Add dependency
  performSpecificListsRefreshRef.current = performSpecificListsRefresh;

  const [pausePolling, setPausePolling] = useState(false);
  const stopRoutinePoll = useCallback(() => {
    const request = partialRefreshOwnerRef.current;
    if (request?.source !== "poll") return;
    pollGenerationRef.current += 1;
    request.releaseRead?.();
    partialRefreshOwnerRef.current = null;
    partialRefreshInFlightRef.current = false;
    if (!fullFetchInFlightRef.current) setIsSyncing(false);
    const ownedLanes = request.lanes.filter((lane) =>
      laneRequestTokensRef.current[lane] === request.laneTokens[lane]
    );
    // A retry may have adopted the same deduplicated read. Do not abort its
    // transport or clear its state just because the poll timer was stopped.
    laneRequestRegistryRef.current.cancelMatching(
      (metadata) => metadata.reason === "poll" && ownedLanes.includes(metadata.lane), "poll-paused-or-stopped"
    );
    setRefreshingLists((previous) => {
      const next = new Set(previous);
      request.lanes.forEach((lane) => next.delete(lane));
      return next;
    });
    setLaneRequestStates((previous) => {
      const next = { ...previous };
      ownedLanes.forEach((lane) => {
        next[lane] = request.previousLaneStates?.[lane] || { status: "loaded", error: null };
      });
      return next;
    });
  }, []);
  const refreshSpecificListsRef = useRef(refreshSpecificLists);
  const refreshPWOStatusesRef = useRef(null);
  const scheduledCardsRef = useRef([]);
  useEffect(() => {
    refreshSpecificListsRef.current = refreshSpecificLists;
  }, [refreshSpecificLists]);

  // NEW function - ONLY called when user clicks refresh button
  const handleHardRefresh = async () => {
    if (fullFetchInFlightRef.current || partialRefreshInFlightRef.current) return;
    boardLoadCoordinatorRef.current.cancel();
    const request = beginExplicitBoardRequest("manual-refresh");
    const finishRead = mutationRefreshQueueRef.current.begin();
    const laneTokens = {};
    visibleProductionLanes.forEach((lane) => {
      laneTokens[lane] = (laneRequestTokensRef.current[lane] || 0) + 1;
      laneRequestTokensRef.current[lane] = laneTokens[lane];
    });
    const isCurrentLane = (lane) => request.isCurrent() && laneRequestTokensRef.current[lane] === laneTokens[lane];
    fullFetchInFlightRef.current = true;
    setIsSyncing(true);
    setLaneRequestStates((prev) => {
      request.previousLaneStates = prev;
      const next = { ...prev };
      ["Planned", ...(SHOW_QC_LANE
        ? ["Scheduled", "In_Production", "QC", "On_Hold", "Completed"]
        : ["Scheduled", "In_Production", "On_Hold", "Completed"])].forEach((status) => {
        next[status] = { status: "loading", error: null };
      });
      return next;
    });
    try {
      // 1. PLANNING CARDS - WITH refresh=true
      // let planningUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/cards?refresh=true`;

      const plannedPageSize = lanePagination.Planned.page * PAGE_SIZE;
      let planningUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/cards?refresh=true&page=1&pageSize=${plannedPageSize}`;
      planningUrl = appendBoardFilters(planningUrl);

      let jsonPlanning = null;
      try {
        jsonPlanning = await fetchProductionLaneRefresh({
          url: planningUrl, laneKey: "Planned", page: lanePagination.Planned.page, pageSize: PAGE_SIZE,
          request: (url) => requestLaneJson(url, { reason: "manual-refresh", generation: request.id }),
          isCurrent: () => isCurrentLane("Planned"),
        });
      } catch (error) {
        if (isCurrentLane("Planned") && error?.name !== "AbortError") {
          setLaneRequestStates((prev) => ({
            ...prev,
            Planned: { status: "error", error: error?.message || "Unable to refresh lane" },
          }));
          console.error("Hard refresh failed for Production Planned:", error);
        }
      }

      // 2. OTHER LANES - WITH refresh=true
      // let stepsUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps?refresh=true&lanes=Scheduled,In_Production,QC,Completed,On_Hold&group=0`;

      const activeLanes = SHOW_QC_LANE
        ? ["Scheduled", "In_Production", "QC", "On_Hold", "Completed"]
        : ["Scheduled", "In_Production", "On_Hold", "Completed"];

      // let stepsUrl = `${
      //   process.env.REACT_APP_NODEJS_SERVER_URL
      // }/production/steps?refresh=true&lanes=${activeLanes.join(",")}&group=0`;

      const laneResponses = (await Promise.all(
        activeLanes.map(async (status) => {
          const lanePageSize = Math.max(
            PAGE_SIZE,
            (lanePagination[status]?.page || 1) * PAGE_SIZE
          );
          let laneUrl = `${
            process.env.REACT_APP_NODEJS_SERVER_URL
          }/production/steps?refresh=true&lanes=${status}&group=0&page=1&pageSize=${lanePageSize}`;
          laneUrl = appendBoardFilters(laneUrl, { includeMachine: true });
          try {
            const json = await fetchProductionLaneRefresh({
              url: laneUrl, laneKey: status, page: lanePagination[status]?.page, pageSize: PAGE_SIZE,
              request: (url) => requestLaneJson(url, { reason: "manual-refresh", generation: request.id }),
              isCurrent: () => isCurrentLane(status),
            });
            return { status, json };
          } catch (error) {
            if (isCurrentLane(status) && error?.name !== "AbortError") {
              setLaneRequestStates((prev) => ({
                ...prev,
                [status]: { status: "error", error: error?.message || "Unable to refresh lane" },
              }));
              console.error(`Hard refresh failed for Production ${status}:`, error);
            }
            return null;
          }
        })
      )).filter((response) => response && isCurrentLane(response.status));
      if (!isCurrentLane("Planned")) jsonPlanning = null;

      const jsonOtherLanes = {
        success: laneResponses.every((r) => r.json?.success),
        data: laneResponses.flatMap((r) => r.json?.data || []),
        totalByLane: laneResponses.reduce((acc, r) => {
          acc[r.status] =
            r.json?.totalByLane?.[r.status] ??
            r.json?.total ??
            (r.json?.data || []).length;
          return acc;
        }, {}),
        ttlSeconds: laneResponses[0]?.json?.ttlSeconds || 0,
      };

      if (!request.isCurrent()) return;
      if (jsonPlanning?.success || laneResponses.length > 0) {
        // Replace only successful lanes so a failed lane keeps its last good data.
        const grouped = { ...columns };
        const successfulStatuses = laneResponses.map((response) => response.status);
        if (jsonPlanning?.success) grouped.Planned = [];
        successfulStatuses.forEach((status) => {
          grouped[status] = [];
        });

        // Process PLANNING cards
        const filteredPlanningCards = (jsonPlanning?.data || []).filter((card) => {
          if (isAggregateProductionRoute(department))
            return true;
          return (
            card.department &&
            card.department.toLowerCase() === department.toLowerCase()
          );
        });

        filteredPlanningCards.forEach((card, i) => {
          const normalizedCard = {
            id: card.external_key,
            orderNumber: card.projectNumber,
            // projectName: card.projectName,
            projectName: card.projectName,
            productName: card.productName,
            binNumber: card.binNumber || "",
            orderRepName: card.orderRepName || "",
            orderRepSource: card.orderRepSource || null,
            sku: card.skuNumber,
            qtyPlanned: card.qtyRequired,
            qtyRequired: card.qtyRequired,
            //  externalkey: card.externalkey,
            external_key: card.external_key,
            department: card.department,
            matrix: card.matrixLabel,
            location: card.processLocation,
            salesOrderStatus: card.salesOrderStatus,
            purchaseOrderStatus: card.purchaseOrderStatus,
            poNumber: card.poNumber,
            purchaseOrderType: card.purchaseOrderType,
            purchaseOrders: Array.isArray(card.purchaseOrders) ? card.purchaseOrders : [],
            relatedPurchaseOrders: Array.isArray(card.relatedPurchaseOrders) ? card.relatedPurchaseOrders : [],
            proofRequired: card.proofRequired,
            proofStatus: card.proofStatus,
            productionProofSummary: card.productionProofSummary,
            allProofsUploaded: card.allProofsUploaded,
            ...productionCardBusinessDateFields(card),
            plannedStart: card.plannedStart,
            plannedFinish: card.plannedFinish,
            machine: card.machine,
            customer: card.customer || "Unknown Customer",
            status: "Planned",
            createdAt: card.createdAt,
            updatedAt: card.updatedAt,
            holdReason: card.holdReason, //
            holdFrom: card.holdFrom,
            orderDbId: card.orderDbId,
            itemId: card.itemId,
            orderItemId: card.itemId,
            productId: card.skuNumber,
            decorationId: card.decorationId,
            chargeId: card.chargeId,
            rank: i,
            isStep: true,
            lifecycle: [],
          };
          grouped.Planned.push(normalizedCard);
        });

        // Process OTHER LANES cards
        jsonOtherLanes.data.forEach((card, i) => {
          const normalizedCard = {
            id: getStepCardId(card),
            step_id: card.step_id,
            orderNumber: card.projectNumber,
            // projectName: card.projectName,
            projectName: card.projectName,
            productName: card.productName,
            binNumber: card.binNumber || "",
            orderRepName: card.orderRepName || "",
            orderRepSource: card.orderRepSource || null,
            sku: card.skuNumber,
            qtyPlanned: card.qtyRequired,
            qtyRequired: card.qtyRequired,
            // externalkey: card.externalkey,
            external_key: card.external_key,
            department: card.department,
            matrix: card.matrixLabel,
            location: card.processLocation,
            salesOrderStatus: card.salesOrderStatus,
            purchaseOrderStatus: card.purchaseOrderStatus,
            poNumber: card.poNumber,
            purchaseOrderType: card.purchaseOrderType,
            purchaseOrders: Array.isArray(card.purchaseOrders) ? card.purchaseOrders : [],
            relatedPurchaseOrders: Array.isArray(card.relatedPurchaseOrders) ? card.relatedPurchaseOrders : [],
            proofRequired: card.proofRequired,
            proofStatus: card.proofStatus,
            productionProofSummary: card.productionProofSummary,
            allProofsUploaded: card.allProofsUploaded,
            ...productionCardBusinessDateFields(card),
            plannedStart: card.plannedStart,
            plannedFinish: card.plannedFinish,
            machine: card.machine || card.machineName,
            machineName: card.machineName,
            machineCode: card.machineCode,
                  isFirm: !!(card.isFirm || card.is_firm),
            customer: card.customerName || "Unknown Customer",
            status: card.status,
            createdAt: card.createdAt,
            updatedAt: card.updatedAt,
            holdReason: card.holdReason, //
            holdFrom: card.holdFrom,
            disabled: card.disabled || false,
            cancelled: card.cancelled || card.disabled || false, // Treat disabled as cancelled
            activeForProduction: card.activeForProduction || false,
            orderDbId: card.orderDbId,
            itemId: card.itemId,
            orderItemId: card.itemId,
            productId: card.skuNumber,
            decorationId: card.decorationId,
            chargeId: card.chargeId,
            batchGroupId: card.batchGroupId,
            batchIndex: card.batchIndex,
            batchLabel: card.batchLabel,
            batchTotal: card.batchTotal,
            rank: card.rank || i,
            isStep: true,
            isFullyCompleted: card.isFullyCompleted || false,
            pendingDepartments: card.pendingDepartments || [],

            isPaused: card.isPaused ?? false,
            runtimeHHMM: card.runtimeHHMM ?? '00:00',
            runtimeMinutes: card.runtimeMinutes ?? 0,
            pauseMinutes: card.pauseMinutes ?? 0,
            startedAt: card.runtime?.startedAt ?? null,


            lifecycle: [],
          };

          //   // Place cards in correct lanes based on their actual status
          //   const statusKey = card.status;
          //   if (statusKey === "Scheduled") {
          //     grouped.Scheduled.push(normalizedCard);
          //   } else if (statusKey === "In_Production") {
          //     grouped.In_Production.push(normalizedCard);
          //    }
          //     // else if (statusKey === "QC") {
          //   //   grouped.QC.push(normalizedCard);

          //  else if (statusKey === "QC") {
          //   // ✅ Only place in QC if enabled
          //   if (SHOW_QC_LANE) {
          //     grouped.QC.push(normalizedCard);
          //   } else {
          //     // Route QC cards to Completed when QC is hidden
          //     grouped.Completed.push(normalizedCard);
          //   }
          //   } else if (statusKey === "Completed") {
          //     grouped.Completed.push(normalizedCard);
          //   } else if (statusKey === "On_Hold") {
          //     grouped.On_Hold.push(normalizedCard);
          //   }

          // ✅ CORRECT: Place cards in correct lanes with proper if-else chain
          const statusKey = card.status;
          if (statusKey === "Scheduled") {
            grouped.Scheduled.push(normalizedCard);
          } else if (statusKey === "In_Production") {
            grouped.In_Production.push(normalizedCard);
          } else if (statusKey === "QC") {
            // ✅ Only place in QC if enabled
            if (SHOW_QC_LANE) {
              grouped.QC.push(normalizedCard);
            } else {
              // Route QC cards to Completed when QC is hidden
              grouped.Completed.push(normalizedCard);
            }
          } else if (statusKey === "Completed") {
            grouped.Completed.push(normalizedCard);
          } else if (statusKey === "On_Hold") {
            grouped.On_Hold.push(normalizedCard);
          } else {
            // If status doesn't match, put in Scheduled
            grouped.Scheduled.push(normalizedCard);
          }
        });

        setColumns((previous) => [...successfulStatuses, ...(jsonPlanning?.success ? ["Planned"] : [])]
          .reduce((next, lane) => replaceProductionLane(next, lane, grouped[lane]), previous));

        // UPDATE ONLY SUCCESSFUL LANE TOTALS
        [...laneResponses, ...(jsonPlanning?.success ? [{ status: "Planned", json: jsonPlanning }] : [])]
          .forEach(({ status, json }) => {
            paginationCursorRef.current[status] = { requestId: request.id, page: json.refreshPage };
          });
        setLanePagination((prev) => ({
          ...prev,
          ...(jsonPlanning?.success && {
            Planned: getProductionLanePageState({
              page: jsonPlanning.refreshPage, total: getLaneTotal(jsonPlanning, "Planned"),
              pageSize: PAGE_SIZE, hasMore: jsonPlanning.hasMore,
            }),
          }),
          ...Object.fromEntries(laneResponses.map(({ status, json }) => [status,
            getProductionLanePageState({
              page: json.refreshPage, total: getLaneTotal(json, status),
              pageSize: PAGE_SIZE, hasMore: json.hasMore,
            }),
          ])),
        }));
        setLaneRequestStates((prev) => {
          const next = { ...prev };
          if (jsonPlanning?.success) next.Planned = { status: "loaded", error: null };
          successfulStatuses.forEach((status) => {
            next[status] = { status: "loaded", error: null };
          });
          return next;
        });

        if (jsonPlanning?.success && successfulStatuses.length === activeLanes.length) {
          SA.success("Data refreshed successfully!", "Cache cleared & updated");
        } else {
          SA.error("Some Production lanes could not be refreshed", "Partial refresh");
        }
      }
    } catch (error) {
      if (request.isCurrent() && error?.name !== "AbortError") {
        console.error("Hard refresh failed:", error);
        SA.error("Failed to refresh data", "Error");
      }
    } finally {
      if (request.isCurrent()) {
        fullFetchInFlightRef.current = false;
        setIsSyncing(false);
        setLaneRequestStates((previous) => {
          const next = { ...previous };
          visibleProductionLanes.forEach((lane) => {
            if (!isCurrentLane(lane) && next[lane]?.status === "loading") {
              next[lane] = request.previousLaneStates?.[lane] || { status: "loaded", error: null };
            }
          });
          return next;
        });
      }
      request.finish();
      finishRead();
    }
  };

  // useEffect(() => {
  //   async function fetchCards() {

  // ========== MOVE fetchCards OUTSIDE useEffect ==========
  const legacyFetchCards = useCallback(
    async (resetPagination = false) => {
      const request = boardRequestGateRef.current.begin("legacy-full-fetch");
      fullFetchInFlightRef.current = true;
      setRefreshingLists(new Set());
      setIsSyncing(false);
      try {
        setIsInitialLoading(true); // initial loading

        // ==========  ==========
        if (resetPagination) {
          setLanePagination({
            Planned: { page: 1, hasMore: true, loading: false, total: 0 },
            Scheduled: { page: 1, hasMore: true, loading: false, total: 0 },
            In_Production: { page: 1, hasMore: true, loading: false, total: 0 },
            QC: { page: 1, hasMore: true, loading: false, total: 0 },
            Completed: { page: 1, hasMore: true, loading: false, total: 0 },
            On_Hold: { page: 1, hasMore: true, loading: false, total: 0 },
          });
        }

        //  Helper to determine if we should show all departments
        const isPlanningPage =
          isAggregateProductionRoute(department);

        console.log(
          `🔍 Fetching for: ${isPlanningPage ? "ALL DEPARTMENTS" : department}`
        );

        //  1. PLANNING CARDS - Conditionally add department parameter
        let planningUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/cards/?page=1&pageSize=${PAGE_SIZE}&ttl=1`;
        planningUrl = appendBoardFilters(planningUrl);
        const resPlanning = await fetch(planningUrl, { signal: request.signal });
        const jsonPlanning = await resPlanning.json();

        //  2. OTHER LANES - Conditionally add department parameter
        // let stepsUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps?lanes=Scheduled,In_Production,QC,Completed,On_Hold&group=0`;

        // 2. OTHER LANES - Build dynamic lanes based on SHOW_QC_LANE
        const activeLanes = SHOW_QC_LANE
          ? ["Scheduled", "In_Production", "QC", "On_Hold", "Completed"]
          : ["Scheduled", "In_Production", "On_Hold", "Completed"];

        const laneResponses = await Promise.all(
          activeLanes.map(async (status) => {
            let laneUrl = `${
              process.env.REACT_APP_NODEJS_SERVER_URL
            }/production/steps?lanes=${status}&group=0&page=1&pageSize=${PAGE_SIZE}`;
            laneUrl = appendBoardFilters(laneUrl, { includeMachine: true });

            const res = await fetch(laneUrl, { signal: request.signal });
            const json = await res.json();
            return { status, json };
          })
        );

        const jsonOtherLanes = {
          success: laneResponses.every((r) => r.json?.success),
          data: laneResponses.flatMap((r) => r.json?.data || []),
          totalByLane: laneResponses.reduce((acc, r) => {
            acc[r.status] =
              r.json?.totalByLane?.[r.status] ??
              r.json?.total ??
              (r.json?.data || []).length;
            return acc;
          }, {}),
          ttlSeconds: laneResponses[0]?.json?.ttlSeconds || 0,
        };

        if (!request.isCurrent()) return;
        if (jsonPlanning.success && jsonOtherLanes.success) {
          // start with empty columns
          const grouped = emptyColumns();

          //  REMOVE THE CLIENT-SIDE FILTERING - Backend now handles it
          const filteredPlanningCards = jsonPlanning.data; // Using all data from API

          console.log(
            `✅ Loaded ${filteredPlanningCards.length} planning cards + ${
              jsonOtherLanes.data.length
            } other cards for ${
              isPlanningPage ? "ALL departments" : department
            }`
          );

          // Process PLANNING cards
          filteredPlanningCards.forEach((card, i) => {
            const normalizedCard = {
              id: card.external_key,
              orderNumber: card.projectNumber,
              projectName: card.projectName,
              productName: card.productName,
              binNumber: card.binNumber || "",
              orderRepName: card.orderRepName || "",
              orderRepSource: card.orderRepSource || null,
              sku: card.skuNumber,
              qtyPlanned: card.qtyRequired,
              qtyRequired: card.qtyRequired,
              external_key: card.external_key,
              department: card.department,
              matrix: card.matrixLabel,
              location: card.processLocation,
              salesOrderStatus: card.salesOrderStatus,
              purchaseOrderStatus: card.purchaseOrderStatus,
              poNumber: card.poNumber,
              purchaseOrderType: card.purchaseOrderType,
              purchaseOrders: Array.isArray(card.purchaseOrders) ? card.purchaseOrders : [],
              relatedPurchaseOrders: Array.isArray(card.relatedPurchaseOrders) ? card.relatedPurchaseOrders : [],
              proofRequired: card.proofRequired,
              proofStatus: card.proofStatus,
              productionProofSummary: card.productionProofSummary,
              allProofsUploaded: card.allProofsUploaded,
              ...productionCardBusinessDateFields(card),
              proofs: Array.isArray(card.proofs) ? card.proofs : [],
              plannedStart: card.plannedStart,
              plannedFinish: card.plannedFinish,
              machine: card.machine,
              customer: card.customer || "Unknown Customer",
              status: "Planned", // Force to Planned
              createdAt: card.createdAt,
              updatedAt: card.updatedAt,
              disabled: card.disabled || false,
              cancelled: card.cancelled || card.disabled || false, // Treat disabled as cancelled
              activeForProduction: card.activeForProduction || false,
              orderDbId: card.orderDbId, //
              itemId: card.itemId, //
              orderItemId: card.itemId,
              productId: card.skuNumber,
              pwo_id: card.pwo_id, //
              decorationId: card.decorationId,
              chargeId: card.chargeId,
              holdReason: card.holdReason, //
              holdFrom: card.holdFrom,
              rank: i,
              isStep: true,
              lifecycle: [],
            };

            grouped.Planned.push(normalizedCard);
          });

          // ========== ADD THIS NEW BLOCK HERE ==========
          // Update pagination state for Planned lane
          // setLanePagination((prev) => ({
          //   ...prev,
          //   Planned: {
          //     page: 1,
          //     total: jsonPlanning.total || 0,
          //     // hasMore: (jsonPlanning.total || 0) > 25,
          //     hasMore: (jsonPlanning.total || 0) > PAGE_SIZE,
          //     loading: false,
          //   },
          // }));

          // ✅ Process OTHER LANES cards - Backend filtering handles department logic
          jsonOtherLanes.data.forEach((card, i) => {
            console.log(
              `🔍 Card ${card.step_id}: status="${card.status}", external_key="${card.external_key}", department="${card.department}"`
            );

            const normalizedCard = {
              id: getStepCardId(card),
              step_id: card.step_id,
              orderNumber: card.projectNumber,
              projectName: card.projectName,
              productName: card.productName,
              binNumber: card.binNumber || "",
              orderRepName: card.orderRepName || "",
              orderRepSource: card.orderRepSource || null,
              sku: card.skuNumber,
              qtyPlanned: card.qtyRequired,
              qtyRequired: card.qtyRequired,
              external_key: card.external_key,
              department: card.department,
              matrix: card.matrixLabel,
              location: card.processLocation,
              salesOrderStatus: card.salesOrderStatus,
              purchaseOrderStatus: card.purchaseOrderStatus,
              poNumber: card.poNumber,
              purchaseOrderType: card.purchaseOrderType,
              purchaseOrders: Array.isArray(card.purchaseOrders) ? card.purchaseOrders : [],
              relatedPurchaseOrders: Array.isArray(card.relatedPurchaseOrders) ? card.relatedPurchaseOrders : [],
              proofRequired: card.proofRequired,
              proofStatus: card.proofStatus,
              productionProofSummary: card.productionProofSummary,
              allProofsUploaded: card.allProofsUploaded,
              ...productionCardBusinessDateFields(card),
              proofs: Array.isArray(card.proofs) ? card.proofs : [],
              plannedStart: card.plannedStart,
              plannedFinish: card.plannedFinish,
              machine: card.machine || card.machineName,
              machineName: card.machineName,
              machineCode: card.machineCode,
                  isFirm: !!(card.isFirm || card.is_firm),
              customer: card.customerName || "Unknown Customer",
              status: card.status,
              createdAt: card.createdAt,
              updatedAt: card.updatedAt,

              disabled: card.disabled || false,
              cancelled: card.cancelled || card.disabled || false, // Treat disabled as cancelled
              activeForProduction: card.activeForProduction || false,
              orderDbId: card.orderDbId, //
              itemId: card.itemId, //
              orderItemId: card.itemId,
              productId: card.skuNumber,
              pwo_id: card.pwo_id, //
              decorationId: card.decorationId,
              chargeId: card.chargeId,
              holdReason: card.holdReason, //
              holdFrom: card.holdFrom,

              batchGroupId: card.batchGroupId,
              batchIndex: card.batchIndex,
              batchLabel: card.batchLabel,
              batchTotal: card.batchTotal,
              rank: card.rank || i,
              isStep: true,
              isFullyCompleted: card.isFullyCompleted || false,
              pendingDepartments: card.pendingDepartments || [],
              
              isPaused: card.isPaused ?? false,
              runtimeHHMM: card.runtimeHHMM ?? '00:00',
              runtimeMinutes: card.runtimeMinutes ?? 0,
              pauseMinutes: card.pauseMinutes ?? 0,
              startedAt: card.runtime?.startedAt ?? null,
              
              lifecycle: [],
            };

            //  Place cards based on their ACTUAL status from API
            const statusKey = card.status;

            console.log(
              `🔍 Placing card ${card.step_id} in lane: ${statusKey}`
            );

            //  Using exact status mapping
            if (statusKey === "Scheduled") {
              grouped.Scheduled.push(normalizedCard);
            } else if (statusKey === "In_Production") {
              grouped.In_Production.push(normalizedCard);
            }
            // else if (statusKey === "QC") {
            //   grouped.QC.push(normalizedCard);
            else if (statusKey === "QC") {
              // ✅ Only place in QC if QC lane is enabled
              if (SHOW_QC_LANE) {
                grouped.QC.push(normalizedCard);
              } else {
                // If QC is hidden, move QC cards to Completed
                grouped.Completed.push(normalizedCard);
              }
            } else if (statusKey === "Completed") {
              grouped.Completed.push(normalizedCard);
            } else if (statusKey === "On_Hold") {
              grouped.On_Hold.push(normalizedCard);
            } else {
              console.warn(
                `⚠️ Unknown status "${statusKey}" for card ${card.step_id}, placing in Scheduled`
              );
              grouped.Scheduled.push(normalizedCard);
            }
          });

          //  Enhanced debug logging
          // console.log("🔍 Final lane counts:", {
          //   Planned: grouped.Planned.length,
          //   Scheduled: grouped.Scheduled.length,
          //   In_Production: grouped.In_Production.length,
          //   QC: grouped.QC.length,
          //   Completed: grouped.Completed.length,
          //   On_Hold: grouped.On_Hold.length,
          //   Total: Object.values(grouped).reduce(
          //     (sum, arr) => sum + arr.length,
          //     0
          //   ),
          // });

          const debugLogs = {
            Planned: grouped.Planned.length,
            Scheduled: grouped.Scheduled.length,
            In_Production: grouped.In_Production.length,
            Completed: grouped.Completed.length,
            On_Hold: grouped.On_Hold.length,
            Total: Object.values(grouped).reduce(
              (sum, arr) => sum + arr.length,
              0
            ),
          };

          // Only add QC to debug logs if it exists
          if (SHOW_QC_LANE && grouped.QC) {
            debugLogs.QC = grouped.QC.length;
          }

          console.log("🔍 Final lane counts:", debugLogs);

          //  Show department breakdown for Production Planning
          if (isPlanningPage) {
            const deptCounts = {};
            [...filteredPlanningCards, ...jsonOtherLanes.data].forEach(
              (card) => {
                const dept = card.department || "Unknown";
                deptCounts[dept] = (deptCounts[dept] || 0) + 1;
              }
            );
            console.log("📊 Department breakdown:", deptCounts);
          }

          setLanePagination((prev) => ({
            ...prev,
            Planned: {
              page: 1,
              total: jsonPlanning.total || 0, // API provides exact total
              hasMore: (jsonPlanning.total || 0) > PAGE_SIZE,
              loading: false,
            },
            //   Scheduled: {
            //     page: 1,
            //     total: grouped.Scheduled.length,
            //     hasMore: grouped.Scheduled.length >= PAGE_SIZE,
            //     loading: false,
            //   },
            //   In_Production: {
            //     page: 1,
            //     total: grouped.In_Production.length, // Per-lane count
            //     hasMore: grouped.In_Production.length >= PAGE_SIZE,
            //     loading: false,
            //   },
            //   QC: {
            //     page: 1,
            //     total: grouped.QC?.length,
            //     hasMore: grouped.QC?.length >= PAGE_SIZE,
            //     loading: false,
            //   },
            //   Completed: {
            //     page: 1,
            //     total: grouped.Completed.length,
            //     hasMore: grouped.Completed.length >= PAGE_SIZE,
            //     loading: false,
            //   },
            //   On_Hold: {
            //     page: 1,
            //     total: grouped.On_Hold.length,
            //     hasMore: grouped.On_Hold.length >= PAGE_SIZE,
            //     loading: false,
            //   },
            // }));

            Scheduled: {
              page: 1,
              total: jsonOtherLanes.totalByLane?.Scheduled || 0, // API total per lane
              hasMore: (jsonOtherLanes.totalByLane?.Scheduled || 0) > PAGE_SIZE,
              loading: false,
            },
            In_Production: {
              page: 1,
              total: jsonOtherLanes.totalByLane?.In_Production || 0, // API total per lane
              hasMore:
                (jsonOtherLanes.totalByLane?.In_Production || 0) > PAGE_SIZE,
              loading: false,
            },
            Completed: {
              page: 1,
              total: jsonOtherLanes.totalByLane?.Completed || 0, // API total per lane
              hasMore: (jsonOtherLanes.totalByLane?.Completed || 0) > PAGE_SIZE,
              loading: false,
            },
            On_Hold: {
              page: 1,
              total: jsonOtherLanes.totalByLane?.On_Hold || 0, // API total per lane
              hasMore: (jsonOtherLanes.totalByLane?.On_Hold || 0) > PAGE_SIZE,
              loading: false,
            },
          }));

          console.log("🔍 Pagination Debug:", {
            Planned: {
              total: jsonPlanning.total,
              loaded: grouped.Planned.length,
              hasMore: (jsonPlanning.total || 0) > PAGE_SIZE,
              remaining: (jsonPlanning.total || 0) - grouped.Planned.length,
            },
            Scheduled: {
              loaded: grouped.Scheduled.length,
              hasMore: grouped.Scheduled.length >= PAGE_SIZE,
              remaining: grouped.Scheduled.length - grouped.Scheduled.length,
            },
          });

          

          setColumns(grouped);





          setTtlInfo({        
            Planned: jsonPlanning.ttlSeconds || 0,
            Scheduled: jsonOtherLanes.ttlSeconds || 0,
            InProduction: jsonOtherLanes.ttlSeconds || 0,
            QC: jsonOtherLanes.ttlSeconds || 0,
            Completed: jsonOtherLanes.ttlSeconds || 0,
            OnHold: jsonOtherLanes.ttlSeconds || 0
          });
        }
      } catch (err) {
        if (request.isCurrent() && err?.name !== "AbortError") console.error("Failed to fetch cards:", err);
      } finally {
        if (request.isCurrent()) {
          fullFetchInFlightRef.current = false;
          setIsInitialLoading(false); // Clear initial loading
        }
        request.finish();
      }
      // }
    },
    [appendBoardFilters, department, setColumns]
  );

  // Keep the pre-existing refresh implementation available for comparison;
  // initial/filter loads use the lane coordinator below.
  void legacyFetchCards;

  const loadBoardLanes = useCallback(
    async ({ request, lanes, resetPagination = false }) => {
      const finishRead = mutationRefreshQueueRef.current.begin();
      try {
      if (resetPagination) {
        setColumns(emptyColumns());
        setLanePagination({
          Planned: { page: 1, hasMore: true, loading: false, total: 0 },
          Scheduled: { page: 1, hasMore: true, loading: false, total: 0 },
          In_Production: { page: 1, hasMore: true, loading: false, total: 0 },
          QC: { page: 1, hasMore: true, loading: false, total: 0 },
          Completed: { page: 1, hasMore: true, loading: false, total: 0 },
          On_Hold: { page: 1, hasMore: true, loading: false, total: 0 },
        });
      }

      const laneTokens = {};
      lanes.forEach((laneKey) => {
        laneTokens[laneKey] = (laneRequestTokensRef.current[laneKey] || 0) + 1;
        laneRequestTokensRef.current[laneKey] = laneTokens[laneKey];
      });

      setLaneRequestStates((prev) => {
        const next = { ...prev };
        visibleProductionLanes.forEach((laneKey) => {
          if (lanes.includes(laneKey)) {
            next[laneKey] = { status: "loading", error: null };
          } else if (resetPagination) {
            next[laneKey] = { status: "idle", error: null };
          }
        });
        return next;
      });

      await runConcurrentLaneRequests({
        lanes,
        requestLane: async (laneKey) => {
          const endpoint = laneKey === "Planned"
            ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/cards/?page=1&pageSize=${PAGE_SIZE}&ttl=1`
            : `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps?lanes=${laneKey}&group=0&page=1&pageSize=${PAGE_SIZE}`;
          const url = appendBoardFilters(endpoint, {
            includeMachine: laneKey !== "Planned",
          });
          return requestLaneJson(url, { reason: "initial-filter-or-retry", generation: request.id });
        },
        isCurrent: (laneKey) => request.isCurrent() &&
          laneRequestTokensRef.current[laneKey] === laneTokens[laneKey],
        onLaneSuccess: (laneKey, json) => {
          paginationCursorRef.current[laneKey] = { requestId: request.id, page: 1 };
          const rawCards = Array.isArray(json?.data) ? json.data : [];
          const normalizedCards = laneKey === "Planned"
            ? rawCards.map(normalizeProductionPlannedCard)
            : rawCards.map(normalizeProductionStepCard);

          setColumns((prev) => replaceProductionLane(prev, laneKey, normalizedCards));
          setLanePagination((prev) => ({
            ...prev,
            [laneKey]: getLanePagination({
              page: 1,
              total: getLaneTotal(json, laneKey),
              pageSize: PAGE_SIZE,
              hasMore: json.hasMore,
            }),
          }));
          setLaneRequestStates((prev) => ({
            ...prev,
            [laneKey]: { status: "loaded", error: null },
          }));
          setTtlInfo((prev) => ({
            ...prev,
            [laneKey]: json?.ttlSeconds || 0,
          }));
        },
        onLaneError: (laneKey, laneError) => {
          if (laneError?.name === "AbortError") return;
          setLanePagination((prev) => ({
            ...prev,
            [laneKey]: { ...prev[laneKey], loading: false },
          }));
          setLaneRequestStates((prev) => ({
            ...prev,
            [laneKey]: {
              status: "error",
              error: laneError?.message || "Unable to load lane",
            },
          }));
          console.error(`Failed to load Production ${laneKey}:`, laneError);
        },
      });
      } finally { finishRead(); }
    },
    [appendBoardFilters, requestLaneJson, visibleProductionLanes, PAGE_SIZE, setColumns]
  );

  const fetchCards = useCallback(
    (resetPagination = false) => {
      const requestKey = `${boardLoadKey}|reset=${resetPagination ? "1" : "0"}`;
      traceRequests("fetch-cards-invoked", { reason: boardLoadCoordinatorRef.current.has(requestKey) ? "deduplicated" : "new-wave" });
      return boardLoadCoordinatorRef.current.run(requestKey, async () => {
        const finishRead = mutationRefreshQueueRef.current.begin();
        const request = beginExplicitBoardRequest("board-initial-or-filter-change");
        currentBoardRequestRef.current = request;
        fullFetchInFlightRef.current = true;
        setRefreshingLists(new Set());
        setIsSyncing(false);
        setIsInitialLoading(true);

        try {
          await loadBoardLanes({
            request,
            lanes: visibleProductionLanes,
            resetPagination,
          });
        } catch (err) {
          if (request.isCurrent() && err?.name !== "AbortError") {
            console.error("Failed to fetch production lanes:", err);
          }
        } finally {
          if (request.isCurrent()) {
            fullFetchInFlightRef.current = false;
            setIsInitialLoading(false);
          }
          request.finish();
          finishRead();
        }
      });
    },
    [boardLoadKey, loadBoardLanes, visibleProductionLanes]
  );

  const retryLane = useCallback(async (laneKey) => {
    if (!visibleProductionLanes.includes(laneKey)) return;
    if (retryInFlightRef.current.has(laneKey)) return;
    retryInFlightRef.current.add(laneKey);
    let request = currentBoardRequestRef.current;
    if (!request || !request.isCurrent()) {
      request = boardRequestGateRef.current.begin("lane-retry");
      currentBoardRequestRef.current = request;
    }
    try {
      await loadBoardLanes({ request, lanes: [laneKey] });
    } finally {
      retryInFlightRef.current.delete(laneKey);
    }
  }, [loadBoardLanes, visibleProductionLanes]);

  // ========== useEffect ==========
  const searchDebounceRef = useRef(null);
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (searchQuery === appliedSearchQuery) return undefined;
    searchDebounceRef.current = setTimeout(() => {
      setAppliedSearchQuery(searchQuery);
      setArchivePage(1);
    }, 350);
    return () => clearTimeout(searchDebounceRef.current);
  }, [appliedSearchQuery, searchQuery]);

  const boardEffectGenerationRef = useRef(0);
  const boardCleanupTimerRef = useRef(null);
  useEffect(() => {
    const effectGeneration = ++boardEffectGenerationRef.current;
    requestDiagnosticsRef.current.identity("board", {
      instance: instanceUniqueId, department, selectedDepartment, query: appliedSearchQuery,
      machine: appliedAdvancedFilters.machine, dateFrom: appliedAdvancedFilters.dateFrom,
      dateTo: appliedAdvancedFilters.dateTo,
      fetchCallback: fetchCards,
    });
    traceRequests("effect-setup", { scope: "board", effectGeneration });
    fetchCards(true);
    return () => {
      traceRequests("effect-cleanup", { scope: "board", effectGeneration });
      if (boardCleanupTimerRef.current) clearTimeout(boardCleanupTimerRef.current);
      boardCleanupTimerRef.current = setTimeout(() => {
        if (boardEffectGenerationRef.current !== effectGeneration) {
          traceRequests("cleanup-superseded", { scope: "board", effectGeneration });
          return;
        }
        traceRequests("unmount-confirmed", { scope: "board", effectGeneration });
        pollGenerationRef.current += 1;
        laneRequestRegistryRef.current.cancelAll("board-unmount");
        boardRequestGateRef.current.cancel("board-unmount");
        boardLoadCoordinatorRef.current.cancel();
        fullFetchInFlightRef.current = false;
      }, 0);
    };
  }, [department, fetchCards]);

  useEffect(() => {
    traceRequests("poll-effect-setup", { reason: pausePolling ? "paused" : "start-30s-timer" });
    if (pausePolling) return undefined;

    const activeStatuses = getProductionPollingLanes(SHOW_QC_LANE);
    const controller = createProductionPollingController({
      intervalMs: 30000,
      canRun: () => (
        !fullFetchInFlightRef.current &&
        !partialRefreshInFlightRef.current &&
        paginationInFlightRef.current.size === 0 &&
        retryInFlightRef.current.size === 0
      ),
      isHidden: () => document.hidden,
      poll: async () => {
        try {
          setIsSyncing(true);
          const ok = await refreshSpecificListsRef.current?.(activeStatuses, { source: "poll" });
          if (ok === false) return { ok: false };
          return { ok: true };
        } catch (err) {
          console.error("Polling error:", err);
        } finally {
          if (!fullFetchInFlightRef.current && !partialRefreshInFlightRef.current) setIsSyncing(false);
        }
      },
    });
    controller.start();

    const handleVisibilityChange = () => {
      if (!document.hidden) controller.run();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      traceRequests("poll-effect-cleanup", { source: "poll-effect-cleanup" });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      controller.stop();
      stopRoutinePoll();
    };
  }, [instanceUniqueId, pausePolling, stopRoutinePoll]);

  useEffect(() => {
    if (!pausePolling) return;

    const timer = setTimeout(() => {
      console.warn("⚠️ Auto-resetting stuck pausePolling");
      setPausePolling(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [pausePolling]);

  //  PREMIUM Style: Handler for marking In_Production card as complete
  async function handleMarkAsComplete(card) {
    if (blockCardMutation(card)) return;
    if (!["In_Production", "QC"].includes(card.status)) return;
    if (card?.isArchived || card?.status === "Archive") {
      return SA.info("This Production card is completed and archived. Editing is disabled.", "Archived Production");
    }
    const result = await window.Swal.fire({
      title: "Complete Production?",
      html: `
      <div style="text-align: left; padding: 4px 0 8px 0;">
        <p style="margin: 0 0 16px 0; color: #6b7280; font-size: 13.5px; line-height: 1.5;">
          Confirming this action will move the order to <strong style="color: #059669;">Completed</strong> status for <strong>${
            card.department || "this department"
          }</strong>.
        </p>
        
        <div style="background: #f8fafc; padding: 14px; border-radius: 8px; border: 1px solid #e2e8f0; margin-bottom: 14px;">
          <div style="display: grid; gap: 10px;">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="font-size: 12px; color: #64748b; font-weight: 500;">Order #</span>
              <span style="font-size: 13px; color: #1e293b; font-weight: 600;">${
                card.orderNumber || "—"
              }</span>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="font-size: 12px; color: #64748b; font-weight: 500;">Project Name</span>
              <span style="font-size: 13px; color: #1e293b; font-weight: 600; text-align: right; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${
                card.projectName || card.productName || card.sku || "—"
              }</span>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="font-size: 12px; color: #64748b; font-weight: 500;">Quantity</span>
              <span style="font-size: 13px; color: #1e293b; font-weight: 600;">${
                card.qtyPlanned || 0
              } units</span>
            </div>
          </div>
        </div>
        
        <div style="display: flex; align-items: center; gap: 8px; padding: 10px 12px; background: #ecfdf5; border-radius: 6px; border: 1px solid #d1fae5;">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="#059669" style="flex-shrink: 0;">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
            <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z"/>
          </svg>
          <span style="font-size: 12.5px; color: #065f46; font-weight: 500; line-height: 1.4;">
            This order will be marked as completed and moved to the Completed lane.
          </span>
        </div>
      </div>
    `,
      icon: null,
      showCancelButton: true,
      confirmButtonText: "Confirm & Complete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#059669",
      cancelButtonColor: "#94a3b8",
      reverseButtons: true,
      width: "480px",
      customClass: {
        popup: "swal-premium-popup",
        title: "swal-premium-title",
        htmlContainer: "swal-premium-html",
        confirmButton: "swal-premium-confirm",
        cancelButton: "swal-premium-cancel",
      },
    });

    if (!result.isConfirmed) return;

    try {
      const saved = await patchStatus(card, {
        fromStatus: card.status,
        toStatus: "Completed",
        beforestep_id: null,
        afterstep_id: null,
      });

      await refreshSpecificLists([card.status, "Completed"]);

      // Professional success message
      SA.success(saved.recovered && saved.to !== "Completed"
        ? `Completion was saved. Current server status is ${String(saved.to).replace(/_/g, " ")}.`
        :
        `Order #${
          card.orderNumber || ""
        } has been successfully completed and moved to the Completed lane.`,
        "Production Completed"
      );
    } catch (err) {
      // The original card never moved; the per-card error/recovery control is
      // authoritative. Never restore a whole-board snapshot over other users.
      console.error("Completion could not be confirmed:", err);
    } finally {
      setPausePolling(false);
    }
  }

  // Handler for Pause button - Informational modal
  // async function handlePauseProduction(card) {
  //   await window.Swal.fire({
  //     title: "Production Pause",
  //     html: `
  //     <div style="text-align: left; padding: 4px 0 8px 0;">
  //       <p style="margin: 0 0 16px 0; color: #6b7280; font-size: 13.5px; line-height: 1.5;">
  //         The production pause functionality is currently under development and will be available in a future release.
  //       </p>
        
  //       <div style="background: #fef3c7; padding: 14px; border-radius: 8px; border: 1px solid #fbbf24; margin-bottom: 14px;">
  //         <div style="display: flex; align-items: start; gap: 10px;">
  //           <svg width="20" height="20" viewBox="0 0 16 16" fill="#d97706" style="flex-shrink: 0; margin-top: 2px;">
  //             <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
  //             <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
  //           </svg>
  //           <div>
  //             <p style="margin: 0 0 8px 0; font-size: 13px; color: #92400e; font-weight: 600;">Coming Soon</p>
  //             <p style="margin: 0; font-size: 12.5px; color: #78350f; line-height: 1.5;">
  //               This feature will allow you to temporarily pause production without removing the job from the active queue. 
  //               The system will track pause duration and automatically adjust scheduling.
  //             </p>
  //           </div>
  //         </div>
  //       </div>
        
  //       <div style="background: #f8fafc; padding: 12px; border-radius: 6px; border: 1px solid #e2e8f0;">
  //         <p style="margin: 0; font-size: 12px; color: #64748b;">
  //           <strong style="color: #475569;">Temporary Workaround:</strong> Use the <strong>"Hold"</strong> button to move jobs that need to be paused.
  //         </p>
  //       </div>
  //     </div>
  //   `,
  //     icon: "info",
  //     confirmButtonText: "Got It",
  //     confirmButtonColor: "#3b82f6",
  //     width: "520px",
  //     customClass: {
  //       popup: "swal-premium-popup",
  //       title: "swal-premium-title",
  //       htmlContainer: "swal-premium-html",
  //       confirmButton: "swal-premium-confirm",
  //     },
  //   });
  // }

// Pause / Resume 
const opPause = async (stepId) => {
  const card = Object.values(columns).flat().find((row) => getProductionStepId(row) === Number(stepId));
  if (!card || blockCardMutation(card)) throw new Error("Scheduling is still saving. Please wait.");
  const res = await fetch(
    `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps/op-pause`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ step_id: stepId }),
    }
  );
  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.message || `HTTP ${res.status}`);
  return data;
};

const opResume = async (stepId) => {
  const card = Object.values(columns).flat().find((row) => getProductionStepId(row) === Number(stepId));
  if (!card || blockCardMutation(card)) throw new Error("Scheduling is still saving. Please wait.");
  const res = await fetch(
    `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps/op-resume`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ step_id: stepId }),
    }
  );
  const data = await res.json();
  if (!res.ok || !data.success) throw new Error(data.message || `HTTP ${res.status}`);
  return data;
};

// Patch a single card's fields in columns state 
const patchCardInState = (cardId, patch) => {
  setColumns(prev => {
    const next = { ...prev };
    for (const lane of Object.keys(next)) {
      next[lane] = next[lane].map(c =>
        c.id === cardId ? { ...c, ...patch } : c
      );
    }
    return next;
  });
};

  async function handleUnholdProduction(card) {
    if (blockCardMutation(card)) return;
    if (card?.isArchived || card?.status === "Archive") {
      return SA.info("This Production card is completed and archived. Editing is disabled.", "Archived Production");
    }
    const rollback = beginCardRollback(card);
    if (!rollback) return;
    const fromLane = "On_Hold";
    const toLane = getProductionHoldReturnLane(card) || "In_Production";
    setPausePolling(true);

    setColumns((prev) => {
      const next = clone(prev);
      const holdArr = next[fromLane] || [];
      const idx = holdArr.findIndex((c) => c.id === card.id);
      if (idx === -1) return prev;

      const [movedCard] = holdArr.splice(idx, 1);
      movedCard.status = toLane;
      movedCard.holdReason = null;
      movedCard.holdFrom = null;
      movedCard.lifecycle = [
        ...(movedCard.lifecycle || []),
        {
          when: new Date().toLocaleString(),
          text: "Unheld and returned to In Production",
        },
      ];
      const inserted = assignRankTop(next[toLane], movedCard);
      next[toLane].unshift(inserted);
      return next;
    }, rollback);

    try {
      await patchStatus(card, {
        fromStatus: fromLane,
        toStatus: toLane,
      }, rollback);
      await refreshSpecificLists([fromLane, toLane]);
      SA.success(`Card returned to ${toLane.replace(/_/g, " ")}.`, "Unhold complete");
    } catch (e) {
      rollbackCardMutation(rollback);
      SA.error(e.message || "Could not unhold card.", "Save failed");
    } finally {
      setPausePolling(false);
    }
  }

// Main pause/resume handler 
  async function handlePauseProduction(card) {
    if (blockCardMutation(card)) return;
  if (card?.isArchived || card?.status === "Archive") {
    return SA.info("This Production card is completed and archived. Editing is disabled.", "Archived Production");
  }
  if (card.status !== 'In_Production') return;

  const stepId = card.stepid || card.step_id;
  if (!stepId) {
    SA.error('This card has no step ID.', 'Cannot Pause');
    return;
  }

  const wasPaused = card.isPaused;

  // Shared modal custom classes 
  const sharedClass = {
    popup: 'swal-premium-popup',
    title: 'swal-premium-title',
    htmlContainer: 'swal-premium-html',
    confirmButton: 'swal-premium-confirm',
    cancelButton: 'swal-premium-cancel',
  };

  // Shared order info grid reused in both modals
  const orderInfoBlock = `
    <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;
      background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px;
      padding:14px; margin-bottom:12px">
      <div>
        <p style="margin:0 0 3px 0; font-size:10px; color:#94a3b8;
          font-weight:700; text-transform:uppercase; letter-spacing:0.6px">
          Order
        </p>
        <p style="margin:0; font-size:13px; color:#1e293b; font-weight:700">
          #${card.orderNumber}
        </p>
      </div>
      <div>
        <p style="margin:0 0 3px 0; font-size:10px; color:#94a3b8;
          font-weight:700; text-transform:uppercase; letter-spacing:0.6px">
          Department
        </p>
        <p style="margin:0; font-size:13px; color:#1e293b; font-weight:600">
          ${card.department || '—'}
        </p>
      </div>
      <div>
        <p style="margin:0 0 3px 0; font-size:10px; color:#94a3b8;
          font-weight:700; text-transform:uppercase; letter-spacing:0.6px">
          Product
        </p>
        <p style="margin:0; font-size:13px; color:#1e293b; font-weight:600;
          line-height:1.4">
          ${card.projectName || card.productName || '—'}
        </p>
      </div>
      <div>
        <p style="margin:0 0 3px 0; font-size:10px; color:#94a3b8;
          font-weight:700; text-transform:uppercase; letter-spacing:0.6px">
          Machine
        </p>
        <p style="margin:0; font-size:13px; color:#1e293b; font-weight:600">
          ${card.machineName || card.machine || '—'}
        </p>
      </div>
    </div>
  `;

  if (!wasPaused) {
    
    // Pause Flow
    
    const result = await window.Swal.fire({
      title: 'Pause Production?',
      html: `
        <div style="text-align:left">

          ${orderInfoBlock}

          ${card.runtimeHHMM && card.runtimeHHMM !== '00:00' ? `
            <div style="display:flex; align-items:center; gap:8px;
              background:#fefce8; border:1px solid #fef08a;
              border-radius:6px; padding:10px 12px; margin-bottom:10px">
              <i class="bi bi-stopwatch" style="color:#ca8a04; font-size:14px"></i>
              <span style="font-size:12.5px; color:#854d0e; font-weight:600">
                Active runtime:
                <span style="font-family:monospace">${card.runtimeHHMM}</span>
                ${card.pauseMinutes > 0
                  ? `&nbsp;·&nbsp; ${card.pauseMinutes}m already paused`
                  : ''}
              </span>
            </div>
          ` : ''}

          <div style="display:flex; align-items:flex-start; gap:10px;
            background:#fff7ed; border:1px solid #fed7aa;
            border-radius:6px; padding:10px 12px">
            <i class="bi bi-pause-circle-fill"
              style="color:#ea580c; font-size:15px; flex-shrink:0; margin-top:1px"></i>
            <p style="margin:0; font-size:12.5px; color:#9a3412; line-height:1.5">
              The active timer will pause and production events will be recorded.
              Resume this job when work continues.
            </p>
          </div>

        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Yes, Pause',
      cancelButtonText: 'Keep Running',
      confirmButtonColor: '#d97706',
      cancelButtonColor: '#94a3b8',
      reverseButtons: true,
      width: 480,
      customClass: sharedClass,
        didOpen: (popup) => {                                    
      popup.addEventListener('mouseenter', () => window.Swal.stopTimer());
      popup.addEventListener('mouseleave', () => window.Swal.resumeTimer());
    },
    });
    

    if (!result.isConfirmed) return;

    // Optimistic update
    patchCardInState(card.id, { isPaused: true });

    try {
      await opPause(stepId);
      await window.Swal.fire({
    icon: 'warning',
    title: 'Production Paused',
    html: `
      <p style="margin:0; font-size:13.5px; color:#64748b; line-height:1.6">
        Order <strong style="color:#1e293b">#${card.orderNumber}</strong>
        has been paused.<br/>
        <span style="font-size:12px; color:#94a3b8">
          Timer is on hold — resume when work continues.
        </span>
      </p>
    `,
    showConfirmButton: false,
    timer: 2500,
    timerProgressBar: true,
    width: 380,
    customClass: {
      popup: 'swal-premium-popup',
      title: 'swal-premium-title',
      htmlContainer: 'swal-premium-html',
    },
  });
    } catch (e) {
      patchCardInState(card.id, { isPaused: false }); // revert on fail
      SA.error(e.message || 'Could not pause job.', 'Pause Failed');
    }

  } else {

    // Resume Flow

    const result = await window.Swal.fire({
      title: 'Resume Production?',
      html: `
        <div style="text-align:left">

          ${orderInfoBlock}

          ${card.pauseMinutes > 0 ? `
            <div style="display:flex; align-items:center; gap:8px;
              background:#fefce8; border:1px solid #fef08a;
              border-radius:6px; padding:10px 12px; margin-bottom:10px">
              <i class="bi bi-clock-history" style="color:#ca8a04; font-size:14px"></i>
              <span style="font-size:12.5px; color:#854d0e; font-weight:600">
                Paused for ${card.pauseMinutes} minute${card.pauseMinutes !== 1 ? 's' : ''}
              </span>
            </div>
          ` : ''}

          <div style="display:flex; align-items:flex-start; gap:10px;
            background:#f0fdf4; border:1px solid #bbf7d0;
            border-radius:6px; padding:10px 12px">
            <i class="bi bi-play-circle-fill"
              style="color:#16a34a; font-size:15px; flex-shrink:0; margin-top:1px"></i>
            <p style="margin:0; font-size:12.5px; color:#14532d; line-height:1.5">
              The production timer will resume from where it was paused.
            </p>
          </div>

        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Yes, Resume',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#94a3b8',
      reverseButtons: true,
      width: 480,
      customClass: sharedClass,
         didOpen: (popup) => {                                    
      popup.addEventListener('mouseenter', () => window.Swal.stopTimer());
      popup.addEventListener('mouseleave', () => window.Swal.resumeTimer());
    },
    });

    if (!result.isConfirmed) return;

    // Optimistic update
    patchCardInState(card.id, { isPaused: false });

    try {
      await opResume(stepId);
      await window.Swal.fire({
    icon: 'success',
    title: 'Production Resumed',
    html: `
      <p style="margin:0; font-size:13.5px; color:#64748b; line-height:1.6">
        Order <strong style="color:#1e293b">#${card.orderNumber}</strong>
        is now running.<br/>
        <span style="font-size:12px; color:#94a3b8">
          Timer is active — production in progress.
        </span>
      </p>
    `,
    showConfirmButton: false,
    timer: 2500,
    timerProgressBar: true,
    width: 380,
    customClass: {
      popup: 'swal-premium-popup',
      title: 'swal-premium-title',
      htmlContainer: 'swal-premium-html',
    },
  });
    } catch (e) {
      patchCardInState(card.id, { isPaused: true }); // revert on fail
      SA.error(e.message || 'Could not resume job.', 'Resume Failed');
    }
  }
}



// Tick every 60s to update displayed runtime for running cards
useEffect(() => {
  const hasRunning = Object.values(columns)
    .flat()
    .some(c => c.status === 'In_Production' && !c.isPaused && c.startedAt);

  if (!hasRunning) return;
  const id = setInterval(() => {
    setColumns(prev => {
      const next = { ...prev };
      for (const lane of Object.keys(next)) {
        next[lane] = next[lane].map(c => {
          if (c.status !== 'In_Production' || c.isPaused || !c.startedAt) return c;
          const pausedMs = (c.pauseMinutes || 0) * 60000;
          const activeMs = Math.max(0, Date.now() - new Date(c.startedAt).getTime() - pausedMs);
          const mins = Math.floor(activeMs / 60000);
          const hh = String(Math.floor(mins / 60)).padStart(2, '0');
          const mm = String(mins % 60).padStart(2, '0');
          return { ...c, runtimeHHMM: `${hh}:${mm}`, runtimeMinutes: mins };
        });
      }
      return next;
    });
  }, 60000);
  return () => clearInterval(id);
}, [columns, setColumns]);








  // ✨ Handler for Hold button - Moves to On Hold lane
  async function handlePutOnHold(card) {
    if (blockCardMutation(card)) return;
    // Prompt for reason (required for On Hold)
    const result = await window.Swal.fire({
      title: "Put Production on Hold?",
      html: `
      <div style="text-align: left; padding: 4px 0 8px 0;">
        <p style="margin: 0 0 16px 0; color: #6b7280; font-size: 13.5px; line-height: 1.5;">
          This order will be moved to <strong style="color: #dc2626;">On Hold</strong> status and removed from the active production queue.
        </p>
        
        <div style="background: #f8fafc; padding: 16px; border-radius: 8px; border: 1px solid #e2e8f0; margin-bottom: 14px;">
          <div style="display: grid; gap: 14px;">
            <div style="display: grid; gap: 5px;">
              <span style="font-size: 11px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">Order Number</span>
              <span style="font-size: 14px; color: #1e293b; font-weight: 600;">${
                card.orderNumber || "—"
              }</span>
            </div>
            <div style="display: grid; gap: 5px;">
              <span style="font-size: 11px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">Project</span>
              <span style="font-size: 14px; color: #1e293b; font-weight: 600; line-height: 1.5; word-break: break-word;">${
                card.projectName || card.productName || card.sku || "—"
              }</span>
            </div>
          </div>
        </div>
        
        <div style="display: flex; align-items: center; gap: 8px; padding: 10px 12px; background: #fef2f2; border-radius: 6px; border: 1px solid #fecaca; margin-bottom: 12px;">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="#dc2626" style="flex-shrink: 0;">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
            <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
          </svg>
          <span style="font-size: 12.5px; color: #991b1b; font-weight: 500; line-height: 1.4;">
            Production will be stopped and the job will require manual action to resume.
          </span>
        </div>
      </div>
    `,
      input: "textarea",
      inputLabel: "Reason for Hold (Required)",
      inputPlaceholder:
        "E.g., Waiting for materials, customer requested changes, quality issue...",
      inputAttributes: {
        "aria-label": "Reason for hold",
        style:
          "font-size: 13px; padding: 10px; border-radius: 6px; border: 1.5px solid #e2e8f0;",
      },
      inputValidator: (value) => {
        if (!value || value.trim() === "") {
          return "Please provide a reason for putting this job on hold";
        }
        if (value.trim().length < 5) {
          return "Please provide a more detailed reason (at least 5 characters)";
        }
        return null;
      },
      showCancelButton: true,
      confirmButtonText: "Put on Hold",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#94a3b8",
      reverseButtons: true,
      width: "540px",
      customClass: {
        popup: "swal-premium-popup",
        title: "swal-premium-title",
        htmlContainer: "swal-premium-html",
        confirmButton: "swal-premium-confirm",
        cancelButton: "swal-premium-cancel",
      },
    });

    if (!result.isConfirmed) return;

    const holdReason = result.value.trim();

    // Optimistic UI update
    const rollback = beginCardRollback(card);
    if (!rollback) return;
    setPausePolling(true);

    setColumns((prev) => {
      const next = clone(prev);
      const prodArr = next.In_Production;
      const idx = prodArr.findIndex((c) => c.id === card.id);

      if (idx === -1) return prev;

      const [movedCard] = prodArr.splice(idx, 1);
      movedCard.status = "On_Hold";
      movedCard.lastMain = "In_Production"; // Remember where it came from
      movedCard.holdReason = holdReason;
      movedCard.lifecycle = [
        ...(movedCard.lifecycle || []),
        {
          when: new Date().toLocaleString(),
          text: `Put on hold: ${holdReason}`,
        },
      ];

      const inserted = assignRankTop(next.On_Hold, movedCard);
      next.On_Hold.unshift(inserted);

      return next;
    }, rollback);

    try {
      // Call the SAME API used in drag-and-drop for On Hold
      await patchStatus(card, {
        fromStatus: "In_Production",
        toStatus: "On_Hold",
        reason: holdReason,
        beforestep_id: null,
        afterstep_id: null,
      }, rollback);

      await refreshSpecificLists(["In_Production", "On_Hold"]);

      SA.success(
        `Order #${card.orderNumber || ""} has been put on hold.`,
        "Production Paused"
      );
    } catch (err) {
      rollbackCardMutation(rollback);
      SA.error(
        err.message || "Unable to put the order on hold. Please try again.",
        "Operation Failed"
      );
    } finally {
      setPausePolling(false);
    }
  }

  const loadMoreCards = useCallback(
    async (laneKey) => {
      const pageInfo = lanePagination[laneKey];
      if (!pageInfo?.hasMore || pageInfo.loading || paginationInFlightRef.current.has(laneKey)) return;
      if (refreshingLists.has(laneKey) || laneRequestStates[laneKey]?.status === "loading" ||
          retryInFlightRef.current.has(laneKey)) {
        setLanePagination((previous) => ({
          ...previous,
          [laneKey]: { ...previous[laneKey], error: "This lane is updating. Retry when it finishes." },
        }));
        return;
      }

      const boardRequest = currentBoardRequestRef.current;
      const laneRequestToken = (laneRequestTokensRef.current[laneKey] || 0) + 1;
      laneRequestTokensRef.current[laneKey] = laneRequestToken;
      paginationTokensRef.current[laneKey] = laneRequestToken;
      paginationInFlightRef.current.add(laneKey);
      const finishRead = mutationRefreshQueueRef.current.begin();
      const isCurrentLaneRequest = () =>
        (!boardRequest || boardRequest.isCurrent()) &&
        laneRequestTokensRef.current[laneKey] === laneRequestToken;
      const cursor = paginationCursorRef.current[laneKey];
      const previousPage = cursor?.requestId === boardRequest?.id
        ? Math.max(Number(pageInfo.page), cursor.page) : Number(pageInfo.page);
      const nextPage = previousPage + 1;

      setLanePagination((previous) => ({
        ...previous,
        [laneKey]: { ...previous[laneKey], loading: true, error: null },
      }));
      try {
        const endpoint = laneKey === "Planned"
          ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/cards?page=${nextPage}&pageSize=${PAGE_SIZE}&ttl=1`
          : `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps?lanes=${laneKey}&group=0&page=${nextPage}&pageSize=${PAGE_SIZE}`;
        const url = appendBoardFilters(endpoint, { includeMachine: laneKey !== "Planned" });
        const json = await requestLaneJson(url, { reason: "pagination", generation: laneRequestToken });
        if (!isCurrentLaneRequest()) return;
        if (json.page != null && Number(json.page) !== nextPage) {
          throw new Error("The server returned a different page. Please retry.");
        }
        const total = getLaneTotal(json, laneKey);
        const pagination = getProductionLanePageState({
          page: nextPage, total, pageSize: PAGE_SIZE, hasMore: json.hasMore,
        });
        if (!Array.isArray(json.data) || (!json.data.length && pagination.hasMore)) {
          throw new Error("No cards returned for this page. Please retry.");
        }
        const normalize = laneKey === "Planned" ? normalizeProductionPlannedCard : normalizeProductionStepCard;
        const newCards = json.data.map((card, index) => normalize(card, previousPage * PAGE_SIZE + index));
        paginationCursorRef.current[laneKey] = { requestId: boardRequest?.id, page: nextPage };
        setColumns((previous) => replaceProductionLane(previous, laneKey,
          mergeProductionLaneCards(previous[laneKey], newCards)));
        setLanePagination((previous) => ({ ...previous, [laneKey]: pagination }));
      } catch (error) {
        if (isCurrentLaneRequest() && error?.name !== "AbortError") {
          setLanePagination((previous) => ({
            ...previous,
            [laneKey]: { ...previous[laneKey], loading: false, error: error?.message || "Unable to load more cards" },
          }));
        }
      } finally {
        if (paginationTokensRef.current[laneKey] === laneRequestToken) {
          delete paginationTokensRef.current[laneKey];
          paginationInFlightRef.current.delete(laneKey);
          if (!boardRequest || boardRequest.isCurrent()) setLanePagination((previous) => ({
            ...previous,
            [laneKey]: { ...previous[laneKey], loading: false },
          }));
        }
        finishRead();
      }
    },
    [appendBoardFilters, lanePagination, laneRequestStates, refreshingLists, PAGE_SIZE, requestLaneJson, setColumns]
  );

  const getStatusColor = (key) => {
    const colors = {
      // More sophisticated, modern colors
      Planned: "#64748b", // Slate gray - neutral but warmer
      Scheduled: "#3b82f6", // Modern blue - less harsh than bootstrap blue
      In_Production: "#f59e0b", // Amber - warmer than harsh yellow
      QC: "#ef4444", // Modern red - better than orange for quality check
      Completed: "#10b981", // Emerald green - more modern than bootstrap green
      On_Hold: "#8b5cf6", // Purple - less aggressive than red for holds
      Archive: "#64748b",
    };
    return colors[key] || "#64748b";
  };

  //  UPDATED - Professional button palette
  //  MODERN MINIMAL - Inspired by top enterprise software
  const getButtonColors = (action, variant = "primary") => {
    const buttonStyles = {
      primary: {
        open: { bg: "#18181b", hover: "#27272a", text: "#ffffff" }, // Near black - clean & modern
        "start-now": { bg: "#16a34a", hover: "#15803d", text: "#ffffff" }, // Clean green - not too bright
        "ready-schedule": { bg: "#2563eb", hover: "#1d4ed8", text: "#ffffff" }, // Professional blue
      },
      secondary: {
        split: { bg: "#f4f4f5", hover: "#e4e4e7", text: "#18181b" }, // Light gray - subtle
        reschedule: { bg: "#f4f4f5", hover: "#e4e4e7", text: "#18181b" }, // Same clean approach
        partial: { bg: "#f4f4f5", hover: "#e4e4e7", text: "#71717a" }, // Muted text
        "move-up": { bg: "#f4f4f5", hover: "#e4e4e7", text: "#71717a" }, // Consistent subtle
        "move-down": { bg: "#f4f4f5", hover: "#e4e4e7", text: "#71717a" }, // Consistent subtle
      },
      destructive: {
        cancel: { bg: "#f4f4f5", hover: "#e4e4e7", text: "#71717a" }, // Neutral cancel
      },
    };

    return buttonStyles[variant]?.[action] || buttonStyles.secondary.partial;
  };

  const getOverdueCount = (columns) => {
    return Object.values(columns)
      .flat()
      .filter((card) => {
        if (!card.plannedFinish) return false;
        return new Date(card.plannedFinish) < new Date();
      }).length;
  };

  function getOrderIdNumber(card) {
    const raw =
      card?.orderNumber ?? card?.raw?.order?.orderNumber ?? card?.title ?? "";
    const match = String(raw).match(/\d+/);
    return match ? Number(match[0]) : null;
  }

  const getUniqueMachines = (columns) => {
    const machines = new Set();
    Object.values(columns)
      .flat()
      .forEach((card) => {
        if (card.machine && card.machine !== "TBD" && card.machine !== "") {
          machines.add(card.machine);
        }
      });
    return Array.from(machines).sort();
  };

  const getDepartmentCounts = (columns) => {
    const counts = {};
    Object.values(columns)
      .flat()
      .forEach((card) => {
        const dept = card.department || "Unknown";
        counts[dept] = (counts[dept] || 0) + 1;
      });
    return counts;
  };

  const filteredColumns = useMemo(() => {
    const filtered = { ...columns };

    Object.keys(filtered).forEach((status) => {
      filtered[status] = filtered[status].filter((card) => {
        // Search filter
        if (appliedSearchQuery) {
          const searchLower = appliedSearchQuery.toLowerCase();
          const matchesSearch =
            (card.orderNumber || "").toLowerCase().includes(searchLower) ||
            (card.customer || "").toLowerCase().includes(searchLower) ||
            (card.sku || "").toLowerCase().includes(searchLower) ||
            (card.projectName || "").toLowerCase().includes(searchLower);
          if (!matchesSearch) return false;
        }

        // Department filter (Production Planning only)
        if (
          (isAggregateProductionRoute(department)) &&
          selectedDepartment
        ) {
          if (
            (card.department || "").toLowerCase() !==
            selectedDepartment.toLowerCase()
          )
            return false;
        }

        // Priority filter
        if (quickFilter) {
          const cardDate = new Date(card.plannedFinish);
          const today = new Date();
          const weekFromNow = new Date(
            today.getTime() + 7 * 24 * 60 * 60 * 1000
          );

          switch (quickFilter) {
            case "overdue":
              if (cardDate >= today) return false;
              break;
            case "today":
              if (cardDate.toDateString() !== today.toDateString())
                return false;
              break;
            case "week":
              if (cardDate > weekFromNow || cardDate < today) return false;
              break;
            case "urgent":
              if (card.priority === "urgent") return true;
              return false;
            default:
              break;
          }
        }

        // Machine filter
        if (
          appliedAdvancedFilters.machine &&
          (card.machine || "").toLowerCase() !== appliedAdvancedFilters.machine.toLowerCase()
        ) {
          return false;
        }

        // Date range filter
        if (
          appliedAdvancedFilters.dateFrom &&
          new Date(card.plannedStart) < new Date(appliedAdvancedFilters.dateFrom)
        ) {
          return false;
        }
        if (
          appliedAdvancedFilters.dateTo &&
          new Date(card.plannedStart) > new Date(`${appliedAdvancedFilters.dateTo}T23:59:59`)
        ) {
          return false;
        }

        return true;
      });

      // Preserve server order across appends, refreshes and scope-key ties.
    });

    if (showArchive && archiveEnabled) {
      const archiveCards = archiveVisibleRows.map((row) => ({
        ...row,
        ...productionCardBusinessDateFields(row),
        id: row.archiveScopeKey || row.external_key || row.externalKey || row.projectNumber,
        external_key: getProductionDetailExternalKey(row),
        orderNumber: row.projectNumber || "",
        projectName: row.projectName || "Unnamed project",
        productName: row.productName || "",
        sku: row.skuNumber || "",
        customer: row.customerName || "Unknown Customer",
        orderRepName: row.orderRepName || "",
        orderRepSource: row.orderRepSource || null,
        binNumber: row.binNumber || "",
        salesOrderStatus: row.salesOrderStatus || "",
        purchaseOrderStatus: row.purchaseOrderStatus || "",
        poNumber: row.poNumber || "",
        purchaseOrderType: row.purchaseOrderType || "",
        proofRequired: row.proofRequired ?? false,
        proofStatus: row.proofStatus || "",
        allProofsUploaded: row.allProofsUploaded ?? false,
        qtyRequired: row.qtyRequired,
        qtyPlanned: row.qtyRequired,
        qtyDone: row.qtyDone,
        location: row.location || "",
        processLocation: row.processLocation || row.location || "",
        matrix: row.matrixLabel || "",
        plannedStart: row.plannedStart || "",
        plannedFinish: row.plannedFinish || "",
        department: row.department || "",
        machine: row.machine || "",
        machineName: row.machineName || "",
        machineCode: row.machineCode || "",
        step_id: getProductionStepId(row),
        stepId: getProductionStepId(row),
        pwo_id: row.pwo_id ?? row.pwoId ?? null,
        orderDbId: row.orderDbId ?? null,
        itemId: row.itemId ?? null,
        decorationId: row.decorationId ?? null,
        status: "Archive",
        completedAt: row.completedAt,
        isArchived: true,
        isStep: true,
        cancelled: false,
      }));
      filtered.Archive = archiveCards.filter((card) => {
        if (!quickFilter) return true;
        const cardDate = new Date(card.plannedFinish);
        const today = new Date();
        const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
        if (quickFilter === "overdue") return cardDate < today;
        if (quickFilter === "today") return cardDate.toDateString() === today.toDateString();
        if (quickFilter === "week") return cardDate >= today && cardDate <= weekFromNow;
        if (quickFilter === "urgent") return card.priority === "urgent";
        return true;
      });
      // Preserve server order across appends, refreshes and scope-key ties.
    }

    return filtered;
  }, [
    columns,
    appliedSearchQuery,
    selectedDepartment,
    quickFilter,
    appliedAdvancedFilters,
    department,
    orderIdSort,
    archiveEnabled,
    archiveRows,
    archiveIdentity,
    archiveRowsIdentity,
    showArchive,
  ]);

  const boardStatuses = useMemo(
    () => showArchive && archiveEnabled
      ? [...STATUSES, { key: "Archive", label: "Archive" }]
      : STATUSES,
    [archiveEnabled, showArchive]
  );

  {
    /* CSS for the pulse animation */
  }
  <style>
    {`
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
  }
  `}
  </style>;

  const [viewMode, setViewMode] = useState("kanban"); // 'kanban' or 'table'

  const TableView = ({ columns, onCardClick, onStatusChange, laneRequestStates, onRetryLane }) => {
    const [sortConfig, setSortConfig] = useState({
      key: null,
      direction: "asc",
    });
    const tableRef = useRef(null);
    const SCROLL_KEY = "production-table-scroll";

    // Save scroll position to sessionStorage
    const saveScrollPosition = useCallback(() => {
      if (tableRef.current) {
        sessionStorage.setItem(
          SCROLL_KEY,
          tableRef.current.scrollTop.toString()
        );
      }
    }, []);

    // Restore scroll position from sessionStorage
    const restoreScrollPosition = useCallback(() => {
      if (tableRef.current) {
        const savedPosition = sessionStorage.getItem(SCROLL_KEY);
        if (savedPosition) {
          // Use requestAnimationFrame for smooth restoration
          requestAnimationFrame(() => {
            if (tableRef.current) {
              tableRef.current.scrollTop = parseInt(savedPosition);
            }
          });
        }
      }
    }, []);

    // Save on every scroll
    const handleScroll = useCallback(() => {
      saveScrollPosition();
    }, [saveScrollPosition]);

    // Restore after every render (including polling updates)
    useLayoutEffect(() => {
      restoreScrollPosition();
    });

    // Save before component unmounts
    useEffect(() => {
      return () => {
        saveScrollPosition();
      };
    }, [saveScrollPosition]);

    // Flatten all cards from all columns
    const allCards = useMemo(() => {
      let cards = Object.values(columns).flat();

      // Sort cards if needed
      if (sortConfig.key) {
        cards.sort((a, b) => {
          if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === "asc" ? -1 : 1;
          }
          if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === "asc" ? 1 : -1;
          }
          return 0;
        });
      }

      return cards;
    }, [columns, sortConfig]);

    const handleSort = useCallback((key) => {
      setSortConfig((prev) => {
        let direction = "asc";
        if (prev.key === key && prev.direction === "asc") {
          direction = "desc";
        }
        return { key, direction };
      });
    }, []);

    const handleTableStatusChange = useCallback(
      async (card, newStatus) => {
        // Save current scroll before status change
        saveScrollPosition();

        if (card.status === "Planned" && newStatus === "Scheduled") {
          openScheduleGate(card);
        } else {
          await onStatusChange(card.id, newStatus, card);
        }

        // Restore scroll after status change
        setTimeout(() => {
          restoreScrollPosition();
        }, 100);
      },
      [onStatusChange, saveScrollPosition, restoreScrollPosition]
    );

    return (
      <div
        style={{
          backgroundColor: "#f8f9fa",
          padding: "20px",
          borderRadius: "8px",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "12px" }}>
          {STATUSES.map(({ key, label }) => {
            const laneState = laneRequestStates[key] || { status: "idle" };
            if (laneState.status !== "loading" && laneState.status !== "error") return null;
            return (
              <div
                key={key}
                role={laneState.status === "error" ? "alert" : "status"}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 9px",
                  borderRadius: "6px",
                  color: laneState.status === "error" ? "#991b1b" : "#475569",
                  background: laneState.status === "error" ? "#fef2f2" : "#fff",
                  border: `1px solid ${laneState.status === "error" ? "#fecaca" : "#e2e8f0"}`,
                  fontSize: "12px",
                }}
              >
                {laneState.status === "loading" ? `Loading ${label}...` : `${label} failed`}
                {laneState.status === "error" && (
                  <button type="button" onClick={() => onRetryLane(key)} style={{ fontSize: "11px" }}>
                    Retry
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            padding: "0 4px",
          }}
        >
          <h5 style={{ margin: 0, color: "#495057", fontWeight: "600" }}>
            Production Orders ({allCards.length})
          </h5>
          <div
            style={{
              fontSize: "14px",
              color: "#6c757d",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <i className="bi bi-arrow-repeat text-success"></i>
            {/* Auto-refreshing every 5 seconds */}
            Auto-refreshing every 30 seconds
          </div>
        </div>

        {/* Modern Table */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
            overflow: "hidden",
          }}
        >
          <div
            ref={tableRef}
            onScroll={handleScroll}
            style={{
              overflowX: "auto",
              maxHeight: "700px",
              scrollBehavior: "smooth", // Smooth scrolling
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "14px",
              }}
            >
              {/* Sticky Header */}
              <thead
                style={{
                  backgroundColor: "#f1f3f4",
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                }}
              >
                <tr>
                  {[
                    { key: "orderNumber", label: "Order #", width: "120px" },
                    { key: "customer", label: "Customer", width: "180px" },
                    { key: "projectName", label: "Product", width: "200px" },
                    { key: "qtyPlanned", label: "Qty", width: "80px" },
                    { key: "status", label: "Status", width: "140px" },
                    { key: "machine", label: "Machine", width: "120px" },
                    { key: "assignedUserName", label: "Operator", width: "140px" },
                    { key: "department", label: "Department", width: "120px" },
                    {
                      key: "plannedStart",
                      label: "Start Date",
                      width: "120px",
                    },
                    { key: "actions", label: "Actions", width: "100px" },
                  ].map((col) => (
                    <th
                      key={col.key}
                      onClick={() =>
                        col.key !== "actions" && handleSort(col.key)
                      }
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        borderBottom: "2px solid #e5e7eb",
                        cursor: col.key !== "actions" ? "pointer" : "default",
                        userSelect: "none",
                        width: col.width,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        {col.label}
                        {col.key !== "actions" && (
                          <i
                            className={`bi bi-chevron-${
                              sortConfig.key === col.key
                                ? sortConfig.direction === "asc"
                                  ? "up"
                                  : "down"
                                : "expand"
                            }`}
                            style={{ fontSize: "12px", opacity: 0.6 }}
                          />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Table Body */}
              <tbody>
                {allCards.map((card, index) => (
                  <tr
                    key={`${card.id}-${card.status}`} // Stable key
                    style={{
                      backgroundColor: index % 2 === 0 ? "white" : "#fafbfc",
                      borderBottom: "1px solid #e1e5e9",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.closest("tr").style.backgroundColor = "#f8f9fa")
                    }
                    onMouseLeave={(e) =>
                      (e.target.closest("tr").style.backgroundColor =
                        index % 2 === 0 ? "white" : "#fafbfc")
                    }
                  >
                    {/* Order Number */}
                    <td
                      style={{
                        padding: "12px 16px",
                        fontWeight: "600",
                        color: "#1a73e8",
                      }}
                    >
                      {card.orderNumber}
                    </td>

                    {/* Customer */}
                    <td style={{ padding: "12px 16px", color: "#374151" }}>
                      <div
                        style={{
                          maxWidth: "160px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {card.customer || "N/A"}
                      </div>
                    </td>

                    {/* Product */}
                    <td style={{ padding: "12px 16px", color: "#374151" }}>
                      <div
                        style={{
                          maxWidth: "180px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {card.projectName || card.sku || "N/A"}
                      </div>
                    </td>

                    {/* Quantity */}
                    <td
                      style={{
                        padding: "12px 16px",
                        textAlign: "right",
                        fontWeight: "500",
                      }}
                    >
                      {card.qtyPlanned || 0}
                    </td>

                    {/* Status Dropdown */}
                    <td style={{ padding: "12px 16px" }}>
                      <select
                        value={card.status}
                        onChange={(e) =>
                          handleTableStatusChange(card, e.target.value)
                        }
                        style={{
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          padding: "4px 8px",
                          fontSize: "13px",
                          backgroundColor: "white",
                          cursor: "pointer",
                          minWidth: "120px",
                        }}
                      >
                        {STATUSES.map((status) => (
                          <option key={status.key} value={status.key}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Machine */}
                    <td style={{ padding: "12px 16px", color: "#6b7280" }}>
                      {card.machine || card.machineName || "Unassigned"}
                    </td>

                    {/* Operator */}
                    <td style={{ padding: "12px 16px", color: "#6b7280" }}>
                      {card.assignedUserName || card.operator || "Unassigned"}
                    </td>

                    {/* Department Badge */}
                    <td style={{ padding: "12px 16px" }}>
                      <span
                        style={{
                          padding: "3px 8px",
                          fontSize: "12px",
                          borderRadius: "12px",
                          backgroundColor: "#e0f2fe",
                          color: "#0277bd",
                          fontWeight: "500",
                        }}
                      >
                        {card.department || "N/A"}
                      </span>
                    </td>

                    {/* Start Date */}
                    <td
                      style={{
                        padding: "12px 16px",
                        color: "#6b7280",
                        fontSize: "13px",
                      }}
                    >
                      {card.plannedStart ? DATEFMT(card.plannedStart) : "-"}
                    </td>

                    {/* Actions */}
                    <td style={{ padding: "12px 16px" }}>
                      <button
                        onClick={() => {
                          saveScrollPosition(); // Save before opening modal
                          onCardClick(card);
                        }}
                        style={{
                          padding: "6px 12px",
                          fontSize: "13px",
                          fontWeight: "500",
                          border: "1px solid #1a73e8",
                          backgroundColor: "white",
                          color: "#1a73e8",
                          borderRadius: "4px",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#1a73e8";
                          e.target.style.color = "white";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "white";
                          e.target.style.color = "#1a73e8";
                        }}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {allCards.length === 0 && (
            <div
              style={{
                padding: "40px",
                textAlign: "center",
                color: "#6b7280",
              }}
            >
              <i
                className="bi bi-inbox"
                style={{ fontSize: "48px", marginBottom: "16px" }}
              />
              <p>No orders found. Try adjusting your filters above.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleStatusChange = async (cardId, newStatus, card) => {
    if (blockCardMutation(card)) return;
    if (newStatus === "Completed") return handleMarkAsComplete(card);
    if (card.status === "On_Hold" && newStatus !== getProductionHoldReturnLane(card)) return;
    if (card?.isArchived || card?.status === "Archive" || newStatus === "Archive") {
      return SA.info("This Production card is completed and archived. Editing is disabled.", "Archived Production");
    }
    // This triggers the same logic as your drag & drop
    const fromStatus = card.status;

    // Show scheduling modal for Planned → Scheduled
    if (fromStatus === "Planned" && newStatus === "Scheduled") {
      openScheduleGate(card);
      return;
    }

    // Use your existing move logic
    const rollback = beginCardRollback(card);
    if (!rollback) return;

    try {
      // Update UI optimistically
      setColumns((prev) => {
        const next = clone(prev);

        // Remove from current column
        const fromArr = next[fromStatus];
        const cardIndex = fromArr.findIndex((c) => c.id === cardId);
        if (cardIndex !== -1) {
          const moved = fromArr.splice(cardIndex, 1)[0];

          // Add to new column
          moved.status = newStatus;
          moved.lifecycle = [
            ...moved.lifecycle,
            {
              when: new Date().toLocaleString(),
              text: `Moved to ${newStatus.replace(/([A-Z])/g, " $1")}`,
            },
          ];

          next[newStatus].unshift(assignRankTop(next[newStatus], moved));
        }

        return next;
      }, rollback);

      // Call your existing API
      if (card.isStep) {
        await patchStatus(card, {
          fromStatus,
          toStatus: newStatus,
        }, rollback);
        await refreshSpecificLists([fromStatus, newStatus]);
      }

      SA.success("Status updated successfully!");
    } catch (error) {
      // Revert on error
      rollbackCardMutation(rollback);
      SA.error(error.message || "Failed to update status");
    }
  };

  // Add this helper function
  const DATEFMT = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return "-";
    }
  };

  const getBatchColor = (batch_group_id) => {
    if (!batch_group_id) return null;

    // Professional, muted colors
    const colors = [
      { bg: "#6366f1" }, // Indigo
      { bg: "#8b5cf6" }, // Purple
      { bg: "#ec4899" }, // Pink
      { bg: "#f59e0b" }, // Amber
      { bg: "#10b981" }, // Emerald
      { bg: "#06b6d4" }, // Cyan
    ];

    let hash = 0;
    for (let i = 0; i < batch_group_id.length; i++) {
      hash = batch_group_id.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  /**
   * Refreshes PWO status for orders in the Scheduled lane.
   * Extracts order numbers from scheduled cards and syncs their status
   * with the backend database via the pwo-status-refresh endpoint.
   *
   * @returns {Promise<void>}
   */
  const refreshPWOStatuses = useCallback(async () => {
    const scheduledCards = scheduledCardsRef.current;

    // Early return if no scheduled cards exist
    if (!scheduledCards?.length) {
      return;
    }

    // Extract unique order numbers, filtering out null/undefined values
    const orderNos = [
      ...new Set(
        scheduledCards.map((card) => card.orderNumber).filter(Boolean)
      ),
    ];

    // Skip API call if no valid order numbers found
    if (!orderNos.length) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/pwo-status-refresh`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ orderNos }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Optionally refresh UI if data was updated
      if (data.success && data.rowsUpdated > 0) {
        // await refreshSpecificLists(['Scheduled']);
      }
    } catch (error) {
      // Log error for debugging but don't disrupt user experience
      console.error("[PWO Status Refresh]", error.message);
    }
  }, []);

  useEffect(() => {
    scheduledCardsRef.current = columns?.Scheduled || [];
  }, [columns]);
  useEffect(() => {
    refreshPWOStatusesRef.current = refreshPWOStatuses;
  }, [refreshPWOStatuses]);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          padding: "16px 0",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "20px",
            fontWeight: 600,
            color: "#111827",
          }}
        >
          Production Management
        </h3>

        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          {/* Refresh Button - Standalone */}
          <button
            onClick={handleHardRefresh}
            disabled={isSyncing}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 16px",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              background: isSyncing ? "#f9fafb" : "#ffffff",
              color: isSyncing ? "#9ca3af" : "#374151",
              fontSize: "14px",
              fontWeight: 500,
              cursor: isSyncing ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            }}
            onMouseEnter={(e) => {
              if (!isSyncing) {
                e.currentTarget.style.background = "#f9fafb";
                e.currentTarget.style.borderColor = "#d1d5db";
              }
            }}
            onMouseLeave={(e) => {
              if (!isSyncing) {
                e.currentTarget.style.background = "#ffffff";
                e.currentTarget.style.borderColor = "#e5e7eb";
              }
            }}
          >
            <i
              className={`bi bi-arrow-clockwise ${isSyncing ? "spin" : ""}`}
              style={{ fontSize: "16px" }}
            ></i>
            {isSyncing ? "Syncing..." : "Refresh"}
          </button>

          {/* View Toggle - Segmented Control */}
          <div
            style={{
              display: "inline-flex",
              background: "#f3f4f6",
              borderRadius: "8px",
              padding: "4px",
              gap: "4px",
            }}
            role="group"
            aria-label="View mode toggle"
           >
            <button
              onClick={() => setViewMode("kanban")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 16px",
                border: "none",
                borderRadius: "6px",
                background: viewMode === "kanban" ? "#ffffff" : "transparent",
                color: viewMode === "kanban" ? "#1f2937" : "#6b7280",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow:
                  viewMode === "kanban"
                    ? "0 1px 3px rgba(0, 0, 0, 0.1)"
                    : "none",
              }}
            >
              <i className="bi bi-kanban" style={{ fontSize: "16px" }}></i>
              Board
            </button>
            <button
              onClick={() => setViewMode("table")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 16px",
                border: "none",
                borderRadius: "6px",
                background: viewMode === "table" ? "#ffffff" : "transparent",
                color: viewMode === "table" ? "#1f2937" : "#6b7280",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow:
                  viewMode === "table"
                    ? "0 1px 3px rgba(0, 0, 0, 0.1)"
                    : "none",
              }}
            >
              <i className="bi bi-table" style={{ fontSize: "16px" }}></i>
              Table
            </button>
           </div>
           <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, marginLeft: 10 }}>
             <button
               type="button"
               disabled={!archiveEnabled}
               onClick={() => handleShowArchiveChange(!showArchive)}
               className={`px-3 py-2 text-sm font-medium rounded-xl transition-all duration-200 border ${showArchive ? "bg-blue-100 text-blue-700 border-blue-300 shadow-sm" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"} ${!archiveEnabled ? "opacity-50 cursor-not-allowed" : ""}`}
               title={archiveEnabled ? "Toggle archived Production projects" : "Archive is disabled in Production Settings"}
             >
               <i className={`bi ${showArchive ? "bi-archive-fill" : "bi-archive"}`} style={{ marginRight: 6 }} />
               Archive
             </button>
           </div>
           {showArchive && archiveEnabled && (
             <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: 10 }}>
               <input
                 aria-label="Search archive"
                 placeholder="Search archive by project #, customer, or project name"
                 value={archiveSearch}
                 onChange={(event) => { setArchiveSearch(event.target.value); setArchivePage(1); }}
                 style={{ width: 300, padding: "7px 9px", border: "1px solid #d1d5db", borderRadius: 6 }}
               />
               {archiveSearch && <button type="button" onClick={() => { setArchiveSearch(""); setArchivePage(1); }} style={{ padding: "7px 10px" }}>Clear</button>}
             </div>
           )}
        </div>
      </div>
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm mb-4">
        <div className="flex items-center justify-between px-6 h-16">
          {/* Left: Title + Stats */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <h1 className="text-lg font-semibold text-gray-900">
                {isAggregateProductionRoute(department)
                  ? "Production Planning"
                  : department.charAt(0).toUpperCase() +
                    department.slice(1).replace(/-/g, " ")}
              </h1>

              {/* {planningLoading && (
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-blue-600 font-medium">
                      Syncing...
                    </span>
                  </div>
                )} */}

              {/* Loading Indicators */}
              {isInitialLoading && (
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-blue-600 font-medium">
                    Loading cards...
                  </span>
                </div>
              )}

              {isSyncing && !isInitialLoading && (
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-600 font-medium">
                    Syncing...
                  </span>
                </div>
              )}
            </div>

            {/* Stats Pills */}
            <div className="flex items-center space-x-2">
              <div className="px-3 py-1 bg-slate-100 text-slate-700 text-sm rounded-full font-medium">
                {Object.values(filteredColumns).flat().length} Orders
              </div>

              {getOverdueCount(columns) > 0 && (
                <div className="px-3 py-1 bg-red-50 text-red-700 text-sm rounded-full font-medium">
                  {getOverdueCount(columns)} Overdue
                </div>
              )}

              {(isAggregateProductionRoute(department)) && (
                <div className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full font-medium">
                  {Object.keys(getDepartmentCounts(columns)).length} Depts
                </div>
              )}
            </div>
          </div>

          {/* Right: Filters */}
          <div className="flex items-center space-x-3">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-4 w-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search orders, customers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-80 pl-10 pr-4 py-2.5 text-sm text-gray-900 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-200"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <svg
                    className="h-4 w-4 text-gray-400 hover:text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>

            {/* Priority Filter */}
            <select
              value={quickFilter}
              onChange={(e) => setQuickFilter(e.target.value)}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-200 cursor-pointer"
            >
              <option value="">All Priority</option>
              <option value="overdue">🔴 Overdue</option>
              <option value="today">🟡 Due Today</option>
              <option value="week">🟢 This Week</option>
              <option value="urgent">⚡ Rush</option>
            </select>

            {/* Department Filter - Production Planning Only */}
            {(isAggregateProductionRoute(department)) && (
              <select
                value={selectedDepartment}
                onChange={(e) => {
                  setSelectedDepartment(e.target.value);
                  setArchivePage(1);
                }}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-200 cursor-pointer"
              >
                <option value="">All Departments</option>
                <option value="embroidery">🧵 Embroidery</option>
                <option value="screening">🖨️ Screening</option>
                <option value="direct to film">📽️ DTF</option>
                <option value="cresting">⚡ Cresting</option>
                <option value="engraving">✏️ Engraving</option>
                <option value="awards">🏆 Awards</option>
              </select>
            )}

            {/* More Filters Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`relative p-2.5 rounded-xl font-medium transition-all duration-200 ${
                showFilters
                  ? "bg-blue-100 text-blue-700 shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              title="More filters"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
                />
              </svg>

              {/* Active Filter Indicator */}
              {(machineFilter || dateRange.start || dateRange.end) && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
              )}
            </button>

            {/* Clear All Filters */}
              {(searchQuery ||
                quickFilter ||
                selectedDepartment ||
                machineFilter ||
                orderIdSort !== "asc" ||
                dateRange.start ||
                dateRange.end) && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setAppliedSearchQuery("");
                  setQuickFilter("");
                  setSelectedDepartment("");
                  setMachineFilter("");
                  setOrderIdSort("asc");
                  setDateRange({ start: "", end: "" });
                  setAppliedAdvancedFilters({ machine: "", dateFrom: "", dateTo: "" });
                  setArchivePage(1);
                }}
                className="px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-all duration-200"
              >
                Clear All
              </button>
            )}

            {/* Refresh Button */}
            <button
              className="p-2.5 text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200"
              onClick={handleHardRefresh}
              disabled={isSyncing}
              title="Refresh data"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Expandable Advanced Filters */}
        {showFilters && (
          <div className="border-t border-gray-100 bg-gradient-to-r from-gray-50 to-white">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
                    />
                  </svg>
                  <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Advanced Filters
                  </span>
                </div>

                <button
                  onClick={() => setShowFilters(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Machine Filter */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                    Machine
                  </label>
                  <select
                    value={machineFilter}
                    onChange={(e) => setMachineFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">All Machines</option>
                    {getUniqueMachines(columns)
                      .slice(0, 15)
                      .map((machine) => (
                        <option key={machine} value={machine}>
                          {machine}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Date From */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                    From Date
                  </label>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) =>
                      setDateRange((prev) => ({
                        ...prev,
                        start: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  />
                </div>

                {/* Date To */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                    To Date
                  </label>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) =>
                      setDateRange((prev) => ({ ...prev, end: e.target.value }))
                    }
                    className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  />
                </div>

                {/* Quick Actions */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                    Quick Actions
                  </label>
                  <div className="flex space-x-2">
                    <button
                      onClick={() =>
                        setDateRange({
                          start: new Date().toISOString().split("T")[0],
                          end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                            .toISOString()
                            .split("T")[0],
                        })
                      }
                      className="flex-1 px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                    >
                      Next 7 Days
                    </button>
                    <button
                      onClick={() => {
                        setDateRange({ start: "", end: "" });
                        setAppliedAdvancedFilters((previous) => ({
                          ...previous,
                          dateFrom: "",
                          dateTo: "",
                        }));
                        setArchivePage(1);
                      }}
                      className="flex-1 px-3 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                    >
                      Clear Dates
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                    Archive Order ID Sort
                  </label>
                  <select
                    value={orderIdSort}
                    onChange={(e) => {
                      setOrderIdSort(e.target.value);
                      setArchivePage(1);
                    }}
                    className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="asc">Lowest first</option>
                    <option value="desc">Highest first</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => {
                      setAppliedAdvancedFilters({
                        machine: machineFilter,
                        dateFrom: dateRange.start,
                        dateTo: dateRange.end,
                      });
                      setArchivePage(1);
                    }}
                    className="w-full px-3 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
      <div style={ui.wrap}>
        <style>
          {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin {
            animation: spin 1s linear infinite;
          }
        `}
        </style>

        <style>{customScrollbarStyles}</style>

        {/* <style>{modalStyles}</style> */}

        <style>{premiumModalStyles}</style>

        <style>{customStyles}</style>

        {viewMode === "kanban" ? (
          <div style={ui.board}>
            {boardStatuses.map(({ key, label }) => (
              <section
                key={key}
                style={ui.column}
                onDragOver={key === "Archive" ? undefined : (e) => onListOver(e, key)}
                onDrop={key === "Archive" ? undefined : (e) => onDrop(e, key)}
              >
                <div
                  style={{
                    ...ui.colHeader,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 16px",
                    backgroundColor: "white",
                    borderTop: `4px solid ${getStatusColor(key)}`,
                    marginBottom: "8px",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
                    borderRadius: "4px",
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      color: "#2c3e50",
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  >
                    {label}
                  </h3>

                         


                       {/* TTL BADGE */}
                    {/* {ttlInfo?.[key] && (
                      <span style={{
                        fontSize: '11px',
                        color: '#6b7280',
                        backgroundColor: '#f3f4f6',
                        padding: '3px 8px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                        fontWeight: 500
                      }}>
                        {ttlInfo[key] > 60 
                          ? `${Math.round(ttlInfo[key]/60)}m` 
                          : `${ttlInfo[key]}s`
                        }
                      </span>
                    )} */}


                  {/* Enhanced Badge with Loading Indicator */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        backgroundColor: getStatusColor(key),
                        color: "white",
                        padding: "4px 10px",
                        borderRadius: "12px",
                        fontSize: "11px",
                        fontWeight: "600",
                      }}
                    >
                      {/* {refreshingLists.has(key)
                        ? "Updating..."
                        : filteredColumns[key].length} */}

                        {refreshingLists.has(key)
                      ? "Updating..."
                      : key === "Archive"
                        ? (archiveHasData ? archiveViewState.countLabel : "…")
                        : lanePagination[key]?.total || 0}                     {/* USE API TOTAL */}
                    </span>

                    {/* Loading Indicator */}
                    {refreshingLists.has(key) && (
                      <div
                        style={{
                          width: "6px",
                          height: "6px",
                          backgroundColor: "#3b82f6",
                          borderRadius: "50%",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      ></div>
                    )}
                  </div>
                </div>

                {/* <div
                  ref={listRefs[key]}
                  style={ui.cardList}
                  onScroll={(e) => onScrollCol(key, e)}
                > */}
                {/* 
                <div
                  ref={listRefs[key]}
                  style={{
                    ...ui.cardList,
                    height: "calc(100vh - 240px)",
                    overflowY: "auto",
                    overflowX: "hidden"
                  }}
                  onScroll={(e) => handleLaneScroll(e, key)}   */}
                {/* ← Changed from onScrollCol */}
                {/* > */}
                <ProductionLaneList
                  key={key === "Archive" ? archiveIdentity : `${boardLoadKey}|${quickFilter}`}
                  ref={listRefs[key]}
                  className="lane-scroll-container"
                  style={{ ...ui.cardList, height: "calc(100vh - 240px)", overflowY: "auto", overflowX: "hidden", maxHeight: "1000px" }}
                  cards={filteredColumns[key] || []}
                  page={key === "Archive" ? archivePage : lanePagination[key]?.page || 1}
                  replacePage={key === "Archive"}
                  hasMore={key === "Archive" ? archiveHasData && archivePage * 50 < archiveTotal : lanePagination[key]?.hasMore}
                  loading={key !== "Archive" && !!lanePagination[key]?.loading}
                  error={key !== "Archive" ? lanePagination[key]?.error : null}
                  busyLabel={key === "Archive"
                    ? (archiveIsLoading ? "Loading Archive..." : "")
                    : (refreshingLists.has(key) || laneRequestStates[key]?.status === "loading" ? "Updating lane..." : "")}
                  onLoadMore={key === "Archive"
                    ? () => setArchivePage(archivePage + 1)
                    : () => loadMoreCards(key)}
                >
                  {(visibleCards) => (
                  <>
                    {key !== "Archive" && laneRequestStates[key]?.status === "loading" && (
                      <div
                        role="status"
                        aria-label={`Loading ${label}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          padding: "12px 8px",
                          color: "#64748b",
                          fontSize: "12px",
                        }}
                      >
                        <div
                          className="spinner-border spinner-border-sm text-primary"
                          style={{ width: "16px", height: "16px" }}
                        />
                        Loading {label}...
                      </div>
                    )}
                    {key !== "Archive" && laneRequestStates[key]?.status === "error" && (
                      <div
                        role="alert"
                        style={{
                          margin: "8px",
                          padding: "10px",
                          color: "#991b1b",
                          background: "#fef2f2",
                          border: "1px solid #fecaca",
                          borderRadius: "6px",
                          fontSize: "12px",
                        }}
                      >
                        <div>{laneRequestStates[key].error || `Unable to load ${label}.`}</div>
                        <button
                          type="button"
                          onClick={() => retryLane(key)}
                          style={{
                            marginTop: "8px",
                            padding: "4px 9px",
                            border: "1px solid #fca5a5",
                            borderRadius: "4px",
                            background: "#fff",
                            color: "#991b1b",
                            fontSize: "11px",
                            fontWeight: 600,
                          }}
                        >
                          Retry
                        </button>
                      </div>
                    )}
                    {key !== "Archive" && laneRequestStates[key]?.status === "loading" && !(filteredColumns[key] || []).length && (
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "8px" }}>
                        {[1, 2].map((i) => (
                          <div
                            key={i}
                            style={{
                              backgroundColor: "#e5e7eb",
                              borderRadius: "8px",
                              height: "88px",
                              animation: "pulse 1.5s ease-in-out infinite",
                            }}
                          />
                        ))}
                      </div>
                    )}
                    <>
                      {/* {(columns[key] || []).map((c, idx) => { */}
                      {visibleCards.map((c, idx) => {
                        const isArchiveCard = key === "Archive";
                        const pending = pendingMutations.find(record => record === pendingMutationStoreRef.current.find(c));
                        const scheduling = isSchedulingCard(c) || pendingSchedules.has(`${boardScopeRef.current}|${getProductionScheduleKey(c)}`);
                        const completing = pending?.kind === "complete" && pending.phase !== "failed";
                        const actionsBlocked = blockCardMutation(c) || scheduling || (key !== "Planned" && key !== "Archive" && !getProductionStepId(c));
                        const capKey =
                          c.machine && c.plannedStart
                            ? `${c.machine}::${c.plannedStart}`
                            : null;
                        const cap = capKey ? capacityMap[capKey] : null;
                        const percent = cap
                          ? Math.round((cap.used / Math.max(1, cap.cap)) * 100)
                          : null;
                        const over = percent != null && percent > 100;
                        const showBatchBadge =
                          siblingsAcrossAllLanes(c, filteredColumns).length > 1;

                        // ✅ Determine card styling class
                        const isFullyCompleted =
                          c.status === "Completed" && c.isFullyCompleted;
                        const isPartiallyCompleted =
                          c.status === "Completed" && !c.isFullyCompleted;

                        return (
                          <article
                            key={c.id}
                            data-index={idx}
                            style={{
                              ...ui.card,
                              overflow: 'hidden',
                              ...(c.cancelled ? ui.cardCancelled : null),
                              // }}
                              //  Premium styling for fully completed
                              // ...(isFullyCompleted
                              //   ? {
                              //       borderLeft: "3px solid #10b981",
                              //       background:
                              //         "linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)",
                              //       opacity: 0.95,
                              //       boxShadow:
                              //         "0 1px 3px rgba(16, 185, 129, 0.12)",
                              //     }
                              //   : null),

                                   // Fully Completed — green (same pattern)
...(isFullyCompleted ? {
  background: "#f0fdf4",              // soft green
  borderTop: "3px solid #10b981",    // green
  opacity: 0.95,
  boxShadow: "0 1px 3px rgba(16, 185, 129, 0.12)",
} : null),
                              // Subtle styling for partially completed
                              // ...(isPartiallyCompleted
                              //   ? {
                              //       borderLeft: "3px solid #d1d5db",
                              //       opacity: 0.92,
                              //     }
                              //   : null),

                                                 // Partially Completed — grey
...(isPartiallyCompleted ? {
  borderTop: "3px solid #d1d5db",   
  opacity: 0.92,
} : null),



                                  // paused card tint only when not completed
                    ...(!isFullyCompleted && !isPartiallyCompleted && c.isPaused
                      ? {
                          background: "#fffdf7",
                        }
                      : null),



  ...(!isFullyCompleted && !isPartiallyCompleted && c.isPaused ? {
    background: "#fffdf7",
    borderTop: "3px solid #f59e0b",  // orange line on the article itself
  } : null),

       cursor: actionsBlocked || !canDragProductionCard(c) ? "not-allowed" : "grab",

                            }}
                            // draggable={!c.cancelled}
                            // onDragStart={(e) => onDragStart(e, c, key, idx)}
                            // onDragEnter={(e) => onDragEnterCard(e, key, idx)}

                            aria-busy={scheduling || completing}
                            onClickCapture={(event) => {
                              if (blockCardMutation(c) && !event.target.closest("[data-mutation-recovery]") && event.target.closest("button,a,input,select,[role='button']")) {
                                event.preventDefault(); event.stopPropagation();
                              }
                            }}
                            draggable={!actionsBlocked && key !== "Archive" && canDragProductionCard(c)}
  onDragStart={(e) => key !== "Archive" && onDragStart(e, c, key, idx)}
  onDragEnter={(e) => key !== "Archive" && canDragProductionCard(c) && onDragEnterCard(e, key, idx)}
  onDragEnd={() => { dragRef.current.cardId = null; dragRef.current.lastDragEndAt = Date.now(); }}
  onClick={(e) => {
    if (dragRef.current.cardId === c.id || Date.now() - dragRef.current.lastDragEndAt < 500) { dragRef.current.cardId = null; return; }
    if (e.target.closest("button,a,input,select,textarea,[role='button']")) return;
    openCard(c);
  }}
                          >
                            {scheduling && <div role="status">Scheduling… Saving step and work order.</div>}
                            {completing && <div role="status">Completing… Awaiting saved Production status.</div>}
                            {pending?.error && <div role="alert">{pending.error}</div>}
                            {pending?.phase === "uncertain" && <button data-mutation-recovery disabled={pending.checking}
                              onClick={(event) => { event.stopPropagation(); void reconcilePendingMutation(pending); }}>
                              {pending.checking ? "Checking saved status…" : "Check saved status"}
                            </button>}
                            {pending?.phase === "failed" && <button data-mutation-recovery onClick={(event) => {
                              event.stopPropagation();
                              if (pending.kind === "complete") void handleMarkAsComplete(c);
                              else setMoveModal({ open: true, to: "Scheduled", card: c, fields: pending.retryFields || {} });
                            }}>Retry {pending.kind === "complete" ? "completion" : "schedule"}</button>}
                            <fieldset disabled={actionsBlocked} style={{ display: "contents" }}>
                            {/* Fully Completed Checkmark Badge (Top-Right Corner) */}
                            {/* {c.status === 'Completed' && c.isFullyCompleted && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    width: '22px',
                                    height: '22px',
                                    backgroundColor: '#10b981',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 6px rgba(16, 185, 129, 0.3)',
                                    zIndex: 10,
                                  }}
                                  title="Completed in all departments"
                                >
                                  <svg width="13" height="13" viewBox="0 0 16 16" fill="white">
                                    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                                  </svg>
                                </div>
                              )} */}
                            {/*      
                            {c.isPaused && !isFullyCompleted && (
                              <div
                                className="position-absolute top-0 end-0 m-2"
                                style={{
                                  padding: '2px 7px',
                                  background: '#fef3c7',
                                  color: '#92400e',
                                  border: '1px solid #fde68a',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  letterSpacing: '0.5px',
                                  textTransform: 'uppercase',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '3px',
                                  zIndex: 10,
                                }}
                              >
                                <i className="bi bi-pause-fill" style={{ fontSize: '9px' }} />
                                Paused
                              </div>
                            )} */}
                            {c.isPaused && (
                            <div style={{
                              height: '3px',
                              background: '#f59e0b',
                              borderRadius: '6px 6px 0 0',
                              margin: '-12px -12px 8px -12px',
                              transition: 'background 0.3s ease',
                            }} />
                          )}
                            {/* Checkmark Badge (Bootstrap positioned) */}
                            {isFullyCompleted && (
                              <div
                                className="position-absolute top-0 end-0 m-2"
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  backgroundColor: "#10b981",
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow:
                                    "0 2px 4px rgba(16, 185, 129, 0.4)",
                                  zIndex: 10,
                                }}
                                title="✓ Completed in all departments"
                              >
                                <svg
                                  width="11"
                                  height="11"
                                  viewBox="0 0 16 16"
                                  fill="white"
                                >
                                  <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                                </svg>
                              </div>
                            )}
                            {key === "On_Hold" && (
                              <>
                                {/* {c.holdReason && (
                                          <div style={{ 
                                            fontSize: 12, 
                                            color: '#dc2626', 
                                            backgroundColor: '#fee2e2', 
                                            padding: '4px 8px', 
                                            borderRadius: 4, 
                                            marginTop: 6,
                                            marginBottom: 4,
                                            fontWeight: 500
                                          }}>
                                            <strong>Reason:</strong> {c.holdReason}
                                          </div>
                                        )} */}
                                {c.holdFrom && (
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: "#2563eb",
                                      backgroundColor: "#dbeafe",
                                      padding: "4px 8px",
                                      borderRadius: 4,
                                      marginBottom: 4,
                                      fontWeight: 500,
                                    }}
                                  >
                                    <strong>From:</strong> {c.holdFrom}
                                    {/* {c.lastMain.replace(/([A-Z])/g, ' $1').trim()} */}
                                  </div>
                                )}
                                {c.holdReason && (
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: "#dc2626",
                                      backgroundColor: "#fee2e2",
                                      padding: "4px 8px",
                                      borderRadius: 4,
                                      marginTop: 6,
                                      marginBottom: 4,
                                      fontWeight: 500,
                                    }}
                                  >
                                    <strong>Reason:</strong> {c.holdReason}
                                  </div>
                                )}
                              </>
                            )}
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: 8,
                                // flexWrap: "wrap",
                                flexWrap: "nowrap",
                              }}
                            >
                              {/* <div>
                                <div style={{ fontWeight: 700 }}>
                                  {c.orderNumber || c.title || "—"}{" "} */}

                              <div style={{ flex: 1 }}>
                                {/* Order Number with Badges */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    marginBottom: 6,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 16,
                                      fontWeight: 700,
                                      color: "#1a73e8",
                                      letterSpacing: "-0.02em",
                                    }}
                                  >
                                    #{c.orderNumber || c.title || "—"} -{" "}
                                    {c.step_id}
                                    {shouldShowFirmBadge(c) && (
                                      <span
                                        style={{
                                          fontSize: 10,
                                          fontWeight: 700,
                                          color: "#dc2626",
                                          letterSpacing: "0.04em",
                                        }}
                                      >
                                        {" "}FIRM
                                      </span>
                                    )}
                                  </span>

                                  {/* {c.batch_label && (
                                          <span style={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: '#fff',
                                            fontSize: 11,
                                            padding: '3px 10px',
                                            borderRadius: 12,
                                            fontWeight: 700,
                                            letterSpacing: '0.5px',
                                            boxShadow: '0 2px 4px rgba(102, 126, 234, 0.4)'
                                          }}>
                                            {c.batch_label}
                                          </span>
                                          
                                        )} */}

                                  {normalizeProductionBatchFields(c).batchLabel &&
                                    (() => {
                                      const normalizedBatch = normalizeProductionBatchFields(c);
                                      const color = getBatchColor(
                                        normalizedBatch.batchGroupId
                                      );
                                      const isOriginal = normalizedBatch.batchIndex === 1;

                                      return (
                                        <span
                                          style={{
                                            background: isOriginal
                                              ? `linear-gradient(135deg, ${color.bg}15, ${color.bg}25)`
                                              : `${color.bg}12`,
                                            border: `1.5px solid ${color.bg}`,
                                            color: color.bg,
                                            fontSize: 11,
                                            padding: "3px 9px",
                                            borderRadius: 8,
                                            fontWeight: 700,
                                            letterSpacing: "0.4px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 4,
                                          }}
                                        >
                                          {isOriginal && (
                                            <svg
                                              width="10"
                                              height="10"
                                              viewBox="0 0 10 10"
                                              style={{ marginRight: 1 }}
                                            >
                                              <circle
                                                cx="5"
                                                cy="5"
                                                r="4"
                                                fill={color.bg}
                                                opacity="0.9"
                                              />
                                            </svg>
                                          )}
                                          {normalizedBatch.batchLabel}
                                        </span>
                                      );
                                    })()}

                                  {key === "Scheduled" &&
                                    !c.startedAt &&
                                    c.plannedStart &&
                                    daysLate(c.plannedStart) > 0 && (
                                      <span style={ui.overduePill}>
                                        Overdue {daysLate(c.plannedStart)}d
                                      </span>
                                    )}
                                  {showBatchBadge && (
                                    <span
                                      style={{ ...ui.badge, marginLeft: 6 }}
                                    >
                                      Batch
                                    </span>
                                  )}
                                </div>

                                {/* PREMIUM: "All Departments Done" Badge */}
                                {isFullyCompleted && (
                                  <div className="mb-2">
                                    <span
                                      className="badge d-inline-flex align-items-center gap-1"
                                      style={{
                                        padding: "4px 8px",
                                        backgroundColor: "#ecfdf5",
                                        border: "1px solid #a7f3d0",
                                        color: "#065f46",
                                        fontSize: "9px",
                                        fontWeight: 700,
                                        letterSpacing: "0.5px",
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      <svg
                                        width="9"
                                        height="9"
                                        viewBox="0 0 16 16"
                                        fill="currentColor"
                                      >
                                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                                      </svg>
                                      All Depts Done
                                    </span>
                                  </div>
                                )}

                                {/* <div
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "#333",
                                    marginBottom: 2,
                                  }}
                                >
                                  {c.projectName || ""}
                                </div> */}

                                <div
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: "#111827",
                                    marginBottom: 4,
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {c.projectName || "No Project Name"}
                                </div>

                                {/* 
                                <div style={{ fontSize: 12, color: "#555" }}>
                                  {c.customer || ""}
                                </div> */}

                                {/* Customer */}
                                <div
                                  style={{
                                    fontSize: 13,
                                    color: "#6b7280",
                                    marginBottom: 4,
                                    fontWeight: 500,
                                  }}
                                >
                                  <i
                                    className="bi bi-person-circle"
                                    style={{ marginRight: 4 }}
                                  ></i>
                                  {c.customer || "Unknown Customer"}
                                </div>

                                <div
                                  title={formatFullBin(c.binNumber)}
                                  style={{
                                    fontSize: 12,
                                    color: "#6b7280",
                                    marginBottom: 4,
                                    fontWeight: 500,
                                    maxWidth: "100%",
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <i
                                    className="bi bi-box"
                                    style={{ marginRight: 4 }}
                                  ></i>
                                  Bin: {formatCompactBin(c.binNumber)}
                                </div>

                                {/* <div style={{ fontSize: 12, color: "#667" }}>
                              
                                  {c.productName || ""}{" "}
                                  <span style={{ color: "#999" }}>
                                    ({c.sku || ""})
                                  </span>
                                </div> */}

                                <div
                                  style={{
                                    fontSize: 12,
                                    color: "#9ca3af",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {c.productName && (
                                    <span style={{ fontWeight: 500 }}>
                                      {c.productName}
                                    </span>
                                  )}
                                  {/* {c.sku && (
          <span style={{
            background: "#f3f4f6",
            color: "#6b7280",
            padding: "2px 6px",
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 500,
            border: "1px solid #e5e7eb"
          }}>
            SKU: {c.sku}
          </span>
        )} */}

                                  <span style={{ color: "#999", gap: 2 }}>
                                    ({c.sku || ""})
                                  </span>
                                </div>
                              </div>

                              {/* <span style={ui.badge} title={`Rank ${c.rank}`}>
                                {c.cancelled
                                  ? "Cancelled"
                                  : c.claimedDept
                                  ? `${c.claimedDept} • Planned`
                                  : c.department || key.replace(/_/g, " ")}
                              </span> */}


                              <span
  style={{
    ...ui.badge,
    ...(c.isPaused && !c.cancelled ? {
      background: '#fef3c7',
      color: '#92400e',
      border: '1px solid #fde68a',
    } : {}),
  }}
  title={`Rank ${c.rank}`}
>
  {formatDepartmentBadge(c, key)}
</span>
                            </div>
                            {/* ADD ORDER STATUS HERE */}
                            <div
                              style={{
                                fontSize: 11,
                                marginBottom: 3,
                                display: "flex",
                                flexWrap: "wrap",
                                // flexWrap: "nowrap",
                                gap: 4,
                              }}
                            >
                              {/* Sales Order Status */}
                              <span
                                style={{
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  fontSize: 10,
                                  fontWeight: 500,
                                  backgroundColor:
                                    c.salesOrderStatus === "Sales Order Ready"
                                      ? "#dcfce7"
                                      : "#f3f4f6",
                                  color:
                                    c.salesOrderStatus === "Sales Order Ready"
                                      ? "#166534"
                                      : "#6b7280",
                                  border: "1px solid",
                                  borderColor:
                                    c.salesOrderStatus === "Sales Order Ready"
                                      ? "#bbf7d0"
                                      : "#e5e7eb",
                                }}
                              >
                                SO: {c.salesOrderStatus || "–"}
                              </span>

                              {/* Purchase Order Status */}
                              {/* <span
                              style={{
                                padding: "2px 6px",
                                borderRadius: 4,
                                fontSize: 10,
                                fontWeight: 500,
                                backgroundColor:
                                  c.purchaseOrderStatus === "Supplier Confirmed"
                                    ? "#dbeafe"
                                    : "#f3f4f6",
                                color:
                                  c.purchaseOrderStatus === "Supplier Confirmed"
                                    ? "#1d4ed8"
                                    : "#6b7280",
                                border: "1px solid",
                                borderColor:
                                  c.purchaseOrderStatus === "Supplier Confirmed"
                                    ? "#bfdbfe"
                                    : "#e5e7eb",
                              }}
                            >
                              PO: {c.purchaseOrderStatus || "–"}
                            </span> */}

                              {/* <span
                                style={{
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  fontSize: 10,
                                  fontWeight: 500,
                                  // ✅ UPDATE THESE COLORS
                                  backgroundColor:
                                    c.purchaseOrderStatus ===
                                    "Confirmed By Shipping"
                                      ? "#dcfce7" // Green background
                                      : "#f3f4f6", // Gray background
                                  color:
                                    c.purchaseOrderStatus ===
                                    "Confirmed By Shipping"
                                      ? "#166534" // Dark green text
                                      : "#6b7280", // Gray text
                                  border: "1px solid",
                                  borderColor:
                                    c.purchaseOrderStatus ===
                                    "Confirmed By Shipping"
                                      ? "#bbf7d0" // Green border
                                      : "#e5e7eb", // Gray border
                                }}
                              >
                                PO: {c.purchaseOrderStatus || "Pending"}
                              </span> */}
                              <span
                                style={{
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  fontSize: "10px",
                                  fontWeight: "500",
                                  backgroundColor:
                                    c.purchaseOrderStatus === "Shipped"
                                      ? "#dcfce7"
                                      : "#f3f4f6",
                                  color:
                                    c.purchaseOrderStatus === "Shipped"
                                      ? "#166534"
                                      : "#6b7280",
                                  border: "1px solid",
                                  borderColor:
                                    c.purchaseOrderStatus === "Shipped"
                                      ? "#bbf7d0"
                                      : "#e5e7eb",
                                }}
                              >
                                PO: {c.purchaseOrderStatus}
                              </span>
                              {(() => {
                                const proofingBadge = getProofingBadge(c);
                                if (!proofingBadge) return null;
                                return (
                                  <span
                                    title={proofingBadge.tooltip}
                                    style={{
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                      fontSize: "10px",
                                      fontWeight: "500",
                                      backgroundColor: proofingBadge.approved
                                        ? "#dcfce7"
                                        : proofingBadge.rejected
                                        ? "#fee2e2"
                                        : "#fef3c7",
                                      color: proofingBadge.approved
                                        ? "#166534"
                                        : proofingBadge.rejected
                                        ? "#991b1b"
                                        : "#92400e",
                                      border: "1px solid",
                                      borderColor: proofingBadge.approved
                                        ? "#bbf7d0"
                                        : proofingBadge.rejected
                                        ? "#fca5a5"
                                        : "#fde68a",
                                    }}
                                  >
                                    {proofingBadge.label}
                                  </span>
                                );
                              })()}
                            </div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(3,1fr)",
                                gap: 8,
                                marginTop: 6,
                                fontSize: 12,
                              }}
                            >
                              <div>
                                <div style={ui.subtle}>Qty</div>
                                <div style={{ fontWeight: 600 }}>
                                  {c.qtyPlanned ?? 0}
                                </div>
                              </div>
                              <div>
                                <div style={ui.subtle}>Location</div>
                                <div style={{ fontWeight: 600 }}>
                                  {c.location || "–"}
                                </div>
                              </div>
                              <div>
                                <div style={ui.subtle}>Matrix</div>
                                <div style={{ fontWeight: 600 }}>
                                  {c.matrix || "–"}
                                </div>
                              </div>
                              {key !== "Planned" && (
                                <div style={{ gridColumn: "1/-1" }}>
                                  <div style={ui.subtle}>Scheduled Date</div>
                                  <div style={{ fontWeight: 600 }}>
                                    {c.plannedStart
                                      ? DATE_FMT(c.plannedStart)
                                      : "–"}
                                  </div>
                                </div>
                              )}
                              <div style={{ gridColumn: "1/-1" }}>
                                <div style={ui.subtle}>Decorator PO In-Hands</div>
                                <div style={{ fontWeight: 600 }}>
                                  {formatProductionBusinessDate(c.decoratorPoInHandsDate)}
                                </div>
                              </div>
                            </div>
                            {/* Capacity meter (Scheduled only) */}
                            {key === "Scheduled" && cap && (
                              <div style={{ marginTop: 8 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    fontSize: 12,
                                    marginBottom: 4,
                                  }}
                                >
                                  <span>
                                    Machine load • {c.machine} •{" "}
                                    {DATE_FMT(c.plannedStart)}
                                  </span>
                                  <span>
                                    {cap.used}/{cap.cap} min
                                    {over ? " • High" : ""}
                                  </span>
                                </div>
                                <div style={capStyles.track}>
                                  <div
                                    style={{
                                      ...capStyles.fill,
                                      width: `${Math.min(100, percent)}%`,
                                      background: over ? "#fca5a5" : "#86efac",
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                            <div
                              style={{
                                marginTop: 8,
                                display: "flex",
                                gap: 6,
                                alignItems: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              <button
                                // style={ui.linkBtn}
                                style={{
                                  ...ui.linkBtn,
                                  backgroundColor: getButtonColors(
                                    "open",
                                    "primary"
                                  ).bg,
                                  color: getButtonColors("open", "primary")
                                    .text,
                                }}
                                 onClick={(event) => { event.stopPropagation(); openCard(c); }}
                              >
                                Open
                              </button>

                              {key === "On_Hold" &&
                                c.status === "On_Hold" &&
                                (c.holdReason || c.holdFrom) && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleUnholdProduction(c);
                                    }}
                                    style={{
                                      flex: 1,
                                      padding: "6px 8px",
                                      background: "#16a34a",
                                      color: "#ffffff",
                                      border: "1.5px solid #16a34a",
                                      borderRadius: "5px",
                                      fontSize: "11.5px",
                                      fontWeight: 600,
                                      cursor: "pointer",
                                      transition: "all 0.15s ease",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "4px",
                                      letterSpacing: "0.2px",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = "#15803d";
                                      e.currentTarget.style.borderColor = "#15803d";
                                      e.currentTarget.style.transform = "translateY(-1px)";
                                      e.currentTarget.style.boxShadow =
                                        "0 2px 8px rgba(22, 163, 74, 0.25)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = "#16a34a";
                                      e.currentTarget.style.borderColor = "#16a34a";
                                      e.currentTarget.style.transform = "translateY(0)";
                                      e.currentTarget.style.boxShadow = "none";
                                    }}
                                  >
                                    <i className="bi bi-play-fill" style={{ fontSize: "12px" }} />
                                    Unhold
                                  </button>
                                )}

                              {key === "Scheduled" && !c.cancelled && (
                                <>
                                  
                                  <button
                                    style={{
                                      ...ui.linkBtn,
                                      backgroundColor: getButtonColors(
                                        "start-now",
                                        "primary"
                                      ).bg,
                                      color: getButtonColors(
                                        "start-now",
                                        "primary"
                                      ).text,
                                    }}
                                    onClick={async () => {
                                      // Use Swal directly for custom message
                                      const result = await window.Swal.fire({
                                        title: "Start Production?",
                                        text: "This will move the job to In Production and begin work.",
                                        icon: "question",
                                        showCancelButton: true,
                                        confirmButtonText: "Start Now",
                                        cancelButtonText: "Cancel",
                                        confirmButtonColor: "#10b981",
                                      });

                                      if (!result.isConfirmed) return; // User cancelled

                                      // Your existing optimistic update logic
                                      const rollback = beginCardRollback(c);
                                      if (!rollback) return;
                                      setColumns((prev) => {
                                        const next = clone(prev);
                                        const i = next.Scheduled.findIndex(
                                          (x) => x.id === c.id
                                        );
                                        if (i === -1) return prev;
                                        const [card] = next.Scheduled.splice(
                                          i,
                                          1
                                        );
                                        card.status = "In_Production";
                                        card.startedAt = todayYMD();
                                        card.lifecycle = [
                                          ...(card.lifecycle || []),
                                          {
                                            when: new Date().toLocaleString(),
                                            text: "Started production",
                                          },
                                        ];
                                        const inserted = assignRankTop(
                                          next.In_Production,
                                          card
                                        );
                                        next.In_Production.unshift(inserted);
                                        if (inserted.isStep)
                                          // queueRankPatch(
                                          //   inserted.id,
                                          //   inserted.rank
                                          // );
                                          return next;
                                      }, rollback);

                                      if (c.isStep) {
                                        try {
                                          await startProduction(c, rollback);
                                          SA.success(
                                            "Production started!",
                                            "Job moved to In Production"
                                          );
                                        } catch (e) {
                                          rollbackCardMutation(rollback);
                                          SA.error(
                                            e.message || "Could not start job.",
                                            "Save failed"
                                          );
                                        }
                                      }
                                    }}
                                  >
                                    Start Now
                                  </button>

                                  <button
                                    // style={ui.linkBtn}
                                    style={{
                                      ...ui.linkBtn,
                                      backgroundColor: getButtonColors(
                                        "reschedule",
                                        "secondary"
                                      ).bg,
                                      color: getButtonColors(
                                        "reschedule",
                                        "secondary"
                                      ).text,
                                    }}
                                    onClick={async () => {
                                      const reason =
                                        window.prompt(
                                          "Reschedule to today (optional note):",
                                          ""
                                        ) || "";
                                      const rollback = beginCardRollback(c);
                                      if (!rollback) return;
                                      setColumns((prev) => {
                                        const next = clone(prev);
                                        const i = next.Scheduled.findIndex(
                                          (x) => x.id === c.id
                                        );
                                        if (i === -1) return prev;
                                        const card = next.Scheduled.splice(
                                          i,
                                          1
                                        )[0]; // Remove from Scheduled
                                        card.status = "In_Production"; // Update status
                                        card.startedAt = todayYMD();
                                        card.plannedStart = todayYMD();
                                        card.plannedFinish = todayYMD();
                                        card.lifecycle = [
                                          ...(card.lifecycle || []),
                                          {
                                            when: new Date().toLocaleString(),
                                            text: `Rescheduled to today and started${
                                              reason ? ` • ${reason}` : ""
                                            }`,
                                          },
                                        ];
                                        next.In_Production.unshift(
                                          assignRankTop(
                                            next.In_Production,
                                            card
                                          )
                                        ); // Add to In_Production

                                        return next;
                                      }, rollback);
                                      if (c.isStep) {
                                        try {
                                          // await patchStatus(c.id, {
                                          await patchStatus(c, {
                                            fromStatus: "Scheduled",
                                            // toStatus: "Scheduled",
                                            toStatus: "In_Production",
                                            plannedStart: toISOStart(
                                              todayYMD()
                                            ),
                                            plannedFinish: toISOEnd(todayYMD()),
                                            reason: reason || "",
                                          }, rollback);

                                          // ✅ ADD THIS - Refresh affected lanes immediately
                                          await refreshSpecificLists([
                                            "Scheduled",
                                            "In_Production",
                                          ]);

                                          SA.success(
                                            "Rescheduled to today.",
                                            "Updated"
                                          );
                                        } catch (e2) {
                                          rollbackCardMutation(rollback);
                                          SA.error(
                                            e2.message ||
                                              "Could not reschedule.",
                                            "Save failed"
                                          );
                                        }
                                      }
                                    }}
                                  >
                                    Reschedule to today
                                  </button>

                                  <button
                                    // style={ui.linkBtn}
                                    style={{
                                      ...ui.linkBtn,
                                      backgroundColor: getButtonColors(
                                        "split",
                                        "secondary"
                                      ).bg,
                                      color: getButtonColors(
                                        "split",
                                        "secondary"
                                      ).text,
                                    }}
                                    onClick={() =>
                                      setSplitUI({ open: true, card: c })
                                    }
                                  >
                                    Split…
                                  </button>
                                </>
                              )}

                              {key === "In_Production" && !c.cancelled && (
                                <button
                                  // style={ui.linkBtn}
                                  style={{
                                    ...ui.linkBtn,
                                    backgroundColor: getButtonColors(
                                      "partial",
                                      "secondary"
                                    ).bg,
                                    color: getButtonColors(
                                      "partial",
                                      "secondary"
                                    ).text,
                                  }}
                                  onClick={() =>
                                    setPartialMove({
                                      open: true,
                                      from: "In_Production",
                                      card: c,
                                      defaultTo: "QC",
                                    })
                                  }
                                >
                                  Partial…
                                </button>
                              )}

                              {!isArchiveCard && <div
                                style={{
                                  marginLeft: "auto",
                                  display: "flex",
                                  gap: 6,
                                }}
                              >
                                <button
                                  title="Move up"
                                  style={{
                                    ...ui.iconBtn,
                                    backgroundColor: getButtonColors(
                                      "move-up",
                                      "secondary"
                                    ).bg,
                                    color: getButtonColors(
                                      "move-up",
                                      "secondary"
                                    ).text,
                                  }}
                                  onClick={() => reorder("up", key, c.id)}
                                >
                                  ↑
                                </button>
                                <button
                                  title="Move down"
                                  style={{
                                    ...ui.iconBtn,
                                    backgroundColor: getButtonColors(
                                      "move-down",
                                      "secondary"
                                    ).bg,
                                    color: getButtonColors(
                                      "move-down",
                                      "secondary"
                                    ).text,
                                  }}
                                  onClick={() => reorder("down", key, c.id)}
                                >
                                  ↓
                                </button>
                              </div>}

                              {/* Runtime timer only for In_Production */}
{c.status === 'In_Production' && c.runtimeHHMM && c.runtimeHHMM !== '00:00' && (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '8px',
    paddingTop: '6px',
    borderTop: '1px solid #f3f4f6',
  }}>
    <span style={{
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '11px',
      color: c.isPaused ? '#d97706' : '#9ca3af',
      fontFamily: 'monospace',
      fontWeight: 600,
    }}>
      <i className="bi bi-clock" style={{ fontSize: '10px' }} />
      {c.runtimeHHMM}
      {c.pauseMinutes > 0 && (
        <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: '10px', fontFamily: 'inherit' }}>
          &nbsp;({c.pauseMinutes}m paused)
        </span>
      )}
    </span>

    {c.isPaused && (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        padding: '2px 7px',
        background: '#fef3c7',
        color: '#92400e',
        border: '1px solid #fde68a',
        borderRadius: '4px',
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
      }}>
        <i className="bi bi-pause-circle" style={{ fontSize: '9px' }} />
        &nbsp;Paused
      </span>
    )}
  </div>
)}


                              {/* Premium Action Buttons - Only for In Production */}
                              {key === "In_Production" && !c.cancelled && (
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    marginTop: "10px",
                                    width: "100%",
                                  }}
                                >
                                  {/* Complete Button */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMarkAsComplete(c);
                                    }}
                                    style={{
                                      flex: 1,
                                      padding: "6px 8px",
                                      // background: "#ffffff",
                                      // color: "#059669",
                                      background: "#059669",
                                      color: "#ffffff",
                                      border: "1.5px solid #059669",
                                      borderRadius: "5px",
                                      fontSize: "11.5px",
                                      fontWeight: 600,
                                      cursor: "pointer",
                                      transition: "all 0.15s ease",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "4px",
                                      letterSpacing: "0.2px",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background =
                                        "#047857";
                                      e.currentTarget.style.borderColor =
                                        "#047857";
                                      e.currentTarget.style.color = "#ffffff";
                                      e.currentTarget.style.transform =
                                        "translateY(-1px)";
                                      e.currentTarget.style.boxShadow =
                                        "0 2px 8px rgba(5, 150, 105, 0.25)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background =
                                        "#059669";
                                      e.currentTarget.style.borderColor =
                                        "#059669";
                                      e.currentTarget.style.color = "#ffffff";
                                      e.currentTarget.style.transform =
                                        "translateY(0)";
                                      e.currentTarget.style.boxShadow = "none";
                                    }}
                                  >
                                    <svg
                                      width="13"
                                      height="13"
                                      viewBox="0 0 16 16"
                                      fill="currentColor"
                                    >
                                      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                                    </svg>
                                    <span>Complete</span>
                                  </button>

                                  {/* Pause Button */}
                                  {/* <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePauseProduction(c);
                                    }}
                                    style={{
                                      flex: 1,
                                      padding: "6px 8px",
                                      background: "#f59e0b",
                                      color: "#ffffff",
                                      border: "1.5px solid #f59e0b",
                                      borderRadius: "5px",
                                      fontSize: "11.5px",
                                      fontWeight: 600,
                                      cursor: "pointer",
                                      transition: "all 0.15s ease",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "4px",
                                      letterSpacing: "0.2px",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background =
                                        "#d97706";
                                      e.currentTarget.style.borderColor =
                                        "#d97706";
                                      e.currentTarget.style.color = "#ffffff";
                                      e.currentTarget.style.transform =
                                        "translateY(-1px)";
                                      e.currentTarget.style.boxShadow =
                                        "0 2px 8px rgba(245, 158, 11, 0.25)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background =
                                        "#f59e0b";
                                      e.currentTarget.style.borderColor =
                                        "#f59e0b";
                                      e.currentTarget.style.color = "#ffffff";
                                      e.currentTarget.style.transform =
                                        "translateY(0)";
                                      e.currentTarget.style.boxShadow = "none";
                                    }}
                                  >
                                    <svg
                                      width="13"
                                      height="13"
                                      viewBox="0 0 16 16"
                                      fill="currentColor"
                                    >
                                      <path d="M5.5 3.5A1.5 1.5 0 0 1 7 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5zm5 0A1.5 1.5 0 0 1 12 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5z" />
                                    </svg>
                                    <span>Pause</span>
                                  </button> */}



     

<button
  onClick={e => { e.stopPropagation(); handlePauseProduction(c); }}
  style={{
    flex: 1,
    padding: '6px 8px',
    // Resume → blue,  Pause → amber
    background: c.isPaused ? '#2563eb' : '#f59e0b',
    color: '#ffffff',
    border: `1.5px solid ${c.isPaused ? '#2563eb' : '#f59e0b'}`,
    borderRadius: '5px',
    fontSize: '11.5px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    letterSpacing: '0.2px',
  }}
  onMouseEnter={e => {
    e.currentTarget.style.background = c.isPaused ? '#1d4ed8' : '#d97706';
    e.currentTarget.style.borderColor = c.isPaused ? '#1d4ed8' : '#d97706';
    e.currentTarget.style.transform = 'translateY(-1px)';
    e.currentTarget.style.boxShadow = c.isPaused
      ? '0 2px 8px rgba(37, 99, 235, 0.25)'
      : '0 2px 8px rgba(245, 158, 11, 0.25)';
  }}
  onMouseLeave={e => {
    e.currentTarget.style.background = c.isPaused ? '#2563eb' : '#f59e0b';
    e.currentTarget.style.borderColor = c.isPaused ? '#2563eb' : '#f59e0b';
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = 'none';
  }}
>
  <i
    className={`bi ${c.isPaused ? 'bi-play-fill' : 'bi-pause-fill'}`}
    style={{ fontSize: '12px' }}
  />
  {c.isPaused ? 'Resume' : 'Pause'}
</button>


                                  {/* Hold Button */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePutOnHold(c);
                                    }}
                                    style={{
                                      flex: 1,
                                      padding: "6px 8px",
                                      background: "#8b5cf6",
                                      color: "#ffffff",
                                      border: "1.5px solid #8b5cf6",
                                      borderRadius: "5px",
                                      fontSize: "11.5px",
                                      fontWeight: 600,
                                      cursor: "pointer",
                                      transition: "all 0.15s ease",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "4px",
                                      letterSpacing: "0.2px",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background =
                                        "#7c3aed";
                                      e.currentTarget.style.borderColor =
                                        "#7c3aed";
                                      e.currentTarget.style.color = "#ffffff";
                                      e.currentTarget.style.transform =
                                        "translateY(-1px)";
                                      e.currentTarget.style.boxShadow =
                                        "0 2px 8px rgba(139, 92, 246, 0.25)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background =
                                        "#8b5cf6";
                                      e.currentTarget.style.borderColor =
                                        "#8b5cf6";
                                      e.currentTarget.style.color = "#ffffff";
                                      e.currentTarget.style.transform =
                                        "translateY(0)";
                                      e.currentTarget.style.boxShadow = "none";
                                    }}
                                  >
                                    {/* <svg
                                      width="13"
                                      height="13"
                                      viewBox="0 0 16 16"
                                      fill="currentColor"
                                    >
                                      <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z" />
                                    </svg> */}
                                    {/* Clock icon instead of cross */}
                                    <svg
                                      width="13"
                                      height="13"
                                      viewBox="0 0 16 16"
                                      fill="currentColor"
                                      style={{ flexShrink: 0 }}
                                    >
                                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm8-7A8 8 0 1 0 0 8a8 8 0 0 0 16 0z" />
                                      <path d="M7.5 3a.5.5 0 0 1 .5.5v5.21l3.248 1.856a.5.5 0 0 1-.496.868l-3.5-2A.5.5 0 0 1 7 9V3.5a.5.5 0 0 1 .5-.5z" />
                                    </svg>
                                    <span>Hold</span>
                                  </button>
                                </div>
                              )}

                              {/* this button ONLY for In_Production cards */}
                              {/* {key === 'In_Production' && !c.cancelled && (
  <button
    onClick={(e) => {
      e.stopPropagation(); // Prevent card click event
      handleMarkAsComplete(c);
    }}
    style={{
      width: '100%',
      marginTop: '8px',
      padding: '8px 12px',
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
      color: '#ffffff',
      border: 'none',
      borderRadius: '6px',
      fontSize: '13px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 4px rgba(5, 150, 105, 0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.4)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 2px 4px rgba(5, 150, 105, 0.2)';
    }}
  >
    <i className="bi bi-check-circle" style={{ fontSize: '14px' }}></i>
    <span>Mark as Complete</span>
  </button>
)} */}
                              {/* Improved Premium "Complete" Button - Only for In Production */}

                              {/* 

{key === 'In_Production' && !c.cancelled && (
  <button
    onClick={(e) => {
      e.stopPropagation();
      handleMarkAsComplete(c);
    }}
    style={{
      width: '100%',
      marginTop: '10px',
      padding: '6px 10px',
      background: '#ffffff',
      color: '#059669',
      border: '1.5px solid #059669',
      borderRadius: '5px',
      fontSize: '12px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.15s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '5px',
      letterSpacing: '0.3px'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = '#059669';
      e.currentTarget.style.color = '#ffffff';
      e.currentTarget.style.transform = 'translateY(-1px)';
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(5, 150, 105, 0.25)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = '#ffffff';
      e.currentTarget.style.color = '#059669';
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}
  >
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
    </svg>
    <span>Mark as Complete</span>
  </button>
)} */}

                              {/* <div
                                style={{
                                  marginLeft: "auto",
                                  display: "flex",
                                  gap: 6,
                                }}
                              >
                           

                                <button
                                  title="Move up"
                                  style={{
                                    ...ui.iconBtn,
                                    backgroundColor: getButtonColors(
                                      "move-up",
                                      "secondary"
                                    ).bg,
                                    color: getButtonColors(
                                      "move-up",
                                      "secondary"
                                    ).text,
                                  }}
                                  onClick={() => reorder("up", key, c.id)}
                                >
                                  ↑
                                </button>
                                <button
                                  title="Move down"
                                  style={{
                                    ...ui.iconBtn,
                                    backgroundColor: getButtonColors(
                                      "move-down",
                                      "secondary"
                                    ).bg,
                                    color: getButtonColors(
                                      "move-down",
                                      "secondary"
                                    ).text,
                                  }}
                                  onClick={() => reorder("down", key, c.id)}
                                >
                                  ↓
                                </button>
                              </div> */}
                            </div>
                            </fieldset>
                          </article>
                        );
                      })}
                      {key === "Archive" && archiveIsLoading && (
                        <div
                          role="status"
                          aria-label={archiveVisibleRows.length ? "Updating Archive" : "Loading Archive"}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            padding: "12px 8px",
                            color: "#64748b",
                            fontSize: "12px",
                          }}
                        >
                          <div className="spinner-border spinner-border-sm text-primary" aria-hidden="true" />
                          <span>{archiveVisibleRows.length ? "Updating Archive..." : "Loading Archive..."}</span>
                        </div>
                      )}
                      {key === "Archive" && archiveViewState.showSkeleton && !(filteredColumns.Archive || []).length && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "8px" }}>
                          {[1, 2].map((i) => (
                            <div
                              key={i}
                              style={{
                                backgroundColor: "#e5e7eb",
                                borderRadius: "8px",
                                height: "88px",
                                opacity: 0.75,
                                animation: "pulse 1.5s ease-in-out infinite",
                              }}
                            />
                          ))}
                        </div>
                      )}
                      {key === "Archive" && archiveViewState.showError && (
                        <div style={{ ...ui.endRow, color: "#b91c1c" }}>
                          <div>{archiveRequestState.error || "Unable to load archived projects."}</div>
                          <button type="button" onClick={() => loadArchive()}>Retry</button>
                        </div>
                      )}
                      {key === "Archive" && archiveViewState.showEmpty && !(filteredColumns.Archive || []).length && (
                        <div style={ui.endRow}>No archived projects found.</div>
                      )}
                      {key === "Archive" && archiveTotal > 0 && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, padding: "10px 0", fontSize: 12, color: "#6b7280" }}>
                          <span>{archiveHasData ? `${(archivePage - 1) * 50 + 1}-${Math.min(archivePage * 50, archiveTotal)} of ${archiveTotal}` : "..."}</span>
                          <span style={{ display: "flex", gap: 6 }}>
                            <button type="button" disabled={archivePage <= 1 || archiveIsLoading} onClick={() => setArchivePage(Math.max(1, archivePage - 1))}>Previous</button>
                            <button type="button" disabled={archivePage * 50 >= archiveTotal || archiveIsLoading} onClick={() => setArchivePage(archivePage + 1)}>Next</button>
                          </span>
                        </div>
                      )}
                      {key === "Planned" && planningLoading && (
                        <div style={ui.loadingRow}>Loading more…</div>
                      )}
                      {key === "Planned" &&
                        !planningHasMore &&
                        (columns.Planned || []).length > 0 && (
                          <div style={ui.endRow}>End of list</div>
                        )}

                      {/* ==========  ========== */}

                    </>
                  </>
                  )}
                </ProductionLaneList>
              </section>
            ))}
          </div>
        ) : (
          // New table view
          (<TableView
            columns={columns}
            onCardClick={openCard}
            onStatusChange={handleStatusChange}
            laneRequestStates={laneRequestStates}
            onRetryLane={retryLane}
          />)
        )}
        {/* Job Card popup */}
        {modalOpen && (
          <JobCardModal
            open={modalOpen}
            onClose={closeJobCard}
            card={modalCard}
            mutationPending={modalCard ? blockCardMutation(modalCard) : false}
            isMutationBlocked={() => modalCard ? blockCardMutation(modalCard) : false}
            modalLoading={modalLoading}
            detailedCard={detailedCard}
            orderMeta={orderMeta}
            orderCosting={orderCosting}
            orderCharges={orderCharges}
            onPatchCard={onPatchCard}
            onCancelCard={onCancelCard}
            onReinstateCard={onReinstateCard}
            siblingsAll={
              modalCard ? siblingsAcrossAllLanes(modalCard, columns) : []
            }
            siblingsSameLane={
              modalCard ? siblingsSameLane(modalCard, columns) : []
            }
            onOpenSplit={(card) => { if (!blockCardMutation(card)) setSplitUI({ open: true, card }); }}
            onOpenJoin={(card) => { if (!blockCardMutation(card)) setJoinUI({ open: true, card }); }}
            onNavigateDepartmentBoard={goToDepartmentBoard}
            hasNestedDialog={splitUI.open || joinUI.open || moveModal.open || partialMove.open}
          />
        )}

        {/* Move Gate popup */}
        {moveModal.open && (
          <MoveGateModal
            to={moveModal.to}
            fields={moveModal.fields}
            department={moveModal.fields?.schedulingDepartment || department} // Pass the department from URL
            saving={isSchedulingCard(moveModal.card)}
            onCancel={() => { if (!isSchedulingCard(moveModal.card)) setMoveModal({ ...moveModal, open: false }); }}
            onConfirm={(form) => confirmSchedule(form)}
          />
        )}

        {/* Split wizard */}
        {splitUI.open && (
          <SplitWizard
            open={splitUI.open}
            card={splitUI.card}
            department={department}
            onCancel={() => setSplitUI({ open: false, card: null })}
            onConfirm={async (data) => {
              if (blockCardMutation(splitUI.card)) return;
              const { rows } = data;

              try {
                const available = Math.max(
                  0,
                  Number(splitUI.card.qtyPlanned || 0) -
                    Number(splitUI.card.qtyDone || 0)
                );

                const valid = rows.filter((r) => parseInt(r.qty) > 0);
                const total = valid.reduce((t, r) => t + parseInt(r.qty), 0);

                if (total !== available) {
                  SA.error(
                    `Total must equal ${available}. Current: ${total}`,
                    "Invalid split"
                  );
                  return;
                }

                if (valid.length < 2) {
                  SA.error("Must create at least 2 batches", "Invalid split");
                  return;
                }

                const payload = {
                  step_id: splitUI.card.step_id,
                  instanceUniqueId,
                  batches: valid.map((r) => ({
                    qty: parseInt(r.qty),
                    plannedStart: r.date,
                    plannedFinish: r.date,
                    machineId: r.machineId || undefined,
                    machine: !r.machineId ? r.machine || undefined : undefined,
                  })),
                };

                const response = await fetch(
                  `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps/split`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                  }
                );

                if (!response.ok) {
                  const error = await response.json().catch(() => ({}));
                  throw new Error(error.message || `HTTP ${response.status}`);
                }

                const result = await response.json();

                if (!result.success) {
                  throw new Error(result.message || "Split failed");
                }

                //  UPDATE CARDS WITH BATCH INFO FROM API RESPONSE
                setColumns((prev) => {
                  const next = { ...prev };

                  // Create a lookup map for batch labels
                  const labelMap = {};
                  result.labels.forEach((label) => {
                    labelMap[label.step_id] = {
                      batchGroupId: result.batch_group_id,
                      batchIndex: label.batch_index,
                      batchTotal: result.batch_total,
                      batchLabel: label.batch_label,
                    };
                  });

                  // Update all cards that are part of this batch
                  Object.keys(next).forEach((status) => {
                    next[status] = next[status].map((card) => {
                      if (labelMap[card.step_id]) {
                        return {
                          ...card,
                          ...labelMap[card.step_id],
                        };
                      }
                      return card;
                    });
                  });

                  return next;
                });

                // Refresh the Scheduled lane to get new cards from backend
                await refreshSpecificLists(["Scheduled"]);

                SA.success(
                  `Split into ${result.batch_total} batches successfully.${persistedPoStageMessage(result)}`,
                  "Split Complete"
                );
              } catch (error) {
                SA.error(error.message || "Could not split", "Split failed");
              }

              setSplitUI({ open: false, card: null });
            }}
          />
        )}

        {/* Join wizard (same-lane) */}
        {joinUI.open && (
          <JoinWizard
            open={joinUI.open}
            card={joinUI.card}
            siblings={joinUI.card ? siblingsSameLane(joinUI.card, columns) : []}
            onCancel={() => setJoinUI({ open: false, card: null })}
            onConfirm={async () => {
              if (blockCardMutation(joinUI.card)) return;
              console.log("Join Debug:", {
                card: joinUI.card,
                step_id: joinUI.card?.step_id,
                mode: "all-batches",
              });

              if (!joinUI.card?.step_id) {
                SA.error("This card doesn't have a step_id.", "Cannot join");
                setJoinUI({ open: false, card: null });
                return;
              }

              try {
                // ✅ USE step_id instead of batch_group_id
                const response = await fetch(
                  `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps/join`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      step_id: joinUI.card.step_id, // ✅ Your API supports this
                      instanceUniqueId,
                    }),
                  }
                );

                if (!response.ok) {
                  const error = await response.json().catch(() => ({}));
                  throw new Error(error.message || `HTTP ${response.status}`);
                }

                const result = await response.json();
                if (!result.success) {
                  throw new Error(result.message || "Join operation failed");
                }

                await refreshSpecificLists([joinUI.card.status]);
                SA.success(`Merged all batches in the group.${persistedPoStageMessage(result)}`, "Joined");
              } catch (error) {
                console.error("Join error:", error);
                SA.error(
                  error.message || "Could not join batches.",
                  "Join failed"
                );
              }

              setJoinUI({ open: false, card: null });
            }}
          />
        )}

        {/* Partial move */}
        {partialMove.open && (
          <PartialMoveModal
            open={partialMove.open}
            from={partialMove.from}
            defaultTo={partialMove.defaultTo}
            card={partialMove.card}
            onCancel={() =>
              setPartialMove({
                open: false,
                from: null,
                card: null,
                defaultTo: "QC",
              })
            }
            onConfirm={async ({ qty, to }) => {
              if (blockCardMutation(partialMove.card)) return;

              const res = createBatchFrom(partialMove.card, qty, to);
              if (!res) {
                SA.error("Invalid quantity.", "Validation");
                return;
              }

              const { child, parentAfter } = res;

              setColumns((prev) => {
                const next = clone(prev);
                const fromLane =
                  partialMove.from ||
                  (partialMove.card?.status ?? "In_Production");
                const toLane = to;

                const fromArr = next[fromLane] || [];
                const idx = fromArr.findIndex(
                  (x) => x.id === partialMove.card.id
                );
                if (idx === -1) return prev;

                // Update/replace the parent card with the reduced qty.
                if (parentAfter.qtyPlanned > 0) {
                  fromArr[idx] = parentAfter;
                } else {
                  fromArr.splice(idx, 1);
                }

                // Insert the child into destination lane at TOP with a fresh rank.
                const inserted = assignRankTop(next[toLane], child);
                next[toLane].unshift(inserted);
                if (inserted.isStep)
                  // queueRankPatch(inserted.id, inserted.rank);

                  return next;
              });

              setPartialMove({
                open: false,
                from: null,
                card: null,
                defaultTo: "QC",
              });
              SA.success("Partial move created.", "Done");
            }}
          />
        )}

        {/* Join wizard launcher from Job Card (same-lane) */}
        {joinUI.open && null /* already rendered above with JoinWizard */}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
    Move Gate (validates inputs; backend receives ISO datetime)
    ────────────────────────────────────────────────────────────────────────── */

function MoveGateModal({ to, fields, onCancel, onConfirm, department, saving = false }) {
  const { instanceUniqueId } = useParams();

  const [form, setForm] = useState(
    fields || {
      operator: "",
      operatorName: "",
      machine: "",
      plannedStart: "",
      plannedFinish: "",
      isFirm: false,
      reason: "",
      qty: "",
    }
  );

  const [machines, setMachines] = useState([]);
  const [machinesLoading, setMachinesLoading] = useState(false);
  const [showMachineDropdown, setShowMachineDropdown] = useState(false);
  const [departmentMap, setDepartmentMap] = useState({});

  // Operator-related state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [operatorPolicy, setOperatorPolicy] = useState({
    configured: false,
    operatorIds: [],
  });
  const [operatorPolicyLoading, setOperatorPolicyLoading] = useState(false);
  const [showOperatorDropdown, setShowOperatorDropdown] = useState(false);
  const [operatorSearchQuery, setOperatorSearchQuery] = useState("");

  // Refs for click outside detection
  const operatorDropdownRef = useRef(null);
  const machineDropdownRef = useRef(null);

  const req = to === "Scheduled";
  const disabled =
    req &&
    (!form.operator ||
      !form.machine ||
      !form.plannedStart ||
      !form.plannedFinish ||
      !validScheduleDateRange(form.plannedStart, form.plannedFinish));

  // Click outside detection for operator dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        operatorDropdownRef.current &&
        !operatorDropdownRef.current.contains(event.target)
      ) {
        setShowOperatorDropdown(false);
      }
      if (
        machineDropdownRef.current &&
        !machineDropdownRef.current.contains(event.target)
      ) {
        setShowMachineDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Initialize search query with selected operator name
  useEffect(() => {
    if (form.operatorName && operatorSearchQuery === "") {
      setOperatorSearchQuery(form.operatorName);
    }
  }, [form.operatorName, operatorSearchQuery]);

  // Fetch users/operators
  // useEffect(() => {
  //   const fetchUsers = async () => {
  //     if (!instanceUniqueId) return;

  //     setUsersLoading(true);
  //     try {
  //       const response = await fetch(
  //         `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/byinstanceUniqueId/${instanceUniqueId}`,
  //         {
  //           headers: {
  //             accessToken: localStorage.getItem("accessToken"),
  //             "Content-Type": "application/json",
  //           },
  //         }
  //       );

  //       if (!response.ok) {
  //         throw new Error("Failed to fetch users");
  //       }

  //       const data = await response.json();

  //       // Filter users that could be operators
  //       const operatorUsers = (data.listOfUsers || []).filter(
  //         (user) =>
  //           user.role === "production-rep" ||
  //           user.role === "administrator" ||
  //           user.role === "order-rep"
  //       );

  //       setUsers(operatorUsers);
  //     } catch (error) {
  //       console.error("Error fetching users:", error);
  //       setUsers([]);
  //     } finally {
  //       setUsersLoading(false);
  //     }
  //   };

  //   fetchUsers();
  // }, [instanceUniqueId]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!instanceUniqueId) return;

      setUsersLoading(true);
      try {
        // NEW API - Use production-rep specific endpoint
        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/users?role=production-rep`,
          {
            headers: {
              accessToken: localStorage.getItem("accessToken"),
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch users");
        }

        const data = await response.json();

        // Set users directly from the API response - no filtering needed
        setUsers(data.users || []);
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, [instanceUniqueId]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const loadOperatorPolicy = async () => {
      if (!department || Object.keys(departmentMap).length === 0) return;
      const departmentSlug = String(department).toLowerCase().replace(/\s+/g, "-");
      const departmentId = departmentMap[departmentSlug]?.id;
      if (!departmentId) {
        // A real scheduling department that cannot be resolved must not broaden
        // the picker to every Production rep.
        setOperatorPolicy({ configured: true, operatorIds: [] });
        setOperatorPolicyLoading(false);
        return;
      }

      setOperatorPolicyLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/departments/${departmentId}/operators`,
          {
            headers: {
              accessToken: localStorage.getItem("accessToken"),
              "Content-Type": "application/json",
            },
            signal: controller.signal,
          }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success) {
          throw new Error(data.message || "Failed to load department operators");
        }
        if (!cancelled) {
          setOperatorPolicy({
            configured: Boolean(data.configured),
            operatorIds: (data.operatorIds || []).map(Number),
          });
        }
      } catch (error) {
        if (cancelled || error.name === "AbortError") return;
        console.error("Error fetching department operator policy:", error);
        if (!cancelled) {
          // Fail closed in the picker; the backend remains authoritative.
          setOperatorPolicy({ configured: true, operatorIds: [] });
        }
      } finally {
        if (!cancelled) setOperatorPolicyLoading(false);
      }
    };

    loadOperatorPolicy();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [department, departmentMap, instanceUniqueId]);

  // Create department mapping
  useEffect(() => {
    const fetchDepartmentMapping = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/departments?active=true`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch departments");
        }

        const data = await response.json();

        const mapping = {};
        data.data.forEach((dept) => {
          const slug = dept.name.toLowerCase().replace(/\s+/g, "-");
          mapping[slug] = {
            id: dept.id,
            name: dept.name,
            slug: slug,
          };
        });

        mapping["production"] = {
          id: 0,
          name: "Production Planning",
          slug: "production",
        };

        setDepartmentMap(mapping);
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };

    fetchDepartmentMapping();
  }, []);

  // Fetch machines based on department
  // useEffect(() => {
  //   const fetchMachines = async () => {
  //     if (
  //       !department ||
  //       !departmentMap ||
  //       Object.keys(departmentMap).length === 0
  //     ) {
  //       setMachines([]);
  //       return;
  //     }

  //     setMachinesLoading(true);
  //     try {
  //       let machinesData = [];

  //       if (isAggregateProductionRoute(department)) {
  //         console.log("Fetching all machines for production planning...");

  //         const response = await fetch(
  //           `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/machines/`,
  //           {
  //             headers: {
  //               accessToken: localStorage.getItem("accessToken"),
  //               "Content-Type": "application/json",
  //             },
  //           }
  //         );

  //         if (!response.ok) {
  //           throw new Error("Failed to fetch all machines");
  //         }

  //         const data = await response.json();

  //         machinesData = (data.data || []).map((machine) => ({
  //           ...machine,
  //           departmentName: machine.department,
  //           departmentId:
  //             departmentMap[
  //               machine.department?.toLowerCase()?.replace(/\s+/g, "-")
  //             ]?.id || null,
  //         }));
  //       } else {

  //         const departmentId = departmentMap[department]?.id;
  //         if (!departmentId) {
  //           setMachines([]);
  //           setMachinesLoading(false);
  //           return;
  //         }

  //         console.log(
  //           `Fetching machines for department: ${department} (ID: ${departmentId})`
  //         );

  //         const response = await fetch(
  //           `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/machines?departmentId=${departmentId}&status=&search=&limit=100&offset=0&active=true`,
  //           {
  //             headers: {
  //               accessToken: localStorage.getItem("accessToken"),
  //               "Content-Type": "application/json",
  //             },
  //           }
  //         );

  //         if (!response.ok) {
  //           throw new Error("Failed to fetch machines");
  //         }

  //         const data = await response.json();
  //         machinesData = (data.data || []).map((machine) => ({
  //           ...machine,
  //           departmentName: departmentMap[department]?.name,
  //           departmentId: departmentMap[department]?.id,
  //         }));
  //       }

  //       console.log("Fetched machines:", machinesData.length);
  //       setMachines(machinesData);
  //     } catch (error) {
  //       console.error("Error fetching machines:", error);
  //       setMachines([]);
  //     } finally {
  //       setMachinesLoading(false);
  //     }
  //   };

  //   if (Object.keys(departmentMap).length > 0) {
  //     fetchMachines();
  //   }
  // }, [department, departmentMap]);

  // Fetch machines based on department
  useEffect(() => {
    const fetchMachines = async () => {
      if (
        !department ||
        !departmentMap ||
        Object.keys(departmentMap).length === 0
      ) {
        setMachines([]);
        return;
      }

      setMachinesLoading(true);
      try {
        let machinesData = [];

        if (isAggregateProductionRoute(department)) {
          console.log("Fetching all machines for production planning...");
          const response = await fetch(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/machines?active=true&limit=100`,
            {
              headers: {
                accessToken: localStorage.getItem("accessToken"),
                "Content-Type": "application/json",
              },
            }
          );

          if (!response.ok) throw new Error("Failed to fetch all machines");
          const data = await response.json();
          machinesData = data.data.map((machine) => ({
            ...machine,
            departmentName: machine.department,
            departmentId:
              departmentMap[
                machine.department?.toLowerCase()?.replace(/\s+/g, "-")
              ]?.id || null,
          }));
        } else {
          // Convert department to slug format (replace spaces with hyphens)
          const departmentSlug = department.toLowerCase().replace(/\s+/g, "-");
          const departmentInfo = departmentMap[departmentSlug];

          if (!departmentInfo || !departmentInfo.id) {
            console.error(`No department found for: "${department}"`);
            console.error(`   Tried slug: "${departmentSlug}"`);
            console.log(
              "   Available departments in map:",
              Object.keys(departmentMap)
            );
            setMachines([]);
            setMachinesLoading(false);
            return;
          }

          const departmentId = departmentInfo.id;
          console.log(
            `Fetching machines for department: ${department}, ID: ${departmentId}`
          );

          const response = await fetch(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/machines?departmentId=${departmentId}&status=&search=&limit=100&offset=0&active=true`,
            {
              headers: {
                accessToken: localStorage.getItem("accessToken"),
                "Content-Type": "application/json",
              },
            }
          );

          if (!response.ok) throw new Error("Failed to fetch machines");
          const data = await response.json();
          console.log(`Machines fetched:`, data.data.length);

          machinesData = data.data.map((machine) => ({
            ...machine,
            departmentName: departmentInfo.name,
            departmentId: departmentInfo.id,
          }));
        }

        setMachines(machinesData);
      } catch (error) {
        console.error("Error fetching machines:", error);
        setMachines([]);
      } finally {
        setMachinesLoading(false);
      }
    };

    if (Object.keys(departmentMap).length > 0) {
      fetchMachines();
    }
  }, [department, departmentMap]);

  // Filter users based on search query
  // const filteredUsers = users.filter((user) => {
  //   if (!operatorSearchQuery) return true;

  //   const fullName = `${user.first_name || ""} ${
  //     user.last_name || ""
  //   }`.toLowerCase();
  //   const firstName = (user.first_name || "").toLowerCase();
  //   const lastName = (user.last_name || "").toLowerCase();
  //   const email = (user.email || "").toLowerCase();
  //   const query = operatorSearchQuery.toLowerCase();

  //   return (
  //     fullName.includes(query) ||
  //     firstName.includes(query) ||
  //     lastName.includes(query) ||
  //     email.includes(query)
  //   );
  // });

  const currentAssignedUserId = Number(fields?.currentAssignedUserId || 0);
  const currentAssignedUserName = fields?.currentAssignedUserName || "";
  const displayedUsers = useMemo(() => {
    const configuredOperatorIds = new Set(operatorPolicy.operatorIds.map(Number));
    const policyUsers = operatorPolicy.configured
      ? users.filter((user) => configuredOperatorIds.has(Number(user.id)))
      : users;
    const currentAssignmentIsUnavailable =
      currentAssignedUserId > 0 &&
      !policyUsers.some((user) => Number(user.id) === currentAssignedUserId);

    return currentAssignmentIsUnavailable
      ? [
          ...policyUsers,
          {
            id: currentAssignedUserId,
            name: currentAssignedUserName || `Operator #${currentAssignedUserId}`,
            currentAssignmentOnly: true,
          },
        ]
      : policyUsers;
  }, [
    users,
    operatorPolicy.configured,
    operatorPolicy.operatorIds,
    currentAssignedUserId,
    currentAssignedUserName,
  ]);

  const filteredUsers = useMemo(() => {
    if (!operatorSearchQuery) return displayedUsers;
    const query = operatorSearchQuery.toLowerCase();
    return displayedUsers.filter((user) =>
      (user.name || "").toLowerCase().includes(query)
    );
  }, [displayedUsers, operatorSearchQuery]);

  // const handleOperatorSelect = (userId, userFirstName, userLastName) => {
  //   const fullName = `${userFirstName || ""} ${userLastName || ""}`.trim();
  //   setForm({
  //     ...form,
  //     operator: userId,
  //     operatorName: fullName,
  //   });
  //   setOperatorSearchQuery(fullName);
  //   setShowOperatorDropdown(false); // Close dropdown immediately
  // };

  const handleOperatorSelect = (userId, userName) => {
    setForm(prev => ({
      ...prev,
      operator: userId,
      operatorName: userName, // Use full name from API
    }));
    setOperatorSearchQuery(userName);
    setShowOperatorDropdown(false);
  };

  const handleOperatorInputChange = (e) => {
    const value = e.target.value;
    setOperatorSearchQuery(value);
    setShowOperatorDropdown(true);

    // If input is cleared, clear the selected operator
    if (!value) {
      setForm({ ...form, operator: "", operatorName: "" });
    }
  };

  const handleOperatorClear = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    setForm({ ...form, operator: "", operatorName: "" });
    setOperatorSearchQuery("");
    setShowOperatorDropdown(false);
  };

  const handleMachineSelect = (machineId, machineName, departmentName) => {
    setForm(prev => ({
      ...prev,
      machine: machineId,
      machineName: machineName,
      machineDepartment: departmentName,
    }));
    setShowMachineDropdown(false);
  };

  const handleDropdownKeyDown = (event, kind) => {
    const operator = kind === "operator";
    const root = operator ? operatorDropdownRef.current : machineDropdownRef.current;
    const isOpen = operator ? showOperatorDropdown : showMachineDropdown;
    const setOpen = operator ? setShowOperatorDropdown : setShowMachineDropdown;
    if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      event.stopPropagation();
      root?.querySelector("input, button")?.focus();
      setOpen(false);
    } else if (["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(true);
      const options = Array.from(root?.querySelectorAll('[role="option"]:not([aria-disabled="true"])') || []);
      const current = options.indexOf(document.activeElement);
      const next = event.key === "ArrowDown" ? current + 1 : current < 0 ? options.length - 1 : current - 1;
      options[(next + options.length) % options.length]?.focus();
    } else if (["Enter", " "].includes(event.key) && event.target.getAttribute("role") === "option") {
      event.preventDefault();
      event.stopPropagation();
      event.target.click();
    }
  };

  const getSelectedMachineName = () => {
    if (!form.machine) return "Select a machine";
    const machine = machines.find((m) => String(m.id) === String(form.machine));
    if (machine) {
      return isAggregateProductionRoute(department)
        ? `${machine.name} (${machine.departmentName})`
        : machine.name;
    }
    return form.machineName || "Unknown Machine";
  };

  // Group machines by department for production planning
  const groupedMachines =
    isAggregateProductionRoute(department)
      ? machines.reduce((acc, machine) => {
          const deptName = machine.departmentName || "Unknown Department";
          if (!acc[deptName]) {
            acc[deptName] = [];
          }
          acc[deptName].push(machine);
          return acc;
        }, {})
      : null;

  return (
    <div style={modal.overlay} onClick={onCancel}>
      <div
        style={{ ...modal.card, width: "min(720px,95vw)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={modal.header}>
          <h3 style={{ margin: 0 }}>Move to {to}</h3>
          <button onClick={onCancel} style={ui.btnGhost}>
            Cancel
          </button>
        </div>

        <div
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))", gap: 12 }}
        >
          {/* Searchable Operator Dropdown */}
          <label>
            <div style={ui.subtle}>Operator *</div>
            <div style={{ position: "relative" }} ref={operatorDropdownRef}>
              <div style={{ position: "relative" }}>
                <input
                  style={ui.field}
                  value={operatorSearchQuery}
                  onChange={handleOperatorInputChange}
                  onFocus={() => setShowOperatorDropdown(true)}
                  onKeyDown={event => handleDropdownKeyDown(event, "operator")}
                  placeholder="Search operator by name..."
                  autoComplete="off"
                  role="combobox"
                  aria-expanded={showOperatorDropdown}
                  aria-controls="production-schedule-operator-options"
                  aria-label="Operator"
                />
                {operatorSearchQuery && (
                  <button
                    type="button"
                    style={{
                      position: "absolute",
                      right: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      color: "#666",
                      fontSize: "16px",
                      padding: "0",
                      width: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={handleOperatorClear}
                  >
                    ×
                  </button>
                )}
              </div>

              {showOperatorDropdown && (
                <div
                  id="production-schedule-operator-options"
                  role="listbox"
                  aria-label="Operators"
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    zIndex: 1001,
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}
                >
                  {usersLoading || operatorPolicyLoading ? (
                    <div style={{ padding: "8px 12px", color: "#666" }}>
                      Loading operators...
                    </div>
                  ) : filteredUsers.length === 0 ? (
                    <div style={{ padding: "8px 12px", color: "#666" }}>
                      {operatorSearchQuery
                        ? "No operators found"
                        : "No operators available"}
                    </div>
                  ) : (
                    <>
                      {!form.operator && (
                        <div
                          style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            borderBottom: "1px solid #eee",
                            fontStyle: "italic",
                            color: "#666",
                          }}
                          onClick={handleOperatorClear}
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor = "#f5f5f5")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor = "#fff")
                          }
                        >
                          No Operator Selected
                        </div>
                      )}
                      {/* {filteredUsers.map((user) => (
                          <div
                            key={user.id}
                            style={{
                              padding: "8px 12px",
                              cursor: "pointer",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              backgroundColor:
                                form.operator === user.id ? "#e3f2fd" : "#fff",
                            }}
                            onClick={() =>
                              handleOperatorSelect(
                                user.id,
                                user.first_name,
                                user.last_name
                              )
                            }
                            onMouseEnter={(e) => {
                              if (form.operator !== user.id) {
                                e.target.style.backgroundColor = "#f5f5f5";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (form.operator !== user.id) {
                                e.target.style.backgroundColor = "#fff";
                              }
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: "500" }}>
                                {user.first_name} {user.last_name}
                              </div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                {user.email} • {user.role.replace("-", " ")}
                              </div>
                            </div>
                          </div>
                        ))} */}

                      {filteredUsers.map((user) => (
                        <div
                          key={user.id}
                          role="option"
                          tabIndex={user.currentAssignmentOnly ? -1 : 0}
                          aria-disabled={Boolean(user.currentAssignmentOnly)}
                          aria-selected={String(form.operator) === String(user.id)}
                          onKeyDown={event => handleDropdownKeyDown(event, "operator")}
                          onMouseDown={event => event.preventDefault()}
                          style={{
                            padding: "8px 12px",
                            cursor: user.currentAssignmentOnly ? "not-allowed" : "pointer",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor:
                              form.operator === user.id ? "#e3f2fd" : "#fff",
                          }}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            if (!user.currentAssignmentOnly) {
                              handleOperatorSelect(user.id, user.name);
                            }
                          }}
                          onMouseEnter={(e) => {
                            if (form.operator !== user.id) {
                              e.target.style.backgroundColor = "#f5f5f5";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (form.operator !== user.id) {
                              e.target.style.backgroundColor = "#fff";
                            }
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: "500" }}>{user.name}</div>
                            <div style={{ fontSize: "12px", color: "#666" }}>
                              {user.currentAssignmentOnly
                                ? "Current historic assignment (display only)"
                                : `Production Rep • ID: ${user.id}`}
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </label>

          {/* Machine Dropdown */}
          <label>
            <div style={ui.subtle}>
              Machine *
              {isAggregateProductionRoute(department) && (
                <span style={{ fontSize: "12px", color: "#666" }}>
                  (All Departments)
                </span>
              )}
            </div>
            <div style={{ position: "relative" }} ref={machineDropdownRef}>
              <button
                type="button"
                style={{
                  ...ui.field,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  backgroundColor: "#fff",
                  border: "1px solid #ddd",
                }}
                aria-label="Machine"
                aria-haspopup="listbox"
                aria-expanded={showMachineDropdown}
                aria-controls="production-schedule-machine-options"
                onKeyDown={event => handleDropdownKeyDown(event, "machine")}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setShowMachineDropdown(value => !value);
                }}
              >
                <span>{getSelectedMachineName()}</span>
                <span style={{ fontSize: "12px" }}>
                  {showMachineDropdown ? "▲" : "▼"}
                </span>
              </button>

              {showMachineDropdown && (
                <div
                  id="production-schedule-machine-options"
                  role="listbox"
                  aria-label="Machines"
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                    zIndex: 1000,
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                >
                  {/* Rest of machine dropdown content remains the same */}
                  {machinesLoading ? (
                    <div style={{ padding: "8px 12px", color: "#666" }}>
                      Loading machines...
                    </div>
                  ) : machines.length === 0 ? (
                    <div style={{ padding: "8px 12px", color: "#666" }}>
                      No machines available
                    </div>
                  ) : (
                    <>
                      <div
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderBottom: "1px solid #eee",
                        }}
                        role="option"
                        tabIndex={0}
                        aria-selected={!form.machine}
                        onKeyDown={event => handleDropdownKeyDown(event, "machine")}
                        onMouseDown={event => event.preventDefault()}
                        onClick={event => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleMachineSelect("", "", "");
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor = "#f5f5f5")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                      >
                        No Machine Selected
                      </div>

                      {/* Machine options (same as before) */}
                      {isAggregateProductionRoute(department) && groupedMachines
                        ? Object.entries(groupedMachines)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([deptName, deptMachines]) => (
                              <div key={deptName}>
                                <div
                                  style={{
                                    padding: "6px 12px",
                                    backgroundColor: "#f8f9fa",
                                    fontWeight: "bold",
                                    fontSize: "14px",
                                    color: "#495057",
                                    borderBottom: "1px solid #dee2e6",
                                  }}
                                >
                                  {deptName} ({deptMachines.length} machines)
                                </div>
                                {deptMachines.map((machine) => (
                                  <div
                                    key={machine.id}
                                    role="option"
                                    tabIndex={0}
                                    aria-selected={String(form.machine) === String(machine.id)}
                                    onKeyDown={event => handleDropdownKeyDown(event, "machine")}
                                    onMouseDown={event => event.preventDefault()}
                                    style={{
                                      padding: "8px 12px",
                                      paddingLeft: "20px",
                                      cursor: "pointer",
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      backgroundColor:
                                        form.machine === machine.id
                                          ? "#e3f2fd"
                                          : "#fff",
                                    }}
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      handleMachineSelect(
                                        machine.id,
                                        machine.name,
                                        machine.departmentName
                                      );
                                    }}
                                    onMouseEnter={(e) => {
                                      if (form.machine !== machine.id) {
                                        e.target.style.backgroundColor =
                                          "#f5f5f5";
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (form.machine !== machine.id) {
                                        e.target.style.backgroundColor = "#fff";
                                      }
                                    }}
                                  >
                                    <div>
                                      <span>{machine.name}</span>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#666",
                                        }}
                                      >
                                        {machine.code} •{" "}
                                        {machine.capacityPerHour || 0}/hr
                                        {!machine.powerOn && " • Power off"}
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "8px",
                                        alignItems: "center",
                                      }}
                                    >
                                      {machine.status && (
                                        <span
                                          style={{
                                            fontSize: "12px",
                                            padding: "2px 6px",
                                            borderRadius: "12px",
                                            backgroundColor:
                                              machine.status.toLowerCase() ===
                                              "idle"
                                                ? "#fff3e0"
                                                : machine.status.toLowerCase() ===
                                                  "running"
                                                ? "#e8f5e8"
                                                : machine.status.toLowerCase() ===
                                                  "maintenance"
                                                ? "#ffebee"
                                                : "#f5f5f5",
                                            color:
                                              machine.status.toLowerCase() ===
                                              "idle"
                                                ? "#f57c00"
                                                : machine.status.toLowerCase() ===
                                                  "running"
                                                ? "#2e7d32"
                                                : machine.status.toLowerCase() ===
                                                  "maintenance"
                                                ? "#d32f2f"
                                                : "#666",
                                          }}
                                        >
                                          {machine.status}
                                        </span>
                                      )}
                                      {machine.utilization && (
                                        <span
                                          style={{
                                            fontSize: "12px",
                                            color: "#666",
                                          }}
                                        >
                                          {machine.utilization}%
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ))
                        : machines.map((machine) => (
                            <div
                              key={machine.id}
                              role="option"
                              tabIndex={0}
                              aria-selected={String(form.machine) === String(machine.id)}
                              onKeyDown={event => handleDropdownKeyDown(event, "machine")}
                              onMouseDown={event => event.preventDefault()}
                              style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                backgroundColor:
                                  form.machine === machine.id
                                    ? "#e3f2fd"
                                    : "#fff",
                              }}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleMachineSelect(
                                  machine.id,
                                  machine.name,
                                  machine.departmentName
                                );
                              }}
                              onMouseEnter={(e) => {
                                if (form.machine !== machine.id) {
                                  e.target.style.backgroundColor = "#f5f5f5";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (form.machine !== machine.id) {
                                  e.target.style.backgroundColor = "#fff";
                                }
                              }}
                            >
                              <div>
                                <span>{machine.name}</span>
                                <div
                                  style={{ fontSize: "12px", color: "#666" }}
                                >
                                  {machine.code} •{" "}
                                  {machine.capacityPerHour || 0}/hr
                                        {!machine.powerOn && " • Power off"}
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "8px",
                                  alignItems: "center",
                                }}
                              >
                                {machine.status && (
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      padding: "2px 6px",
                                      borderRadius: "12px",
                                      backgroundColor:
                                        machine.status.toLowerCase() === "idle"
                                          ? "#fff3e0"
                                          : machine.status.toLowerCase() ===
                                            "running"
                                          ? "#e8f5e8"
                                          : machine.status.toLowerCase() ===
                                            "maintenance"
                                          ? "#ffebee"
                                          : "#f5f5f5",
                                      color:
                                        machine.status.toLowerCase() === "idle"
                                          ? "#f57c00"
                                          : machine.status.toLowerCase() ===
                                            "running"
                                          ? "#2e7d32"
                                          : machine.status.toLowerCase() ===
                                            "maintenance"
                                          ? "#d32f2f"
                                          : "#666",
                                    }}
                                  >
                                    {machine.status}
                                  </span>
                                )}
                                {machine.utilization && (
                                  <span
                                    style={{ fontSize: "12px", color: "#666" }}
                                  >
                                    {machine.utilization}%
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </label>

          <label>
            <div style={ui.subtle}>Planned Start *</div>
            <input
              style={ui.field}
              type="date"
              value={scheduleDateValue(form.plannedStart)}
              onChange={(e) => {
                const start = e.target.value;
                setForm(prev => ({ ...prev, plannedStart: start, plannedFinish: start }));
              }}
            />
          </label>

          <label>
            <div style={ui.subtle}>Planned Finish *</div>
            <input
              style={ui.field}
              type="date"
              value={scheduleDateValue(form.plannedFinish)}
              min={scheduleDateValue(form.plannedStart) || undefined}
              onChange={(e) => {
                const finish = e.target.value;
                setForm(prev => ({ ...prev, plannedFinish: finish && finish < scheduleDateValue(prev.plannedStart)
                  ? scheduleDateValue(prev.plannedStart) : finish }));
              }}
            />
          </label>

          {/* Read-only Quantity field */}
          <label>
            <div style={ui.subtle}>Quantity</div>
            <input
              style={{
                ...ui.field,
                backgroundColor: "#f9fafb",
                color: "#6b7280",
                cursor: "not-allowed",
              }}
              type="text"
              value={form.qty}
              readOnly
              placeholder="Quantity from card"
            />
          </label>

          {to === "Scheduled" && (
            <label style={{ minWidth: 0, cursor: "pointer" }}>
              <div style={ui.subtle}>Firm deadline</div>
              <div
                style={{
                  ...ui.field,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  minHeight: 40,
                  boxSizing: "border-box",
                }}
              >
                <input
                  type="checkbox"
                  style={{ margin: 0, width: 16, height: 16, flexShrink: 0 }}
                  checked={!!form.isFirm}
                  onChange={(e) =>
                    setForm({ ...form, isFirm: e.target.checked })
                  }
                />
                <span style={{ whiteSpace: "normal", lineHeight: 1.35 }}>Must finish by planned end date</span>
              </div>
            </label>
          )}

          <label style={{ gridColumn: "1/-1" }}>
            <div style={ui.subtle}>Reason (optional)</div>
            <input
              style={ui.field}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </label>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button
            disabled={disabled || saving}
            onClick={() => { if (!saving) onConfirm(form); }}
            style={{ ...ui.btnPrimary, opacity: disabled || saving ? 0.6 : 1 }}
          >
            {saving ? "Scheduling… Saving" : "Confirm Move"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
    Table cell styles for small inline tables
    ────────────────────────────────────────────────────────────────────────── */
const th = {
  textAlign: "left",
  padding: "6px 8px",
  borderBottom: "1px solid #eee",
};
const td = { padding: "6px 8px" };

/* ──────────────────────────────────────────────────────────────────────────
    Styles (clearer inputs; SweetAlert-friendly look)
    ────────────────────────────────────────────────────────────────────────── */
const ui = {
  wrap: { padding: "16 0", overflowX: "auto" },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  board: {
    display: "flex",
    //  height: "calc(100vh - 120px)", // Full height minus header
    gap: 16,
    minWidth: "1800px",
    minHeight: "1000px", //-------------------------------------new
  },
  column: {
    flex: "0 0 340px",
    background: "#f8f9fb",
    border: "1px solid #eef0f5",
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    // maxHeight: "calc(100vh - 160px)", ///------------------------
    minHeight: "1000px",
  },
  colHeader: {
    padding: 12,
    borderBottom: "1px solid #eef0f5",
    background: "#f1f5f9",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  cardList: {
    padding: 10,
    overflowY: "auto",
    flex: 1,

    //  height: 700, // Much taller fixed height
    //  height: "calc(100vh - 180px)", // Full viewport minus header space
  },
  card: {
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 10,
    padding: 10,
    marginBottom: 10,
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },
  cardCancelled: { opacity: 0.6, filter: "grayscale(.25)" },

  // Clear, visible inputs (requested)
  field: {
    width: "100%",
    padding: "8px 10px",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    outline: "none",
    background: "#fff",
    fontSize: 13,
    color: "#111827",
    boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
  },

  linkBtn: {
    border: "1px solid #ccc",
    borderRadius: 6,
    padding: "4px 8px",
    fontSize: 12,
    cursor: "pointer",
    background: "#fff",
  },
  iconBtn: {
    border: "1px solid #ccc",
    borderRadius: 6,
    padding: "2px 6px",
    fontSize: 12,
    cursor: "pointer",
    background: "#fff",
  },
  btnGhost: {
    border: "1px solid #ccc",
    background: "#fff",
    borderRadius: 8,
    padding: "6px 10px",
    cursor: "pointer",
  },
  btnPrimary: {
    border: "1px solid #2563eb",
    background: "#3b82f6",
    color: "#fff",
    borderRadius: 8,
    padding: "6px 10px",
    cursor: "pointer",
  },
  btnDanger: {
    border: "1px solid #fecaca",
    background: "#fee2e2",
    color: "#991b1b",
    borderRadius: 8,
    padding: "6px 10px",
    cursor: "pointer",
  },
  btnWarn: {
    border: "1px solid #fbbf24",
    background: "#fef3c7",
    color: "#92400e",
    borderRadius: 8,
    padding: "6px 10px",
    cursor: "pointer",
  },
  pill: {
    border: "1px solid #dbeafe",
    background: "#eff6ff",
    color: "#1d4ed8",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 12,
  },
  errPill: {
    border: "1px solid #fecaca",
    background: "#fee2e2",
    color: "#991b1b",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 12,
  },
  badge: {
    background: "#eef7ff",
    color: "#175cd3",
    border: "1px solid #d6e8ff",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  overduePill: {
    marginLeft: 6,
    border: "1px solid #fecaca",
    background: "#fee2e2",
    color: "#991b1b",
    borderRadius: 999,
    padding: "2px 6px",
    fontSize: 11,
  },
  statusPill: {
    marginLeft: 6,
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
    color: "#111827",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 12,
  },
  cancelBadge: {
    border: "1px solid #fecaca",
    background: "#fee2e2",
    color: "#991b1b",
    borderRadius: 6,
    padding: "2px 6px",
    fontSize: 12,
  },
  subtle: { fontSize: 12, color: "#667" },
  label: { fontSize: 12, color: "#666" },
  value: { fontSize: 14, fontWeight: 500 },
  chip: {
    border: "1px solid #ddd",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 12,
    background: "#fff",
  },
  section: {
    border: "1px solid #eee",
    borderRadius: 12,
    padding: 12,
    marginTop: 10,
    // height: "1600px",//-----------------------------
    // minHeight: "1000px",
  },
  rowCard: {
    display: "grid",
    gridTemplateColumns:
      "minmax(36px,60px) 1.2fr 1fr 1fr 1fr minmax(90px,120px) minmax(120px,160px) auto",
    gap: 8,
    padding: 10,
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    background: "#fff",
  },
  empty: { padding: 12, color: "#666", fontStyle: "italic" },
  loadingRow: { padding: 8, textAlign: "center", color: "#555" },
  endRow: { padding: 8, textAlign: "center", color: "#888" },
};

const modal = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  card: {
    background: "#fff",
    borderRadius: 12,
    padding: 20,
    width: "85vw", // 👈 always 85% of viewport width
    // height: "85vh", // 👈 always 85% of viewport height
    height: "auto",
    maxWidth: "1200px", // optional safeguard for very large screens
    maxHeight: "95vh", // avoid overflow on very small screens
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 10,
  },
};

const sectionCard = {
  background: "#fff", // white background
  border: "1px solid #e5e7eb", // light border
  borderRadius: 12, // rounded corners
  padding: "16px 20px", // internal spacing
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)", // subtle shadow for depth
  display: "flex", // flex layout inside section
  flexDirection: "column", // stack content vertically
  gap: 8, // spacing between items
};

const tabs = {
  wrap: {
    display: "flex",
    gap: 6,
    padding: "8px 12px",
    borderBottom: "1px solid #eee",
  },
  tab: {
    border: "1px solid #ddd",
    background: "#fff",
    borderRadius: 999,
    padding: "6px 10px",
    fontSize: 12,
    cursor: "pointer",
  },
  active: { background: "#eef7ff", borderColor: "#c7e1ff", color: "#175cd3" },
};

const chatStyles = {
  box: {
    border: "1px solid #eee",
    borderRadius: 8,
    padding: 8,
    maxHeight: 280,
    overflowY: "auto",
    background: "#fff",
  },
  msg: { borderBottom: "1px dashed #eee", padding: "6px 2px" },
  meta: { fontSize: 12, color: "#666" },
};

const capStyles = {
  track: {
    width: "100%",
    height: 8,
    background: "#e5e7eb",
    borderRadius: 999,
    overflow: "hidden",
  },
  fill: { height: "100%", transition: "width .2s ease" },
};
